import React from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Avatar,
  AvatarGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  TableSortLabel,
  Checkbox,
  Autocomplete,
  Backdrop,
  Divider,
  Drawer,
} from '@mui/material';
import { 
  Search,
  MoreVert,
  Visibility,
  Edit,
  Add,
  CheckCircle,
  DoNotDisturbOn,
  ErrorOutline,
  PendingActions,
  KeyboardArrowRight,
  KeyboardArrowDown,
  HelpOutline,
  InfoOutlined,
  ReportProblem,
  ContentCopy,
  DragIndicator,
} from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import Close from '@mui/icons-material/Close';
import { createTransferInvoice, listTransferInvoices, quickApproveInvoice, syncInvoicesBookedFromMssql, listSettlements, settleInvoice, unsettleInvoice, reconcileSettlement, unreconcileSettlement, loadReconciliationSuggestions, saveReconciliationSuggestions, deleteReconciliationSuggestion, listReconciliationLinks, addReconciliationLink, removeReconciliationLink, type SettlementRow } from '../services/invoicesService';
import { getAllProjects } from '../services/projectService';
import { listUsersForBusinessForms, type AppUser } from '../services/userService';
import { Menu } from '@mui/material';
import { getSocket } from '../services/realtimeService';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../components/layouts/Layout';
import KsefSettingsDrawer from '../components/invoices/KsefSettingsDrawer';
import SettleInvoiceDrawer, { type SettleInvoiceData } from '../components/invoices/SettleInvoiceDrawer';
import { checkKsefConnection, listKsefInvoices, getKsefInvoice, syncKsefInvoices, type KsefConnectionStatus, type KsefInvoiceListItem } from '../services/ksefService';
import api, { API_BASE_URL } from '../services/api';
import { listBankOperations, exportMbank, updateBankOperationStatus, type BankOperation } from '../services/bankOperationsService';
import { listBankStatements, listBankStatementTransactions, importMT940, deleteBankStatement, type BankStatement, type BankStatementTransaction } from '../services/bankStatementsService';
import { listBankAccounts, formatAccountNumber, shortenAccountNumber, accountParts, type BankAccount } from '../services/bankAccountService';
import { listWarehouseStock, listWarehouseDocuments, listWarehouses, addInvoiceToWarehouse, type WarehouseStockRow, type WarehouseDocumentRow, type Warehouse } from '../services/warehouseService';
import AddWarehouseDialog from '../components/invoices/AddWarehouseDialog';

type Invoice = {
  id: string;
  invoiceDate: string; // ISO YYYY-MM-DD
  createdAt: string; // ISO
  project: string;
  contractor: string;
  netAmount: number;
  grossAmount: number;
  dueDate: string; // ISO
  paid: boolean;
  requester: { name: string; color: string };
  approvers: Array<{ name: string; color: string }>;
  approvalStatus: 'in_progress' | 'approved' | 'rejected' | 'needs_info';
};

const currency = (value: number) =>
  new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN', maximumFractionDigits: 2 }).format(value);

const renderMainTabBadge = (count: number | null | undefined) => {
  if (count === null || count === undefined || count <= 0) return null;
  const isDoubleDigit = count > 9;
  const size = isDoubleDigit ? 18 : 16;
  return (
    <Box
      component="span"
      sx={{
        position: 'absolute',
        top: -8,
        right: -4,
        minWidth: size,
        height: size,
        px: isDoubleDigit ? 0.5 : 0,
        borderRadius: '999px',
        backgroundColor: '#D92D20',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Roboto, sans-serif',
        fontSize: isDoubleDigit ? 10 : 9,
        fontWeight: 500,
        lineHeight: 1,
        boxShadow: '0 0 0 2px #FAFAFA',
        pointerEvents: 'none'
      }}
    >
      {count}
    </Box>
  );
};

function monthOptions(count = 18) {
  const now = new Date();
  const items: { key: string; label: string }[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('pl-PL', { year: 'numeric', month: 'long' });
    items.push({ key, label });
  }
  return items;
}

const headerCellSx: any = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  backgroundColor: '#FAFAFA',
  borderRight: '1px solid #d4d4d9',
  borderRadius: 0,
  boxShadow: 'none',
  whiteSpace: 'nowrap'
};

// Sortowanie – ikona i style jak w tabeli „Projekty”
const sortLabelBaseSx = {
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  color: '#2D2D32',
  display: 'inline-flex',
  alignItems: 'center',
  flexDirection: 'row',
  gap: '4px',
  '& .MuiTableSortLabel-icon': {
    opacity: 1,
    width: 12,
    height: 12,
    ml: '4px'
  }
} as const;
const ActiveSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#2D2D32' }} />);
const MutedSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#d4d4d9' }} />);
const getSortLabelProps = (isActive: boolean) => ({
  IconComponent: (isActive ? ActiveSortIcon : MutedSortIcon) as any,
  sx: { 
    ...sortLabelBaseSx, 
    '& .MuiTableSortLabel-icon': { 
      ...(sortLabelBaseSx as any)['& .MuiTableSortLabel-icon'], 
      color: isActive ? '#2D2D32' : '#d4d4d9' 
    } 
  }
}) as const;

// Mapowanie sekcji ↔ URL slug
const SECTION_SLUGS = ['sales', 'warehouses', 'purchases', 'other-expenses', 'employee-payouts', 'settlements', 'bank-operations', 'bank-statements', 'hermes-export', 'taxes-zus'] as const;
const SECTION_DISPLAY_ORDER = [0, 1, 2, 3, 4, 9, 5, 6, 7, 8];
const slugToSection = (slug: string | undefined): number => {
  if (!slug) return 2; // domyślnie Zakupy
  const idx = (SECTION_SLUGS as readonly string[]).indexOf(slug);
  return idx >= 0 ? idx : 2;
};

const InvoicesPage: React.FC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const location = useLocation();
  const navigate = useNavigate();
  // Zakładki główne (w stylu strony projektu)
  // 0=KSEF, 1=Faktury przelewowe, 2=Faktury karta, 3=Faktury gotówkowe, 4=Zaliczki, 5=Korekty, 6=Wszystkie
  // Ustawiamy domyślnie „Faktury przelewowe”, żeby zachować dotychczasowe zachowanie strony.
  const [mainTab, setMainTab] = React.useState(1);
  const [tab, setTab] = React.useState(0);
  const [query, setQuery] = React.useState('');
  const [projectFilter, setProjectFilter] = React.useState('');
  const [requesterFilter, setRequesterFilter] = React.useState('');
  const [monthFilter, setMonthFilter] = React.useState('');
  const [addOpen, setAddOpen] = React.useState(false);
  const [selectedPdf, setSelectedPdf] = React.useState<File | null>(null);
  // Excel-style podświetlenie wiersza (jednorazowy wybór). Klucz wiersza
  // odpowiada propowi `key` użytemu w `<TableRow>`, np. `all-123`,
  // `cash-456`, lub `ksef:abc-def`. Dzięki temu selekcja jest unikalna
  // pomiędzy różnymi tabelkami w sekcji „Zakupy".
  const [selectedRowKey, setSelectedRowKey] = React.useState<string | null>(null);
  // Mutowalna kopia – używamy jej w handlerze keydown, żeby kolejne klawisze
  // przytrzymania (key-repeat) widziały NAJNOWSZĄ wartość synchronicznie,
  // bez czekania na React batching. To samo rozwiązanie zastosowane w
  // arkuszu pozycji EasyEstimate (sheetCursorRef + flushSync).
  const selectedRowKeyRef = React.useRef<string | null>(null);
  React.useEffect(() => { selectedRowKeyRef.current = selectedRowKey; }, [selectedRowKey]);
  // Funkcja zwracająca styl Excel-like ramki wiersza tylko gdy zaznaczony.
  const getRowSelectionSx = React.useCallback((selected: boolean) => {
    if (!selected) return {};
    const blue = '#4040DB';
    const blueBg = '#DCE5FF';
    return {
      '& .MuiTableCell-root': {
        backgroundColor: `${blueBg} !important`,
        borderTop: `2px solid ${blue}`,
        borderBottom: `2px solid ${blue}`,
      },
      '& .MuiTableCell-root:first-of-type': {
        borderLeft: `2px solid ${blue}`,
      },
      '& .MuiTableCell-root:last-of-type': {
        borderRight: `2px solid ${blue}`,
      },
      '&:hover .MuiTableCell-root': {
        backgroundColor: `${blueBg} !important`,
      },
    };
  }, []);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewInvoice, setPreviewInvoice] = React.useState<any | null>(null);
  const [newComment, setNewComment] = React.useState('');
  const [editOpen, setEditOpen] = React.useState(false);
  const [ksefSettingsOpen, setKsefSettingsOpen] = React.useState(false);
  const [ksefConnectionStatus, setKsefConnectionStatus] = React.useState<KsefConnectionStatus | null>(null);
  const [ksefStatusTab, setKsefStatusTab] = React.useState<'all' | 'approved' | 'rejected' | 'pending'>('pending'); // all=Wszystkie, approved=Zatwierdzone, rejected=Odrzucone, pending=Do weryfikacji
  const [ksefApprovals, setKsefApprovals] = React.useState<'all' | 'approved' | 'unapproved'>('all'); // Tylko dla zakładki "Wszystkie"
  const [ksefQuery, setKsefQuery] = React.useState('');
  const [ksefMonth, setKsefMonth] = React.useState('');
  const [ksefDateFrom, setKsefDateFrom] = React.useState(''); // YYYY-MM-DD
  const [ksefDateTo, setKsefDateTo] = React.useState(''); // YYYY-MM-DD
  const [ksefProjectFilter, setKsefProjectFilter] = React.useState('');
  const [ksefRows, setKsefRows] = React.useState<KsefInvoiceListItem[]>([]);
  // Filtr „Płatność" w zakładce KSeF (Zakupy). Pusty = wszystkie.
  // Filtruje po polu inv.payment_method (case-insensitive, contains).
  const [ksefPaymentMethod, setKsefPaymentMethod] = React.useState<string>('');
  const [ksefLoading, setKsefLoading] = React.useState(false);
  const [ksefError, setKsefError] = React.useState<string | null>(null);
  const [ksefRefreshKey, setKsefRefreshKey] = React.useState(0); // Klucz do wymuszenia re-renderu tabeli
  const [ksefProjects, setKsefProjects] = React.useState<Array<{ project_id: string; project_name: string }>>([]);
  const [ksefLoadingProjects, setKsefLoadingProjects] = React.useState(false);
  // Sekcja: 0=Faktury sprzedaży, 1=Magazyny, 2=Faktury zakupu (domyślnie), 3=Inne wydatki (osobna strona), 4=Wypłaty pracownicze (osobna strona), 5=Rozrachunki, 6=Operacje bankowe, 7=Wyciągi i uzgodnienia, 8=Eksport do Hermesu, 9=Podatki i ZUS (osobna strona)
  // Synchronizacja z URL: /accounting/sales, /accounting/warehouses, itd.
  const currentSlug = location.pathname.replace(/^\/accounting\/?/, '').split('/')[0] || '';
  const sectionChip = slugToSection(currentSlug);
  /** Zakupy + rola „user”: brak wyboru wnioskującego – zawsze tylko własne faktury (oprócz zakładki KSEF – osobna lista). */
  const isPurchasesBasicUser = sectionChip === 2 && String(user?.role || '').toLowerCase() === 'user';
  const purchasesApplicantUserId = String((user as any)?.user_id || (user as any)?.id || '');
  const purchasesInvoicesFilterGrid = isPurchasesBasicUser ? '300px 200px 200px 1fr' : '300px 300px 200px 200px 1fr';
  const setSectionChip = React.useCallback((idx: number) => {
    const slug = SECTION_SLUGS[idx] || 'purchases';
    navigate(`/accounting/${slug}`, { replace: true });
  }, [navigate]);
  const [sectionMenuAnchorEl, setSectionMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const sectionChipRef = React.useRef<HTMLDivElement>(null);
  const SECTION_CHIPS = ['Faktury sprzedaży', 'Magazyny', 'Faktury zakupu', 'Inne wydatki', 'Wypłaty pracownicze', 'Rozrachunki', 'Operacje bankowe', 'Wyciągi i uzgodnienia', 'Eksport do Hermesu', 'Podatki i ZUS'];
  const SECTION_COLORS = ['#B2DFDB', '#FFE0B2', '#FEEED5', '#FCE4EC', '#E8EAF6', '#E6F0FF', '#E6F5E6', '#FFF3E0', '#F3E5F5', '#DCEDC8']; // turkusowy, pomarańczowy, beżowy, różowy, indygo, niebieski, zielony, jasnopomarańczowy, fioletowy, jasnozielony

  // Reset selekcji wiersza przy zmianie sekcji.
  React.useEffect(() => { setSelectedRowKey(null); }, [sectionChip]);

  // === SEKCJA SPRZEDAŻ (sectionChip === 0) ===
  const [salesMainTab, setSalesMainTab] = React.useState(0); // 0=KSEF, 1=Faktury Sprzedażowe
  const [salesTab, setSalesTab] = React.useState(0); // 0=Wszystkie, 1=Opłacone, 2=Nieopłacone
  const [salesQuery, setSalesQuery] = React.useState('');
  const [salesProjectFilter, setSalesProjectFilter] = React.useState('');
  const [salesMonthFilter, setSalesMonthFilter] = React.useState('');
  const [salesDateFrom, setSalesDateFrom] = React.useState('');
  const [salesDateTo, setSalesDateTo] = React.useState('');
  const salesDateFromRef = React.useRef<HTMLInputElement | null>(null);
  const salesDateToRef = React.useRef<HTMLInputElement | null>(null);

  // Dane faktur sprzedażowych (tabele mysql dorobimy później)
  const [salesRows] = React.useState<any[]>([]);

  // === KSEF w sekcji Sprzedaż ===
  const [salesKsefStatusTab, setSalesKsefStatusTab] = React.useState<'all' | 'approved' | 'rejected' | 'pending'>('pending');
  const [salesKsefApprovals, setSalesKsefApprovals] = React.useState<'all' | 'approved' | 'unapproved'>('all');
  const [salesKsefQuery, setSalesKsefQuery] = React.useState('');
  const [salesKsefMonth, setSalesKsefMonth] = React.useState('');
  const [salesKsefDateFrom, setSalesKsefDateFrom] = React.useState('');
  const [salesKsefDateTo, setSalesKsefDateTo] = React.useState('');
  const [salesKsefProjectFilter, setSalesKsefProjectFilter] = React.useState('');
  const salesKsefDateFromRef = React.useRef<HTMLInputElement | null>(null);
  const salesKsefDateToRef = React.useRef<HTMLInputElement | null>(null);
  const [salesKsefRows] = React.useState<KsefInvoiceListItem[]>([]);
  const [salesKsefLoading] = React.useState(false);
  const [salesKsefError] = React.useState<string | null>(null);

  // Liczniki KSEF Sprzedaż
  const salesKsefTabCounts = React.useMemo(() => {
    const filtered = salesKsefRows.filter((inv) => {
      if (salesKsefQuery) {
        const q = salesKsefQuery.toLowerCase();
        const matches =
          (inv.invoice_number || '').toLowerCase().includes(q) ||
          (inv.seller_name || '').toLowerCase().includes(q) ||
          (inv.seller_tax_id || '').toLowerCase().includes(q);
        if (!matches) return false;
      }
      if (salesKsefMonth && inv.issue_date) {
        const invoiceMonth = String(inv.issue_date).slice(0, 7);
        if (invoiceMonth !== salesKsefMonth) return false;
      }
      return true;
    });
    const invoiceStatus = (inv: KsefInvoiceListItem) => inv.status || 'Do weryfikacji';
    const allFiltered = salesKsefApprovals === 'all'
      ? filtered
      : salesKsefApprovals === 'approved'
        ? filtered.filter(inv => invoiceStatus(inv) === 'Zatwierdzona')
        : filtered.filter(inv => invoiceStatus(inv) !== 'Zatwierdzona');
    return {
      pending: filtered.filter(inv => invoiceStatus(inv) === 'Do weryfikacji').length,
      all: allFiltered.length,
      approved: filtered.filter(inv => invoiceStatus(inv) === 'Zatwierdzona').length,
      rejected: filtered.filter(inv => invoiceStatus(inv) === 'Odrzucona').length,
    };
  }, [salesKsefRows, salesKsefQuery, salesKsefMonth, salesKsefApprovals]);

  // Kolumny KSEF Sprzedaż
  const DEFAULT_VIS_SALES_KSEF: Record<string, boolean> = React.useMemo(() => ({
    dates: true, invoiceType: true, invoiceNumber: true, payment: true,
    seller: true, nip: true, net: true, vatRate: true, vatAmount: true,
    gross: true, status: true, actions: true
  }), []);
  const VISIBLE_KEY_SALES_KSEF = React.useMemo(() => `salesKsef.columns.visible:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnsVisibleSalesKsef, setColumnsVisibleSalesKsef] = React.useState<Record<string, boolean>>(() => {
    try { const raw = localStorage.getItem(VISIBLE_KEY_SALES_KSEF); if (raw) return JSON.parse(raw); } catch {}
    return { ...DEFAULT_VIS_SALES_KSEF };
  });
  const colVSalesKsef = React.useCallback((key: string) => columnsVisibleSalesKsef[key] !== false, [columnsVisibleSalesKsef]);

  const COLUMNS_ORDER_DEFAULT_SALES_KSEF: string[] = ['dates', 'invoiceType', 'invoiceNumber', 'payment', 'seller', 'nip', 'net', 'vatRate', 'vatAmount', 'gross', 'status', 'actions'];
  const ORDER_KEY_SALES_KSEF = React.useMemo(() => `salesKsef.columns.order:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnOrderSalesKsef, setColumnOrderSalesKsef] = React.useState<string[]>(() => {
    try { const raw = localStorage.getItem(ORDER_KEY_SALES_KSEF); if (raw) return JSON.parse(raw); } catch {}
    return [...COLUMNS_ORDER_DEFAULT_SALES_KSEF];
  });

  const [colsAnchorElSalesKsef, setColsAnchorElSalesKsef] = React.useState<null | HTMLElement>(null);
  const colsOpenSalesKsef = Boolean(colsAnchorElSalesKsef);
  const [draftColumnsVisibleSalesKsef, setDraftColumnsVisibleSalesKsef] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrderSalesKsef, setDraftOrderSalesKsef] = React.useState<string[] | null>(null);

  const defaultColumnWidthsSalesKsef = React.useMemo<Record<string, number>>(() => ({
    dates: 150, invoiceType: 100, invoiceNumber: 120, payment: 120,
    seller: 200, nip: 130, net: 120, vatRate: 100, vatAmount: 100,
    gross: 120, status: 100, actions: 120,
  }), []);
  const STORAGE_KEY_SALES_KSEF = React.useMemo(() => `salesKsefTableColWidths:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnWidthsSalesKsef, setColumnWidthsSalesKsef] = React.useState<Record<string, number>>(defaultColumnWidthsSalesKsef);
  const getColWSalesKsef = (key: string) => `${columnWidthsSalesKsef[key] ?? defaultColumnWidthsSalesKsef[key] ?? 80}px`;

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_SALES_KSEF);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') setColumnWidthsSalesKsef((prev) => ({ ...prev, ...parsed }));
      }
    } catch {}
  }, [STORAGE_KEY_SALES_KSEF]);

  React.useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_SALES_KSEF, JSON.stringify(columnWidthsSalesKsef)); } catch {}
  }, [STORAGE_KEY_SALES_KSEF, columnWidthsSalesKsef]);

  // === SEKCJA MAGAZYNY (sectionChip === 1) ===
  const [whMainTab, setWhMainTab] = React.useState(0); // 0=Stany, 1=PZ, 2=WZ, 3=WW, 4=MM
  const [whQuery, setWhQuery] = React.useState('');
  const [whProjectFilter, setWhProjectFilter] = React.useState('');
  const [whWarehouseFilter, setWhWarehouseFilter] = React.useState('');
  const [whMonthFilter, setWhMonthFilter] = React.useState('');
  const [whDateFrom, setWhDateFrom] = React.useState('');
  const [whDateTo, setWhDateTo] = React.useState('');
  const whDateFromRef = React.useRef<HTMLInputElement | null>(null);
  const whDateToRef = React.useRef<HTMLInputElement | null>(null);
  const [whStockRows, setWhStockRows] = React.useState<WarehouseStockRow[]>([]);
  const [whDocRows, setWhDocRows] = React.useState<WarehouseDocumentRow[]>([]);
  const [addWarehouseOpen, setAddWarehouseOpen] = React.useState(false);
  const [warehousesList, setWarehousesList] = React.useState<Warehouse[]>([]);
  const [whLoading, setWhLoading] = React.useState(false);

  const WH_TABS = ['Stany magazynowe', 'Przyjęcia zewnętrzne', 'Wydania zewnętrzne', 'Wydania wewnętrzne', 'Przesunięcia międzymagazynowe'];
  const WH_DOC_TYPES = ['', 'PZ', 'WZ', 'WW', 'MM'] as const;

  const refreshWarehouse = React.useCallback(async () => {
    setWhLoading(true);
    try {
      const filters = {
        search: whQuery || undefined,
        project_id: whProjectFilter || undefined,
        warehouse_code: whWarehouseFilter || undefined,
        month: whMonthFilter || undefined,
        date_from: whDateFrom || undefined,
        date_to: whDateTo || undefined,
      };
      if (whMainTab === 0) {
        const rows = await listWarehouseStock(filters);
        setWhStockRows(rows);
      } else {
        const docType = WH_DOC_TYPES[whMainTab];
        const rows = await listWarehouseDocuments({ ...filters, document_type: docType });
        setWhDocRows(rows);
      }
    } catch (err) {
      console.error('[Warehouse] refresh error:', err);
    } finally {
      setWhLoading(false);
    }
  }, [whMainTab, whQuery, whProjectFilter, whWarehouseFilter, whMonthFilter, whDateFrom, whDateTo]);

  React.useEffect(() => {
    if (sectionChip === 1) {
      refreshWarehouse();
    }
  }, [sectionChip, refreshWarehouse]);

  const loadWarehouses = React.useCallback(async () => {
    try {
      const w = await listWarehouses();
      setWarehousesList(w || []);
    } catch {
      setWarehousesList([]);
    }
  }, []);

  React.useEffect(() => {
    if (sectionChip === 1 || sectionChip === 2) {
      loadWarehouses();
    }
  }, [sectionChip, loadWarehouses]);

  // Sekcje 3 (Inne wydatki), 4 (Wypłaty pracownicze), 9 (Podatki i ZUS) → osobne strony

  // === SEKCJA ROZRACHUNKI (sectionChip === 5) ===
  const [settlementsMainTab, setSettlementsMainTab] = React.useState(0); // 0=Zakupy, 1=Sprzedaż
  const [settlementsTab, setSettlementsTab] = React.useState(0); // 0=Nierozliczone, 1=Rozliczone, 2=Wszystkie
  const [settlementsQuery, setSettlementsQuery] = React.useState('');
  const [settlementsProjectFilter, setSettlementsProjectFilter] = React.useState('');
  const [settlementsMonthFilter, setSettlementsMonthFilter] = React.useState('');
  const [settlementsDateFrom, setSettlementsDateFrom] = React.useState('');
  const [settlementsDateTo, setSettlementsDateTo] = React.useState('');
  const settlementsDateFromRef = React.useRef<HTMLInputElement | null>(null);
  const settlementsDateToRef = React.useRef<HTMLInputElement | null>(null);

  // Dane rozrachunków
  const [settlementsRows, setSettlementsRows] = React.useState<SettlementRow[]>([]);
  const [settlementsLoading, setSettlementsLoading] = React.useState(false);

  // Kebab menu rozrachunków
  const [settleMenuRow, setSettleMenuRow] = React.useState<any>(null);
  const [settleMenuAnchor, setSettleMenuAnchor] = React.useState<null | HTMLElement>(null);

  // Drawer rozliczania faktury
  const [settleDrawerOpen, setSettleDrawerOpen] = React.useState(false);
  const [settleDrawerData, setSettleDrawerData] = React.useState<SettleInvoiceData | null>(null);
  const [settleDrawerViewMode, setSettleDrawerViewMode] = React.useState(false);

  // Zaznaczanie wierszy rozrachunków (Google Sheets style)
  const [settleSelectedIds, setSettleSelectedIds] = React.useState<Set<number | string>>(new Set());
  const [settleLastClickedIdx, setSettleLastClickedIdx] = React.useState<number | null>(null);
  const settleRowsRef = React.useRef<any[]>([]);

  // Paginacja rozrachunków (lazy scroll)
  const SETTLE_PAGE_CHUNK = 60;
  const [settleVisibleCount, setSettleVisibleCount] = React.useState(SETTLE_PAGE_CHUNK);

  // Wyczyść zaznaczenie, reset paginacji i resetuj filtry dat przy zmianie zakładki
  React.useEffect(() => {
    setSettleSelectedIds(new Set());
    setSettleLastClickedIdx(null);
    setSettleVisibleCount(SETTLE_PAGE_CHUNK);
    // Reset filtrów dat (różne pole w zależności od zakładki)
    setSettlementsMonthFilter('');
    setSettlementsDateFrom('');
    setSettlementsDateTo('');
  }, [settlementsTab]);
  // Reset paginacji przy zmianie filtrów
  React.useEffect(() => {
    setSettleSelectedIds(new Set());
    setSettleLastClickedIdx(null);
    setSettleVisibleCount(SETTLE_PAGE_CHUNK);
  }, [settlementsQuery, settlementsProjectFilter, settlementsMonthFilter, settlementsDateFrom, settlementsDateTo]);

  // Sortowanie rozrachunków
  const [settleSortKey, setSettleSortKey] = React.useState<string>('due_date');
  const [settleSortDir, setSettleSortDir] = React.useState<'asc' | 'desc'>('asc');
  const handleSettleSort = React.useCallback((key: string) => {
    const fieldMap: Record<string, string> = {
      dueDate: 'due_date', daysLeft: 'due_date', projectCode: 'project_code', warehouse: 'warehouse',
      docType: 'document_type', contractor: 'contractor', nip: 'nip', invoiceNumber: 'invoice_number',
      grossAmount: 'gross_amount', vatAmount: 'vat_amount', paidDate: 'paid_date', accountNumber: 'account_number', createdDate: 'created_date',
    };
    const field = fieldMap[key] || key;
    if (settleSortKey === field) {
      setSettleSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSettleSortKey(field);
      setSettleSortDir('asc');
    }
  }, [settleSortKey]);

  // Rozdziel na zakupy / sprzedaż (na razie wszystkie to zakupy)
  const settlementsPurchaseRows = React.useMemo(() => settlementsRows, [settlementsRows]);
  const settlementsSalesRows = React.useMemo<SettlementRow[]>(() => [], []);

  // Ładowanie rozrachunków
  const refreshSettlements = React.useCallback(() => {
    setSettlementsLoading(true);
    listSettlements()
      .then(setSettlementsRows)
      .catch(() => setSettlementsRows([]))
      .finally(() => setSettlementsLoading(false));
  }, []);

  React.useEffect(() => {
    if (sectionChip === 5 || sectionChip === 7) {
      refreshSettlements();
    }
  }, [sectionChip, refreshSettlements]);

  // Filtrowanie rozrachunków (bez dat – daty filtrowane per tab poniżej)
  const baseFilteredSettlements = React.useMemo(() => {
    const rows = settlementsMainTab === 0 ? settlementsPurchaseRows : settlementsSalesRows;
    return rows.filter((inv: any) => {
      if (settlementsQuery) {
        const q = settlementsQuery.toLowerCase();
        const matches =
          (inv.invoice_number || '').toLowerCase().includes(q) ||
          (inv.contractor || '').toLowerCase().includes(q) ||
          (inv.nip || '').toLowerCase().includes(q) ||
          (inv.project_code || '').toLowerCase().includes(q);
        if (!matches) return false;
      }
      if (settlementsProjectFilter && inv.project_code !== settlementsProjectFilter) return false;
      return true;
    });
  }, [settlementsMainTab, settlementsPurchaseRows, settlementsSalesRows, settlementsQuery, settlementsProjectFilter]);

  // filteredSettlementsRows z datami (do liczników zakładek i tabeli)
  const filteredSettlementsRows = baseFilteredSettlements;

  const settlementsRowsForTab = React.useMemo(() => {
    let rows: any[];
    if (settlementsTab === 0) rows = baseFilteredSettlements.filter((inv: any) => inv.settled !== true);
    else if (settlementsTab === 1) rows = baseFilteredSettlements.filter((inv: any) => inv.settled === true);
    else if (settlementsTab === 3) rows = baseFilteredSettlements.filter((inv: any) => inv.settled === true && !inv.reconciled_at);
    else if (settlementsTab === 4) rows = baseFilteredSettlements.filter((inv: any) => inv.settled === true && !!inv.reconciled_at);
    else rows = [...baseFilteredSettlements];

    // Filtrowanie dat – pole zależne od zakładki
    // Nierozliczone (0): due_date (termin płatności)
    // Rozliczone (1): paid_date (data zapłaty)
    // Wszystkie (2): due_date
    const dateField = settlementsTab === 1 ? 'paid_date' : 'due_date';
    if (settlementsMonthFilter) {
      rows = rows.filter((inv: any) => {
        const d = inv[dateField];
        return d && String(d).slice(0, 7) === settlementsMonthFilter;
      });
    }
    if (settlementsDateFrom) {
      rows = rows.filter((inv: any) => {
        const d = inv[dateField];
        return d && String(d).slice(0, 10) >= settlementsDateFrom;
      });
    }
    if (settlementsDateTo) {
      rows = rows.filter((inv: any) => {
        const d = inv[dateField];
        return d && String(d).slice(0, 10) <= settlementsDateTo;
      });
    }

    // Sortowanie
    rows.sort((a: any, b: any) => {
      const aVal = a[settleSortKey];
      const bVal = b[settleSortKey];
      let cmp = 0;
      if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) cmp = Number(aVal) - Number(bVal);
      else cmp = String(aVal || '').localeCompare(String(bVal || ''));
      return settleSortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [baseFilteredSettlements, settlementsTab, settleSortKey, settleSortDir, settlementsMonthFilter, settlementsDateFrom, settlementsDateTo]);
  const visibleSettleRows = React.useMemo(() => settlementsRowsForTab.slice(0, settleVisibleCount), [settlementsRowsForTab, settleVisibleCount]);
  settleRowsRef.current = visibleSettleRows;

  // Scroll handler – dociąganie kolejnych wierszy rozrachunków
  React.useEffect(() => {
    if (sectionChip !== 5) return;
    const el = tableContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      if (settleVisibleCount >= settlementsRowsForTab.length) return;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 240;
      if (atBottom) {
        setSettleVisibleCount(prev => Math.min(prev + SETTLE_PAGE_CHUNK, settlementsRowsForTab.length));
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [sectionChip, settleVisibleCount, settlementsRowsForTab.length]);

  const handleSettleRowClick = React.useCallback((rowIdx: number, invId: number | string, e: React.MouseEvent) => {
    // Ignoruj kliknięcia na przyciski/ikony
    if ((e.target as HTMLElement).closest('button, a, .MuiIconButton-root, .MuiChip-root')) return;

    if (e.shiftKey && settleLastClickedIdx !== null) {
      // Shift+klik: zaznacz zakres
      const start = Math.min(settleLastClickedIdx, rowIdx);
      const end = Math.max(settleLastClickedIdx, rowIdx);
      const idsInRange = new Set(settleSelectedIds);
      for (let i = start; i <= end; i++) {
        const row = settleRowsRef.current[i];
        if (row) idsInRange.add(row.id);
      }
      setSettleSelectedIds(idsInRange);
    } else if (e.ctrlKey || e.metaKey) {
      // Ctrl/Cmd+klik: toggle pojedynczego
      setSettleSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(invId)) next.delete(invId); else next.add(invId);
        return next;
      });
      setSettleLastClickedIdx(rowIdx);
    } else {
      // Zwykły klik: zaznacz/odznacz jeden
      if (settleSelectedIds.size === 1 && settleSelectedIds.has(invId)) {
        setSettleSelectedIds(new Set());
        setSettleLastClickedIdx(null);
      } else {
        setSettleSelectedIds(new Set([invId]));
        setSettleLastClickedIdx(rowIdx);
      }
    }
  }, [settleLastClickedIdx, settleSelectedIds]);

  const settlementsTabCounts = React.useMemo(() => ({
    unsettled: filteredSettlementsRows.filter((inv: any) => inv.settled !== true).length,
    settled: filteredSettlementsRows.filter((inv: any) => inv.settled === true).length,
    all: filteredSettlementsRows.length,
    unreconciled: filteredSettlementsRows.filter((inv: any) => inv.settled === true && !inv.reconciled_at).length,
    reconciled: filteredSettlementsRows.filter((inv: any) => inv.settled === true && !!inv.reconciled_at).length,
  }), [filteredSettlementsRows]);

  // Kolumny: tryb zmiany szerokości (przeniesiony wyżej dla bankOps)
  const [colResizeMode, setColResizeMode] = React.useState<boolean>(false);

  // === SEKCJA WYCIĄGI I UZGODNIENIA (sectionChip === 7) ===
  const [bsMainTab, setBsMainTab] = React.useState(0); // 0=Wyciągi, 1=Transakcje, 2=Uzgodnienia
  const [bsSubTab, setBsSubTab] = React.useState(0); // 0=Wyciągi, 1=Wszystkie transakcje, 2=Wpłaty, 3=Wypłaty, 4=Wpłaty z rach. VAT
  const [bsQuery, setBsQuery] = React.useState('');
  const [bsYearFilter, setBsYearFilter] = React.useState('');
  const [bsMonthOnlyFilter, setBsMonthOnlyFilter] = React.useState('');

  // === Uzgodnienia – filtry ===
  const [uzgTxQuery, setUzgTxQuery] = React.useState('');
  const [uzgTxYear, setUzgTxYear] = React.useState('');
  const [uzgTxMonthOnly, setUzgTxMonthOnly] = React.useState('');
  const [uzgTxSubTab, setUzgTxSubTab] = React.useState(0); // 0=Nieuzgodnione, 1=Uzgodnione, 2=Wszystkie
  const [uzgSettQuery, setUzgSettQuery] = React.useState('');
  const [uzgSettYear, setUzgSettYear] = React.useState('');
  const [uzgSettMonthOnly, setUzgSettMonthOnly] = React.useState('');
  const [uzgSettSubTab, setUzgSettSubTab] = React.useState(0); // 0=Nieuzgodnione, 1=Uzgodnione, 2=Wszystkie
  const [uzgTxSortKey, setUzgTxSortKey] = React.useState<'date' | 'amount' | 'contractor' | 'invoice'>('date');
  const [uzgTxSortDir, setUzgTxSortDir] = React.useState<'asc' | 'desc'>('desc');
  const [uzgSettSortKey, setUzgSettSortKey] = React.useState<'date' | 'grossAmount' | 'vatAmount' | 'invoice' | 'contractor'>('date');
  const [uzgSettSortDir, setUzgSettSortDir] = React.useState<'asc' | 'desc'>('desc');
  const [uzgSortSource, setUzgSortSource] = React.useState<'tx' | 'sett'>('tx');
  // === Uzgodnienia – selekcja transakcji ===
  const [uzgSelectedTxId, setUzgSelectedTxId] = React.useState<string | null>(null);

  // === Uzgodnienia – połączenia i linie ===
  const uzgContainerRef = React.useRef<HTMLDivElement>(null);
  const [uzgConnections, setUzgConnections] = React.useState<Array<{txId: string; settId: string}>>([]);
  const uzgConnsRef = React.useRef(uzgConnections);
  React.useEffect(() => { uzgConnsRef.current = uzgConnections; }, [uzgConnections]);
  const [uzgNeedsRefresh, setUzgNeedsRefresh] = React.useState(0);

  // Load existing reconciliation links from MySQL (M:N table)
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const links = await listReconciliationLinks();
        if (cancelled) return;
        const conns = links.map(l => ({ txId: `tx${l.transaction_id}`, settId: `s${l.settlement_id}` }));
        setUzgConnections(conns);
      } catch (err) {
        console.error('[loadReconciliationLinks] error:', err);
        // Fallback: load from settlementsRows if API fails
        const conns: Array<{txId: string; settId: string}> = [];
        settlementsRows.forEach(s => {
          if (s.reconciliation_transaction_id) {
            conns.push({ txId: `tx${s.reconciliation_transaction_id}`, settId: `s${s.id}` });
          }
        });
        if (!cancelled && conns.length > 0) setUzgConnections(conns);
      }
    })();
    return () => { cancelled = true; };
  }, [settlementsRows]);

  const [uzgPreviewPair, setUzgPreviewPair] = React.useState<{ txId: string; settId: string } | null>(null);
  const [uzgDrawerWidths, setUzgDrawerWidths] = React.useState<{ left: number; right: number }>({ left: 0, right: 0 });

  const [uzgDragFrom, setUzgDragFrom] = React.useState<{side: 'tx' | 'sett'; id: string} | null>(null);
  const [uzgDragAnchor, setUzgDragAnchor] = React.useState<{x: number; y: number} | null>(null);
  const [uzgDragMouse, setUzgDragMouse] = React.useState<{x: number; y: number} | null>(null);
  const [uzgSelectedConn, setUzgSelectedConn] = React.useState<{txId: string; settId: string} | null>(null);
  const [uzgFadingConns, setUzgFadingConns] = React.useState<Set<string>>(new Set()); // txId values that are fading out
  // FLIP animation state
  const uzgPreSortPositions = React.useRef<Map<string, number>>(new Map());
  const [uzgFlipTrigger, setUzgFlipTrigger] = React.useState(0);

  // === Auto-sugestie dopasowania ===
  const [uzgDismissedSuggestions, setUzgDismissedSuggestions] = React.useState<Set<string>>(new Set()); // "txId|settId"
  const [uzgAutoMatching, setUzgAutoMatching] = React.useState(false);
  const [uzgAutoMatchProgress, setUzgAutoMatchProgress] = React.useState({ current: 0, total: 0 });

  // bsTransactions musi być przed uzgDemoTx (które z niego korzysta)
  const [bsTransactions, setBsTransactions] = React.useState<BankStatementTransaction[]>([]);

  // Dane uzgodnień z prawdziwych transakcji i rozrachunków
  const uzgDemoTx = React.useMemo(() =>
    bsTransactions.map((tx) => ({
      id: `tx${tx.id}`,
      date: tx.value_date || tx.booking_date || '',
      contractor: tx.split_payment_role === 'vat_transfer' ? 'Rachunek VAT' : (tx.counterparty_name || ''),
      invoice: tx.invoice_number || tx.title || '',
      isOutflow: tx.direction === 'D' || tx.direction === 'RD',
      amount: (tx.direction === 'D' || tx.direction === 'RD') ? -Math.abs(tx.amount) : Math.abs(tx.amount),
      vatAmount: tx.vat_amount != null ? Number(tx.vat_amount) : null,
      isVatTransfer: tx.split_payment_role === 'vat_transfer',
    })),
  [bsTransactions]);

  const uzgDemoSett = React.useMemo(() =>
    settlementsRows
      .filter((s) => s.document_type === 'Przelew')
      .map((s) => ({
        id: `s${s.id}`,
        date: s.paid_date || s.due_date || s.created_date || '',
        contractor: s.contractor || '',
        invoice: s.invoice_number || '',
        grossAmount: Number(s.gross_amount) || 0,
        vatAmount: Number((s as any).vat_amount ?? ((Number(s.gross_amount) || 0) - (Number(s.net_amount) || 0))),
      })),
  [settlementsRows]);

  const handleUzgTxSort = React.useCallback((key: 'date' | 'amount') => {
    setUzgSortSource('tx');
    if (uzgTxSortKey === key) {
      setUzgTxSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setUzgTxSortKey(key);
      setUzgTxSortDir('desc');
    }
  }, [uzgTxSortKey]);

  const handleUzgSettSort = React.useCallback((key: 'date' | 'grossAmount' | 'vatAmount') => {
    setUzgSortSource('sett');
    if (uzgSettSortKey === key) {
      setUzgSettSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setUzgSettSortKey(key);
      setUzgSettSortDir('desc');
    }
  }, [uzgSettSortKey]);

  const [uzgSuggestions, setUzgSuggestions] = React.useState<Array<{txId: string; settId: string; score: 1 | 2; txDate: string}>>([]);
  const [uzgSuggestionsLoaded, setUzgSuggestionsLoaded] = React.useState(false);
  const uzgTxSubTabRef = React.useRef(uzgTxSubTab);
  const uzgSettSubTabRef = React.useRef(uzgSettSubTab);
  React.useEffect(() => { uzgTxSubTabRef.current = uzgTxSubTab; }, [uzgTxSubTab]);
  React.useEffect(() => { uzgSettSubTabRef.current = uzgSettSubTab; }, [uzgSettSubTab]);

  // Load persisted suggestions from DB on mount (once demoTx is available for txDate lookup)
  React.useEffect(() => {
    if (uzgSuggestionsLoaded) return;
    if (uzgDemoTx.length === 0) return;
    console.log('[UZG] Loading suggestions from DB, uzgDemoTx.length =', uzgDemoTx.length);
    const txDateMap = new Map(uzgDemoTx.map(t => [t.id, t.date]));
    loadReconciliationSuggestions().then(rows => {
      console.log('[UZG] Loaded', rows.length, 'suggestions from DB');
      if (rows.length === 0) { setUzgSuggestionsLoaded(true); return; }
      const loaded: Array<{txId: string; settId: string; score: 1 | 2; txDate: string}> = rows.map(r => ({
        txId: `tx${r.transaction_id}`,
        settId: `s${r.settlement_id}`,
        score: (r.score === 2 ? 2 : 1) as 1 | 2,
        txDate: txDateMap.get(`tx${r.transaction_id}`) || '',
      }));
      setUzgSuggestions(loaded);
      setUzgSuggestionsLoaded(true);
      setTimeout(() => recalcUzgLines(), 200);
    }).catch(err => {
      console.error('[UZG] loadReconciliationSuggestions error:', err);
      setUzgSuggestionsLoaded(true);
    });
  }, [uzgDemoTx, uzgSuggestionsLoaded]);

  const computeUzgSuggestions = React.useCallback(() => {
    const existingPairs = new Set(uzgConnsRef.current.map(c => `${c.txId}|${c.settId}`));
    const candidates: Array<{txId: string; settId: string; score: 1 | 2; txDate: string}> = [];
    uzgDemoTx.forEach(tx => {
      uzgDemoSett.forEach(s => {
        const absTxAmount = Math.abs(Number(tx.amount || 0));
        const grossAmount = Math.abs(Number(s.grossAmount || 0));
        const vatAmount = Math.abs(Number(s.vatAmount || 0));
        const grossMatch = Math.abs(absTxAmount - grossAmount) < 0.01;
        const vatMatch = Math.abs(absTxAmount - vatAmount) < 0.01;
        if (!grossMatch && !vatMatch) return;
        const key = `${tx.id}|${s.id}`;
        if (uzgDismissedSuggestions.has(key) || existingPairs.has(key)) return;
        candidates.push({ txId: tx.id, settId: s.id, score: grossMatch ? 2 : 1, txDate: tx.date });
      });
    });
    candidates.sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score;
      return b.txDate.localeCompare(a.txDate);
    });
    return candidates;
  }, [uzgDemoTx, uzgDemoSett, uzgDismissedSuggestions]);

  const uzgSuggestedTxIds = React.useMemo(() => new Set(uzgSuggestions.map(s => s.txId)), [uzgSuggestions]);
  const uzgSuggestedSettIds = React.useMemo(() => new Set(uzgSuggestions.map(s => s.settId)), [uzgSuggestions]);
  const uzgSuggBySettId = React.useMemo(() => new Map(uzgSuggestions.map(s => [s.settId, s])), [uzgSuggestions]);
  const uzgSuggByTxId = React.useMemo(() => new Map(uzgSuggestions.map(s => [s.txId, s])), [uzgSuggestions]);
  const uzgSuggsRef = React.useRef(uzgSuggestions);
  React.useEffect(() => { uzgSuggsRef.current = uzgSuggestions; }, [uzgSuggestions]);

  // Shared pair ordering: confirmed pairs first, then suggestions (score 2, then 1), sorted by date desc
  // Both TX and SETT tables use this to keep paired rows at the same index
  const uzgPairOrder = React.useMemo(() => {
    const txDateMap = new Map(uzgDemoTx.map(t => [t.id, t.date]));
    // Confirmed pairs sorted by TX date desc
    const confirmed = [...uzgConnections].sort((a, b) => (txDateMap.get(b.txId) || '').localeCompare(txDateMap.get(a.txId) || ''));
    // Suggestion pairs sorted by score desc, then TX date desc
    const suggs = [...uzgSuggestions].sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score;
      return (txDateMap.get(b.txId) || '').localeCompare(txDateMap.get(a.txId) || '');
    });
    const allPairs = [...confirmed, ...suggs];
    const txIndex = new Map<string, number>();
    const settIndex = new Map<string, number>();
    allPairs.forEach((p, i) => {
      if (!txIndex.has(p.txId)) txIndex.set(p.txId, i);
      if (!settIndex.has(p.settId)) settIndex.set(p.settId, i);
    });
    return { txIndex, settIndex, pairCount: allPairs.length };
  }, [uzgConnections, uzgSuggestions, uzgDemoTx]);

  // Build aligned slot lists for both TX and SETT tables
  // Each slot is either a real item or null (spacer) to keep paired items aligned
  const uzgAlignedSlots = React.useMemo(() => {
    const txConnMap = new Map<string, string[]>();
    const settConnMap = new Map<string, string[]>();
    uzgConnections.forEach(c => {
      txConnMap.set(c.txId, [...(txConnMap.get(c.txId) || []), c.settId]);
      settConnMap.set(c.settId, [...(settConnMap.get(c.settId) || []), c.txId]);
    });
    const txSuggMap = new Map(uzgSuggestions.map(s => [s.txId, s.settId]));
    const settSuggMap = new Map(uzgSuggestions.map(s => [s.settId, s.txId]));

    // Helper: get pair partner for a TX item
    const txPartners = (txId: string): string[] => {
      const conns = txConnMap.get(txId) || [];
      const sugg = txSuggMap.get(txId);
      return sugg ? [...conns, sugg] : conns;
    };
    const settPartners = (settId: string): string[] => {
      const conns = settConnMap.get(settId) || [];
      const sugg = settSuggMap.get(settId);
      return sugg ? [...conns, sugg] : conns;
    };
    const txPartner = (txId: string): string | undefined => txPartners(txId)[0];
    const settPartner = (settId: string): string | undefined => settPartners(settId)[0];

    // Filter + sort functions
    const filterTx = (items: typeof uzgDemoTx) => {
      let rows = items;
      if (uzgTxSubTab === 0) rows = rows.filter(t => !txConnMap.has(t.id) || uzgFadingConns.has(t.id));
      else if (uzgTxSubTab === 1) rows = rows.filter(t => txConnMap.has(t.id));
      if (uzgTxQuery) { const q = uzgTxQuery.toLowerCase(); rows = rows.filter(t => t.contractor.toLowerCase().includes(q) || t.invoice.toLowerCase().includes(q)); }
      if (uzgTxYear) rows = rows.filter(t => t.date.startsWith(uzgTxYear));
      if (uzgTxMonthOnly) rows = rows.filter(t => t.date.slice(5, 7) === uzgTxMonthOnly);
      return rows;
    };
    const filterSett = (items: typeof uzgDemoSett) => {
      const settConnIds = new Set(uzgConnections.map(c => c.settId));
      const settFadingIds = new Set(uzgConnections.filter(c => uzgFadingConns.has(c.txId)).map(c => c.settId));
      let rows = items;
      if (uzgSettSubTab === 0) rows = rows.filter(s => !settConnIds.has(s.id) || settFadingIds.has(s.id));
      else if (uzgSettSubTab === 1) rows = rows.filter(s => settConnIds.has(s.id));
      if (uzgSettQuery) { const q = uzgSettQuery.toLowerCase(); rows = rows.filter(s => s.contractor.toLowerCase().includes(q) || s.invoice.toLowerCase().includes(q)); }
      if (uzgSettYear) rows = rows.filter(s => s.date.startsWith(uzgSettYear));
      if (uzgSettMonthOnly) rows = rows.filter(s => s.date.slice(5, 7) === uzgSettMonthOnly);
      return rows;
    };
    const compareTxRows = (a: (typeof uzgDemoTx)[0], b: (typeof uzgDemoTx)[0]) => {
      let cmp = 0;
      switch (uzgTxSortKey) {
        case 'amount': cmp = Number(a.amount || 0) - Number(b.amount || 0); break;
        case 'contractor': cmp = (a.contractor || '').localeCompare(b.contractor || ''); break;
        case 'invoice': cmp = (a.invoice || '').localeCompare(b.invoice || ''); break;
        default: cmp = String(a.date || '').localeCompare(String(b.date || ''));
      }
      return uzgTxSortDir === 'asc' ? cmp : -cmp;
    };
    const compareSettRows = (a: (typeof uzgDemoSett)[0], b: (typeof uzgDemoSett)[0]) => {
      let cmp = 0;
      switch (uzgSettSortKey) {
        case 'grossAmount': cmp = Number(a.grossAmount || 0) - Number(b.grossAmount || 0); break;
        case 'vatAmount': cmp = Number(a.vatAmount || 0) - Number(b.vatAmount || 0); break;
        case 'invoice': cmp = (a.invoice || '').localeCompare(b.invoice || ''); break;
        case 'contractor': cmp = (a.contractor || '').localeCompare(b.contractor || ''); break;
        default: cmp = String(a.date || '').localeCompare(String(b.date || ''));
      }
      return uzgSettSortDir === 'asc' ? cmp : -cmp;
    };

    const visibleTx = filterTx(uzgDemoTx);
    const visibleSett = filterSett(uzgDemoSett);
    const shouldAlignSlots = uzgTxSubTab === 1 && uzgSettSubTab === 1;

    // Linie są pokazywane tylko dla Uzgodnione↔Uzgodnione, więc
    // tylko tam utrzymujemy slotowe wyrównanie tabel.
    // W innych kartach zwracamy niezależne, zwarte listy (bez "rozjazdów").
    if (!shouldAlignSlots) {
      const txSorted = [...visibleTx].sort(compareTxRows);
      const settSorted = [...visibleSett].sort(compareSettRows);
      return { txSlots: txSorted, settSlots: settSorted, visibleTx: txSorted, visibleSett: settSorted };
    }

    const visibleTxSet = new Set(visibleTx.map(t => t.id));
    const visibleSettSet = new Set(visibleSett.map(s => s.id));
    const txMap = new Map(visibleTx.map(t => [t.id, t]));
    const settMap = new Map(visibleSett.map(s => [s.id, s]));

    // Build paired items sorted by shared pair order
    const pairedTxIds = visibleTx.filter(t => {
      const partners = txPartners(t.id);
      return partners.some((p) => visibleSettSet.has(p));
    }).map(t => t.id);
    const pairedSettIds = visibleSett.filter(s => {
      const partners = settPartners(s.id);
      return partners.some((p) => visibleTxSet.has(p));
    }).map(s => s.id);

    // Sort paired rows using the active side sort (TX or SETT), fallback to stable pair order
    const sortedPairedTx = [...pairedTxIds].sort((a, b) => {
      if (uzgSortSource === 'sett') {
        const aSett = txPartner(a) ? settMap.get(txPartner(a)!) : undefined;
        const bSett = txPartner(b) ? settMap.get(txPartner(b)!) : undefined;
        if (aSett && bSett) {
          const cmp = compareSettRows(aSett, bSett);
          if (cmp !== 0) return cmp;
        }
      } else {
        const aTx = txMap.get(a);
        const bTx = txMap.get(b);
        if (aTx && bTx) {
          const cmp = compareTxRows(aTx, bTx);
          if (cmp !== 0) return cmp;
        }
      }
      return (uzgPairOrder.txIndex.get(a) ?? 999) - (uzgPairOrder.txIndex.get(b) ?? 999);
    });

    // TX only visible (partner not visible)
    const txOnlyIds = visibleTx.filter(t => {
      const partners = txPartners(t.id);
      return !partners.some((p) => visibleSettSet.has(p));
    }).map(t => t.id);
    txOnlyIds.sort((a, b) => {
      const txA = txMap.get(a);
      const txB = txMap.get(b);
      if (!txA || !txB) return 0;
      return compareTxRows(txA, txB);
    });

    // SETT only visible (partner not visible)
    const settOnlyIds = visibleSett.filter(s => {
      const partners = settPartners(s.id);
      return !partners.some((p) => visibleTxSet.has(p));
    }).map(s => s.id);
    settOnlyIds.sort((a, b) => {
      const sA = settMap.get(a);
      const sB = settMap.get(b);
      if (!sA || !sB) return 0;
      return compareSettRows(sA, sB);
    });

    // Build slot list: pairs first (fading first), then solo items interleaved by date
    type Slot = { txId: string | null; settId: string | null };
    const slots: Slot[] = [];

    // Fading pairs first
    const fadingPairs = sortedPairedTx.filter(txId => uzgFadingConns.has(txId));
    const nonFadingPairs = sortedPairedTx.filter(txId => !uzgFadingConns.has(txId));

    fadingPairs.forEach(txId => {
      const settId = txPartners(txId).find((id) => visibleSettSet.has(id)) || txPartner(txId)!;
      slots.push({ txId, settId });
    });
    nonFadingPairs.forEach(txId => {
      const settId = txPartners(txId).find((id) => visibleSettSet.has(id)) || txPartner(txId)!;
      slots.push({ txId, settId });
    });

    // Build final ordered arrays: paired section (aligned) + unpaired section (independent, no spacers)
    const txSlots: Array<(typeof uzgDemoTx)[0] | null> = [];
    const settSlots: Array<(typeof uzgDemoSett)[0] | null> = [];
    // Paired section — both sides aligned with spacers where needed
    slots.forEach(slot => {
      txSlots.push(slot.txId ? txMap.get(slot.txId) || null : null);
      settSlots.push(slot.settId ? settMap.get(slot.settId) || null : null);
    });

    // Unpaired TX items — appended independently (no spacers on sett side)
    txOnlyIds.forEach(txId => {
      txSlots.push(txMap.get(txId) || null);
    });

    // Unpaired SETT items — appended independently (no spacers on TX side)
    settOnlyIds.forEach(settId => {
      settSlots.push(settMap.get(settId) || null);
    });

    return { txSlots, settSlots, visibleTx, visibleSett };
  }, [uzgDemoTx, uzgDemoSett, uzgConnections, uzgSuggestions, uzgFadingConns, uzgPairOrder,
      uzgTxSubTab, uzgTxQuery, uzgTxYear, uzgTxMonthOnly,
      uzgSettSubTab, uzgSettQuery, uzgSettYear, uzgSettMonthOnly,
      uzgTxSortKey, uzgTxSortDir, uzgSettSortKey, uzgSettSortDir, uzgSortSource]);

  const [uzgLines, setUzgLines] = React.useState<Array<{x1:number;y1:number;x2:number;y2:number;txId:string;settId:string;type:'conn'|'sugg2'|'sugg1'}>>([]);
  const recalcUzgLines = React.useCallback(() => {
    const container = uzgContainerRef.current;
    if (!container) { setUzgLines([]); return; }
    const cRect = container.getBoundingClientRect();
    const txScrollEl = container.querySelector('[data-uzg-tx-scroll]');
    const settScrollEl = container.querySelector('[data-uzg-sett-scroll]');
    if (!txScrollEl || !settScrollEl) { setUzgLines([]); return; }
    const txSR = txScrollEl.getBoundingClientRect();
    const settSR = settScrollEl.getBoundingClientRect();
    const conns = uzgConnsRef.current;
    const newLines: Array<{x1:number;y1:number;x2:number;y2:number;txId:string;settId:string;type:'conn'|'sugg2'|'sugg1'}> = [];
    const addLine = (txId: string, settId: string, type: 'conn'|'sugg2'|'sugg1') => {
      const txDot = container.querySelector<HTMLElement>(`[data-uzg-tx-id="${txId}"]`);
      const settDot = container.querySelector<HTMLElement>(`[data-uzg-sett-id="${settId}"]`);
      if (!txDot || !settDot) return;
      const tRect = txDot.getBoundingClientRect();
      const sRect = settDot.getBoundingClientRect();
      const tCy = tRect.top + tRect.height / 2;
      const sCy = sRect.top + sRect.height / 2;
      if (tCy < txSR.top || tCy > txSR.bottom) return;
      if (sCy < settSR.top || sCy > settSR.bottom) return;
      newLines.push({
        x1: tRect.left + tRect.width / 2 - cRect.left,
        y1: tRect.top + tRect.height / 2 - cRect.top,
        x2: sRect.left + sRect.width / 2 - cRect.left,
        y2: sRect.top + sRect.height / 2 - cRect.top,
        txId, settId, type,
      });
    };
    const txTab = uzgTxSubTabRef.current;
    const settTab = uzgSettSubTabRef.current;
    const sameTab = txTab === settTab;

    // Linie pokazujemy tylko dla Uzgodnione ↔ Uzgodnione.
    if (sameTab && txTab === 1) {
      conns.forEach(conn => addLine(conn.txId, conn.settId, 'conn'));
    }

    // Gdy kilka linii kończy się na tym samym rozrachunku, rozsuń końcówki
    // (mały offset pionowy), aby wizualnie było widać każdą linię osobno.
    const perSettCount = new Map<string, number>();
    newLines.forEach((l) => perSettCount.set(l.settId, (perSettCount.get(l.settId) || 0) + 1));
    const perSettSeen = new Map<string, number>();
    const spreadLines = newLines.map((l) => {
      const total = perSettCount.get(l.settId) || 1;
      if (total <= 1) return l;
      const idx = perSettSeen.get(l.settId) || 0;
      perSettSeen.set(l.settId, idx + 1);
      const offset = (idx - (total - 1) / 2) * 8;
      return { ...l, y2: l.y2 + offset };
    });
    setUzgLines(spreadLines);
  }, []);

  // Recalc on filter/tab/connection changes + scroll/resize listeners
  React.useEffect(() => {
    if (bsMainTab !== 2) return;
    const timer = setTimeout(recalcUzgLines, 60);
    const container = uzgContainerRef.current;
    const txScroll = container?.querySelector('[data-uzg-tx-scroll]');
    const settScroll = container?.querySelector('[data-uzg-sett-scroll]');
    let rafId = 0;
    const onScrollOrResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => { rafId = 0; recalcUzgLines(); });
    };
    txScroll?.addEventListener('scroll', onScrollOrResize, { passive: true });
    settScroll?.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => { clearTimeout(timer); if (rafId) cancelAnimationFrame(rafId); txScroll?.removeEventListener('scroll', onScrollOrResize); settScroll?.removeEventListener('scroll', onScrollOrResize); window.removeEventListener('resize', onScrollOrResize); };
  }, [bsMainTab, uzgTxSubTab, uzgSettSubTab, uzgTxQuery, uzgTxYear, uzgTxMonthOnly, uzgSettQuery, uzgSettYear, uzgSettMonthOnly, uzgConnections, uzgFadingConns, uzgSuggestions, recalcUzgLines]);

  // Synchronized scroll: paired rows (connected + suggested) scroll together when tabs match
  React.useEffect(() => {
    if (bsMainTab !== 2) return;
    if (uzgTxSubTab !== uzgSettSubTab) return;
    const container = uzgContainerRef.current;
    if (!container) return;
    const txScroll = container.querySelector<HTMLElement>('[data-uzg-tx-scroll]');
    const settScroll = container.querySelector<HTMLElement>('[data-uzg-sett-scroll]');
    if (!txScroll || !settScroll) return;

    // Determine which IDs are "paired" based on current tab
    const txPairedIds = new Set<string>();
    const settPairedIds = new Set<string>();
    if (uzgTxSubTab === 1) {
      // Uzgodnione: all visible rows are connected → all paired
      uzgConnections.forEach(c => { txPairedIds.add(c.txId); settPairedIds.add(c.settId); });
    } else {
      // Nieuzgodnione: only suggestions; Wszystkie: connected + suggested
      if (uzgTxSubTab === 2) uzgConnections.forEach(c => { txPairedIds.add(c.txId); settPairedIds.add(c.settId); });
      uzgSuggestions.forEach(s => { txPairedIds.add(s.txId); settPairedIds.add(s.settId); });
    }
    if (txPairedIds.size === 0) return;

    // Get total height of paired region (from top of scroll content to bottom of last paired row)
    const getPairedLimit = (scrollEl: HTMLElement, rowAttr: string, pairedIds: Set<string>) => {
      const scrollRect = scrollEl.getBoundingClientRect();
      let maxBottom = 0;
      scrollEl.querySelectorAll<HTMLElement>(`[${rowAttr}]`).forEach(row => {
        const id = row.getAttribute(rowAttr);
        if (id && pairedIds.has(id)) {
          const bottom = row.getBoundingClientRect().bottom - scrollRect.top + scrollEl.scrollTop;
          if (bottom > maxBottom) maxBottom = bottom;
        }
      });
      return maxBottom;
    };

    let scrollingFrom: HTMLElement | null = null;

    const syncScroll = (source: HTMLElement, target: HTMLElement, rowAttr: string, pairedIds: Set<string>) => {
      if (scrollingFrom === target) { scrollingFrom = null; return; }
      const limit = getPairedLimit(source, rowAttr, pairedIds);
      if (source.scrollTop < limit) {
        scrollingFrom = source;
        target.scrollTop = source.scrollTop;
      }
    };

    const onTxScroll = () => syncScroll(txScroll, settScroll, 'data-uzg-tx-row', txPairedIds);
    const onSettScroll = () => syncScroll(settScroll, txScroll, 'data-uzg-sett-row', settPairedIds);

    txScroll.addEventListener('scroll', onTxScroll, { passive: true });
    settScroll.addEventListener('scroll', onSettScroll, { passive: true });
    return () => { txScroll.removeEventListener('scroll', onTxScroll); settScroll.removeEventListener('scroll', onSettScroll); };
  }, [bsMainTab, uzgTxSubTab, uzgSettSubTab, uzgConnections, uzgSuggestions]);

  // FLIP animation: after sort causes re-render, animate rows from old positions to new
  React.useLayoutEffect(() => {
    if (uzgFlipTrigger === 0) return;
    const container = uzgContainerRef.current;
    if (!container) return;
    const old = uzgPreSortPositions.current;
    if (old.size === 0) return;

    // Scroll both tables to top before measuring new positions
    const txScroll = container.querySelector<HTMLElement>('[data-uzg-tx-scroll]');
    const settScroll = container.querySelector<HTMLElement>('[data-uzg-sett-scroll]');
    if (txScroll) txScroll.scrollTop = 0;
    if (settScroll) settScroll.scrollTop = 0;

    const allRows: HTMLElement[] = [];
    // Process TX rows
    container.querySelectorAll<HTMLElement>('[data-uzg-tx-row]').forEach(el => {
      const key = 'tx-' + el.getAttribute('data-uzg-tx-row');
      const oldTop = old.get(key);
      if (oldTop !== undefined) {
        const newTop = el.getBoundingClientRect().top;
        const delta = oldTop - newTop;
        if (Math.abs(delta) > 2) {
          el.style.transform = `translateY(${delta}px)`;
          el.style.transition = 'none';
          allRows.push(el);
        }
      }
    });
    // Process SETT rows
    container.querySelectorAll<HTMLElement>('[data-uzg-sett-row]').forEach(el => {
      const key = 'sett-' + el.getAttribute('data-uzg-sett-row');
      const oldTop = old.get(key);
      if (oldTop !== undefined) {
        const newTop = el.getBoundingClientRect().top;
        const delta = oldTop - newTop;
        if (Math.abs(delta) > 2) {
          el.style.transform = `translateY(${delta}px)`;
          el.style.transition = 'none';
          allRows.push(el);
        }
      }
    });

    // Force reflow
    void document.body.offsetHeight;

    // Animate to final positions
    allRows.forEach(el => {
      el.style.transition = 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)';
      el.style.transform = '';
    });

    // Clean up after animation + recalc lines
    setTimeout(() => {
      allRows.forEach(el => { el.style.transition = ''; el.style.transform = ''; });
      recalcUzgLines();
    }, 500);

    uzgPreSortPositions.current = new Map();
  }, [uzgFlipTrigger, recalcUzgLines]);

  // Mouse tracking during drag
  React.useEffect(() => {
    if (!uzgDragFrom) return;
    const onMove = (e: MouseEvent) => {
      const container = uzgContainerRef.current;
      if (!container) return;
      const cRect = container.getBoundingClientRect();
      setUzgDragMouse({ x: e.clientX - cRect.left, y: e.clientY - cRect.top });
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setUzgDragFrom(null); setUzgDragAnchor(null); setUzgDragMouse(null); }
    };
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-uzg-tx-id]') && !target.closest('[data-uzg-sett-id]')) {
        setUzgDragFrom(null); setUzgDragAnchor(null); setUzgDragMouse(null);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('click', onClick, true);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('keydown', onKeyDown); document.removeEventListener('click', onClick, true); };
  }, [uzgDragFrom]);

  // Delete/Backspace to remove selected connection + click outside to deselect
  React.useEffect(() => {
    if (!uzgSelectedConn) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const realSettId = uzgSelectedConn.settId.replace(/^s/, '');
        const realTxId = uzgSelectedConn.txId.replace(/^tx/, '');
        removeReconciliationLink(realTxId, realSettId).catch(err => console.error('[removeLink] error:', err));
        const remaining = uzgConnections.filter(c => c.settId === uzgSelectedConn.settId && c.txId !== uzgSelectedConn.txId);
        if (remaining.length === 0) {
          unreconcileSettlement(realSettId).catch(err => console.error('[unreconcile] error:', err));
        }
        setUzgConnections(prev => prev.filter(c => !(c.txId === uzgSelectedConn.txId && c.settId === uzgSelectedConn.settId)));
        setUzgSelectedConn(null);
      }
      if (e.key === 'Escape') setUzgSelectedConn(null);
    };
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-uzg-line]')) setUzgSelectedConn(null);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('click', onClick);
    return () => { document.removeEventListener('keydown', onKeyDown); document.removeEventListener('click', onClick); };
  }, [uzgSelectedConn, settlementsRows]);

  // Escape to close preview pair drawers + calculate drawer widths from dot positions
  React.useEffect(() => {
    if (!uzgPreviewPair) return;
    const container = uzgContainerRef.current;
    if (container) {
      const txDot = container.querySelector<HTMLElement>(`[data-uzg-tx-id="${uzgPreviewPair.txId}"]`);
      const settDot = container.querySelector<HTMLElement>(`[data-uzg-sett-id="${uzgPreviewPair.settId}"]`);
      if (txDot && settDot) {
        const txRect = txDot.getBoundingClientRect();
        const settRect = settDot.getBoundingClientRect();
        setUzgDrawerWidths({
          left: txRect.left,
          right: window.innerWidth - settRect.right,
        });
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUzgPreviewPair(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [uzgPreviewPair]);

  // Shared logic: confirm a TX↔SETT pair with FLIP animation + fading + backend reconcile
  const confirmUzgPair = React.useCallback(async (txId: string, settId: string) => {
    const realTxId = txId.replace(/^tx/, '');
    const realSettId = settId.replace(/^s/, '');

    try {
      await addReconciliationLink(realTxId, realSettId);
      // Zawsze wywołaj reconcile — ustawia reconciled_at na transakcji i rozrachunku
      await reconcileSettlement(realSettId, realTxId);
      deleteReconciliationSuggestion(realTxId, realSettId).catch(() => {});
    } catch (err) {
      console.error('[confirmUzgPair] error:', err);
      return;
    }

    setUzgConnections(prev => {
      if (prev.some(c => c.txId === txId && c.settId === settId)) return prev;
      return [...prev, { txId, settId }];
    });
    setUzgSuggestions(prev => prev.filter(s => !(s.txId === txId && s.settId === settId)));
    setUzgNeedsRefresh(n => n + 1);
  }, []);

  // Compute suggestions on button click and display them one by one with animation
  const handleSuggestMatches = React.useCallback(() => {
    if (uzgAutoMatching) return;
    setUzgAutoMatching(true);
    setUzgSuggestions([]);

    setTimeout(() => {
      const newSuggestions = computeUzgSuggestions();
      if (!newSuggestions.length) {
        setUzgAutoMatching(false);
        return;
      }

      // Persist suggestions to DB
      const toSave = newSuggestions.map(s => ({
        transaction_id: Number(s.txId.replace(/^tx/, '')),
        settlement_id: Number(s.settId.replace(/^s/, '')),
        score: s.score,
      }));
      console.log('[UZG] Saving', toSave.length, 'suggestions to DB');
      saveReconciliationSuggestions(toSave).then(() => console.log('[UZG] Suggestions saved OK')).catch(err => console.error('[UZG] saveReconciliationSuggestions error:', err));

      let i = 0;
      setUzgAutoMatchProgress({ current: 0, total: newSuggestions.length });
      const interval = setInterval(() => {
        if (i >= newSuggestions.length) {
          clearInterval(interval);
          setUzgAutoMatching(false);
          setUzgAutoMatchProgress({ current: 0, total: 0 });
          return;
        }
        const item = newSuggestions[i];
        i++;
        setUzgSuggestions(prev => [...prev, item]);
        setUzgAutoMatchProgress({ current: i, total: newSuggestions.length });
        setTimeout(recalcUzgLines, 50);
      }, 150);
    }, 200);
  }, [computeUzgSuggestions, uzgAutoMatching, recalcUzgLines]);

  const handleUzgDotClick = React.useCallback((side: 'tx' | 'sett', itemId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const container = uzgContainerRef.current;
    if (!container) return;
    const cRect = container.getBoundingClientRect();
    const dotRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const anchor = { x: dotRect.left + dotRect.width / 2 - cRect.left, y: dotRect.top + dotRect.height / 2 - cRect.top };

    if (!uzgDragFrom) {
      setUzgSelectedConn(null);
      setUzgDragFrom({ side, id: itemId }); setUzgDragAnchor(anchor); setUzgDragMouse(anchor);
    } else if (uzgDragFrom.side === side) {
      setUzgDragFrom({ side, id: itemId }); setUzgDragAnchor(anchor); setUzgDragMouse(anchor);
    } else {
      const txId = side === 'sett' ? uzgDragFrom.id : itemId;
      const settId = side === 'sett' ? itemId : uzgDragFrom.id;
      setUzgDragFrom(null); setUzgDragAnchor(null); setUzgDragMouse(null);
      confirmUzgPair(txId, settId);
    }
  }, [uzgDragFrom, confirmUzgPair]);
  const [bsDateFrom, setBsDateFrom] = React.useState('');
  const [bsDateTo, setBsDateTo] = React.useState('');
  const [bsReconciledFilter, setBsReconciledFilter] = React.useState(''); // '', 'yes', 'no'
  const [bsBankFilter, setBsBankFilter] = React.useState('');
  const [bsAccountFilter, setBsAccountFilter] = React.useState('');
  const [bsSortKey, setBsSortKey] = React.useState<string>('');
  const [bsSortDir, setBsSortDir] = React.useState<'asc' | 'desc'>('asc');
  const handleBsSort = React.useCallback((key: string) => {
    setBsSortKey(prev => { if (prev === key) { setBsSortDir(d => d === 'asc' ? 'desc' : 'asc'); return prev; } setBsSortDir('asc'); return key; });
  }, []);
  const [bsStatements, setBsStatements] = React.useState<BankStatement[]>([]);
  const [bsLoading, setBsLoading] = React.useState(false);
  const [bsImporting, setBsImporting] = React.useState(false);
  const bsFileInputRef = React.useRef<HTMLInputElement>(null);
  // Zaznaczanie wierszy (Google Sheets style)
  const [bsSelectedIds, setBsSelectedIds] = React.useState<Set<number>>(new Set());
  const [bsLastClickedIdx, setBsLastClickedIdx] = React.useState<number | null>(null);
  const bsVisibleRowsRef = React.useRef<any[]>([]);
  const [bsBulkDrawerOpen, setBsBulkDrawerOpen] = React.useState(false);
  const [bsBulkDeleting, setBsBulkDeleting] = React.useState(false);
  const [companyBankAccounts, setCompanyBankAccounts] = React.useState<BankAccount[]>([]);

  // === Inteligentna paginacja (infinite scroll) dla tabel Wyciągi / Transakcje ===
  const BS_PAGE_SIZE = 50; // ile wierszy ładujemy na raz
  const [bsVisibleCount, setBsVisibleCount] = React.useState(BS_PAGE_SIZE);
  const bsTableContainerRef = React.useRef<HTMLDivElement>(null);
  const bsSentinelRef = React.useRef<HTMLTableRowElement>(null);

  // Reset visible count kiedy zmieni się sub-tab lub filtry
  React.useEffect(() => {
    setBsVisibleCount(BS_PAGE_SIZE);
    if (bsTableContainerRef.current) bsTableContainerRef.current.scrollTop = 0;
  }, [bsSubTab, bsQuery, bsYearFilter, bsMonthOnlyFilter, bsDateFrom, bsDateTo, bsBankFilter, bsAccountFilter, bsSortKey, bsSortDir]);

  // Załaduj rachunki bankowe firmy
  React.useEffect(() => {
    listBankAccounts().then(setCompanyBankAccounts).catch(() => {});
  }, []);

  const refreshBankStatements = React.useCallback(async () => {
    setBsLoading(true);
    try {
      const [stmts, txs] = await Promise.all([listBankStatements(), listBankStatementTransactions()]);
      setBsStatements(stmts);
      setBsTransactions(txs);
    } catch (e) {
      console.error('Błąd ładowania wyciągów:', e);
    } finally {
      setBsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (sectionChip === 7) {
      refreshBankStatements();
    }
  }, [sectionChip, refreshBankStatements]);

  // Odśwież dane transakcji i rozrachunków po zmianie uzgodnień
  React.useEffect(() => {
    if (uzgNeedsRefresh > 0) {
      refreshSettlements();
      refreshBankStatements();
    }
  }, [uzgNeedsRefresh, refreshSettlements, refreshBankStatements]);

  const handleBsRowClick = React.useCallback((rowIdx: number, rowId: number, e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, a, .MuiIconButton-root, .MuiChip-root')) return;
    if (e.shiftKey && bsLastClickedIdx !== null) {
      const start = Math.min(bsLastClickedIdx, rowIdx);
      const end = Math.max(bsLastClickedIdx, rowIdx);
      const idsInRange = new Set(bsSelectedIds);
      for (let i = start; i <= end; i++) {
        const row = bsVisibleRowsRef.current[i];
        if (row) idsInRange.add(row.id);
      }
      setBsSelectedIds(idsInRange);
    } else if (e.ctrlKey || e.metaKey) {
      setBsSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
        return next;
      });
      setBsLastClickedIdx(rowIdx);
    } else {
      if (bsSelectedIds.size === 1 && bsSelectedIds.has(rowId)) {
        setBsSelectedIds(new Set());
        setBsLastClickedIdx(null);
      } else {
        setBsSelectedIds(new Set([rowId]));
        setBsLastClickedIdx(rowIdx);
      }
    }
  }, [bsLastClickedIdx, bsSelectedIds]);

  // Reset zaznaczenia przy zmianie sub-tab
  React.useEffect(() => { setBsSelectedIds(new Set()); setBsLastClickedIdx(null); }, [bsSubTab]);

  // Opcje filtrów Bank i Rachunek z rachunków firmowych
  const bsBankOptions = React.useMemo(() => {
    const set = new Set<string>();
    companyBankAccounts.forEach(a => { if (a.bank_name) set.add(a.bank_name); });
    return Array.from(set).sort();
  }, [companyBankAccounts]);
  const bsAccountOptions = React.useMemo(() => {
    return companyBankAccounts
      .filter(a => a.is_active && (!bsBankFilter || a.bank_name === bsBankFilter))
      .map(a => ({
        raw: a.account_number,
        formatted: formatAccountNumber(a.account_number),
        description: a.description || '',
        currency: a.currency || 'PLN',
      }));
  }, [companyBankAccounts, bsBankFilter]);

  // Zbiór numerów rachunków pasujących do wybranego banku
  const bsBankAccountNums = React.useMemo(() => {
    if (!bsBankFilter) return null;
    const set = new Set<string>();
    companyBankAccounts.forEach(a => {
      if (a.bank_name === bsBankFilter) set.add(a.account_number.replace(/\s/g, ''));
    });
    return set;
  }, [bsBankFilter, companyBankAccounts]);

  // Filtrowanie wyciągów
  const filteredBsStatements = React.useMemo(() => {
    let rows = [...bsStatements];
    if (bsQuery) {
      const q = bsQuery.toLowerCase();
      rows = rows.filter(r =>
        (r.statement_number || '').toLowerCase().includes(q) ||
        (r.account_number || '').toLowerCase().includes(q) ||
        (r.bank_name || '').toLowerCase().includes(q) ||
        (r.source_filename || '').toLowerCase().includes(q)
      );
    }
    if (bsBankAccountNums) rows = rows.filter(r => r.account_number && bsBankAccountNums.has(r.account_number.replace(/\s/g, '')));
    if (bsAccountFilter) rows = rows.filter(r => r.account_number && r.account_number.replace(/\s/g, '') === bsAccountFilter.replace(/\s/g, ''));
    if (bsYearFilter) {
      rows = rows.filter(r => r.closing_date && r.closing_date.slice(0, 4) === bsYearFilter);
    }
    if (bsMonthOnlyFilter) {
      rows = rows.filter(r => r.closing_date && r.closing_date.slice(5, 7) === bsMonthOnlyFilter);
    }
    if (bsDateFrom) rows = rows.filter(r => r.closing_date && r.closing_date >= bsDateFrom);
    if (bsDateTo) rows = rows.filter(r => r.closing_date && r.closing_date <= bsDateTo);
    // Sortowanie
    if (bsSortKey) {
      const fieldMap: Record<string, (r: BankStatement) => any> = {
        statementNumber: r => r.statement_number || '',
        bankName: r => r.bank_name || '',
        accountNumber: r => r.account_number || '',
        closingDate: r => r.closing_date || '',
        currency: r => r.currency || '',
        openingBalance: r => Number(r.opening_balance) || 0,
        closingBalance: r => Number(r.closing_balance) || 0,
        totalDebit: r => Number(r.total_debit) || 0,
        totalCredit: r => Number(r.total_credit) || 0,
        txCount: r => r.transaction_count_actual || r.transaction_count || 0,
        sourceFilename: r => r.source_filename || '',
      };
      const getter = fieldMap[bsSortKey];
      if (getter) {
        rows.sort((a, b) => {
          const va = getter(a), vb = getter(b);
          const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb), 'pl');
          return bsSortDir === 'asc' ? cmp : -cmp;
        });
      }
    }
    return rows;
  }, [bsStatements, bsQuery, bsBankAccountNums, bsAccountFilter, bsYearFilter, bsMonthOnlyFilter, bsDateFrom, bsDateTo, bsSortKey, bsSortDir]);

  // Filtrowanie transakcji
  const filteredBsTransactions = React.useMemo(() => {
    let rows = [...bsTransactions];
    if (bsQuery) {
      const q = bsQuery.toLowerCase();
      rows = rows.filter(r =>
        (r.title || '').toLowerCase().includes(q) ||
        (r.counterparty_name || '').toLowerCase().includes(q) ||
        (r.counterparty_account || '').toLowerCase().includes(q) ||
        (r.counterparty_nip || '').toLowerCase().includes(q) ||
        (r.invoice_number || '').toLowerCase().includes(q) ||
        (r.tnr || '').toLowerCase().includes(q) ||
        (r.reference || '').toLowerCase().includes(q) ||
        (r.statement_number || '').toLowerCase().includes(q)
      );
    }
    if (bsBankAccountNums) rows = rows.filter(r => r.statement_account && bsBankAccountNums.has(r.statement_account.replace(/\s/g, '')));
    if (bsAccountFilter) rows = rows.filter(r => r.statement_account && r.statement_account.replace(/\s/g, '') === bsAccountFilter.replace(/\s/g, ''));
    if (bsYearFilter) {
      rows = rows.filter(r => r.value_date && r.value_date.slice(0, 4) === bsYearFilter);
    }
    if (bsMonthOnlyFilter) {
      rows = rows.filter(r => r.value_date && r.value_date.slice(5, 7) === bsMonthOnlyFilter);
    }
    if (bsDateFrom) rows = rows.filter(r => r.value_date && r.value_date >= bsDateFrom);
    if (bsDateTo) rows = rows.filter(r => r.value_date && r.value_date <= bsDateTo);
    if (bsReconciledFilter === 'yes') rows = rows.filter(r => !!r.reconciled_at);
    if (bsReconciledFilter === 'no') rows = rows.filter(r => !r.reconciled_at);
    // Sortowanie
    if (bsSortKey) {
      const fieldMap: Record<string, (r: BankStatementTransaction) => any> = {
        reconciled: r => r.reconciled_at || '',
        dates: r => r.value_date || '',
        direction: r => r.direction || '',
        txType: r => r.transaction_type || '',
        statementAccount: r => r.statement_account || '',
        statementNumber: r => r.statement_number || '',
        currency: r => r.statement_currency || '',
        counterpartyName: r => r.counterparty_name || '',
        counterpartyAccount: r => r.counterparty_account || '',
        amount: r => Number(r.amount) || 0,
        vatAmount: r => Number(r.vat_amount) || 0,
        counterpartyNip: r => r.counterparty_nip || '',
        invoiceTitle: r => r.invoice_number || r.title || '',
        tnr: r => r.tnr || '',
      };
      const getter = fieldMap[bsSortKey];
      if (getter) {
        rows.sort((a, b) => {
          const va = getter(a), vb = getter(b);
          const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb), 'pl');
          return bsSortDir === 'asc' ? cmp : -cmp;
        });
      }
    }
    return rows;
  }, [bsTransactions, bsQuery, bsBankAccountNums, bsAccountFilter, bsYearFilter, bsMonthOnlyFilter, bsDateFrom, bsDateTo, bsReconciledFilter, bsSortKey, bsSortDir]);

  // Podfiltrowane tablice transakcji dla dodatkowych zakładek
  const filteredBsTxCredits = React.useMemo(() =>
    filteredBsTransactions.filter(r => (r.direction === 'C' || r.direction === 'RC') && r.split_payment_role !== 'vat_transfer'),
  [filteredBsTransactions]);

  const filteredBsTxDebits = React.useMemo(() =>
    filteredBsTransactions.filter(r => r.direction === 'D' || r.direction === 'RD'),
  [filteredBsTransactions]);

  const filteredBsTxVatCredits = React.useMemo(() =>
    filteredBsTransactions.filter(r => (r.direction === 'C' || r.direction === 'RC') && r.split_payment_role === 'vat_transfer'),
  [filteredBsTransactions]);

  // Pomocnicza funkcja: dane transakcji do wyświetlenia dla danego sub-taba
  const bsSubTabTxData = React.useCallback((tab: number): BankStatementTransaction[] => {
    if (tab === 1) return filteredBsTransactions;
    if (tab === 2) return filteredBsTxCredits;
    if (tab === 3) return filteredBsTxDebits;
    if (tab === 4) return filteredBsTxVatCredits;
    return [];
  }, [filteredBsTransactions, filteredBsTxCredits, filteredBsTxDebits, filteredBsTxVatCredits]);

  // Kolumny transakcji bez R i T (dla zakładek wpłaty/wypłaty/VAT)
  const BS_TX_COLS_NO_RT = React.useMemo(() => ['reconciled', 'dates', 'statementAccount', 'statementNumber', 'currency', 'counterpartyName', 'counterpartyAccount', 'amount', 'vatAmount', 'counterpartyNip', 'invoiceTitle', 'tnr'], []);

  // IntersectionObserver do auto-doładowywania wierszy (infinite scroll)
  const bsTotalRowsForTab = bsSubTab === 0 ? filteredBsStatements.length : bsSubTabTxData(bsSubTab).length;
  React.useEffect(() => {
    const sentinel = bsSentinelRef.current;
    const container = bsTableContainerRef.current;
    if (!sentinel || !container) return;
    if (bsVisibleCount >= bsTotalRowsForTab) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setBsVisibleCount((prev) => Math.min(prev + BS_PAGE_SIZE, bsTotalRowsForTab));
        }
      },
      { root: container, rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [bsSubTab, bsVisibleCount, bsTotalRowsForTab]);

  // Kolumny wyciągów
  const BS_STMT_COLS = ['statementNumber', 'bankName', 'accountNumber', 'closingDate', 'currency', 'openingBalance', 'closingBalance', 'totalDebit', 'totalCredit', 'txCount', 'sourceFilename'];
  const bsStmtColLabel = React.useCallback((key: string) => {
    if (key === 'statementNumber') return 'Nr wyciągu';
    if (key === 'bankName') return 'Bank';
    if (key === 'accountNumber') return 'Rachunek';
    if (key === 'closingDate') return 'Data zamknięcia';
    if (key === 'currency') return 'Waluta';
    if (key === 'openingBalance') return 'Saldo otwarcia';
    if (key === 'closingBalance') return 'Saldo zamknięcia';
    if (key === 'totalDebit') return 'Obciążenia';
    if (key === 'totalCredit') return 'Uznania';
    if (key === 'txCount') return 'Transakcje';
    if (key === 'sourceFilename') return 'Plik źródłowy';
    return key;
  }, []);
  const DEFAULT_VIS_BS_STMT: Record<string, boolean> = { statementNumber: true, bankName: true, accountNumber: true, closingDate: true, currency: true, openingBalance: true, closingBalance: true, totalDebit: true, totalCredit: true, txCount: true, sourceFilename: true };
  const defaultColWidthsBsStmt: Record<string, number> = { statementNumber: 100, bankName: 100, accountNumber: 200, closingDate: 120, currency: 60, openingBalance: 130, closingBalance: 130, totalDebit: 120, totalCredit: 120, txCount: 90, sourceFilename: 200 };

  // Kolumny transakcji
  const BS_TX_COLS = ['reconciled', 'dates', 'direction', 'txType', 'statementAccount', 'statementNumber', 'currency', 'counterpartyName', 'counterpartyAccount', 'amount', 'vatAmount', 'counterpartyNip', 'invoiceTitle', 'tnr'];
  const bsTxColLabel = React.useCallback((key: string) => {
    if (key === 'reconciled') return 'U';
    if (key === 'dates') return 'Data';
    if (key === 'direction') return 'R';
    if (key === 'txType') return 'T';
    if (key === 'statementAccount') return 'Rachunek';
    if (key === 'statementNumber') return 'Nr wyciągu';
    if (key === 'currency') return 'Waluta';
    if (key === 'counterpartyName') return 'Kontrahent';
    if (key === 'counterpartyAccount') return 'Rach. kontrahenta';
    if (key === 'amount') return 'Kwota';
    if (key === 'vatAmount') return 'VAT';
    if (key === 'counterpartyNip') return 'NIP';
    if (key === 'invoiceTitle') return 'Faktura, tytuł';
    if (key === 'tnr') return 'TNR';
    return key;
  }, []);
  // Tooltip dla nagłówków kolumn transakcji
  const bsTxColTooltip = React.useCallback((key: string): string | null => {
    if (key === 'reconciled') return 'Uzgodnienie';
    if (key === 'direction') return 'Rodzaj';
    if (key === 'txType') return 'Typ';
    return null;
  }, []);
  const DEFAULT_VIS_BS_TX: Record<string, boolean> = { reconciled: true, dates: true, direction: true, txType: true, statementAccount: true, statementNumber: true, currency: true, counterpartyName: true, counterpartyAccount: true, amount: true, vatAmount: true, counterpartyNip: true, invoiceTitle: true, tnr: false };
  const defaultColWidthsBsTx: Record<string, number> = { reconciled: 36, dates: 90, direction: 36, txType: 40, statementAccount: 150, statementNumber: 75, currency: 55, counterpartyName: 200, counterpartyAccount: 165, amount: 115, vatAmount: 95, counterpartyNip: 105, invoiceTitle: 280, tnr: 170 };
  const DEFAULT_VIS_BS_TX_NO_RT: Record<string, boolean> = { reconciled: true, dates: true, statementAccount: true, statementNumber: true, currency: true, counterpartyName: true, counterpartyAccount: true, amount: true, vatAmount: true, counterpartyNip: true, invoiceTitle: true, tnr: false };
  const defaultColWidthsBsTxNoRT: Record<string, number> = { reconciled: 36, dates: 90, statementAccount: 150, statementNumber: 75, currency: 55, counterpartyName: 220, counterpartyAccount: 165, amount: 115, vatAmount: 95, counterpartyNip: 105, invoiceTitle: 300, tnr: 170 };

  // Pomocnicze: kolumny dla danej zakładki
  const bsColsForTab = React.useCallback((st: number) => {
    if (st === 0) return BS_STMT_COLS;
    if (st === 1) return BS_TX_COLS;
    return BS_TX_COLS_NO_RT;
  }, [BS_TX_COLS_NO_RT]);
  const bsDefVisForTab = React.useCallback((st: number) => {
    if (st === 0) return DEFAULT_VIS_BS_STMT;
    if (st === 1) return DEFAULT_VIS_BS_TX;
    return DEFAULT_VIS_BS_TX_NO_RT;
  }, []);
  const bsDefWidForTab = React.useCallback((st: number) => {
    if (st === 0) return defaultColWidthsBsStmt;
    if (st === 1) return defaultColWidthsBsTx;
    return defaultColWidthsBsTxNoRT;
  }, []);

  // Wersjonowanie schematu kolumn - zmień przy każdej zmianie definicji kolumn
  const BS_COL_SCHEMA_VERSION = 8; // Bump when column definitions change
  const bsSchemaKey = React.useCallback((st: number) => `bs.columns.schemaVersion.${st}:${user?.user_id || 'anon'}`, [user?.user_id]);

  // Column state for bank statements (per subTab: 0=Wyciągi, 1=Wszystkie transakcje, 2=Wpłaty, 3=Wypłaty, 4=Wpłaty VAT)
  const [colsVisBs, setColsVisBs] = React.useState<Record<string, boolean>>({ ...DEFAULT_VIS_BS_STMT });
  const [colsOrdBs, setColsOrdBs] = React.useState<string[]>([...BS_STMT_COLS]);
  const [colsWidBs, setColsWidBs] = React.useState<Record<string, number>>({ ...defaultColWidthsBsStmt });

  // Zabezpieczenie: upewnij się, że kolumny pasują do aktywnej zakładki
  const safeBsColsOrd = React.useMemo(() => {
    const expected = bsColsForTab(bsSubTab);
    const allSavedValid = colsOrdBs.length > 0 && colsOrdBs.every(c => expected.includes(c));
    if (!allSavedValid) return [...expected];
    const missing = expected.filter(c => !colsOrdBs.includes(c));
    if (missing.length > 0) return [...missing, ...colsOrdBs];
    return colsOrdBs;
  }, [bsSubTab, colsOrdBs, bsColsForTab]);
  const safeBsColsVis = React.useMemo(() => {
    const expected = bsColsForTab(bsSubTab);
    const defVis = bsDefVisForTab(bsSubTab);
    const allValid = expected.every(c => c in colsVisBs);
    return allValid ? colsVisBs : { ...defVis };
  }, [bsSubTab, colsVisBs, bsColsForTab, bsDefVisForTab]);
  const safeBsColsWid = React.useMemo(() => {
    const defWid = bsDefWidForTab(bsSubTab);
    return { ...defWid, ...colsWidBs };
  }, [bsSubTab, colsWidBs, bsDefWidForTab]);

  const colVBs = React.useCallback((key: string) => safeBsColsVis[key] !== false, [safeBsColsVis]);
  const getColWBs = (key: string) => `${safeBsColsWid[key] ?? 120}px`;

  // Dostosuj kolumny panel
  const [colsAnchorElBs, setColsAnchorElBs] = React.useState<null | HTMLElement>(null);
  const colsOpenBs = Boolean(colsAnchorElBs);
  const [draftColsVisBs, setDraftColsVisBs] = React.useState<Record<string, boolean> | null>(null);
  const [draftColsOrdBs, setDraftColsOrdBs] = React.useState<string[] | null>(null);
  const activeBsColsOrd = colsOpenBs ? (draftColsOrdBs ?? safeBsColsOrd) : safeBsColsOrd;

  // Draft kolumn ma dotyczyć wyłącznie bieżącego kontekstu (main/sub tab) i aktywnego panelu.
  React.useEffect(() => {
    setDraftColsVisBs(null);
    setDraftColsOrdBs(null);
  }, [sectionChip, bsMainTab, bsSubTab]);

  // localStorage keys
  const bsVisKey = React.useCallback((st: number) => `bs.columns.visible.${st}:${user?.user_id || 'anon'}`, [user?.user_id]);
  const bsOrdKey = React.useCallback((st: number) => `bs.columns.order.${st}:${user?.user_id || 'anon'}`, [user?.user_id]);
  const bsWidKey = React.useCallback((st: number) => `bs.columns.widths.${st}:${user?.user_id || 'anon'}`, [user?.user_id]);

  const loadBsColSettings = React.useCallback((st: number) => {
    const cols = bsColsForTab(st);
    const defVis = bsDefVisForTab(st);
    const defWid = bsDefWidForTab(st);
    // Sprawdź wersję schematu kolumn - jeśli stara, wyczyść zapisane ustawienia
    try {
      const savedVer = localStorage.getItem(bsSchemaKey(st));
      if (savedVer !== String(BS_COL_SCHEMA_VERSION)) {
        // Stare ustawienia - usuń je i zapisz nową wersję
        localStorage.removeItem(bsVisKey(st));
        localStorage.removeItem(bsOrdKey(st));
        localStorage.removeItem(bsWidKey(st));
        localStorage.setItem(bsSchemaKey(st), String(BS_COL_SCHEMA_VERSION));
        setColsVisBs({ ...defVis });
        setColsOrdBs([...cols]);
        setColsWidBs({ ...defWid });
        return;
      }
    } catch { /* ignore */ }
    try { const r = localStorage.getItem(bsVisKey(st)); if (r) { const saved = JSON.parse(r); const filtered: Record<string, boolean> = {}; for (const k of cols) { filtered[k] = saved[k] !== undefined ? saved[k] : defVis[k]; } setColsVisBs(filtered); } else setColsVisBs({ ...defVis }); } catch { setColsVisBs({ ...defVis }); }
    try { const r = localStorage.getItem(bsOrdKey(st)); if (r) { const s: string[] = JSON.parse(r); const valid = s.filter(c => cols.includes(c)); const m = cols.filter(c => !valid.includes(c)); if (m.length) valid.push(...m); setColsOrdBs(valid); } else setColsOrdBs([...cols]); } catch { setColsOrdBs([...cols]); }
    try { const r = localStorage.getItem(bsWidKey(st)); if (r) { const p = JSON.parse(r); if (p && typeof p === 'object') setColsWidBs({ ...defWid, ...p }); else setColsWidBs({ ...defWid }); } else setColsWidBs({ ...defWid }); } catch { setColsWidBs({ ...defWid }); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bsVisKey, bsOrdKey, bsWidKey, bsSchemaKey, bsColsForTab, bsDefVisForTab, bsDefWidForTab]);

  const saveBsColSettings = React.useCallback((st: number, vis: Record<string, boolean>, ord: string[], wid: Record<string, number>) => {
    try { localStorage.setItem(bsVisKey(st), JSON.stringify(vis)); } catch {}
    try { localStorage.setItem(bsOrdKey(st), JSON.stringify(ord)); } catch {}
    try { localStorage.setItem(bsWidKey(st), JSON.stringify(wid)); } catch {}
    try { localStorage.setItem(bsSchemaKey(st), String(BS_COL_SCHEMA_VERSION)); } catch {}
  }, [bsVisKey, bsOrdKey, bsWidKey, bsSchemaKey]);

  // Kolumny ładowane synchronicznie w handlerze Tabs onChange — efekt zbędny

  // Auto-save kolumn (z walidacją – nie zapisuj kolumn z innej zakładki)
  React.useEffect(() => {
    if (sectionChip !== 7) return;
    const expectedCols = bsColsForTab(bsSubTab);
    const colsMatch = colsOrdBs.length > 0 && colsOrdBs.every(c => expectedCols.includes(c));
    if (!colsMatch) return; // kolumny nie pasują do zakładki – nie zapisuj
    const t = setTimeout(() => { saveBsColSettings(bsSubTab, colsVisBs, colsOrdBs, colsWidBs); }, 400);
    return () => clearTimeout(t);
  }, [colsVisBs, colsOrdBs, colsWidBs, bsSubTab, sectionChip, saveBsColSettings]);

  // Init kolumn na starcie
  React.useEffect(() => { if (sectionChip === 7) loadBsColSettings(bsSubTab); }, [sectionChip, loadBsColSettings, bsSubTab]);

  // ESC zamyka panel "Dostosuj kolumny" BS
  React.useEffect(() => {
    if (!colsOpenBs) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setColsAnchorElBs(null); setDraftColsVisBs(null); setDraftColsOrdBs(null); setColResizeMode(false);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [colsOpenBs]);

  // === SEKCJA OPERACJE BANKOWE (sectionChip === 6) ===
  const [bankOpsMainTab, setBankOpsMainTab] = React.useState(0); // 0=Wypłaty, 1=Wpłaty
  const [bankOpsSubTab, setBankOpsSubTab] = React.useState(0); // 0=Niezrealizowane, 1=Zrealizowane, 2=Uzgodnione
  const [bankOpsQuery, setBankOpsQuery] = React.useState('');
  const [bankOpsProjectFilter, setBankOpsProjectFilter] = React.useState('');
  const [bankOpsMonthFilter, setBankOpsMonthFilter] = React.useState('');
  const [bankOpsStatusFilter, setBankOpsStatusFilter] = React.useState('');
  const [bankOpsDateFrom, setBankOpsDateFrom] = React.useState('');
  const [bankOpsDateTo, setBankOpsDateTo] = React.useState('');
  const bankOpsDateFromRef = React.useRef<HTMLInputElement | null>(null);
  const bankOpsDateToRef = React.useRef<HTMLInputElement | null>(null);

  // Dane operacji bankowych
  const [bankOpsRows, setBankOpsRows] = React.useState<BankOperation[]>([]);
  const [bankOpsLoading, setBankOpsLoading] = React.useState(false);

  const refreshBankOps = React.useCallback(async () => {
    setBankOpsLoading(true);
    try {
      const data = await listBankOperations();
      setBankOpsRows(data);
    } catch (e) {
      console.error('Błąd pobierania operacji bankowych:', e);
    } finally {
      setBankOpsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (sectionChip === 6) {
      refreshBankOps();
    }
  }, [sectionChip, refreshBankOps]);

  // Reset sub-tab i filtrów przy zmianie main tab
  React.useEffect(() => {
    setBankOpsSubTab(0);
    setBankOpsQuery('');
    setBankOpsProjectFilter('');
    setBankOpsMonthFilter('');
    setBankOpsStatusFilter('');
    setBankOpsDateFrom('');
    setBankOpsDateTo('');
  }, [bankOpsMainTab]);

  // Kolumny operacji bankowych
  const BANK_OPS_COLS = ['operationRef', 'status', 'operationDate', 'contractor', 'nip', 'transferTitle', 'paymentType', 'grossAmount', 'vatAmount', 'contractorAccount', 'unitrescoAccount', 'statementNumber', 'reconciliationBank', 'reconciliationStmtNum', 'reconciliationStmtDate'];
  const bankOpsColLabel = React.useCallback((key: string, isWplaty: boolean) => {
    if (key === 'operationRef') return 'ID';
    if (key === 'status') return 'Status';
    if (key === 'operationDate') return 'Data operacji';
    if (key === 'contractor') return 'Kontrahent';
    if (key === 'nip') return 'NIP';
    if (key === 'transferTitle') return 'Tytuł przelewu';
    if (key === 'paymentType') return 'Rodzaj płatności';
    if (key === 'grossAmount') return isWplaty ? 'Kwota wpłaty brutto' : 'Kwota wypłaty brutto';
    if (key === 'vatAmount') return 'Kwota VAT';
    if (key === 'contractorAccount') return 'Rachunek podmiotu';
    if (key === 'unitrescoAccount') return 'Rachunek Unitresco';
    if (key === 'statementNumber') return 'Nr wyciągu';
    if (key === 'reconciliationBank') return 'Bank';
    if (key === 'reconciliationStmtNum') return 'Nr wyciągu (uzg.)';
    if (key === 'reconciliationStmtDate') return 'Data wyciągu';
    return key;
  }, []);

  // Domyślne widoczności i szerokości kolumn
  const DEFAULT_VIS_BANK_OPS: Record<string, boolean> = React.useMemo(() => ({
    operationRef: true, status: true, operationDate: true, contractor: true, nip: true, transferTitle: true, paymentType: true,
    grossAmount: true, vatAmount: true, contractorAccount: true, unitrescoAccount: true, statementNumber: true,
    reconciliationBank: true, reconciliationStmtNum: true, reconciliationStmtDate: true,
  }), []);
  const defaultColumnWidthsBankOps = React.useMemo<Record<string, number>>(() => ({
    operationRef: 155, status: 130, operationDate: 120, contractor: 220, nip: 130, transferTitle: 250, paymentType: 130,
    grossAmount: 160, vatAmount: 130, contractorAccount: 200, unitrescoAccount: 200, statementNumber: 100,
    reconciliationBank: 100, reconciliationStmtNum: 100, reconciliationStmtDate: 110,
  }), []);

  // localStorage keys per tab (mainTab.subTab)
  const BANK_OPS_COL_SCHEMA_VERSION = 3; // Bump when column definitions change
  const bankOpsSchemaKey = React.useCallback((mt: number, st: number) => `bankOps.columns.schemaVersion.${mt}.${st}:${user?.user_id || 'anon'}`, [user?.user_id]);
  const bankOpsVisKey = React.useCallback((mt: number, st: number) => `bankOps.columns.visible.${mt}.${st}:${user?.user_id || 'anon'}`, [user?.user_id]);
  const bankOpsOrdKey = React.useCallback((mt: number, st: number) => `bankOps.columns.order.${mt}.${st}:${user?.user_id || 'anon'}`, [user?.user_id]);
  const bankOpsWidKey = React.useCallback((mt: number, st: number) => `bankOps.columns.widths.${mt}.${st}:${user?.user_id || 'anon'}`, [user?.user_id]);

  // Column state
  const [columnsVisibleBankOps, setColumnsVisibleBankOps] = React.useState<Record<string, boolean>>({ ...DEFAULT_VIS_BANK_OPS });
  const [columnOrderBankOps, setColumnOrderBankOps] = React.useState<string[]>([...BANK_OPS_COLS]);
  const [columnWidthsBankOps, setColumnWidthsBankOps] = React.useState<Record<string, number>>({ ...defaultColumnWidthsBankOps });
  const colVBankOps = React.useCallback((key: string) => columnsVisibleBankOps[key] !== false, [columnsVisibleBankOps]);
  const getColWBankOps = (key: string) => `${columnWidthsBankOps[key] ?? defaultColumnWidthsBankOps[key] ?? 120}px`;

  // Panel Dostosuj kolumny
  const [colsAnchorElBankOps, setColsAnchorElBankOps] = React.useState<null | HTMLElement>(null);
  const colsOpenBankOps = Boolean(colsAnchorElBankOps);
  const [draftColumnsVisibleBankOps, setDraftColumnsVisibleBankOps] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrderBankOps, setDraftOrderBankOps] = React.useState<string[] | null>(null);

  // Zapisz/wczytaj ustawienia kolumn per sub-tab
  const loadBankOpsColSettings = React.useCallback((mt: number, st: number) => {
    // Sprawdź wersję schematu kolumn - jeśli stara, wyczyść zapisane ustawienia
    try {
      const savedVer = localStorage.getItem(bankOpsSchemaKey(mt, st));
      if (savedVer !== String(BANK_OPS_COL_SCHEMA_VERSION)) {
        localStorage.removeItem(bankOpsVisKey(mt, st));
        localStorage.removeItem(bankOpsOrdKey(mt, st));
        localStorage.removeItem(bankOpsWidKey(mt, st));
        localStorage.setItem(bankOpsSchemaKey(mt, st), String(BANK_OPS_COL_SCHEMA_VERSION));
        setColumnsVisibleBankOps({ ...DEFAULT_VIS_BANK_OPS });
        setColumnOrderBankOps([...BANK_OPS_COLS]);
        setColumnWidthsBankOps({ ...defaultColumnWidthsBankOps });
        return;
      }
    } catch { /* ignore */ }
    try {
      const raw = localStorage.getItem(bankOpsVisKey(mt, st));
      if (raw) setColumnsVisibleBankOps({ ...DEFAULT_VIS_BANK_OPS, ...JSON.parse(raw) });
      else setColumnsVisibleBankOps({ ...DEFAULT_VIS_BANK_OPS });
    } catch { setColumnsVisibleBankOps({ ...DEFAULT_VIS_BANK_OPS }); }
    try {
      const raw = localStorage.getItem(bankOpsOrdKey(mt, st));
      if (raw) {
        const saved: string[] = JSON.parse(raw);
        const missing = BANK_OPS_COLS.filter(c => !saved.includes(c));
        if (missing.length > 0) saved.unshift(...missing);
        setColumnOrderBankOps(saved);
      } else setColumnOrderBankOps([...BANK_OPS_COLS]);
    } catch { setColumnOrderBankOps([...BANK_OPS_COLS]); }
    try {
      const raw = localStorage.getItem(bankOpsWidKey(mt, st));
      if (raw) { const p = JSON.parse(raw); if (p && typeof p === 'object') setColumnWidthsBankOps({ ...defaultColumnWidthsBankOps, ...p }); else setColumnWidthsBankOps({ ...defaultColumnWidthsBankOps }); }
      else setColumnWidthsBankOps({ ...defaultColumnWidthsBankOps });
    } catch { setColumnWidthsBankOps({ ...defaultColumnWidthsBankOps }); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankOpsVisKey, bankOpsOrdKey, bankOpsWidKey, bankOpsSchemaKey]);

  const saveBankOpsColSettings = React.useCallback((mt: number, st: number, vis: Record<string, boolean>, ord: string[], wid: Record<string, number>) => {
    try { localStorage.setItem(bankOpsVisKey(mt, st), JSON.stringify(vis)); } catch {}
    try { localStorage.setItem(bankOpsOrdKey(mt, st), JSON.stringify(ord)); } catch {}
    try { localStorage.setItem(bankOpsWidKey(mt, st), JSON.stringify(wid)); } catch {}
    try { localStorage.setItem(bankOpsSchemaKey(mt, st), String(BANK_OPS_COL_SCHEMA_VERSION)); } catch {}
  }, [bankOpsVisKey, bankOpsOrdKey, bankOpsWidKey, bankOpsSchemaKey]);

  // Ostatnio aktywna para zakładek (main/sub) dla zapisów per-tab.
  const prevBankOpsTabRef = React.useRef({ mt: bankOpsMainTab, st: bankOpsSubTab });

  // Wczytaj ustawienia kolumn przy pierwszym renderze
  React.useEffect(() => {
    loadBankOpsColSettings(bankOpsMainTab, bankOpsSubTab);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Po dociągnięciu kontekstu użytkownika przeładuj ustawienia z właściwego klucza localStorage.
  React.useEffect(() => {
    setDraftColumnsVisibleBankOps(null);
    setDraftOrderBankOps(null);
    loadBankOpsColSettings(bankOpsMainTab, bankOpsSubTab);
    prevBankOpsTabRef.current = { mt: bankOpsMainTab, st: bankOpsSubTab };
  }, [user?.user_id, bankOpsMainTab, bankOpsSubTab, loadBankOpsColSettings]);

  // Zapis szerokości przy zmianie (resize) – debounce
  React.useEffect(() => {
    const t = setTimeout(() => {
      saveBankOpsColSettings(bankOpsMainTab, bankOpsSubTab, columnsVisibleBankOps, columnOrderBankOps, columnWidthsBankOps);
    }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnWidthsBankOps]);

  // Przy zmianie zakładek – zapisz stare, załaduj nowe
  React.useEffect(() => {
    const prev = prevBankOpsTabRef.current;
    if (prev.mt !== bankOpsMainTab || prev.st !== bankOpsSubTab) {
      saveBankOpsColSettings(prev.mt, prev.st, columnsVisibleBankOps, columnOrderBankOps, columnWidthsBankOps);
      prevBankOpsTabRef.current = { mt: bankOpsMainTab, st: bankOpsSubTab };
      loadBankOpsColSettings(bankOpsMainTab, bankOpsSubTab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankOpsMainTab, bankOpsSubTab]);

  // Filtrowane wiersze operacji bankowych
  const filteredBankOpsRows = React.useMemo(() => {
    let rows = [...bankOpsRows];
    // Filtr głównego tabu (typ: wyplata/wplata)
    rows = rows.filter((r: any) => bankOpsMainTab === 0 ? r.type === 'wyplata' : r.type === 'wplata');
    // Filtr sub-tabu (status)
    if (bankOpsSubTab === 0) rows = rows.filter((r: any) => r.status === 'niezrealizowane');
    else if (bankOpsSubTab === 1) rows = rows.filter((r: any) => r.status === 'zrealizowane');
    else if (bankOpsSubTab === 2) rows = rows.filter((r: any) => r.status === 'uzgodnione');
    if (bankOpsQuery) {
      const q = bankOpsQuery.toLowerCase();
      rows = rows.filter((r: any) =>
        (r.contractor || '').toLowerCase().includes(q) ||
        (r.nip || '').toLowerCase().includes(q) ||
        (r.transferTitle || '').toLowerCase().includes(q) ||
        (r.statementNumber || '').toLowerCase().includes(q)
      );
    }
    if (bankOpsProjectFilter) rows = rows.filter((r: any) => r.projectCode === bankOpsProjectFilter);
    if (bankOpsMonthFilter && bankOpsMonthFilter !== '') rows = rows.filter((r: any) => r.operationDate && String(r.operationDate).slice(0, 7) === bankOpsMonthFilter);
    if (bankOpsDateFrom) rows = rows.filter((r: any) => r.operationDate && String(r.operationDate).slice(0, 10) >= bankOpsDateFrom);
    if (bankOpsDateTo) rows = rows.filter((r: any) => r.operationDate && String(r.operationDate).slice(0, 10) <= bankOpsDateTo);
    return rows;
  }, [bankOpsRows, bankOpsMainTab, bankOpsSubTab, bankOpsQuery, bankOpsProjectFilter, bankOpsMonthFilter, bankOpsDateFrom, bankOpsDateTo]);

  const bankOpsSubTabCounts = React.useMemo(() => {
    let rows = [...bankOpsRows];
    rows = rows.filter((r: any) => bankOpsMainTab === 0 ? r.type === 'wyplata' : r.type === 'wplata');
    return {
      niezrealizowane: rows.filter((r: any) => r.status === 'niezrealizowane').length,
      zrealizowane: rows.filter((r: any) => r.status === 'zrealizowane').length,
      uzgodnione: rows.filter((r: any) => r.status === 'uzgodnione').length,
    };
  }, [bankOpsRows, bankOpsMainTab]);

  // Kebab menu operacji bankowych
  const [bankOpsMenuAnchor, setBankOpsMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [bankOpsMenuRow, setBankOpsMenuRow] = React.useState<any>(null);

  // Zaznaczanie wierszy operacji bankowych (Google Sheets style)
  const [bankOpsSelectedIds, setBankOpsSelectedIds] = React.useState<Set<number | string>>(new Set());
  const [bankOpsLastClickedIdx, setBankOpsLastClickedIdx] = React.useState<number | null>(null);
  const bankOpsRowsRef = React.useRef<any[]>([]);
  bankOpsRowsRef.current = filteredBankOpsRows;

  // Reset zaznaczenia przy zmianie zakładek/filtrów
  React.useEffect(() => {
    setBankOpsSelectedIds(new Set());
    setBankOpsLastClickedIdx(null);
  }, [bankOpsMainTab, bankOpsSubTab, bankOpsQuery, bankOpsProjectFilter, bankOpsMonthFilter, bankOpsDateFrom, bankOpsDateTo]);

  const handleBankOpsRowClick = React.useCallback((rowIdx: number, rowId: number | string, e: React.MouseEvent) => {
    // Ignoruj kliknięcia na przyciski/ikony
    if ((e.target as HTMLElement).closest('button, a, .MuiIconButton-root, .MuiChip-root')) return;

    if (e.shiftKey && bankOpsLastClickedIdx !== null) {
      // Shift+klik: zaznacz zakres
      const start = Math.min(bankOpsLastClickedIdx, rowIdx);
      const end = Math.max(bankOpsLastClickedIdx, rowIdx);
      const idsInRange = new Set(bankOpsSelectedIds);
      for (let i = start; i <= end; i++) {
        const row = bankOpsRowsRef.current[i];
        if (row) idsInRange.add(row.id);
      }
      setBankOpsSelectedIds(idsInRange);
    } else if (e.ctrlKey || e.metaKey) {
      // Ctrl/Cmd+klik: toggle pojedynczego
      setBankOpsSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
        return next;
      });
      setBankOpsLastClickedIdx(rowIdx);
    } else {
      // Zwykły klik: zaznacz/odznacz jeden
      if (bankOpsSelectedIds.size === 1 && bankOpsSelectedIds.has(rowId)) {
        setBankOpsSelectedIds(new Set());
        setBankOpsLastClickedIdx(null);
      } else {
        setBankOpsSelectedIds(new Set([rowId]));
        setBankOpsLastClickedIdx(rowIdx);
      }
    }
  }, [bankOpsLastClickedIdx, bankOpsSelectedIds]);

  // Resizable header bankOps
  const renderResizableThBankOps = React.useCallback((colKey: string, content: React.ReactNode, align?: 'left' | 'right' | 'center') => {
    return (
      <TableCell
        key={colKey}
        align={align}
        sx={{
          position: 'relative', userSelect: colResizeMode ? 'none' : undefined,
          width: getColWBankOps(colKey), maxWidth: 'none',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {content}
        {colResizeMode ? (
          <Box
            role="separator"
            aria-orientation="vertical"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragCol({ key: colKey, startX: e.clientX, startW: columnWidthsBankOps[colKey] ?? defaultColumnWidthsBankOps[colKey] ?? 120, tableType: 'bankOps' });
            }}
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '12px',
              minHeight: '100%',
              cursor: 'col-resize',
              zIndex: 15,
              touchAction: 'none',
              pointerEvents: 'auto',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: '50%',
                top: 0,
                bottom: 0,
                width: '2px',
                transform: 'translateX(-50%)',
                backgroundColor: '#FF0000',
                zIndex: 16
              },
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.06)',
                '&::before': {
                  width: '3px',
                  backgroundColor: '#CC0000'
                }
              }
            }}
          />
        ) : null}
      </TableCell>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colResizeMode, columnWidthsBankOps, defaultColumnWidthsBankOps]);

  // Kolumny Rozrachunki – widoczność i kolejność (per-tab)
  const SETTLE_COLS = ['settlementId', 'dueDate', 'daysLeft', 'projectCode', 'warehouse', 'docType', 'contractor', 'nip', 'invoiceNumber', 'grossAmount', 'vatAmount', 'paidDate', 'accountNumber', 'createdDate', 'reconciliationBank', 'reconciliationStmtNum', 'reconciliationStmtDate', 'actions'];
  const getDefaultVisSettle = React.useCallback((tab: number): Record<string, boolean> => ({
    settlementId: true, dueDate: true, daysLeft: true, projectCode: true, warehouse: true, docType: true,
    contractor: true, nip: true, invoiceNumber: true, grossAmount: true, vatAmount: true,
    paidDate: tab !== 0,
    accountNumber: true, createdDate: false,
    reconciliationBank: tab === 3 || tab === 4,
    reconciliationStmtNum: tab === 3 || tab === 4,
    reconciliationStmtDate: tab === 3 || tab === 4,
    actions: true
  }), []);

  const SETTLE_COL_SCHEMA_VERSION = 6; // Bump when column definitions change
  const settleSchemaKey = React.useCallback((tab: number) => `settlements.columns.schemaVersion.tab${tab}:${user?.user_id || 'anon'}`, [user?.user_id]);
  const settleVisKey = React.useCallback((tab: number) => `settlements.columns.visible.tab${tab}:${user?.user_id || 'anon'}`, [user?.user_id]);
  const settleOrdKey = React.useCallback((tab: number) => `settlements.columns.order.tab${tab}:${user?.user_id || 'anon'}`, [user?.user_id]);
  const settleWidKey = React.useCallback((tab: number) => `settlementsTableColWidths.tab${tab}:${user?.user_id || 'anon'}`, [user?.user_id]);

  const loadSettleColSettings = React.useCallback((tab: number) => {
    const defaultVis = getDefaultVisSettle(tab);
    // Sprawdź wersję schematu kolumn - jeśli stara, wyczyść zapisane ustawienia
    try {
      const savedVer = localStorage.getItem(settleSchemaKey(tab));
      if (savedVer !== String(SETTLE_COL_SCHEMA_VERSION)) {
        localStorage.removeItem(settleVisKey(tab));
        localStorage.removeItem(settleOrdKey(tab));
        localStorage.removeItem(settleWidKey(tab));
        localStorage.setItem(settleSchemaKey(tab), String(SETTLE_COL_SCHEMA_VERSION));
        setColumnsVisibleSettle({ ...defaultVis });
        setColumnOrderSettle([...SETTLE_COLS]);
        setColumnWidthsSettle({ ...defaultColumnWidthsSettle });
        return;
      }
    } catch { /* ignore */ }
    try {
      const raw = localStorage.getItem(settleVisKey(tab));
      if (raw) { const saved = JSON.parse(raw); setColumnsVisibleSettle({ ...defaultVis, ...saved }); }
      else setColumnsVisibleSettle({ ...defaultVis });
    } catch { setColumnsVisibleSettle({ ...defaultVis }); }

    try {
      const raw = localStorage.getItem(settleOrdKey(tab));
      if (raw) {
        const saved: string[] = JSON.parse(raw);
        const missing = SETTLE_COLS.filter(c => !saved.includes(c));
        if (missing.length > 0) {
          // Wstaw brakujące kolumny na początku (przed istniejącymi)
          saved.unshift(...missing);
        }
        setColumnOrderSettle(saved);
      } else setColumnOrderSettle([...SETTLE_COLS]);
    } catch { setColumnOrderSettle([...SETTLE_COLS]); }

    try {
      const raw = localStorage.getItem(settleWidKey(tab));
      if (raw) { const parsed = JSON.parse(raw); if (parsed && typeof parsed === 'object') setColumnWidthsSettle({ ...defaultColumnWidthsSettle, ...parsed }); else setColumnWidthsSettle({ ...defaultColumnWidthsSettle }); }
      else setColumnWidthsSettle({ ...defaultColumnWidthsSettle });
    } catch { setColumnWidthsSettle({ ...defaultColumnWidthsSettle }); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settleVisKey, settleOrdKey, settleWidKey, settleSchemaKey]);

  const saveSettleColSettings = React.useCallback((tab: number, vis: Record<string, boolean>, ord: string[], wid: Record<string, number>) => {
    try { localStorage.setItem(settleVisKey(tab), JSON.stringify(vis)); } catch {}
    try { localStorage.setItem(settleOrdKey(tab), JSON.stringify(ord)); } catch {}
    try { localStorage.setItem(settleWidKey(tab), JSON.stringify(wid)); } catch {}
    try { localStorage.setItem(settleSchemaKey(tab), String(SETTLE_COL_SCHEMA_VERSION)); } catch {}
  }, [settleVisKey, settleOrdKey, settleWidKey, settleSchemaKey]);

  const [columnsVisibleSettle, setColumnsVisibleSettle] = React.useState<Record<string, boolean>>(() => {
    const defaultVis = getDefaultVisSettle(0);
    // Sprawdź wersję schematu - jeśli stara, zignoruj zapisane ustawienia
    try {
      const savedVer = localStorage.getItem(`settlements.columns.schemaVersion.tab0:${user?.user_id || 'anon'}`);
      if (savedVer !== String(SETTLE_COL_SCHEMA_VERSION)) {
        // Stary schemat - wyczyść i zwróć domyślne
        localStorage.removeItem(`settlements.columns.visible.tab0:${user?.user_id || 'anon'}`);
        localStorage.removeItem(`settlements.columns.order.tab0:${user?.user_id || 'anon'}`);
        localStorage.removeItem(`settlementsTableColWidths.tab0:${user?.user_id || 'anon'}`);
        localStorage.setItem(`settlements.columns.schemaVersion.tab0:${user?.user_id || 'anon'}`, String(SETTLE_COL_SCHEMA_VERSION));
        return defaultVis;
      }
    } catch {}
    try { const raw = localStorage.getItem(`settlements.columns.visible.tab0:${user?.user_id || 'anon'}`); if (raw) return { ...defaultVis, ...JSON.parse(raw) }; } catch {}
    return defaultVis;
  });
  const colVSettle = React.useCallback((key: string) => columnsVisibleSettle[key] !== false, [columnsVisibleSettle]);

  const [columnOrderSettle, setColumnOrderSettle] = React.useState<string[]>(() => {
    // Jeśli schemat został zresetowany (powyżej w columnsVisibleSettle), localStorage jest już puste → zwróć domyślne
    try {
      const raw = localStorage.getItem(`settlements.columns.order.tab0:${user?.user_id || 'anon'}`);
      if (raw) {
        const saved: string[] = JSON.parse(raw);
        const missing = SETTLE_COLS.filter(c => !saved.includes(c));
        if (missing.length > 0) {
          // Wstaw brakujące kolumny na początku (przed istniejącymi)
          saved.unshift(...missing);
        }
        return saved;
      }
    } catch {}
    return [...SETTLE_COLS];
  });

  const [colsAnchorElSettle, setColsAnchorElSettle] = React.useState<null | HTMLElement>(null);
  const colsOpenSettle = Boolean(colsAnchorElSettle);
  const [draftColumnsVisibleSettle, setDraftColumnsVisibleSettle] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrderSettle, setDraftOrderSettle] = React.useState<string[] | null>(null);

  const defaultColumnWidthsSettle = React.useMemo<Record<string, number>>(() => ({
    settlementId: 155, createdDate: 130, projectCode: 120, warehouse: 65, docType: 90, dueDate: 140, daysLeft: 85,
    contractor: 300, nip: 130, invoiceNumber: 150, grossAmount: 160, vatAmount: 130, paidDate: 115, accountNumber: 200,
    reconciliationBank: 140, reconciliationStmtNum: 130, reconciliationStmtDate: 130,
    actions: 105,
  }), []);
  const [columnWidthsSettle, setColumnWidthsSettle] = React.useState<Record<string, number>>(() => {
    try {
      const raw = localStorage.getItem(`settlementsTableColWidths.tab0:${user?.user_id || 'anon'}`);
      if (raw) { const parsed = JSON.parse(raw); if (parsed && typeof parsed === 'object') return { ...defaultColumnWidthsSettle, ...parsed }; }
    } catch {}
    return { ...defaultColumnWidthsSettle };
  });
  const getColWSettle = (key: string) => `${columnWidthsSettle[key] ?? defaultColumnWidthsSettle[key] ?? 80}px`;

  // Zapis szerokości kolumn przy zmianie (resize) – pomijamy zapis tuż po przełączeniu zakładki
  const settleWidthsSaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const settleTabSwitchingRef = React.useRef(false);
  React.useEffect(() => {
    if (settleTabSwitchingRef.current) {
      settleTabSwitchingRef.current = false;
      return;
    }
    if (settleWidthsSaveTimer.current) clearTimeout(settleWidthsSaveTimer.current);
    settleWidthsSaveTimer.current = setTimeout(() => {
      try { localStorage.setItem(settleWidKey(settlementsTab), JSON.stringify(columnWidthsSettle)); } catch {}
    }, 300);
    return () => { if (settleWidthsSaveTimer.current) clearTimeout(settleWidthsSaveTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnWidthsSettle, settleWidKey]);

  // Załaduj ustawienia kolumn przy zmianie zakładki rozrachunków
  const prevSettleTabRef = React.useRef(settlementsTab);
  React.useEffect(() => {
    if (prevSettleTabRef.current !== settlementsTab) {
      // Zapisz ustawienia poprzedniej zakładki
      saveSettleColSettings(prevSettleTabRef.current, columnsVisibleSettle, columnOrderSettle, columnWidthsSettle);
      prevSettleTabRef.current = settlementsTab;
      // Oznacz że przełączamy zakładkę (pomijamy auto-save po załadowaniu nowych ustawień)
      settleTabSwitchingRef.current = true;
      // Załaduj ustawienia nowej zakładki
      loadSettleColSettings(settlementsTab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlementsTab]);

  // Zapisz ustawienia kolumn przy odmontowaniu komponentu (np. nawigacja do innej strony)
  const settleColsVisRef = React.useRef(columnsVisibleSettle);
  const settleColsOrdRef = React.useRef(columnOrderSettle);
  const settleColsWidRef = React.useRef(columnWidthsSettle);
  const settleTabRef = React.useRef(settlementsTab);
  settleColsVisRef.current = columnsVisibleSettle;
  settleColsOrdRef.current = columnOrderSettle;
  settleColsWidRef.current = columnWidthsSettle;
  settleTabRef.current = settlementsTab;
  React.useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      saveSettleColSettings(settleTabRef.current, settleColsVisRef.current, settleColsOrdRef.current, settleColsWidRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Nazwy kolumn Rozrachunki w zależności od zakładki (Zakupy vs Sprzedaż)
  const settleColLabel = React.useCallback((key: string) => {
    if (key === 'settlementId') return 'ID';
    if (key === 'createdDate') return 'Data dokumentu';
    if (key === 'projectCode') return 'Kod projektu';
    if (key === 'warehouse') return 'Mag';
    if (key === 'docType') return 'Typ';
    if (key === 'dueDate') return 'Płatność';
    if (key === 'daysLeft') return 'Dni';
    if (key === 'contractor') return 'Kontrahent';
    if (key === 'nip') return 'NIP';
    if (key === 'invoiceNumber') return 'Nr faktury';
    if (key === 'grossAmount') return 'Kwota brutto';
    if (key === 'vatAmount') return 'VAT';
    if (key === 'paidDate') return 'Data zapłaty';
    if (key === 'accountNumber') return 'Nr rachunku';
    if (key === 'reconciliationBank') return 'Bank';
    if (key === 'reconciliationStmtNum') return 'Nr wyciągu';
    if (key === 'reconciliationStmtDate') return 'Data wyciągu';
    if (key === 'actions') return '';
    return key.toUpperCase();
  }, []);
  const settleColTooltip = React.useCallback((key: string) => {
    if (key === 'dueDate') return 'Termin płatności';
    if (key === 'daysLeft') return 'Pozostało dni';
    if (key === 'grossAmount') return settlementsMainTab === 0 ? 'Zobowiązanie brutto' : 'Należność brutto';
    if (key === 'vatAmount') return 'Kwota VAT z faktury';
    return '';
  }, [settlementsMainTab]);

  // Kolumny Sprzedaż – widoczność i kolejność
  const DEFAULT_VIS_SALES: Record<string, boolean> = React.useMemo(() => ({
    dates: true, invoiceType: true, invoiceNumber: true, payment: true,
    seller: true, nip: true, net: true, vatRate: true, vatAmount: true,
    gross: true, status: true, actions: true
  }), []);
  const VISIBLE_KEY_SALES = React.useMemo(() => `sales.columns.visible:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnsVisibleSales, setColumnsVisibleSales] = React.useState<Record<string, boolean>>(() => {
    try { const raw = localStorage.getItem(VISIBLE_KEY_SALES); if (raw) return JSON.parse(raw); } catch {}
    return { ...DEFAULT_VIS_SALES };
  });
  const colVSales = React.useCallback((key: string) => columnsVisibleSales[key] !== false, [columnsVisibleSales]);

  const COLUMNS_ORDER_DEFAULT_SALES: string[] = ['dates', 'invoiceType', 'invoiceNumber', 'payment', 'seller', 'nip', 'net', 'vatRate', 'vatAmount', 'gross', 'status', 'actions'];
  const ORDER_KEY_SALES = React.useMemo(() => `sales.columns.order:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnOrderSales, setColumnOrderSales] = React.useState<string[]>(() => {
    try { const raw = localStorage.getItem(ORDER_KEY_SALES); if (raw) return JSON.parse(raw); } catch {}
    return [...COLUMNS_ORDER_DEFAULT_SALES];
  });

  const [colsAnchorElSales, setColsAnchorElSales] = React.useState<null | HTMLElement>(null);
  const colsOpenSales = Boolean(colsAnchorElSales);
  const [draftColumnsVisibleSales, setDraftColumnsVisibleSales] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrderSales, setDraftOrderSales] = React.useState<string[] | null>(null);

  // Szerokości kolumn Sales
  const defaultColumnWidthsSales = React.useMemo<Record<string, number>>(() => ({
    dates: 150, invoiceType: 100, invoiceNumber: 120, payment: 120,
    seller: 200, nip: 130, net: 120, vatRate: 100, vatAmount: 100,
    gross: 120, status: 100, actions: 120,
  }), []);
  const STORAGE_KEY_SALES = React.useMemo(() => `salesTableColWidths:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnWidthsSales, setColumnWidthsSales] = React.useState<Record<string, number>>(defaultColumnWidthsSales);
  const getColWSales = (key: string) => `${columnWidthsSales[key] ?? defaultColumnWidthsSales[key] ?? 80}px`;

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_SALES);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') setColumnWidthsSales((prev) => ({ ...prev, ...parsed }));
      }
    } catch {}
  }, [STORAGE_KEY_SALES]);

  React.useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_SALES, JSON.stringify(columnWidthsSales)); } catch {}
  }, [STORAGE_KEY_SALES, columnWidthsSales]);

  // Filtrowanie i zakładki faktur sprzedażowych
  const filteredSalesRows = React.useMemo(() => {
    return salesRows.filter((inv: any) => {
      if (salesQuery) {
        const q = salesQuery.toLowerCase();
        const matches =
          (inv.invoice_number || '').toLowerCase().includes(q) ||
          (inv.seller_name || '').toLowerCase().includes(q) ||
          (inv.seller_tax_id || '').toLowerCase().includes(q);
        if (!matches) return false;
      }
      if (salesMonthFilter && inv.issue_date) {
        const invoiceMonth = String(inv.issue_date).slice(0, 7);
        if (invoiceMonth !== salesMonthFilter) return false;
      }
      if (salesDateFrom && inv.issue_date && String(inv.issue_date).slice(0, 10) < salesDateFrom) return false;
      if (salesDateTo && inv.issue_date && String(inv.issue_date).slice(0, 10) > salesDateTo) return false;
      if (salesProjectFilter && inv.project_id !== salesProjectFilter) return false;
      return true;
    });
  }, [salesRows, salesQuery, salesMonthFilter, salesDateFrom, salesDateTo, salesProjectFilter]);

  const salesRowsForTab = React.useMemo(() => {
    if (salesTab === 1) return filteredSalesRows.filter((inv: any) => inv.paid === true);
    if (salesTab === 2) return filteredSalesRows.filter((inv: any) => inv.paid !== true);
    return filteredSalesRows;
  }, [filteredSalesRows, salesTab]);

  // Liczniki zakładek sprzedaży
  const salesTabCounts = React.useMemo(() => ({
    all: filteredSalesRows.length,
    paid: filteredSalesRows.filter((inv: any) => inv.paid === true).length,
    unpaid: filteredSalesRows.filter((inv: any) => inv.paid !== true).length,
  }), [filteredSalesRows]);

  // Liczniki faktur dla każdej zakładki (po zastosowaniu filtrów)
  const ksefTabCounts = React.useMemo(() => {
    const filtered = ksefRows.filter((inv) => {
      // Filtrowanie po zapytaniu
      if (ksefQuery) {
        const q = ksefQuery.toLowerCase();
        const matches = 
          (inv.invoice_number || '').toLowerCase().includes(q) ||
          (inv.seller_name || '').toLowerCase().includes(q) ||
          (inv.seller_tax_id || '').toLowerCase().includes(q);
        if (!matches) return false;
      }
      // Filtrowanie po miesiącu
      if (ksefMonth && inv.issue_date) {
        const invoiceMonth = String(inv.issue_date).slice(0, 7);
        if (invoiceMonth !== ksefMonth) return false;
      }
      return true;
    });
    
    const invoiceStatus = (inv: KsefInvoiceListItem) => inv.status || 'Do weryfikacji';
    
    // Dla zakładki "Wszystkie" dodatkowe filtrowanie po zatwierdzeniach
    const allFiltered = ksefApprovals === 'all' 
      ? filtered 
      : ksefApprovals === 'approved'
        ? filtered.filter(inv => invoiceStatus(inv) === 'Zatwierdzona')
        : filtered.filter(inv => invoiceStatus(inv) !== 'Zatwierdzona');
    
    return {
      pending: filtered.filter(inv => invoiceStatus(inv) === 'Do weryfikacji').length,
      all: allFiltered.length,
      approved: filtered.filter(inv => invoiceStatus(inv) === 'Zatwierdzona').length,
      rejected: filtered.filter(inv => invoiceStatus(inv) === 'Odrzucona').length,
    };
  }, [ksefRows, ksefQuery, ksefMonth, ksefApprovals]);
  const [ksefPreviewOpen, setKsefPreviewOpen] = React.useState(false);
  const [ksefPreview, setKsefPreview] = React.useState<{
    invoice: any;
    items: any[];
    bankAccounts?: Array<{ nr_rb: string; bank_name?: string | null; swift?: string | null; description?: string | null; currency?: string | null; is_primary?: number | boolean }>;
    calcMode?: 'net' | 'gross';
    isNetto?: boolean;
    totalsFromItems?: { net: number; vat: number; gross: number };
    totalsMatch?: { net: boolean; vat: boolean; gross: boolean };
  } | null>(null);
  const ksefDateFromRef = React.useRef<HTMLInputElement | null>(null);
  const ksefDateToRef = React.useRef<HTMLInputElement | null>(null);
  const [ksefSyncing, setKsefSyncing] = React.useState(false);
  const ksefSyncingRef = React.useRef(false);
  
  // Debug: śledź zmiany ksefSyncing
  React.useEffect(() => {
    ksefSyncingRef.current = ksefSyncing;
    console.log('[KSEF] ksefSyncing zmienione na:', ksefSyncing);
  }, [ksefSyncing]);

  // Funkcja do ręcznego odświeżania faktur KSEF
  const handleKsefRefresh = React.useCallback(async () => {
    console.log('[KSEF] handleKsefRefresh START - ksefSyncingRef.current:', ksefSyncingRef.current, 'ksefSyncing:', ksefSyncing);
    if (ksefSyncingRef.current) {
      console.log('[KSEF] handleKsefRefresh - już trwa synchronizacja, pomijam');
      return;
    }
    
    try {
      console.log('[KSEF] handleKsefRefresh - ustawiam ksefSyncing na true');
      setKsefSyncing(true);
      setKsefError(null);
      const subject = 'purchases'; // Tylko faktury zakupowe
      
      // Komunikat informacyjny - rozpoczęcie synchronizacji
      showNotification(
        `Rozpoczynam synchronizację faktur zakupowych z KSEF...`,
        'info'
      );
      
      // Najpierw synchronizuj faktury z KSEF API
      console.log('[KSEF] handleKsefRefresh - rozpoczynam synchronizację z KSEF API, subject:', subject);
      const syncResult = await syncKsefInvoices({ subject });
      console.log('[KSEF] handleKsefRefresh - synchronizacja zakończona:', syncResult);
      
      // Następnie pobierz zaktualizowaną listę z lokalnej bazy
      const params: any = { subject, limit: 200 };
      if (ksefQuery) params.q = ksefQuery;
      if (ksefMonth) params.month = ksefMonth;
      console.log('[KSEF] handleKsefRefresh - pobieram zaktualizowaną listę z parametrami:', params);
      const invoices = await listKsefInvoices(params);
      console.log('[KSEF] handleKsefRefresh - pobrano faktur:', invoices?.length || 0, 'faktury:', invoices);
      setKsefRows(invoices || []);
      console.log('[KSEF] handleKsefRefresh - ustawiłem ksefRows');
      
      // Komunikaty w zależności od wyniku synchronizacji
      if (syncResult.success) {
        if (syncResult.cached === true) {
          // Użyto cache'owanych danych - ostrzeżenie (żółty)
          showNotification(
            `Użyto cache'owanych danych (${syncResult.synced} faktur z ostatnich ${syncResult.cacheAgeMinutes ?? 0} minut)`,
            'warning'
          );
        } else if (syncResult.synced > 0) {
          // Sukces - zsynchronizowano faktury (zielony)
          showNotification(
            `Synchronizacja zakończona pomyślnie. Zsynchronizowano ${syncResult.synced} faktur${syncResult.errors > 0 ? ` (${syncResult.errors} błędów)` : ''}.`,
            'success'
          );
        } else {
          // Brak faktur do synchronizacji - ostrzeżenie (żółty)
          showNotification(
            syncResult.message || 'Brak faktur do synchronizacji w wybranym zakresie dat.',
            'warning'
          );
        }
      } else {
        // Błąd synchronizacji - już obsłużony w catch
        throw new Error(syncResult.message || 'Błąd synchronizacji');
      }
    } catch (error: any) {
      console.error('[KSEF] handleKsefRefresh - błąd:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Błąd synchronizacji z KSEF';
      setKsefError(errorMessage);
      
      // Określ typ błędu na podstawie statusu odpowiedzi
      let notificationType: 'error' | 'warning' = 'error';
      let message = errorMessage;
      
      if (error.response?.status === 429) {
        // Limit żądań - ostrzeżenie (żółty)
        notificationType = 'warning';
        message = errorMessage;
      } else if (error.response?.status === 401) {
        // Brak autoryzacji - błąd (czerwony)
        notificationType = 'error';
        message = 'Brak autoryzacji. Wymagane ponowne uwierzytelnienie w KSEF.';
      } else if (error.response?.status === 400) {
        // Błąd walidacji - błąd (czerwony)
        notificationType = 'error';
        message = 'Nieprawidłowe żądanie do KSEF API. Sprawdź parametry synchronizacji.';
      }
      
      showNotification(message, notificationType);
    } finally {
      console.log('[KSEF] handleKsefRefresh - zakończono, resetuję ksefSyncing na false');
      setKsefSyncing(false);
    }
  }, [ksefQuery, ksefMonth, ksefSyncing, showNotification]);
  
  // Kolumny KSEF - widoczność i kolejność
  const DEFAULT_VIS_KSEF: Record<string, boolean> = React.useMemo(() => ({
    dates: true,
    invoiceType: true,
    invoiceNumber: true,
    payment: true,
    seller: true,
    nip: true,
    net: true,
    vatRate: true,
    vatAmount: true,
    gross: true,
    status: true,
    actions: true
  }), []);
  const VISIBLE_KEY_KSEF = React.useMemo(() => `ksef.columns.visible:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnsVisibleKsef, setColumnsVisibleKsef] = React.useState<Record<string, boolean>>(() => {
    try { const raw = localStorage.getItem(VISIBLE_KEY_KSEF); if (raw) return JSON.parse(raw); } catch {}
    return { ...DEFAULT_VIS_KSEF };
  });
  const colVKsef = React.useCallback((key: string) => columnsVisibleKsef[key] !== false, [columnsVisibleKsef]);
  
  // Kolejność kolumn KSEF
  const COLUMNS_ORDER_DEFAULT_KSEF: string[] = ['dates', 'invoiceType', 'invoiceNumber', 'payment', 'seller', 'nip', 'net', 'vatRate', 'vatAmount', 'gross', 'status', 'actions'];
  const ORDER_KEY_KSEF = React.useMemo(() => `ksef.columns.order:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnOrderKsef, setColumnOrderKsef] = React.useState<string[]>(() => {
    try { const raw = localStorage.getItem(ORDER_KEY_KSEF); if (raw) return JSON.parse(raw); } catch {}
    return [...COLUMNS_ORDER_DEFAULT_KSEF];
  });
  
  // Panel kolumn KSEF
  const [colsAnchorElKsef, setColsAnchorElKsef] = React.useState<null | HTMLElement>(null);
  const colsOpenKsef = Boolean(colsAnchorElKsef);
  const [draftColumnsVisibleKsef, setDraftColumnsVisibleKsef] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrderKsef, setDraftOrderKsef] = React.useState<string[] | null>(null);

  // Szerokości kolumn KSEF – zapamiętywanie per użytkownik
  const defaultColumnWidthsKsef = React.useMemo<Record<string, number>>(() => ({
    dates: 150,
    invoiceType: 100,
    invoiceNumber: 120,
    payment: 120,
    seller: 200,
    nip: 130,
    net: 120,
    vatRate: 100,
    vatAmount: 100,
    gross: 120,
    status: 100,
    actions: 120,
  }), []);
  const STORAGE_KEY_KSEF = React.useMemo(() => `ksefTableColWidths:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnWidthsKsef, setColumnWidthsKsef] = React.useState<Record<string, number>>(defaultColumnWidthsKsef);
  const getColWKsef = (key: string) => `${columnWidthsKsef[key] ?? defaultColumnWidthsKsef[key] ?? 80}px`;

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_KSEF);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') setColumnWidthsKsef((prev) => ({ ...prev, ...parsed }));
      }
    } catch {}
  }, [STORAGE_KEY_KSEF]);

  React.useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_KSEF, JSON.stringify(columnWidthsKsef)); } catch {}
  }, [STORAGE_KEY_KSEF, columnWidthsKsef]);
  const [viewedIds, setViewedIds] = React.useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('invoices.viewed');
      const arr: string[] = raw ? JSON.parse(raw) : [];
      return new Set(arr.map(String));
    } catch { return new Set(); }
  });

  const markViewed = React.useCallback((id: string | number) => {
    setViewedIds((prev) => {
      const next = new Set(prev);
      next.add(String(id));
      try { localStorage.setItem('invoices.viewed', JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  }, []);

  const [rows, setRows] = React.useState<any[]>([]);
  // Umożliwia otwieranie podglądu przez URL: /invoices?invoice=ID (lub ?id=ID)
  const initialInvoiceIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      initialInvoiceIdRef.current = sp.get('invoice') || sp.get('id');
    } catch { initialInvoiceIdRef.current = null; }
  }, []);
  const [usersMap, setUsersMap] = React.useState<Record<string, AppUser>>({});
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = React.useState<any>(null);
  const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number } | null>(null);
  const [activeProjects, setActiveProjects] = React.useState<Array<{ id: string; name: string }>>([]);
  const STATIC_PROJECTS: Array<{ id: string; name: string }> = [
    { id: 'S14', name: 'SERWIS GWARANCYJNY: USŁUGI' },
    { id: 'S13', name: 'SERWIS GWARANCYJNY: MATERIAŁY' },
    { id: 'S12', name: 'LEASINGI' },
    { id: 'S11', name: 'OBSŁUGA PRAWNA, KSIĘGOWA, DORADCZA' },
    { id: 'S10', name: 'USŁUGI TELEINFORMATYCZNE' },
    { id: 'S9',  name: 'NAJEM MIESZKAN PRACOWNICZYCH' },
    { id: 'S8',  name: 'NAJEM POWIERZCHNI BIUROWEJ' },
    { id: 'S7',  name: 'PRACOWNICY B2B' },
    { id: 'S6',  name: 'ZARZĄD' },
    { id: 'S5',  name: 'USŁUGI WŁASNE' },
    { id: 'S4',  name: 'MATERIAŁY WŁASNE' },
    { id: 'S3',  name: 'DROBNE WYPOSAŻENIE - do 1500 zł' },
    { id: 'S2',  name: 'WYPOSAŻENIE - do 3.000 zł' },
    { id: 'S1',  name: 'ŚRODKI TRWAŁE - powyżej 3.000 zł' },
    { id: 'R11', name: 'PODATEK CIT' },
    { id: 'R10', name: 'PODATEK VAT' },
    { id: 'R9',  name: 'ZUS' },
    { id: 'R8',  name: 'PODATKI - PIT' },
    { id: 'R7',  name: 'POZOSTAŁE NIGDZIE NIE UJĘTE' },
    { id: 'R6',  name: 'OBSŁUGA BANKOWA' },
    { id: 'R5',  name: 'UBEZPIECZENIA I POLISY' },
    { id: 'R4',  name: 'PRACOWNICY UoP, UD, UZ' },
    { id: 'R3',  name: 'ZABEZPIECZENIE KONTRAKTÓW' },
    { id: 'R2',  name: 'KOSZTY KREDYTÓW' },
    { id: 'R1',  name: 'AMORTYZACJA' },
    { id: '.UN', name: 'USŁUGI NOCLEGOWE' },
    { id: '.UG', name: 'USŁUGI GASTRONOMICZNE' },
    { id: '.P2', name: 'POJAZDY - 50% ODLICZENIA VAT' },
    { id: '.P1', name: 'POJAZDY - 100% ODLICZENIA VAT' },
  ];
  const [docTypeFilter, setDocTypeFilter] = React.useState<string>('');
  React.useEffect(() => {
    (async () => {
      try {
        const all = await getAllProjects();
        const active = all.filter((p: any) => String(p.project_phase).toUpperCase() === 'W REALIZACJI');
        const live = active.map((p: any) => ({ id: String(p.project_id), name: String(p.project_name || '') }));
        setActiveProjects([...live, ...STATIC_PROJECTS]);
      } catch { setActiveProjects([]); }
    })();
  }, []);

  // Ładowanie projektów dla KSEF (ta sama lista co w AddTransferInvoiceDialog)
  const KSEF_STATIC_PROJECTS: Array<{ project_id: string; project_name: string }> = [
    { project_id: 'S14', project_name: 'SERWIS GWARANCYJNY: USŁUGI' },
    { project_id: 'S13', project_name: 'SERWIS GWARANCYJNY: MATERIAŁY' },
    { project_id: 'S12', project_name: 'LEASINGI' },
    { project_id: 'S11', project_name: 'OBSŁUGA PRAWNA, KSIĘGOWA, DORADCZA' },
    { project_id: 'S10', project_name: 'USŁUGI TELEINFORMATYCZNE' },
    { project_id: 'S9',  project_name: 'NAJEM MIESZKAN PRACOWNICZYCH' },
    { project_id: 'S8',  project_name: 'NAJEM POWIERZCHNI BIUROWEJ' },
    { project_id: 'S7',  project_name: 'PRACOWNICY B2B' },
    { project_id: 'S6',  project_name: 'ZARZĄD' },
    { project_id: 'S5',  project_name: 'USŁUGI WŁASNE' },
    { project_id: 'S4',  project_name: 'MATERIAŁY WŁASNE' },
    { project_id: 'S3',  project_name: 'DROBNE WYPOSAŻENIE - do 1500 zł' },
    { project_id: 'S2',  project_name: 'WYPOSAŻENIE - do 3.000 zł' },
    { project_id: 'S1',  project_name: 'ŚRODKI TRWAŁE - powyżej 3.000 zł' },
    { project_id: 'R11', project_name: 'PODATEK CIT' },
    { project_id: 'R10', project_name: 'PODATEK VAT' },
    { project_id: 'R9',  project_name: 'ZUS' },
    { project_id: 'R8',  project_name: 'PODATKI - PIT' },
    { project_id: 'R7',  project_name: 'POZOSTAŁE NIGDZIE NIE UJĘTE' },
    { project_id: 'R6',  project_name: 'OBSŁUGA BANKOWA' },
    { project_id: 'R5',  project_name: 'UBEZPIECZENIA I POLISY' },
    { project_id: 'R4',  project_name: 'PRACOWNICY UoP, UD, UZ' },
    { project_id: 'R3',  project_name: 'ZABEZPIECZENIE KONTRAKTÓW' },
    { project_id: 'R2',  project_name: 'KOSZTY KREDYTÓW' },
    { project_id: 'R1',  project_name: 'AMORTYZACJA' },
    { project_id: '.UN', project_name: 'USŁUGI NOCLEGOWE' },
    { project_id: '.UG', project_name: 'USŁUGI GASTRONOMICZNE' },
    { project_id: '.P2', project_name: 'POJAZDY - 50% ODLICZENIA VAT' },
    { project_id: '.P1', project_name: 'POJAZDY - 100% ODLICZENIA VAT' },
  ];
  React.useEffect(() => {
    if (mainTab !== 0) return; // Ładuj tylko gdy zakładka KSEF jest aktywna
    (async () => {
      try {
        setKsefLoadingProjects(true);
        const all = await getAllProjects();
        const active = all.filter((p: any) => p.project_phase === 'W REALIZACJI')
          .map((p: any) => ({ project_id: p.project_id, project_name: p.project_name }));
        setKsefProjects([...active, ...KSEF_STATIC_PROJECTS]);
      } catch { setKsefProjects([]); } finally {
        setKsefLoadingProjects(false);
      }
    })();
  }, [mainTab]);
  const users = React.useMemo(() => Array.from(new Set((rows || []).map((r: any) => String(r.created_by_name || '')).filter((x) => x && x !== 'undefined'))), [rows]);

  const ALL_STATUSES = React.useMemo(() => ['Wprowadzono','Zatwierdzone','Odrzucone','Uzupełnić'], []);
  const [statusFilter, setStatusFilter] = React.useState<string>(''); // '' = wszystkie
  const [bookedFilter, setBookedFilter] = React.useState<string>(''); // ''=wszystkie, 'tak'/'nie'
  const months = React.useMemo(() => monthOptions(), []);

  // --- Filtry i kolumny dla zakładki „Wszystkie" (mainTab === 6) ---
  const [allDateFrom, setAllDateFrom] = React.useState<string>('');
  const [allDateTo, setAllDateTo] = React.useState<string>('');
  const [allPaymentMethod, setAllPaymentMethod] = React.useState<string>('');
  const [allWarehouse, setAllWarehouse] = React.useState<string>('');
  const ALL_PAYMENT_METHODS = React.useMemo(() => ['Przelew', 'Proforma', 'Gotówka', 'Karta', 'Zaliczka (przelew)'], []);
  const WAREHOUSES_OPTIONS = React.useMemo(() => {
    const fromApi = warehousesList.map((w) => ({ value: w.warehouse_code, label: w.warehouse_name ? `${w.warehouse_code} – ${w.warehouse_name}` : w.warehouse_code }));
    const legacy = ['Główny', 'Budowa', 'Zewnętrzny'].filter((code) => !fromApi.some((f) => f.value === code));
    return [...fromApi, ...legacy.map((code) => ({ value: code, label: code }))];
  }, [warehousesList]);

  // Klawiatura: Escape (zamknij kolumny), strzałki góra/dół (nawigacja wierszy)
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Option+ArrowDown – otwórz/zamknij menu sekcji
      if (e.altKey && e.key === 'ArrowDown') {
        const tag = (document.activeElement?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        e.preventDefault();
        if (sectionMenuAnchorEl) {
          setSectionMenuAnchorEl(null);
        } else if (sectionChipRef.current) {
          setSectionMenuAnchorEl(sectionChipRef.current);
        }
        return;
      }
      if (e.key === 'Escape') {
        if (colsAnchorElSettle) {
          setColsAnchorElSettle(null); setDraftColumnsVisibleSettle(null); setDraftOrderSettle(null); setColResizeMode(false);
          return;
        }
        // Escape odznacza wiersze
        if (settleSelectedIds.size > 0) {
          setSettleSelectedIds(new Set());
          setSettleLastClickedIdx(null);
        }
        if (bankOpsSelectedIds.size > 0) {
          setBankOpsSelectedIds(new Set());
          setBankOpsLastClickedIdx(null);
        }
        if (bsSelectedIds.size > 0) {
          setBsSelectedIds(new Set());
          setBsLastClickedIdx(null);
        }
        return;
      }
      // Klawisz "r" – otwórz drawer rozliczania dla zaznaczonego wiersza
      if ((e.key === 'r' || e.key === 'R') && sectionChip === 5 && settleSelectedIds.size === 1) {
        const tag = (document.activeElement?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        e.preventDefault();
        const selectedId = Array.from(settleSelectedIds)[0];
        const inv = settleRowsRef.current.find((r: any) => r.id === selectedId);
        if (inv && !inv.settled) {
          setSettleDrawerData({
            id: inv.id,
            document_type: inv.document_type,
            created_date: inv.created_date,
            received_date: inv.created_date,
            document_date: inv.created_date,
            due_date: inv.due_date,
            paid_date: inv.paid_date,
            project_code: inv.project_code,
            contractor: inv.contractor,
            contractor_address: (inv as any).contractor_address || null,
            contractor_postal_city: (inv as any).contractor_postal_city || null,
            nip: inv.nip,
            invoice_number: inv.invoice_number,
            ksef_number: (inv as any).ksef_number || null,
            gross_amount: Number(inv.gross_amount || 0),
            net_amount: Number(inv.net_amount || 0),
            vat_rate: inv.vat_rate,
            account_number: inv.account_number,
            warehouse: inv.warehouse,
            google_drive_file_id: inv.google_drive_file_id,
            google_drive_file_url: inv.google_drive_file_url,
          });
          setSettleDrawerOpen(true);
        }
        return;
      }
      // Strzałki góra/dół – nawigacja wierszy w rozrachunkach (bez Alt, nie gdy menu sekcji otwarte)
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !e.altKey && !sectionMenuAnchorEl && sectionChip === 5 && settleRowsRef.current.length > 0) {
        // Tylko gdy fokus nie jest w input/textarea/select
        const tag = (document.activeElement?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

        e.preventDefault();
        const rows = settleRowsRef.current;
        const currentIdx = settleLastClickedIdx ?? -1;
        const nextIdx = e.key === 'ArrowDown'
          ? Math.min(currentIdx + 1, rows.length - 1)
          : Math.max(currentIdx - 1, 0);

        if (nextIdx < 0 || nextIdx >= rows.length) return;
        const nextRow = rows[nextIdx];

        if (e.shiftKey) {
          // Shift+strzałka: toggle wiersz z którego odchodzimy vs dodaj nowy
          setSettleSelectedIds(prev => {
            const next = new Set(prev);
            const currentRow = currentIdx >= 0 && currentIdx < rows.length ? rows[currentIdx] : null;
            // Jeśli nowy wiersz jest już zaznaczony = cofamy się, więc odznacz bieżący
            if (next.has(nextRow.id) && currentRow && next.has(currentRow.id)) {
              next.delete(currentRow.id);
            } else {
              next.add(nextRow.id);
            }
            return next;
          });
        } else {
          // Sama strzałka: przenieś zaznaczenie na nowy wiersz
          setSettleSelectedIds(new Set([nextRow.id]));
        }
        setSettleLastClickedIdx(nextIdx);
      }
      // Strzałki góra/dół – nawigacja wierszy w operacjach bankowych
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !e.altKey && !sectionMenuAnchorEl && sectionChip === 6 && bankOpsRowsRef.current.length > 0) {
        const tag = (document.activeElement?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

        e.preventDefault();
        const rows = bankOpsRowsRef.current;
        const currentIdx = bankOpsLastClickedIdx ?? -1;
        const nextIdx = e.key === 'ArrowDown'
          ? Math.min(currentIdx + 1, rows.length - 1)
          : Math.max(currentIdx - 1, 0);

        if (nextIdx < 0 || nextIdx >= rows.length) return;
        const nextRow = rows[nextIdx];

        if (e.shiftKey) {
          setBankOpsSelectedIds(prev => {
            const next = new Set(prev);
            const currentRow = currentIdx >= 0 && currentIdx < rows.length ? rows[currentIdx] : null;
            if (next.has(nextRow.id) && currentRow && next.has(currentRow.id)) {
              next.delete(currentRow.id);
            } else {
              next.add(nextRow.id);
            }
            return next;
          });
        } else {
          setBankOpsSelectedIds(new Set([nextRow.id]));
        }
        setBankOpsLastClickedIdx(nextIdx);
      }
      // Strzałki góra/dół – nawigacja wierszy w wyciągach
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !e.altKey && !sectionMenuAnchorEl && sectionChip === 7 && bsVisibleRowsRef.current.length > 0) {
        const tag = (document.activeElement?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        e.preventDefault();
        const rows = bsVisibleRowsRef.current;
        const currentIdx = bsLastClickedIdx ?? -1;
        const nextIdx = e.key === 'ArrowDown'
          ? Math.min(currentIdx + 1, rows.length - 1)
          : Math.max(currentIdx - 1, 0);
        if (nextIdx < 0 || nextIdx >= rows.length) return;
        const nextRow = rows[nextIdx];
        if (e.shiftKey) {
          setBsSelectedIds(prev => {
            const next = new Set(prev);
            const currentRow = currentIdx >= 0 && currentIdx < rows.length ? rows[currentIdx] : null;
            if (next.has(nextRow.id) && currentRow && next.has(currentRow.id)) {
              next.delete(currentRow.id);
            } else {
              next.add(nextRow.id);
            }
            return next;
          });
        } else {
          setBsSelectedIds(new Set([nextRow.id]));
        }
        setBsLastClickedIdx(nextIdx);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [colsAnchorElSettle, settleSelectedIds, settleLastClickedIdx, bankOpsSelectedIds, bankOpsLastClickedIdx, bsSelectedIds, bsLastClickedIdx, sectionChip, sectionMenuAnchorEl]);
  const [dragCol, setDragCol] = React.useState<{ key: string; startX: number; startW: number; tableType?: 'transfer' | 'ksef' | 'sales' | 'salesKsef' | 'settle' | 'bankOps' | 'bs' } | null>(null);
  const defaultColumnWidths = React.useMemo<Record<string, number>>(() => ({
    sel: 56,
    created: 96,
    date: 140,
    dates: 140,
    invoiceNumber: 120,
    project: 220,
    contractor: 150,
    net: 100,
    gross: 100,
    due: 96,
    paid: 70,
    approvals: 130,
    preview: 40,
    actions: 40,
    warehouse: 100,
    nip: 120,
    ksefNumber: 140,
    paymentMethod: 100,
  }), []);
  const STORAGE_KEY = React.useMemo(() => `invoicesTableColWidths:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>(defaultColumnWidths);
  const getColW = (key: string) => `${columnWidths[key] ?? defaultColumnWidths[key] ?? 40}px`;

  // Wysokość tabeli jak w „Projekty”
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [tableHeight, setTableHeight] = React.useState<number | null>(null);

  // Dostosuj kolumny – widoczność
  const DEFAULT_VIS: Record<string, boolean> = React.useMemo(() => ({
    sel: true, dates: true, invoiceNumber: true, project: true, contractor: true, net: true, gross: true, due: true,
    paid: true, approvals: true, preview: true, actions: true
  }), []);
  const VISIBLE_KEY = React.useMemo(() => `invoices.columns.visible:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnsVisible, setColumnsVisible] = React.useState<Record<string, boolean>>(() => {
    try { const raw = localStorage.getItem(VISIBLE_KEY); if (raw) return JSON.parse(raw); } catch {}
    return { ...DEFAULT_VIS };
  });
  const colV = React.useCallback((key: string) => columnsVisible[key] !== false, [columnsVisible]);
  
  // Kolejność kolumn
  const COLUMNS_ORDER_DEFAULT: string[] = ['dates', 'sel', 'project', 'contractor', 'invoiceNumber', 'net', 'gross', 'due', 'paid', 'approvals', 'preview', 'actions'];
  const ORDER_KEY = React.useMemo(() => `invoices.columns.order:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnOrder, setColumnOrder] = React.useState<string[]>(() => {
    try { const raw = localStorage.getItem(ORDER_KEY); if (raw) return JSON.parse(raw); } catch {}
    return [...COLUMNS_ORDER_DEFAULT];
  });
  
  // Panel kolumn
  const [colsAnchorEl, setColsAnchorEl] = React.useState<null | HTMLElement>(null);
  const colsOpen = Boolean(colsAnchorEl);
  const [draftColumnsVisible, setDraftColumnsVisible] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrder, setDraftOrder] = React.useState<string[] | null>(null);
  const [syncing, setSyncing] = React.useState<boolean>(false);

  // --- Kolumny dla zakładki „Wszystkie" (mainTab === 6) ---
  const ALL_DEFAULT_VIS: Record<string, boolean> = React.useMemo(() => ({
    dates: true, sel: true, project: true, warehouse: true, contractor: true, nip: true,
    invoiceNumber: true, ksefNumber: true, net: true, gross: true, paymentMethod: true,
    paid: true, preview: true, actions: true
  }), []);
  const ALL_VISIBLE_KEY = React.useMemo(() => `invoices.all.columns.visible:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [allColumnsVisible, setAllColumnsVisible] = React.useState<Record<string, boolean>>(() => {
    try { const raw = localStorage.getItem(ALL_VISIBLE_KEY); if (raw) return JSON.parse(raw); } catch {}
    return { ...ALL_DEFAULT_VIS };
  });
  const allColV = React.useCallback((key: string) => allColumnsVisible[key] !== false, [allColumnsVisible]);

  const ALL_COLUMNS_ORDER_DEFAULT: string[] = ['dates', 'sel', 'project', 'warehouse', 'contractor', 'nip', 'invoiceNumber', 'ksefNumber', 'net', 'gross', 'paymentMethod', 'paid', 'preview', 'actions'];
  const ALL_ORDER_KEY = React.useMemo(() => `invoices.all.columns.order:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [allColumnOrder, setAllColumnOrder] = React.useState<string[]>(() => {
    try { const raw = localStorage.getItem(ALL_ORDER_KEY); if (raw) return JSON.parse(raw); } catch {}
    return [...ALL_COLUMNS_ORDER_DEFAULT];
  });

  const [allDraftColumnsVisible, setAllDraftColumnsVisible] = React.useState<Record<string, boolean> | null>(null);
  const [allDraftOrder, setAllDraftOrder] = React.useState<string[] | null>(null);

  // Upewnij się, że kolumna invoiceNumber jest zawsze widoczna i w odpowiedniej kolejności
  React.useEffect(() => {
    setColumnsVisible((prev) => {
      if (prev.invoiceNumber === false) {
        return { ...prev, invoiceNumber: true };
      }
      return prev;
    });
    setColumnOrder((prev) => {
      if (!prev.includes('invoiceNumber')) {
        // Dodaj kolumnę za 'contractor'
        const contractorIndex = prev.indexOf('contractor');
        if (contractorIndex >= 0) {
          const newOrder = [...prev];
          newOrder.splice(contractorIndex + 1, 0, 'invoiceNumber');
          return newOrder;
        }
        return [...prev, 'invoiceNumber'];
      }
      return prev;
    });
  }, []);

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') setColumnWidths((prev) => ({ ...prev, ...parsed }));
      }
    } catch {}
  }, [STORAGE_KEY]);

  React.useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(columnWidths)); } catch {}
  }, [STORAGE_KEY, columnWidths]);

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      const dx = e.clientX - dragCol.startX;
      const newW = Math.max(16, dragCol.startW + dx);
      if (dragCol.tableType === 'ksef') {
        setColumnWidthsKsef((prev) => ({ ...prev, [dragCol.key]: newW }));
      } else if (dragCol.tableType === 'sales') {
        setColumnWidthsSales((prev) => ({ ...prev, [dragCol.key]: newW }));
      } else if (dragCol.tableType === 'salesKsef') {
        setColumnWidthsSalesKsef((prev) => ({ ...prev, [dragCol.key]: newW }));
      } else if (dragCol.tableType === 'settle') {
        setColumnWidthsSettle((prev) => ({ ...prev, [dragCol.key]: newW }));
      } else if (dragCol.tableType === 'bankOps') {
        setColumnWidthsBankOps((prev) => ({ ...prev, [dragCol.key]: newW }));
      } else if (dragCol.tableType === 'bs') {
        setColsWidBs((prev) => ({ ...prev, [dragCol.key]: newW }));
      } else {
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
      }
    };
    const onUp = () => setDragCol(null);
    if (dragCol) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, { once: true } as any);
    }
    return () => {
      window.removeEventListener('mousemove', onMove);
    };
  }, [dragCol]);

  const renderResizableTh = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center') => (
    <TableCell align={align} sx={{ ...headerCellSx, width: getColW(keyId), maxWidth: 'none', position: 'relative', userSelect: 'none' }}>
      {node}
      {colResizeMode ? (
        <Box
          role="separator"
          aria-orientation="vertical"
          onMouseDown={(e) => { 
            e.preventDefault(); 
            e.stopPropagation(); 
            setDragCol({ 
              key: keyId, 
              startX: e.clientX, 
              startW: columnWidths[keyId] ?? defaultColumnWidths[keyId] ?? 40 
            }); 
          }}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '12px',
            minHeight: '100%',
            cursor: 'col-resize',
            zIndex: 15,
            touchAction: 'none',
            pointerEvents: 'auto',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '50%',
              top: 0,
              bottom: 0,
              width: '2px',
              transform: 'translateX(-50%)',
              backgroundColor: '#FF0000',
              zIndex: 16
            },
            '&:hover': { 
              backgroundColor: 'rgba(0,0,0,0.06)',
              '&::before': {
                width: '3px',
                backgroundColor: '#CC0000'
              }
            }
          }}
        />
      ) : null}
    </TableCell>
  );

  const renderResizableThKsef = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center') => (
    <TableCell align={align} sx={{ ...headerCellSx, width: getColWKsef(keyId), maxWidth: 'none', position: 'relative', userSelect: 'none' }}>
      {node}
      {colResizeMode ? (
        <Box
          role="separator"
          aria-orientation="vertical"
          onMouseDown={(e) => { 
            e.preventDefault(); 
            e.stopPropagation(); 
            setDragCol({ 
              key: keyId, 
              startX: e.clientX, 
              startW: columnWidthsKsef[keyId] ?? defaultColumnWidthsKsef[keyId] ?? 80,
              tableType: 'ksef'
            }); 
          }}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '12px',
            minHeight: '100%',
            cursor: 'col-resize',
            zIndex: 15,
            touchAction: 'none',
            pointerEvents: 'auto',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '50%',
              top: 0,
              bottom: 0,
              width: '2px',
              transform: 'translateX(-50%)',
              backgroundColor: '#FF0000',
              zIndex: 16
            },
            '&:hover': { 
              backgroundColor: 'rgba(0,0,0,0.06)',
              '&::before': {
                width: '3px',
                backgroundColor: '#CC0000'
              }
            }
          }}
        />
      ) : null}
    </TableCell>
  );

  const renderResizableThSales = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center') => (
    <TableCell align={align} sx={{ ...headerCellSx, width: getColWSales(keyId), maxWidth: 'none', position: 'relative', userSelect: 'none' }}>
      {node}
      {colResizeMode ? (
        <Box
          role="separator"
          aria-orientation="vertical"
          onMouseDown={(e) => { 
            e.preventDefault(); 
            e.stopPropagation(); 
            setDragCol({ 
              key: keyId, 
              startX: e.clientX, 
              startW: columnWidthsSales[keyId] ?? defaultColumnWidthsSales[keyId] ?? 80,
              tableType: 'sales'
            }); 
          }}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '12px',
            minHeight: '100%',
            cursor: 'col-resize',
            zIndex: 15,
            touchAction: 'none',
            pointerEvents: 'auto',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '50%',
              top: 0,
              bottom: 0,
              width: '2px',
              transform: 'translateX(-50%)',
              backgroundColor: '#FF0000',
              zIndex: 16
            },
            '&:hover': { 
              backgroundColor: 'rgba(0,0,0,0.06)',
              '&::before': {
                width: '3px',
                backgroundColor: '#CC0000'
              }
            }
          }}
        />
      ) : null}
    </TableCell>
  );

  const renderResizableThSalesKsef = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center') => (
    <TableCell align={align} sx={{ ...headerCellSx, width: getColWSalesKsef(keyId), maxWidth: 'none', position: 'relative', userSelect: 'none' }}>
      {node}
      {colResizeMode ? (
        <Box
          role="separator"
          aria-orientation="vertical"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragCol({
              key: keyId,
              startX: e.clientX,
              startW: columnWidthsSalesKsef[keyId] ?? defaultColumnWidthsSalesKsef[keyId] ?? 80,
              tableType: 'salesKsef'
            });
          }}
          sx={{
            position: 'absolute', right: 0, top: 0, bottom: 0, width: '12px', minHeight: '100%',
            cursor: 'col-resize', zIndex: 15, touchAction: 'none', pointerEvents: 'auto',
            '&::before': { content: '""', position: 'absolute', left: '50%', top: 0, bottom: 0, width: '2px', transform: 'translateX(-50%)', backgroundColor: '#FF0000', zIndex: 16 },
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)', '&::before': { width: '3px', backgroundColor: '#CC0000' } }
          }}
        />
      ) : null}
    </TableCell>
  );

  const renderResizableThSettle = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center') => (
    <TableCell align={align} sx={{ ...headerCellSx, width: getColWSettle(keyId), maxWidth: 'none', position: 'relative', userSelect: 'none' }}>
      {node}
      {colResizeMode ? (
        <Box
          role="separator"
          aria-orientation="vertical"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragCol({
              key: keyId,
              startX: e.clientX,
              startW: columnWidthsSettle[keyId] ?? defaultColumnWidthsSettle[keyId] ?? 80,
              tableType: 'settle'
            });
          }}
          sx={{
            position: 'absolute', right: 0, top: 0, bottom: 0, width: '12px', minHeight: '100%',
            cursor: 'col-resize', zIndex: 15, touchAction: 'none', pointerEvents: 'auto',
            '&::before': { content: '""', position: 'absolute', left: '50%', top: 0, bottom: 0, width: '2px', transform: 'translateX(-50%)', backgroundColor: '#FF0000', zIndex: 16 },
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)', '&::before': { width: '3px', backgroundColor: '#CC0000' } }
          }}
        />
      ) : null}
    </TableCell>
  );

  const refresh = React.useCallback(() => {
    listTransferInvoices().then(setRows).catch(() => setRows([]));
  }, []);

  React.useEffect(() => {
    refresh();
    // Nieblokująca synchronizacja z MSSQL -> po zakończeniu odśwież listę
    (async () => {
      try {
        await syncInvoicesBookedFromMssql();
        refresh();
      } catch {
        // cicho ignoruj – MSSQL może być niedostępny
      }
    })();
    // Załaduj użytkowników do mapy (avatar, imię)
    listUsersForBusinessForms().then((list) => {
      const map: Record<string, AppUser> = {};
      list.forEach((u) => { map[u.user_id] = u; });
      setUsersMap(map);
    }).catch(() => {});
    
    // Realtime: odśwież listę po dodaniu nowej faktury
    const socket = getSocket();
    const onInvoiceCreated = () => {
      refresh();
    };
    socket.on('transfer-invoice:created', onInvoiceCreated);

    // Odśwież listę po zamknięciu karty z formularzem nowej faktury
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === 'invoice:created' || e.data?.type === 'invoice:updated') {
        refresh();
        refreshSettlements();
        // Wymuś przeładowanie listy KSEF (np. po zmianie statusu z „Do weryfikacji"
        // na „Zatwierdzona"/„Odrzucona" faktura powinna zniknąć z bieżącej zakładki).
        setKsefRefreshKey((k) => k + 1);
      }
    };
    window.addEventListener('message', onMessage);

    return () => {
      try { socket.off('transfer-invoice:created', onInvoiceCreated); } catch {}
      window.removeEventListener('message', onMessage);
    };
  }, [refresh]);

  React.useEffect(() => {
    const onExternal = (e: any) => { if (e?.detail?.page === 'invoices' && typeof e.detail.tab === 'number') setTab(e.detail.tab); };
    window.addEventListener('ui:tab:set:external', onExternal as any);
    return () => window.removeEventListener('ui:tab:set:external', onExternal as any);
  }, []);

  // Po załadowaniu listy otwórz podgląd, jeśli URL zawiera ?invoice=ID lub ?id=ID
  React.useEffect(() => {
    const targetId = initialInvoiceIdRef.current;
    if (!targetId || !rows || rows.length === 0) return;
    const inv = rows.find((r: any) => String(r.id) === String(targetId));
    if (inv) {
      setPreviewInvoice(inv);
      setPreviewOpen(true);
      markViewed(inv.id);
      initialInvoiceIdRef.current = null;
    }
  }, [rows, markViewed]);

  // Sprawdź status połączenia KSEF gdy użytkownik przełączy się na zakładkę KSEF
  React.useEffect(() => {
    if (mainTab === 0) {
      checkKsefConnection().then(setKsefConnectionStatus).catch(() => {
        setKsefConnectionStatus({ connected: false, error: 'Błąd sprawdzania połączenia' });
      });
    }
  }, [mainTab]);

  // Globalny heartbeat dla wskaźnika połączenia KSEF (kropka przy zakładce)
  React.useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const st = await checkKsefConnection();
        if (mounted) setKsefConnectionStatus(st);
      } catch {
        if (mounted) setKsefConnectionStatus({ connected: false, error: 'Błąd sprawdzania połączenia' });
      }
    };
    tick();
    const id = window.setInterval(tick, 60_000); // co 60s
    return () => { mounted = false; window.clearInterval(id); };
  }, []);

  // KSEF: Ładowanie faktur zakupowych/sprzedażowych (tylko automatyczne przy zmianie filtrów)
  React.useEffect(() => {
    if (mainTab !== 0) return;
    
    let cancelled = false;
    setKsefLoading(true);
    setKsefError(null);

    const loadKsefInvoices = async () => {
      console.log('[KSEF] useEffect - automatyczne ładowanie faktur');
      try {
        const subject = 'purchases'; // Tylko faktury zakupowe
        const params: any = {
          subject,
          limit: 200
        };
        
        if (ksefQuery) params.q = ksefQuery;
        if (ksefMonth) params.month = ksefMonth;
        
        console.log('[KSEF] useEffect - wywołuję listKsefInvoices z parametrami:', params);
        const invoices = await listKsefInvoices(params);
        console.log('[KSEF] useEffect - pobrano faktur:', invoices?.length || 0);
        if (!cancelled) {
          setKsefRows(invoices);
          setKsefLoading(false);
        }
      } catch (error: any) {
        console.error('[KSEF] useEffect - błąd:', error);
        if (!cancelled) {
          setKsefError(error.response?.data?.message || 'Błąd pobierania faktur KSEF');
          setKsefLoading(false);
          setKsefRows([]);
        }
      }
    };

    loadKsefInvoices();
    return () => { cancelled = true; };
  }, [mainTab, ksefQuery, ksefMonth, ksefDateFrom, ksefDateTo, ksefApprovals, ksefRefreshKey]);

  const openKsefPreview = React.useCallback(async (ksefInvoiceId: string) => {
    try {
      console.log('[KSEF] openKsefPreview - otwieram podgląd dla ksef_invoice_id:', ksefInvoiceId, 'typ:', typeof ksefInvoiceId, 'długość:', ksefInvoiceId?.length);
      const details = await getKsefInvoice(ksefInvoiceId);
      console.log('[KSEF] openKsefPreview - pobrane dane:', details);
      console.log('[KSEF] openKsefPreview - invoice:', details?.invoice);
      console.log('[KSEF] openKsefPreview - items:', details?.items);
      console.log('[KSEF] openKsefPreview - items.length:', details?.items?.length);
      console.log('[KSEF] openKsefPreview - items jest tablicą:', Array.isArray(details?.items));
      if (details?.items && details.items.length > 0) {
        console.log('[KSEF] openKsefPreview - pierwsza pozycja:', details.items[0]);
      }
      setKsefPreview(details);
      setKsefPreviewOpen(true);
    } catch (error: any) {
      console.error('Błąd pobierania szczegółów faktury KSEF:', error);
      console.error('Błąd szczegóły:', error.response?.data);
      setKsefError('Błąd pobierania szczegółów faktury');
    }
  }, []);

  const formatMoney = React.useCallback((v: any) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '';
    return currency(n);
  }, []);

  const ksefTotals = React.useMemo(() => {
    const toNum = (v: any) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    const net = ksefRows.reduce((s, r) => s + toNum((r as any)?.net_amount), 0);
    const gross = ksefRows.reduce((s, r) => s + toNum((r as any)?.gross_amount), 0);
    return { net, gross };
  }, [ksefRows]);

  const getEffectiveStatus = React.useCallback((inv: any): string => {
    const acc = String(inv.accounting_approval_status || 'pending');
    const adm = String(inv.admin_approval_status || 'pending');
    // Zatwierdzone tylko jeśli obie strony zatwierdziły: admin i księgowość
    if (acc === 'approved' && adm === 'approved') return 'Zatwierdzone';
    if (acc === 'rejected' || adm === 'rejected') return 'Odrzucone';
    if (acc === 'needs_info' || adm === 'needs_info') return 'Uzupełnić';
    return inv.priority === 'pilny' ? 'Pilny' : 'Wprowadzono';
  }, []);

  const matchesPurchasesApplicant = (inv: any) => {
    if (!isPurchasesBasicUser) {
      return requesterFilter ? String(inv.created_by_name || '') === requesterFilter : true;
    }
    return String(inv.created_by_user_id || '') === purchasesApplicantUserId;
  };

  const filtered = rows.filter((inv) => {
    const matchesQuery = [inv.id, inv.project_id, inv.contractor_name]
      .join(' ')
      .toLowerCase()
      .includes(query.toLowerCase());
    const matchesProject = projectFilter ? inv.project_id === projectFilter : true;
    const matchesMonth = monthFilter ? String(inv.document_date || '').startsWith(monthFilter) : true;
    const matchesStatus = statusFilter ? getEffectiveStatus(inv) === statusFilter : true;
    // Faktury przelewowe: Rodzaj=Faktura VAT lub Proforma + Sposób płatności=Przelew
    const kind = inv.document_kind || (inv.document_type === 'Zaliczka (przelew)' ? 'advance' : inv.document_type === 'Proforma' ? 'proforma' : 'vat');
    const docType = String(inv.document_type || '');
    const matchesType = (kind === 'vat' || kind === 'proforma') && docType === 'Przelew';
    const matchesRequester = matchesPurchasesApplicant(inv);
    const matchesBooked = bookedFilter ? ((bookedFilter === 'tak') ? Boolean(inv.booked) : !Boolean(inv.booked)) : true;
    return matchesQuery && matchesProject && matchesMonth && matchesStatus && matchesRequester && matchesType && matchesBooked;
  });

  // Sortowanie
  type Order = 'asc' | 'desc';
  const [orderBy, setOrderBy] = React.useState<string>('created_at');
  const [order, setOrder] = React.useState<Order>('desc');
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const sortRows = React.useCallback((list: any[]) => {
    const copy = [...list];
    copy.sort((a: any, b: any) => {
      const aVal = a[orderBy];
      const bVal = b[orderBy];
      let cmp = 0;
      if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) cmp = Number(aVal) - Number(bVal);
      else cmp = String(aVal || '').localeCompare(String(bVal || ''));
      return order === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [order, orderBy]);

  const isFullyApproved = React.useCallback((inv: any) => {
    const acc = String(inv.accounting_approval_status || 'pending');
    const adm = String(inv.admin_approval_status || 'pending');
    // Wymagana akceptacja obu stron: admin i księgowość
    return acc === 'approved' && adm === 'approved';
  }, []);
  const acceptanceRows = React.useMemo(() => filtered.filter((inv: any) => !isFullyApproved(inv)), [filtered, isFullyApproved]);
  const toEnterRows = React.useMemo(
    () => filtered.filter((inv: any) => {
      const inSubiekt = Number(inv.subiekt) === 1 || inv.subiekt === true;
      return isFullyApproved(inv) && !inSubiekt;
    }),
    [filtered, isFullyApproved]
  );
  const subiektRows = React.useMemo(() => filtered.filter((inv: any) => Number(inv.subiekt) === 1 || inv.subiekt === true), [filtered]);
  const unpaidRows = React.useMemo(() => filtered.filter((inv: any) => !inv.paid_date), [filtered]);
  const paidRows = React.useMemo(() => filtered.filter((inv: any) => Boolean(inv.paid_date)), [filtered]);
  const sortedAcceptance = React.useMemo(() => sortRows(acceptanceRows), [sortRows, acceptanceRows]);
  const sortedToEnter = React.useMemo(() => sortRows(toEnterRows), [sortRows, toEnterRows]);
  const sortedBooked = React.useMemo(() => sortRows(subiektRows), [sortRows, subiektRows]);
  const sortedUnpaid = React.useMemo(() => sortRows(unpaidRows), [sortRows, unpaidRows]);
  const sortedPaid = React.useMemo(() => sortRows(paidRows), [sortRows, paidRows]);

  // --- Sekcja: Wszystkie faktury (siódma zakładka główna, indeks 6) ---
  const filteredAll = React.useMemo(() => rows.filter((inv) => {
    const matchesQuery = [inv.id, inv.project_id, inv.contractor_name, inv.invoice_number, inv.nip, inv.ksef_number]
      .join(' ')
      .toLowerCase()
      .includes(query.toLowerCase());
    const matchesProject = projectFilter ? inv.project_id === projectFilter : true;
    const matchesMonth = monthFilter ? String(inv.document_date || '').startsWith(monthFilter) : true;
    const docDate = String(inv.document_date || '');
    const matchesDateFrom = allDateFrom ? docDate >= allDateFrom : true;
    const matchesDateTo = allDateTo ? docDate <= allDateTo : true;
    const matchesPayment = allPaymentMethod ? String(inv.document_type || '') === allPaymentMethod : true;
    const matchesWarehouse = allWarehouse ? String(inv.warehouse || '') === allWarehouse : true;
    const matchesApplicant = matchesPurchasesApplicant(inv);
    return matchesQuery && matchesProject && matchesMonth && matchesDateFrom && matchesDateTo && matchesPayment && matchesWarehouse && matchesApplicant;
  }), [rows, query, projectFilter, monthFilter, allDateFrom, allDateTo, allPaymentMethod, allWarehouse, isPurchasesBasicUser, requesterFilter, purchasesApplicantUserId]);
  const sortedAll = React.useMemo(() => sortRows(filteredAll), [sortRows, filteredAll]);

  // --- Sekcja: Faktury gotówkowe (czwarta zakładka główna) ---
  const [cashTab, setCashTab] = React.useState(0);
  const cashFiltered = rows.filter((inv) => {
    const matchesQuery = [inv.id, inv.project_id, inv.contractor_name].join(' ').toLowerCase().includes(query.toLowerCase());
    const matchesProject = projectFilter ? inv.project_id === projectFilter : true;
    const matchesMonth = monthFilter ? String(inv.document_date || '').startsWith(monthFilter) : true;
    const matchesStatus = statusFilter ? getEffectiveStatus(inv) === statusFilter : true;
    const matchesRequester = matchesPurchasesApplicant(inv);
    const matchesBooked = bookedFilter ? ((bookedFilter === 'tak') ? Boolean(inv.booked) : !Boolean(inv.booked)) : true;
    // Faktury gotówkowe: Rodzaj=Faktura VAT + Sposób płatności=Gotówka
    const kind = inv.document_kind || (inv.document_type === 'Zaliczka (przelew)' ? 'advance' : inv.document_type === 'Proforma' ? 'proforma' : 'vat');
    const matchesType = kind === 'vat' && String(inv.document_type || '') === 'Gotówka';
    return matchesQuery && matchesProject && matchesMonth && matchesStatus && matchesRequester && matchesBooked && matchesType;
  });
  const cashInSubiekt = (inv: any) => Number(inv.subiekt) === 1 || inv.subiekt === true;
  // Dla gotówkowych: nie wymagamy akceptacji – od razu „Do wprowadzenia”
  const cashToEnter = React.useMemo(() => cashFiltered.filter((inv) => !cashInSubiekt(inv)), [cashFiltered]);
  const cashBooked = React.useMemo(() => cashFiltered.filter((inv) => cashInSubiekt(inv)), [cashFiltered]);
  const sortedCashToEnter = React.useMemo(() => sortRows(cashToEnter), [sortRows, cashToEnter]);
  const sortedCashBooked = React.useMemo(() => sortRows(cashBooked), [sortRows, cashBooked]);
  const cashRowsForTab = React.useMemo(() => {
    if (cashTab === 0) return sortedCashToEnter;
    if (cashTab === 1) return sortedCashBooked;
    return sortedCashToEnter;
  }, [sortedCashToEnter, sortedCashBooked, cashTab]);


  // --- Sekcja: Faktury karta (trzecia zakładka główna) ---
  const [cardTab, setCardTab] = React.useState(0);
  const cardFiltered = rows.filter((inv) => {
    const matchesQuery = [inv.id, inv.project_id, inv.contractor_name].join(' ').toLowerCase().includes(query.toLowerCase());
    const matchesProject = projectFilter ? inv.project_id === projectFilter : true;
    const matchesMonth = monthFilter ? String(inv.document_date || '').startsWith(monthFilter) : true;
    const matchesStatus = statusFilter ? getEffectiveStatus(inv) === statusFilter : true;
    const matchesRequester = matchesPurchasesApplicant(inv);
    const matchesBooked = bookedFilter ? ((bookedFilter === 'tak') ? Boolean(inv.booked) : !Boolean(inv.booked)) : true;
    // Faktury karta: Rodzaj=Faktura VAT + Sposób płatności=Karta
    const kind = inv.document_kind || (inv.document_type === 'Zaliczka (przelew)' ? 'advance' : inv.document_type === 'Proforma' ? 'proforma' : 'vat');
    const matchesCard = kind === 'vat' && (String(inv.payment_method || '').toLowerCase().includes('karta') || String(inv.document_type || '') === 'Karta');
    return matchesQuery && matchesProject && matchesMonth && matchesStatus && matchesRequester && matchesBooked && matchesCard;
  });
  const cardInSubiekt = (inv: any) => Number(inv.subiekt) === 1 || inv.subiekt === true;
  // Dla kartowych: nie wymagamy akceptacji – od razu „Do wprowadzenia”
  const cardToEnter = React.useMemo(() => cardFiltered.filter((inv) => !cardInSubiekt(inv)), [cardFiltered]);
  const cardBooked = React.useMemo(() => cardFiltered.filter((inv) => cardInSubiekt(inv)), [cardFiltered]);
  const sortedCardToEnter = React.useMemo(() => sortRows(cardToEnter), [sortRows, cardToEnter]);
  const sortedCardBooked = React.useMemo(() => sortRows(cardBooked), [sortRows, cardBooked]);
  const cardRowsForTab = React.useMemo(() => {
    if (cardTab === 0) return sortedCardToEnter;
    if (cardTab === 1) return sortedCardBooked;
    return sortedCardToEnter;
  }, [sortedCardToEnter, sortedCardBooked, cardTab]);

  // --- Sekcja: Zaliczki (piąta zakładka główna) ---
  const advanceFiltered = rows.filter((inv) => {
    const matchesQuery = [inv.id, inv.project_id, inv.contractor_name].join(' ').toLowerCase().includes(query.toLowerCase());
    const matchesProject = projectFilter ? inv.project_id === projectFilter : true;
    const matchesMonth = monthFilter ? String(inv.document_date || '').startsWith(monthFilter) : true;
    const matchesStatus = statusFilter ? getEffectiveStatus(inv) === statusFilter : true;
    const matchesRequester = matchesPurchasesApplicant(inv);
    const matchesBooked = bookedFilter ? ((bookedFilter === 'tak') ? Boolean(inv.booked) : !Boolean(inv.booked)) : true;
    // Zaliczki: Rodzaj=Zaliczka (bez względu na sposób płatności)
    const kind = inv.document_kind || (inv.document_type === 'Zaliczka (przelew)' ? 'advance' : 'vat');
    const matchesType = kind === 'advance';
    return matchesQuery && matchesProject && matchesMonth && matchesStatus && matchesRequester && matchesBooked && matchesType;
  });
  const [advanceTab, setAdvanceTab] = React.useState(0);
  const advanceToEnter = React.useMemo(() => advanceFiltered.filter((inv) => !(Number(inv.subiekt) === 1 || inv.subiekt === true)), [advanceFiltered]);
  const advanceBooked = React.useMemo(() => advanceFiltered.filter((inv) => Number(inv.subiekt) === 1 || inv.subiekt === true), [advanceFiltered]);
  const sortedAdvanceToEnter = React.useMemo(() => sortRows(advanceToEnter), [sortRows, advanceToEnter]);
  const sortedAdvanceBooked = React.useMemo(() => sortRows(advanceBooked), [sortRows, advanceBooked]);
  const advanceRowsForTab = React.useMemo(() => {
    if (advanceTab === 0) return sortedAdvanceToEnter;
    if (advanceTab === 1) return sortedAdvanceBooked;
    return sortedAdvanceToEnter;
  }, [sortedAdvanceToEnter, sortedAdvanceBooked, advanceTab]);

  // --- Sekcja: Korekty (szósta zakładka główna) ---
  const [correctionTab, setCorrectionTab] = React.useState(0);
  const correctionFiltered = rows.filter((inv) => {
    const matchesQuery = [inv.id, inv.project_id, inv.contractor_name].join(' ').toLowerCase().includes(query.toLowerCase());
    const matchesProject = projectFilter ? inv.project_id === projectFilter : true;
    const matchesMonth = monthFilter ? String(inv.document_date || '').startsWith(monthFilter) : true;
    const matchesStatus = statusFilter ? getEffectiveStatus(inv) === statusFilter : true;
    const matchesRequester = matchesPurchasesApplicant(inv);
    const matchesBooked = bookedFilter ? ((bookedFilter === 'tak') ? Boolean(inv.booked) : !Boolean(inv.booked)) : true;
    // Korekty: Rodzaj=Korekta (bez względu na sposób płatności)
    const kind = inv.document_kind || (inv.document_type === 'Korekta' ? 'correction' : 'vat');
    const matchesType = kind === 'correction';
    return matchesQuery && matchesProject && matchesMonth && matchesStatus && matchesRequester && matchesBooked && matchesType;
  });
  const correctionAcceptanceRows = React.useMemo(() => correctionFiltered.filter((inv: any) => !isFullyApproved(inv)), [correctionFiltered, isFullyApproved]);
  const correctionToEnterRows = React.useMemo(
    () => correctionFiltered.filter((inv: any) => {
      const inSubiekt = Number(inv.subiekt) === 1 || inv.subiekt === true;
      return isFullyApproved(inv) && !inSubiekt;
    }),
    [correctionFiltered, isFullyApproved]
  );
  const correctionSubiektRows = React.useMemo(() => correctionFiltered.filter((inv: any) => Number(inv.subiekt) === 1 || inv.subiekt === true), [correctionFiltered]);
  const correctionUnpaidRows = React.useMemo(() => correctionFiltered.filter((inv: any) => !inv.paid_date), [correctionFiltered]);
  const correctionPaidRows = React.useMemo(() => correctionFiltered.filter((inv: any) => Boolean(inv.paid_date)), [correctionFiltered]);
  const sortedCorrectionAcceptance = React.useMemo(() => sortRows(correctionAcceptanceRows), [sortRows, correctionAcceptanceRows]);
  const sortedCorrectionToEnter = React.useMemo(() => sortRows(correctionToEnterRows), [sortRows, correctionToEnterRows]);
  const sortedCorrectionBooked = React.useMemo(() => sortRows(correctionSubiektRows), [sortRows, correctionSubiektRows]);
  const sortedCorrectionUnpaid = React.useMemo(() => sortRows(correctionUnpaidRows), [sortRows, correctionUnpaidRows]);
  const sortedCorrectionPaid = React.useMemo(() => sortRows(correctionPaidRows), [sortRows, correctionPaidRows]);
  const correctionRowsForTab = React.useMemo(() => {
    if (correctionTab === 0) return sortedCorrectionAcceptance;
    if (correctionTab === 1) return sortedCorrectionToEnter;
    if (correctionTab === 2) return sortedCorrectionBooked;
    if (correctionTab === 3) return sortedCorrectionUnpaid;
    if (correctionTab === 4) return sortedCorrectionPaid;
    return sortedCorrectionAcceptance;
  }, [sortedCorrectionAcceptance, sortedCorrectionToEnter, sortedCorrectionBooked, sortedCorrectionUnpaid, sortedCorrectionPaid, correctionTab]);

  const mainTabs = React.useMemo(
    () => [
      { label: 'KSEF', badge: ksefTabCounts.pending },
      { label: 'Faktury przelewowe', badge: acceptanceRows.length },
      { label: 'Faktury karta', badge: cardToEnter.length },
      { label: 'Faktury gotówkowe', badge: cashToEnter.length },
      { label: 'Zaliczki', badge: advanceToEnter.length + advanceBooked.length },
      { label: 'Korekty', badge: correctionAcceptanceRows.length },
      { label: 'Wszystkie', badge: filteredAll.length }
    ],
    [acceptanceRows.length, cardToEnter.length, cashToEnter.length, ksefTabCounts.pending, advanceToEnter.length, advanceBooked.length, correctionAcceptanceRows.length, filteredAll.length]
  );

  // Skrót klawiszowy "§" do przechodzenia między zakładkami (zależnie od sekcji)
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '§') {
        // Nie reaguj gdy fokus jest na polu tekstowym
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
        e.preventDefault();
        const reverse = e.shiftKey;
        if (sectionChip === 0) {
          // Sprzedaż: 2 zakładki
          setSalesMainTab(prev => reverse ? (prev - 1 + 2) % 2 : (prev + 1) % 2);
        } else if (sectionChip === 1) {
          // Magazyny: 5 zakładek
          setWhMainTab(prev => reverse ? (prev - 1 + WH_TABS.length) % WH_TABS.length : (prev + 1) % WH_TABS.length);
        } else if (sectionChip === 2) {
          // Zakupy: mainTabs
          setMainTab(prev => reverse ? (prev - 1 + mainTabs.length) % mainTabs.length : (prev + 1) % mainTabs.length);
        } else if (sectionChip === 5) {
          // Rozrachunki: 2 zakładki (Zakupy/Sprzedaż)
          setSettlementsMainTab(prev => reverse ? (prev - 1 + 2) % 2 : (prev + 1) % 2);
        } else if (sectionChip === 6) {
          // Operacje bankowe: 2 zakładki (Wypłaty/Wpłaty)
          setBankOpsMainTab(prev => reverse ? (prev - 1 + 2) % 2 : (prev + 1) % 2);
        } else if (sectionChip === 7) {
          // Wyciągi i uzgodnienia: 3 zakładki (Wyciągi/Transakcje/Uzgodnienia)
          setBsMainTab(prev => reverse ? (prev - 1 + 3) % 3 : (prev + 1) % 3);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mainTabs.length, sectionChip]);

  // Zakładki główne dla sekcji Sprzedaż
  const salesMainTabs = React.useMemo(
    () => [
      { label: 'KSEF', badge: 0 },
      { label: 'Faktury Sprzedażowe', badge: salesTabCounts.all }
    ],
    [salesTabCounts.all]
  );

  // --- Inteligentna paginacja (lazy reveal przy scrollu) ---
  const PAGE_CHUNK = 60;
  const [visibleCount, setVisibleCount] = React.useState<number>(PAGE_CHUNK);
  // Resetuj licznik gdy zmienią się główne konteksty (zakładki lub źródło danych)
  React.useEffect(() => {
    setVisibleCount(PAGE_CHUNK);
  }, [mainTab, tab, cashTab, cardTab, advanceTab, correctionTab, query, projectFilter, requesterFilter, monthFilter, statusFilter, bookedFilter, docTypeFilter]);
  // Dociąganie przy scrollu – przeniesione niżej po deklaracjach list

  const rowsForTab = React.useMemo(() => {
    if (tab === 0) return sortedAcceptance; // Faktury do akceptacji
    if (tab === 1) return sortedToEnter;    // Do wprowadzenia
    if (tab === 2) return sortedBooked;     // Wprowadzone do Subiekta
    if (tab === 3) return sortedUnpaid;     // Nierozliczone
    if (tab === 4) return sortedPaid;       // Rozliczone
    return sortedAcceptance;
  }, [sortedAcceptance, sortedToEnter, sortedBooked, sortedUnpaid, sortedPaid, tab]);

  React.useEffect(() => {
    const measureHeights = () => {
      try {
        const root = tableContainerRef.current as HTMLElement | null;
        if (!root) return;
        const headerRow = root.querySelector('thead tr') as HTMLElement | null;
        const bodyRows = root.querySelectorAll('tbody tr:not([data-sum-row="true"])');
        const sumRow = root.querySelector('tbody tr[data-sum-row="true"]') as HTMLElement | null;
        const headerH = Math.round(headerRow?.getBoundingClientRect().height || 32);
        let rowH = 36;
        const sampleCount = Math.min(12, bodyRows.length);
        if (sampleCount > 0) {
          let maxH = 0;
          for (let i = 0; i < sampleCount; i++) {
            const h = (bodyRows[i] as HTMLElement).getBoundingClientRect().height;
            if (h && h > maxH) maxH = h;
          }
          if (maxH > 0) rowH = Math.round(maxH);
        }
        const sumH = Math.round(sumRow?.getBoundingClientRect().height || rowH);
        const borders = 2;
        const fudgePx = 20;
        const heightForNineRows = headerH + (rowH * 9) + sumH + borders + fudgePx;
        const top = root.getBoundingClientRect().top;
        const viewportAvail = Math.max(300, Math.floor((window.innerHeight || 0) - top - 16));
        const finalHeight = Math.max(heightForNineRows, viewportAvail);
        setTableHeight(finalHeight);
      } catch {}
    };
    const t = setTimeout(measureHeights, 0);
    const t2 = setTimeout(measureHeights, 100);
    window.addEventListener('resize', measureHeights);
    return () => { clearTimeout(t); clearTimeout(t2); window.removeEventListener('resize', measureHeights); };
  }, [mainTab, rowsForTab, correctionRowsForTab, columnsVisible, ksefRows.length]);

  const renderMenu = () => {
    // Sprawdź czy to faktura KSEF (ma ksef_invoice_id)
    const isKsefInvoice = menuRow?.ksef_invoice_id != null;
    
    return (
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={menuPosition ? { top: menuPosition.top, left: menuPosition.left } : undefined}
        open={Boolean(menuPosition)}
        keepMounted
        onClose={() => { setMenuPosition(null); setMenuRow(null); }}
      >
        {isKsefInvoice ? (
          <>
            {/* Opcje "Podgląd" i "Edycja" zostały przeniesione jako ikony przed menu kebab */}
            <MenuItem onClick={async () => { 
              if(menuRow) { 
                try {
                  const response = await api.get(
                    `/ksef/invoices/${encodeURIComponent(menuRow.ksef_invoice_id)}/pdf`,
                    { responseType: 'blob' }
                  );
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `faktura-${menuRow.invoice_number || menuRow.ksef_invoice_id}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                  showNotification('PDF został pobrany', 'success');
                } catch (error: any) {
                  console.error('[KSEF] Błąd pobierania PDF:', error);
                  const errorMessage = error.response?.data?.message || error.message || 'Nieznany błąd';
                  showNotification('Błąd pobierania PDF: ' + errorMessage, 'error');
                }
              } 
              setMenuPosition(null); 
              setMenuRow(null); 
            }}>Pobierz PDF</MenuItem>
            <MenuItem onClick={async () => {
              if(menuRow) {
                try {
                  showNotification('Aktualizowanie faktury z KSEF...', 'info');
                  const response = await api.post(`/ksef/invoices/${encodeURIComponent(menuRow.ksef_invoice_id)}/refresh`);
                  if (response.data.success) {
                    showNotification('Faktura została zaktualizowana z KSEF', 'success');
                    // Odśwież listę faktur z lokalnej bazy (bez synchronizacji)
                    // Zawsze odświeżaj, niezależnie od zakładki (użytkownik może być na zakładce KSEF)
                    try {
                      console.log('[KSEF] Odświeżanie listy faktur po aktualizacji...', 'mainTab:', mainTab);
                      // Pobierz zaktualizowaną listę z lokalnej bazy (użyj tej samej logiki co handleKsefRefresh)
                      const params: any = { subject: 'purchases', limit: 200 };
                      if (ksefQuery) params.q = ksefQuery;
                      if (ksefMonth) params.month = ksefMonth;
                      console.log('[KSEF] Pobieram zaktualizowaną listę z parametrami:', params);
                      const invoices = await listKsefInvoices(params);
                      console.log('[KSEF] Pobrano faktur po aktualizacji:', invoices?.length || 0);
                      // Stwórz całkowicie nową tablicę z nowymi obiektami aby wymusić re-render
                      const newInvoices = invoices ? invoices.map(inv => ({ ...inv })) : [];
                      console.log('[KSEF] Utworzono nową tablicę z', newInvoices.length, 'fakturami');
                      // Użyj funkcji aktualizującej aby wymusić re-render
                      setKsefRows(() => newInvoices);
                      // Wymuś re-render tabeli poprzez zmianę klucza (użyj timestamp dla pewności)
                      const newKey = Date.now();
                      setKsefRefreshKey(newKey);
                      console.log('[KSEF] Zmieniono ksefRefreshKey na', newKey);
                      console.log('[KSEF] Zaktualizowano ksefRows i wymuszono re-render tabeli');
                    } catch (refreshError) {
                      console.error('[KSEF] Błąd odświeżania listy faktur:', refreshError);
                      showNotification('Faktura zaktualizowana, ale nie udało się odświeżyć listy', 'warning');
                    }
                  } else {
                    showNotification(response.data.message || 'Błąd aktualizacji faktury', 'error');
                  }
                } catch (error: any) {
                  console.error('[KSEF] Błąd aktualizacji faktury:', error);
                  const errorMessage = error.response?.data?.message || error.message || 'Nieznany błąd';
                  showNotification('Błąd aktualizacji faktury: ' + errorMessage, 'error');
                }
              }
              setMenuPosition(null);
              setMenuRow(null);
            }}>Aktualizuj z KSEF</MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={() => { 
              if(menuRow) { 
                const editId = menuRow.ksef_invoice_id || menuRow.id;
                const editData = menuRow.ksef_invoice_id ? {
                  ...menuRow,
                  document_type: 'Przelew',
                  document_kind: menuRow.invoice_type === 'correction' ? 'correction' : 'vat',
                  document_date: menuRow.issue_date || '',
                  received_date: menuRow.invoicing_date || '',
                  contractor_name: menuRow.seller_name || '',
                  nip: menuRow.seller_tax_id || '',
                  account_number: menuRow.bank_account_number || '',
                  net_amount: Number(menuRow.net_amount || 0),
                  gross_amount: Number(menuRow.gross_amount || 0),
                  ksef_status: menuRow.status || 'Do weryfikacji',
                } : menuRow;
                sessionStorage.setItem(`edit-invoice-${editId}`, JSON.stringify(editData));
                window.open(`/accounting/edit/${encodeURIComponent(editId)}`, '_blank');
              } 
              setMenuPosition(null); setMenuRow(null); 
            }}>Edycja</MenuItem>
            <MenuItem onClick={async () => {
              if (!menuRow) { setMenuPosition(null); setMenuRow(null); return; }
              const editId = menuRow.ksef_invoice_id || menuRow.id;
              if (!menuRow.warehouse) {
                const editData = { ...menuRow, highlightWarehouse: true };
                sessionStorage.setItem(`edit-invoice-${editId}`, JSON.stringify(editData));
                window.open(`/accounting/edit/${encodeURIComponent(editId)}`, '_blank');
                showNotification('Uzupełnij pole Magazyn w edycji faktury', 'info');
                setMenuPosition(null); setMenuRow(null);
                return;
              }
              try {
                const result = await addInvoiceToWarehouse(Number(menuRow.id));
                showNotification(`Utworzono PZ ${result.document_number} – asortyment dodany do magazynu`, 'success');
                refresh();
                refreshWarehouse?.();
              } catch (err: any) {
                const code = err?.response?.data?.code;
                const msg = err?.response?.data?.message || 'Błąd dodawania asortymentu';
                if (code === 'NO_WAREHOUSE' || code === 'WAREHOUSE_NOT_FOUND') {
                  const editData = { ...menuRow, highlightWarehouse: true };
                  sessionStorage.setItem(`edit-invoice-${editId}`, JSON.stringify(editData));
                  window.open(`/accounting/edit/${encodeURIComponent(editId)}`, '_blank');
                  showNotification(msg, 'warning');
                } else {
                  showNotification(msg, 'error');
                }
              }
              setMenuPosition(null); setMenuRow(null);
            }}>Dodaj asortyment do magazynu</MenuItem>
            <MenuItem onClick={() => { if(menuRow) window.open(`/accounting/approve/${menuRow.id}`, '_blank'); setMenuPosition(null); setMenuRow(null); }}>Okno zatwierdzenia</MenuItem>
            <MenuItem onClick={async () => { if(menuRow) { await quickApproveInvoice(menuRow.id, 'approved'); refresh(); } setMenuPosition(null); setMenuRow(null); }}>Zatwierdzam</MenuItem>
            <MenuItem onClick={async () => { if(menuRow) { await quickApproveInvoice(menuRow.id, 'rejected'); refresh(); } setMenuPosition(null); setMenuRow(null); }}>Odrzucam</MenuItem>
          </>
        )}
      </Menu>
    );
  };

  const renderRequester = (r: Invoice['requester']) => (
    <Tooltip title={`Wnioskuje: ${r.name}`} arrow>
      <Avatar sx={{ bgcolor: r.color, width: 25, height: 25, fontSize: 12 }}>
        {r.name.substring(0, 1)}
      </Avatar>
    </Tooltip>
  );

  const renderApprovers = (list: Invoice['approvers']) => (
    <AvatarGroup max={2} sx={{ '& .MuiAvatar-root': { width: 25, height: 25, fontSize: 12 } }}>
      {list.map((p, idx) => (
        <Tooltip key={`${p.name}-${idx}`} title={`Zatwierdził(a): ${p.name}`} arrow>
          <Avatar sx={{ bgcolor: p.color }}>
            {p.name.substring(0, 1)}
          </Avatar>
        </Tooltip>
      ))}
    </AvatarGroup>
  );

  const statusIcon = (status: Invoice['approvalStatus']) => {
    switch (status) {
      case 'approved':
        return <Tooltip title="Zatwierdzone" arrow><CheckCircle sx={{ fontSize: 18, color: 'success.main' }} /></Tooltip>;
      case 'rejected':
        return <Tooltip title="Odrzucone" arrow><DoNotDisturbOn sx={{ fontSize: 18, color: 'error.main' }} /></Tooltip>;
      case 'needs_info':
        return <Tooltip title="Uzupełnić" arrow><HelpOutline sx={{ fontSize: 18, color: 'warning.main' }} /></Tooltip>;
      default:
        return <Tooltip title="Wprowadzono/W toku" arrow><InfoOutlined sx={{ fontSize: 18, color: 'info.main' }} /></Tooltip>;
    }
  };

  const Filters = null;

  const TableHeaderAcceptance = (
    <TableHead>
      <TableRow>
        {(draftOrder ?? columnOrder).map((key) => {
          if (!colV(key)) return null;
          if (key === 'dates') {
            return renderResizableTh('dates', (
              <TableSortLabel {...getSortLabelProps(orderBy === 'created_at')} active={orderBy === 'created_at'} direction={order} onClick={() => handleRequestSort('created_at')}>
                Daty
              </TableSortLabel>
            ));
          }
          if (key === 'sel') {
            return renderResizableTh('sel', (
              <TableSortLabel
                {...getSortLabelProps(orderBy === 'created_by_name')}
                active={orderBy === 'created_by_name'}
                direction={order}
                onClick={() => handleRequestSort('created_by_name')}
              >
                W
              </TableSortLabel>
            ));
          }
          if (key === 'invoiceNumber') {
            return renderResizableTh('invoiceNumber', 'Nr faktury');
          }
          if (key === 'project') {
            return renderResizableTh('project', (
              <TableSortLabel {...getSortLabelProps(orderBy === 'project_id')} active={orderBy === 'project_id'} direction={order} onClick={() => handleRequestSort('project_id')}>
                Projekt
              </TableSortLabel>
            ));
          }
          if (key === 'contractor') {
            return renderResizableTh('contractor', (
              <TableSortLabel {...getSortLabelProps(orderBy === 'contractor_name')} active={orderBy === 'contractor_name'} direction={order} onClick={() => handleRequestSort('contractor_name')}>
                Kontrahent
              </TableSortLabel>
            ));
          }
          if (key === 'net') {
            return renderResizableTh('net', (
              <TableSortLabel {...getSortLabelProps(orderBy === 'net_amount')} active={orderBy === 'net_amount'} direction={order} onClick={() => handleRequestSort('net_amount')}>
                Wartość netto
              </TableSortLabel>
            ), 'right');
          }
          if (key === 'gross') {
            return renderResizableTh('gross', (
              <TableSortLabel {...getSortLabelProps(orderBy === 'gross_amount')} active={orderBy === 'gross_amount'} direction={order} onClick={() => handleRequestSort('gross_amount')}>
                Wartość brutto
              </TableSortLabel>
            ), 'right');
          }
          if (key === 'due') {
            return renderResizableTh('due', (
              <TableSortLabel {...getSortLabelProps(orderBy === 'due_date')} active={orderBy === 'due_date'} direction={order} onClick={() => handleRequestSort('due_date')}>
                Termin płatności
              </TableSortLabel>
            ));
          }
          if (key === 'paid') {
            return renderResizableTh('paid', 'Opłacono');
          }
          if (key === 'approvals') {
            return renderResizableTh('approvals', null as any);
          }
          if (key === 'preview') {
            return renderResizableTh('preview', null as any, 'center');
          }
          if (key === 'actions') {
            return renderResizableTh('actions', null as any, 'right');
          }
          return null;
        })}
      </TableRow>
    </TableHead>
  );

  const TableHeaderTransfer = (
    <TableHead>
      <TableRow>
        {(draftOrder ?? columnOrder).map((key) => {
          if (!colV(key)) return null;
          if (key === 'dates') {
            return renderResizableTh('dates', (
              <TableSortLabel {...getSortLabelProps(orderBy === 'created_at')} active={orderBy === 'created_at'} direction={order} onClick={() => handleRequestSort('created_at')}>
                Daty
              </TableSortLabel>
            ));
          }
          if (key === 'sel') {
            return renderResizableTh('sel', (
              <TableSortLabel
                {...getSortLabelProps(orderBy === 'created_by_name')}
                active={orderBy === 'created_by_name'}
                direction={order}
                onClick={() => handleRequestSort('created_by_name')}
              >
                W
              </TableSortLabel>
            ));
          }
          if (key === 'project') {
            return renderResizableTh('project', (
              <TableSortLabel {...getSortLabelProps(orderBy === 'project_id')} active={orderBy === 'project_id'} direction={order} onClick={() => handleRequestSort('project_id')}>
                Projekt
              </TableSortLabel>
            ));
          }
          if (key === 'contractor') {
            return renderResizableTh('contractor', (
              <TableSortLabel {...getSortLabelProps(orderBy === 'contractor_name')} active={orderBy === 'contractor_name'} direction={order} onClick={() => handleRequestSort('contractor_name')}>
                Kontrahent
              </TableSortLabel>
            ));
          }
          if (key === 'invoiceNumber') {
            return renderResizableTh('invoiceNumber', 'Nr faktury');
          }
          if (key === 'net') {
            return renderResizableTh('net', (
              <TableSortLabel {...getSortLabelProps(orderBy === 'net_amount')} active={orderBy === 'net_amount'} direction={order} onClick={() => handleRequestSort('net_amount')}>
                Wartość netto
              </TableSortLabel>
            ), 'right');
          }
          if (key === 'gross') {
            return renderResizableTh('gross', (
              <TableSortLabel {...getSortLabelProps(orderBy === 'gross_amount')} active={orderBy === 'gross_amount'} direction={order} onClick={() => handleRequestSort('gross_amount')}>
                Wartość brutto
              </TableSortLabel>
            ), 'right');
          }
          if (key === 'due') {
            return renderResizableTh('due', (
              <TableSortLabel {...getSortLabelProps(orderBy === 'due_date')} active={orderBy === 'due_date'} direction={order} onClick={() => handleRequestSort('due_date')}>
                Termin płatności
              </TableSortLabel>
            ));
          }
          if (key === 'paid') {
            return renderResizableTh('paid', 'Opłacono');
          }
          if (key === 'approvals') {
            return renderResizableTh('approvals', null as any);
          }
          if (key === 'preview') {
            return renderResizableTh('preview', null as any, 'center');
          }
          if (key === 'actions') {
            return renderResizableTh('actions', null as any, 'right');
          }
          return null;
        })}
      </TableRow>
    </TableHead>
  );

  const TableRowCommon: React.FC<{ inv: any; withActions?: boolean; rowKey?: string }> = ({ inv, withActions, rowKey }) => {
    // Klucz wiersza (Excel-like selekcja). Jeżeli rodzic nie podał, używamy
    // surowego id faktury – w obrębie jednej tabeli to wystarczy.
    const effectiveRowKey = rowKey || `row-${inv.id}`;
    const isSelectedRow = selectedRowKey === effectiveRowKey;
    const delayDays = React.useMemo(() => {
      const toUTC = (iso?: string) => {
        if (!iso) return NaN as any;
        const d = new Date(String(iso));
        return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
      };
      const d1 = toUTC(inv.document_date);
      const d2 = toUTC(inv.received_date);
      if (isNaN(d1) || isNaN(d2)) return 0;
      const ms = d2 - d1;
      return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
    }, [inv.document_date, inv.received_date]);
    const introduceDelayDays = React.useMemo(() => {
      const toUTC = (iso?: string) => {
        if (!iso) return NaN as any;
        const d = new Date(String(iso));
        return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
      };
      const created = toUTC(inv.created_at);
      const documentDate = toUTC(inv.document_date);
      if (isNaN(created) || isNaN(documentDate)) return 0;
      const diff = created - documentDate;
      return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
    }, [inv.created_at, inv.document_date]);
    return (
    <TableRow
      hover
      data-rowkey={effectiveRowKey}
      onClick={() => {
        // Excel-like jednorazowy wybór wiersza – klik ponownie tego samego
        // wiersza odznacza go. Podgląd faktury otwiera się WYŁĄCZNIE
        // przyciskiem „Podgląd" w kolumnie akcji (zob. żądanie usera).
        setSelectedRowKey((prev) => (prev === effectiveRowKey ? null : effectiveRowKey));
      }}
      sx={{
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#F5F5FF !important' },
        '&.MuiTableRow-hover:hover': { backgroundColor: '#F5F5FF !important' },
        '&.Mui-selected': { backgroundColor: '#F5F5FF !important' },
        '&.Mui-selected:hover': { backgroundColor: '#F5F5FF !important' },
        ...getRowSelectionSx(isSelectedRow),
      }}
    >
      {(draftOrder ?? columnOrder).map((key) => {
        if (!colV(key)) return null;
        if (key === 'dates') return (
        <TableCell key={key} sx={{ width: getColW('dates'), whiteSpace: 'nowrap', pr: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
            <Tooltip title={`Data wprowadzenia: ${String(inv.created_at || '')}`} arrow>
              <span>{String(inv.created_at || '').slice(0,10)}</span>
            </Tooltip>
            {introduceDelayDays > 0 ? (
              <Box component="span" sx={{ ml: 0.5, bgcolor: 'primary.main', color: 'common.white', height: 12, lineHeight: '12px', px: 0.375, borderRadius: '6px', fontSize: 9, fontWeight: 700 }}>{`+${introduceDelayDays}`}</Box>
            ) : null}
          </Box>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
            <Tooltip title={`Data faktury: ${String(inv.document_date || '')}`} arrow>
              <span>{inv.document_date || ''}</span>
            </Tooltip>
            {delayDays > 0 ? (
              <Box component="span" sx={{ ml: 0.5, bgcolor: 'error.main', color: 'common.white', height: 12, lineHeight: '12px', px: 0.375, borderRadius: '6px', fontSize: 9, fontWeight: 700 }}>{`+${delayDays}`}</Box>
            ) : null}
          </Box>
        </Box>
        </TableCell>);
        if (key === 'sel') return (
        <TableCell key={key} sx={{ p: 0.5, width: getColW('sel') }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {inv.created_by_avatar_url || inv.created_by_name ? (
            <Tooltip title={inv.created_by_name || ''} arrow>
              <Avatar src={inv.created_by_avatar_url || undefined} sx={{ width: 25, height: 25, fontSize: 12 }}>
                {(inv.created_by_name || '').substring(0,1)}
              </Avatar>
            </Tooltip>
          ) : null}
        </Box>
        </TableCell>);
        if (key === 'invoiceNumber') return (
        <TableCell key={key} sx={{ width: getColW('invoiceNumber'), whiteSpace: 'nowrap' }}>
          {inv.invoice_number || ''}
        </TableCell>);
        if (key === 'project') return (
        <TableCell key={key} sx={{ width: getColW('project'), pl: 0 }}>
        {(() => {
          const full = `${inv.project_id} ${inv.project_name || ''}`.trim();
          return (
            <Tooltip title={full} arrow>
              <Box sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.25,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                maxHeight: '2.5em'
              }}>
                {full}
              </Box>
            </Tooltip>
          );
        })()}
        </TableCell>);
        if (key === 'contractor') return (
        <TableCell key={key} sx={{ width: getColW('contractor') }}>
        {(() => {
          const full = String(inv.contractor_name || '');
          return (
            <Tooltip title={full} arrow>
              <Box sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.25,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                maxHeight: '2.5em'
              }}>
                {full}
              </Box>
            </Tooltip>
          );
        })()}
        </TableCell>);
        if (key === 'net') return <TableCell key={key} align="right" sx={{ width: getColW('net'), whiteSpace: 'nowrap' }}>{currency(Number(inv.net_amount || 0))}</TableCell>;
        if (key === 'gross') return <TableCell key={key} align="right" sx={{ width: getColW('gross'), whiteSpace: 'nowrap' }}>{currency(Number(inv.gross_amount || 0))}</TableCell>;
        if (key === 'due') return (
        <TableCell key={key} sx={{ width: getColW('due'), whiteSpace: 'nowrap' }}>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'flex-end', position: 'relative' }}>
          <span>{inv.due_date || ''}</span>
          {delayDays > 0 ? (
            <Box component="span" sx={{ ml: 0.5, bgcolor: 'error.main', color: 'common.white', height: 12, lineHeight: '12px', px: 0.375, borderRadius: '6px', fontSize: 9, fontWeight: 700 }}>{`+${delayDays}`}</Box>
          ) : null}
        </Box>
        </TableCell>);
        if (key === 'paid' && withActions) return (
        <TableCell key={key} sx={{ width: getColW('paid'), whiteSpace: 'nowrap' }}>
          {(() => {
            // Dla faktur gotówkowych zawsze pokazujemy datę faktury (są zawsze opłacone w dniu wystawienia)
            if (String(inv.document_type || '') === 'Gotówka') {
              return inv.document_date ? String(inv.document_date).slice(0, 10) : 'NIE';
            }
            // Dla innych faktur pokazujemy datę płatności
            return inv.paid_date ? String(inv.paid_date).slice(0, 10) : 'NIE';
          })()}
        </TableCell>);
        if (key === 'approvals') return (
        <TableCell key={key} sx={{ p: 0.5, width: getColW('approvals') }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {(() => {
              const pending = !inv.accounting_approval_status || String(inv.accounting_approval_status) === 'pending';
              const title = pending ? 'Księgowość: oczekuje' : `Księgowość: ${inv.accounting_approver_name || ''}`;
              const letter = pending ? 'K' : String(inv.accounting_approver_name || '?').substring(0,1);
              const src = pending ? undefined : (inv.accounting_approver_avatar_url || undefined);
              return (
                <Tooltip title={title} arrow>
                  <Avatar src={src} sx={{ width: 25, height: 25, fontSize: 12, bgcolor: pending ? '#e0e0e0' : undefined }}>
                    {letter}
                  </Avatar>
                </Tooltip>
              );
            })()}
            {(() => {
              const pending = !inv.admin_approval_status || String(inv.admin_approval_status) === 'pending';
              const title = pending ? 'Admin: oczekuje' : `Admin: ${inv.admin_approver_name || ''}`;
              const letter = pending ? 'A' : String(inv.admin_approver_name || '?').substring(0,1);
              const src = pending ? undefined : (inv.admin_approver_avatar_url || undefined);
              return (
                <Tooltip title={title} arrow>
                  <Avatar src={src} sx={{ width: 25, height: 25, fontSize: 12, bgcolor: pending ? '#e0e0e0' : undefined }}>
                    {letter}
                  </Avatar>
                </Tooltip>
              );
            })()}
          </Box>
          {(() => {
            const status = getEffectiveStatus(inv);
            const statusDate: string = String(
              inv.updated_at || inv.accounting_approval_at || inv.admin_approval_at || inv.created_at || ''
            );
            const dateLabel = statusDate ? ` • ${statusDate.slice(0,16).replace('T',' ')}` : '';
            const tip = `${status}${dateLabel}`;
            if (status === 'Zatwierdzone') return (
              <Tooltip title={tip} arrow><CheckCircle sx={{ fontSize: 18, color: 'success.main' }} /></Tooltip>
            );
            if (status === 'Odrzucone') return (
              <Tooltip title={tip} arrow><DoNotDisturbOn sx={{ fontSize: 18, color: 'error.main' }} /></Tooltip>
            );
            if (status === 'Uzupełnić') return (
              <Tooltip title={tip} arrow><ReportProblem sx={{ fontSize: 18, color: '#FFA000' }} /></Tooltip>
            );
            if (status === 'Pilny') return (
              <Tooltip title={tip} arrow><ErrorOutline sx={{ fontSize: 18, color: '#D32F2F' }} /></Tooltip>
            );
            return (
              <Tooltip title={tip || 'Wprowadzono'} arrow><InfoOutlined sx={{ fontSize: 18, color: 'info.main' }} /></Tooltip>
            );
          })()}
          {inv.google_drive_file_url ? (
            <Tooltip title="Skopiuj link do PDF" arrow>
              <IconButton size="small" onClick={async () => { 
                try {
                  await navigator.clipboard.writeText(String(inv.google_drive_file_url));
                  showNotification('Link do PDF skopiowany do schowka', 'success');
                } catch (err) {
                  showNotification('Nie udało się skopiować linku', 'error');
                }
              }}>
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          ) : null}
          </Box>
        </TableCell>);
        if (key === 'preview' && withActions) return (
        <TableCell key={key} align="center" sx={{ width: getColW('preview') }}>
            <IconButton size="small" color="primary" title="Podgląd PDF" onClick={() => { setPreviewInvoice(inv); setPreviewOpen(true); markViewed(inv.id); }}>
              <Visibility fontSize="small" />
            </IconButton>
        </TableCell>);
        if (key === 'actions' && withActions) return (
        <TableCell key={key} align="right" sx={{ width: getColW('actions'), position: 'relative' }}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenuRow(inv); setMenuPosition({ top: e.clientY, left: e.clientX }); }}>
              <MoreVert fontSize="small" />
            </IconButton>
        </TableCell>);
        return null;
      })}
    </TableRow>
  ); };

  const calcTotals = React.useCallback((list: any[]) => {
    const net = list.reduce((sum: number, inv: any) => sum + Number(inv.net_amount || 0), 0);
    const gross = list.reduce((sum: number, inv: any) => sum + Number(inv.gross_amount || 0), 0);
    return { net, gross };
  }, []);
  const totalsTransfer = React.useMemo(() => calcTotals(rowsForTab), [rowsForTab, calcTotals]);
  // Widoki z paginacją po stronie UI
  const visibleTransferRows = React.useMemo(() => rowsForTab.slice(0, visibleCount), [rowsForTab, visibleCount]);
  const visibleCardRows = React.useMemo(() => cardRowsForTab.slice(0, visibleCount), [cardRowsForTab, visibleCount]);
  const visibleCashRows = React.useMemo(() => cashRowsForTab.slice(0, visibleCount), [cashRowsForTab, visibleCount]);
  const visibleAdvanceRows = React.useMemo(() => advanceRowsForTab.slice(0, visibleCount), [advanceRowsForTab, visibleCount]);
  const visibleCorrectionRows = React.useMemo(() => correctionRowsForTab.slice(0, visibleCount), [correctionRowsForTab, visibleCount]);
  const totalsVisibleTransfer = React.useMemo(() => calcTotals(visibleTransferRows), [visibleTransferRows, calcTotals]);
  const totalsVisibleCard = React.useMemo(() => calcTotals(visibleCardRows), [visibleCardRows, calcTotals]);
  const totalsVisibleCash = React.useMemo(() => calcTotals(visibleCashRows), [visibleCashRows, calcTotals]);
  const totalsVisibleAdvance = React.useMemo(() => calcTotals(visibleAdvanceRows), [visibleAdvanceRows, calcTotals]);
  const totalsVisibleCorrection = React.useMemo(() => calcTotals(visibleCorrectionRows), [visibleCorrectionRows, calcTotals]);
  const visibleAllRows = React.useMemo(() => sortedAll.slice(0, visibleCount), [sortedAll, visibleCount]);
  const totalsVisibleAll = React.useMemo(() => calcTotals(visibleAllRows), [visibleAllRows, calcTotals]);

  // Widoczne wiersze KSEF (po wszystkich filtrach) – używane też do
  // nawigacji strzałkami góra/dół.
  const visibleKsefRows = React.useMemo(() => {
    if (!Array.isArray(ksefRows)) return [] as typeof ksefRows;
    return ksefRows.filter((inv) => {
      const invoiceStatus = inv.status || 'Do weryfikacji';
      if (ksefStatusTab === 'approved' && invoiceStatus !== 'Zatwierdzona') return false;
      if (ksefStatusTab === 'rejected' && invoiceStatus !== 'Odrzucona') return false;
      if (ksefStatusTab === 'pending' && invoiceStatus !== 'Do weryfikacji') return false;
      if (ksefStatusTab === 'all') {
        if (ksefApprovals === 'approved' && invoiceStatus !== 'Zatwierdzona') return false;
        if (ksefApprovals === 'unapproved' && invoiceStatus === 'Zatwierdzona') return false;
      }
      if (ksefQuery) {
        const q = ksefQuery.toLowerCase();
        const matches =
          (inv.invoice_number || '').toLowerCase().includes(q) ||
          (inv.seller_name || '').toLowerCase().includes(q) ||
          (inv.seller_tax_id || '').toLowerCase().includes(q);
        if (!matches) return false;
      }
      if (ksefMonth && inv.issue_date) {
        const invoiceMonth = String(inv.issue_date).slice(0, 7);
        if (invoiceMonth !== ksefMonth) return false;
      }
      if (ksefPaymentMethod) {
        const pm = String((inv as any).payment_method || '').toLowerCase().trim();
        if (!pm.includes(ksefPaymentMethod.toLowerCase())) return false;
      }
      return true;
    });
  }, [ksefRows, ksefStatusTab, ksefApprovals, ksefQuery, ksefMonth, ksefPaymentMethod]);

  // Lista unikalnych metod płatności występujących w aktualnym widoku KSeF
  // – używana do wypełnienia dropdownu „Płatność".
  const ksefPaymentMethodOptions = React.useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const inv of ksefRows) {
      const pm = String((inv as any).payment_method || '').trim();
      if (pm) set.add(pm);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pl'));
  }, [ksefRows]);

  // Lista kluczy widocznych wierszy w aktualnie aktywnej zakładce sekcji
  // „Zakupy". Używana do nawigacji ↑/↓ – kolejność musi odpowiadać
  // kolejności w DOM, dlatego budujemy ją z tych samych źródeł, których
  // używają same tabele.
  const purchasesVisibleRowKeys = React.useMemo<string[]>(() => {
    if (sectionChip !== 2) return [];
    switch (mainTab) {
      case 0: // KSEF
        return visibleKsefRows.map((r: any) => `ksef-${r.ksef_invoice_id}`);
      case 1: // Faktury przelewowe
        return visibleTransferRows.map((r: any) => `transfer-${r.id}`);
      case 2: // Faktury karta
        return visibleCardRows.map((r: any) => `card-${r.id}`);
      case 3: // Faktury gotówkowe
        return visibleCashRows.map((r: any) => `cash-${r.id}`);
      case 4: // Zaliczki
        return visibleAdvanceRows.map((r: any) => `advance-${r.id}`);
      case 5: // Korekty
        return visibleCorrectionRows.map((r: any) => `correction-${r.id}`);
      case 6: // Wszystkie
        return visibleAllRows.map((r: any) => `all-${r.id}`);
      default:
        return [];
    }
  }, [sectionChip, mainTab, visibleKsefRows, visibleTransferRows, visibleCardRows, visibleCashRows, visibleAdvanceRows, visibleCorrectionRows, visibleAllRows]);

  // Reset selekcji przy przełączeniu zakładki głównej w „Zakupach".
  React.useEffect(() => { if (sectionChip === 2) setSelectedRowKey(null); }, [sectionChip, mainTab]);

  // Pełna liczba wierszy w aktualnej zakładce (przed paginacją scrollową) –
  // żeby strzałka w dół na końcu listy dociągała kolejne strony.
  const purchasesTotalRowsForTab = React.useMemo<number>(() => {
    if (sectionChip !== 2) return 0;
    switch (mainTab) {
      case 0: return visibleKsefRows.length; // KSeF nie używa paginacji visibleCount
      case 1: return rowsForTab.length;
      case 2: return cardRowsForTab.length;
      case 3: return cashRowsForTab.length;
      case 4: return advanceRowsForTab.length;
      case 5: return correctionRowsForTab.length;
      case 6: return sortedAll.length;
      default: return 0;
    }
  }, [sectionChip, mainTab, visibleKsefRows.length, rowsForTab.length, cardRowsForTab.length, cashRowsForTab.length, advanceRowsForTab.length, correctionRowsForTab.length, sortedAll.length]);

  // Nawigacja klawiszami ↑/↓ po liście. Pomija wpisywanie w polach tekstowych
  // (input/textarea/contentEditable). Po wybraniu wiersza scrolluje go do
  // widoku wewnątrz odpowiedniego kontenera (block: 'nearest'). Gdy strzałka
  // w dół dochodzi do końca załadowanej strony, dociągana jest kolejna porcja
  // (visibleCount += PAGE_CHUNK) – żeby user mógł nawigować po pełnej liście.
  //
  // PŁYNNOŚĆ PRZY PRZYTRZYMANIU KLAWISZA:
  //   - bieżącą selekcję czytamy z `selectedRowKeyRef.current`, NIE z React
  //     state – inaczej kolejne wywołania z key-repeat widzą stare wartości
  //     (closure capture) i nawigacja gubi się/cofa.
  //   - aktualny snapshot listy kluczy trzymamy w `purchasesVisibleRowKeysRef`,
  //     żeby handler nie był odrejestrowywany / rejestrowany na każdy
  //     re-render (co kosztowało 2-3 ms i widocznie przycinało).
  //   - `setSelectedRowKey` zawijamy w `flushSync`, żeby DOM (klasa, scroll)
  //     był spójny z kolejnymi keydownami i nie kumulował się w jednym
  //     rendererze. Tak samo robi arkusz pozycji w EasyEstimate.
  const purchasesVisibleRowKeysRef = React.useRef<string[]>([]);
  React.useEffect(() => { purchasesVisibleRowKeysRef.current = purchasesVisibleRowKeys; }, [purchasesVisibleRowKeys]);
  const purchasesTotalRowsForTabRef = React.useRef<number>(0);
  React.useEffect(() => { purchasesTotalRowsForTabRef.current = purchasesTotalRowsForTab; }, [purchasesTotalRowsForTab]);
  const mainTabRef = React.useRef<number>(mainTab);
  React.useEffect(() => { mainTabRef.current = mainTab; }, [mainTab]);
  const sectionChipRefForKeys = React.useRef<number>(sectionChip);
  React.useEffect(() => { sectionChipRefForKeys.current = sectionChip; }, [sectionChip]);

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (sectionChipRefForKeys.current !== 2) return;
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) return;
        if (target.closest('.MuiDialog-root, .MuiPopover-root, .MuiMenu-root')) return;
      }
      const keys = purchasesVisibleRowKeysRef.current;
      if (keys.length === 0) return;
      e.preventDefault();
      const current = selectedRowKeyRef.current;
      const currentIdx = current ? keys.indexOf(current) : -1;
      // Strzałka w dół na końcu załadowanej strony – jeżeli pełna lista
      // ma więcej elementów, dociągamy kolejną porcję. Selekcję ruszamy
      // dopiero po doładowaniu (kolejny keydown).
      if (
        e.key === 'ArrowDown' &&
        currentIdx === keys.length - 1 &&
        purchasesTotalRowsForTabRef.current > keys.length &&
        mainTabRef.current !== 0
      ) {
        setVisibleCount((prev) => Math.min(prev + PAGE_CHUNK, purchasesTotalRowsForTabRef.current));
        return;
      }
      let nextIdx: number;
      if (currentIdx === -1) {
        nextIdx = e.key === 'ArrowDown' ? 0 : keys.length - 1;
      } else if (e.key === 'ArrowDown') {
        nextIdx = Math.min(currentIdx + 1, keys.length - 1);
      } else {
        nextIdx = Math.max(currentIdx - 1, 0);
      }
      if (nextIdx === currentIdx) return;
      const nextKey = keys[nextIdx];
      // Synchronizujemy ref od razu, żeby kolejne wciśnięcie z key-repeat
      // miało aktualny punkt startowy – nawet jeżeli React jeszcze nie
      // zdążył wyrenderować nowego stanu.
      selectedRowKeyRef.current = nextKey;
      flushSync(() => {
        setSelectedRowKey(nextKey);
      });
      const el = document.querySelector(`[data-rowkey="${nextKey}"]`) as HTMLElement | null;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // Pusta tablica zależności – handler operuje wyłącznie na ref-ach,
    // nie chcemy go re-rejestrować przy każdej zmianie stanu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dociąganie przy scrollu – po deklaracji list zależnych
  React.useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      const threshold = 240;
      const rowsTotal = (
        mainTab === 1 ? rowsForTab :
        mainTab === 2 ? cardRowsForTab :
        mainTab === 3 ? cashRowsForTab :
        mainTab === 4 ? advanceRowsForTab :
        mainTab === 5 ? correctionRowsForTab :
        mainTab === 6 ? sortedAll :
        []
      ).length;
      if (visibleCount >= rowsTotal) return;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
      if (atBottom) {
        setVisibleCount((prev) => Math.min(prev + PAGE_CHUNK, rowsTotal));
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [mainTab, tab, cashTab, advanceTab, correctionTab, rowsForTab.length, cardRowsForTab.length, cashRowsForTab.length, advanceRowsForTab.length, correctionRowsForTab.length, sortedAll.length, visibleCount]);

  const renderSummaryRow = React.useCallback((totalsArg?: { net: number; gross: number }) => {
    const totals = totalsArg ?? totalsTransfer;
    const visible = (draftOrder ?? columnOrder).filter(colV);
    const sumKeys = ['net','gross'];
    const firstSumIndex = visible.findIndex((k) => sumKeys.includes(String(k)));
    const beforeCount = firstSumIndex < 0 ? visible.length : firstSumIndex;
    if (visible.length === 0) return null;
    const cells: React.ReactNode[] = [];
    // Label cell (Suma:)
    if (beforeCount > 0) {
      cells.push(
        <TableCell
          key="sum-label"
          colSpan={beforeCount}
          align="right"
          sx={{ p: '6px 12px', height: 32, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#020242' }}
        >
          Suma:
        </TableCell>
      );
    }
    // Cells after label
    const tailKeys = firstSumIndex < 0 ? [] : visible.slice(firstSumIndex);
    tailKeys.forEach((key) => {
      if (key === 'net') {
        cells.push(
          <TableCell key="sum-net" align="right" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#020242' }}>
            {currency(totals.net)}
          </TableCell>
        );
      } else if (key === 'gross') {
        cells.push(
          <TableCell key="sum-gross" align="right" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#020242' }}>
            {currency(totals.gross)}
          </TableCell>
        );
      } else {
        cells.push(<TableCell key={`sum-empty-${String(key)}`} sx={{ backgroundColor: '#F1F1F6' }} />);
      }
    });
    return (
      <TableRow data-sum-row sx={{ position: 'sticky', bottom: 0, backgroundColor: '#F1F1F6', zIndex: 1, '& .MuiTableCell-root': { borderBottom: 'none' } }}>
        {cells}
      </TableRow>
    );
  }, [totalsTransfer, columnsVisible]);

  // Wysyłanie komentarza w modalu podglądu (wspólne dla przycisku i Enter)
  const sendPreviewComment = React.useCallback(async () => {
    if (!previewInvoice || !newComment.trim()) return;
    try {
      const res = await (await import('../services/invoicesService')).addInvoiceComment(previewInvoice.id, newComment.trim());
      setNewComment('');
      const updated = res?.data || null;
      setPreviewInvoice(updated);
      refresh();
    } catch {}
  }, [previewInvoice, newComment, refresh]);

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 40, mt: 0, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '20px',
              lineHeight: '24px',
              fontWeight: 400,
              color: '#020242',
              m: 0
            }}
          >
            Księgowość
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              ref={sectionChipRef}
              label={SECTION_CHIPS[sectionChip]}
              size="small"
              variant="filled"
              onClick={(e) => setSectionMenuAnchorEl(e.currentTarget)}
              sx={{
                cursor: 'pointer',
                bgcolor: SECTION_COLORS[sectionChip],
                color: '#020242',
                borderRadius: '4px',
                minHeight: 28,
                height: 28,
                '& .MuiChip-label': {
                  px: 1,
                  py: 0,
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '16px',
                  color: '#020242'
                }
              }}
            />
            <IconButton
              size="small"
              onClick={(e) => setSectionMenuAnchorEl(e.currentTarget)}
              sx={{
                p: 0.25,
                width: 24,
                height: 28,
                borderRadius: '4px',
                color: '#020242'
              }}
              aria-label="Wybierz sekcję"
            >
              <KeyboardArrowDown fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={sectionMenuAnchorEl}
              open={Boolean(sectionMenuAnchorEl)}
              onClose={() => setSectionMenuAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { mt: 1.5, minWidth: 180 } }}
            >
              {SECTION_DISPLAY_ORDER.map((idx) => (
                <MenuItem
                  key={SECTION_CHIPS[idx]}
                  selected={sectionChip === idx}
                  onClick={() => {
                    setSectionChip(idx);
                    setSectionMenuAnchorEl(null);
                  }}
                  sx={{
                    gap: 1,
                    '&.Mui-selected': { bgcolor: `${SECTION_COLORS[idx]}40` }
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: 1,
                      bgcolor: SECTION_COLORS[idx],
                      flexShrink: 0
                    }}
                  />
                  {SECTION_CHIPS[idx]}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {sectionChip === 7 && bsMainTab === 2 && (
            <Tooltip title={uzgAutoMatching ? `Szukanie ${uzgAutoMatchProgress.current}/${uzgAutoMatchProgress.total}…` : 'Sugeruj połączenia'} arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={uzgAutoMatching}
                  onClick={handleSuggestMatches}
                  sx={{
                    p: 0.25, width: 32, height: 32,
                    animation: uzgAutoMatching ? 'uzg-pulse 1.5s ease-in-out infinite' : 'none',
                    '@keyframes uzg-pulse': {
                      '0%': { boxShadow: '0 0 0 0 rgba(63,63,220,0.4)' },
                      '70%': { boxShadow: '0 0 0 8px rgba(63,63,220,0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(63,63,220,0)' },
                    },
                  }}
                >
                  {uzgAutoMatching ? (
                    <CircularProgress size={20} sx={{ color: '#3F3FDC' }} />
                  ) : (
                    <Box component="img" src="/assets/icons/sync_alt.svg" alt="Sugeruj połączenia" sx={{ width: 24, height: 24 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton
            size="small"
            sx={{ p: 0.25, width: 32, height: 32 }}
            aria-label="Ustawienia KSEF"
            onClick={() => setKsefSettingsOpen(true)}
          >
            <Box component="img" src="/assets/icons/settings_ksef.svg" alt="Ustawienia KSEF" sx={{ width: 24, height: 24 }} />
          </IconButton>
          <Button 
            variant="contained" 
            onClick={() => {
              if (sectionChip === 1) {
                setAddWarehouseOpen(true);
              } else if (sectionChip === 7) {
                bsFileInputRef.current?.click();
              } else {
                window.open('/accounting/new', '_blank');
              }
            }}
            data-action="add"
            sx={{ 
              minWidth: 116,
              height: 40,
              borderRadius: '4px',
              textTransform: 'none',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              wordBreak: 'keep-all',
              backgroundColor: '#4040DB',
              color: '#fff',
              '&:hover': { backgroundColor: '#1F1FB0' },
              '&:active': { backgroundColor: '#1A1A98' }
            }}
          >
            {sectionChip === 1 ? 'Dodaj magazyn' : sectionChip === 7 ? 'Dodaj wyciąg' : 'Dodaj fakturę'}
          </Button>
        </Box>
      </Box>

      {/* === SEKCJA ZAKUPY (sectionChip === 2) === */}
      {sectionChip === 2 && (<>
      {/* Pasek zakładek głównych – identyczny styl jak na stronie projektu */}
      <Box sx={{ mt: '28px', mb: 2, borderBottom: '1px solid #EDEDED', display: 'flex', gap: 3, alignItems: 'flex-end' }}>
        {mainTabs.map(({ label, badge }, idx) => (
          <Box
            key={label}
            onClick={() => setMainTab(idx)}
            sx={{
              position: 'relative',
              pb: 1,
              cursor: 'pointer',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '16px',
              color: '#020242',
              fontWeight: mainTab === idx ? 500 : 400,
              userSelect: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              pr: '12px'
            }}
          >
            <Box component="span" sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <Box 
                component="span"
                sx={{ 
                  color: label === 'KSEF' 
                    ? (ksefConnectionStatus?.connected ? '#16A34A' : '#FBBF24') 
                    : 'inherit' 
                }}
              >
                {label}
              </Box>
            </Box>
            {renderMainTabBadge(badge)}
            {mainTab === idx && (
              <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, bgcolor: '#000000' }} />
            )}
          </Box>
        ))}
      </Box>


      {/* Zawartość dla Faktury przelewowe – oryginalny widok */}
      {(mainTab === 1) ? (
      <>
      {/* Linie filtrów w stylu jak na stronie „Projekty” */}
      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: purchasesInvoicesFilterGrid,
          columnGap: '24px',
          alignItems: 'start',
          height: 56,
          mb: 2,
          bgcolor: '#FAFAFA'
        }}
      >
        {/* Szukaj faktury 300x36 jak „Szukaj w projektach” */}
        <TextField
          size="small"
          placeholder="Szukaj faktury"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          variant="outlined"
          inputProps={{ 
            'data-role': 'page-filter',
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                try { (e.currentTarget as HTMLInputElement).blur(); } catch {}
              }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ width: 20, height: 20, color: '#595966' }} />
              </InputAdornment>
            )
          }}
          sx={{ 
            width: 300,
            mt: '20px',
            '& .MuiOutlinedInput-root': {
              height: 36,
              backgroundColor: '#fff',
              borderRadius: '4px'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d4d4d9'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966'
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966 !important'
            },
            '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
            '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
          }}
        />

        {!isPurchasesBasicUser && (
        <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Wnioskuje</Typography>
          <Select
            size="small"
            value={requesterFilter}
            onChange={(e) => setRequesterFilter(String(e.target.value))}
            displayEmpty
            renderValue={requesterFilter !== '' ? undefined : () => 'Wybierz z listy'}
            IconComponent={(iconProps:any) => (
              <Box
                component="img"
                alt="dropdown"
                src="/assets/filters/keyboard_arrow_down.png"
                {...iconProps}
                sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
              />
            )}
            sx={{ 
              height: 36,
              pr: '8px',
              '& .MuiSelect-select': {
                display: 'flex', alignItems: 'center', height: 36,
                fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: requesterFilter ? '#2D2D32' : '#B9B9B9'
              },
              backgroundColor: '#fff',
              borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' },
              '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszyscy</MenuItem>
            {Object.values(usersMap).map((u) => (
              <MenuItem key={u.user_id} value={String(u.display_name || '')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar src={u.avatar_url || undefined} sx={{ width: 22, height: 22, fontSize: 12 }}>
                    {String(u.display_name || '?').substring(0,1)}
                  </Avatar>
                  <span>{u.display_name}</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>
        )}

        {/* Projekt – 200x36, z Autocomplete ale wystylizowany jak Select */}
        <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Projekt</Typography>
          <Autocomplete
            size="small"
            options={activeProjects}
            getOptionLabel={(p) => `${p.id} ${p.name}`}
            value={activeProjects.find((p) => p.id === projectFilter) || null}
            onChange={(_, val) => setProjectFilter(val ? val.id : '')}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Wybierz z listy"
                sx={{
                  '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px', pr: '8px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                  '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: projectFilter ? '#2D2D32' : '#B9B9B9' }
                }}
              />
            )}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            clearOnBlur={false}
          />
        </Box>

        {/* Miesiąc – w 1. linii */}
        <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Miesiąc</Typography>
          <Select
            size="small"
            value={monthFilter}
            onChange={(e) => setMonthFilter(String(e.target.value))}
            displayEmpty
            renderValue={monthFilter !== '' ? () => (months.find((m) => m.key === monthFilter)?.label || '') : () => 'Wybierz z listy'}
            IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
            sx={{ 
              height: 36, pr: '8px',
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: monthFilter ? '#2D2D32' : '#B9B9B9' },
              backgroundColor: '#fff', borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {months.map((m) => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
          </Select>
        </Box>

        {/* Status – w 1. linii, elastyczny aby licował z prawą krawędzią tabeli */}
        <Box sx={{ minWidth: 160, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Status</Typography>
          <Select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(String(e.target.value))}
            displayEmpty
            renderValue={statusFilter !== '' ? undefined : () => 'Wybierz z listy'}
            IconComponent={(iconProps:any) => (
              <Box
                component="img"
                alt="dropdown"
                src="/assets/filters/keyboard_arrow_down.png"
                {...iconProps}
                sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
              />
            )}
            sx={{ 
              width: '100%',
              height: 36,
              pr: '8px',
              '& .MuiSelect-select': {
                display: 'flex', alignItems: 'center', height: 36,
                fontFamily: 'Roboto, sans-serif', fontSize: '14px',
                color: statusFilter ? '#2D2D32' : '#B9B9B9'
              },
              backgroundColor: '#fff',
              borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' },
              '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {ALL_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </Box>

        
      </Box>

      {/* Druga linia filtrów – usunięta */}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => { setTab(v); try { getSocket().emit('ui:tab:set', { page: 'invoices', tab: v }); } catch {} }}
          aria-label="invoices tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            height: 40,
            flexGrow: 1,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: '2px' }
          }}
        >
          <Tab
            disableRipple
            label={`Faktury do akceptacji (${acceptanceRows.length})`}
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': {
                mt: 0,
                height: 40,
                bgcolor: '#E6E6EC',
                color: '#020242',
                fontWeight: 500,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0
              },
              '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
            }}
          />
          <Tab
            disableRipple
            label={`Do wprowadzenia (${toEnterRows.length})`}
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': {
                mt: 0,
                height: 40,
                bgcolor: '#E6E6EC',
                color: '#020242',
                fontWeight: 500,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0
              },
              '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
            }}
          />
          <Tab
            disableRipple
            label={`Wprowadzone do Subiekta (${subiektRows.length})`}
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': {
                mt: 0,
                height: 40,
                bgcolor: '#E6E6EC',
                color: '#020242',
                fontWeight: 500,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0
              },
              '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
            }}
          />
          <Tab
            disableRipple
            label={`Nierozliczone (${unpaidRows.length})`}
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': {
                mt: 0,
                height: 40,
                bgcolor: '#E6E6EC',
                color: '#020242',
                fontWeight: 500,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0
              },
              '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
            }}
          />
          <Tab
            disableRipple
            label={`Rozliczone (${paidRows.length})`}
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': {
                mt: 0,
                height: 40,
                bgcolor: '#E6E6EC',
                color: '#020242',
                fontWeight: 500,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0
              },
              '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
            }}
          />
        </Tabs>
        <IconButton
          size="small"
          title="Synchronizuj z Subiektem"
          aria-label="Synchronizuj z Subiektem"
          sx={{
            borderRadius: '4px',
            p: 0,
            alignSelf: 'center',
            bgcolor: syncing ? '#E0E0F5' : 'transparent',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
          }}
          disabled={syncing}
          onClick={async ()=> {
            try {
              setSyncing(true);
              await syncInvoicesBookedFromMssql(true); // pełna synchronizacja
              refresh();
            } catch {
              // pomiń
            } finally {
              setSyncing(false);
            }
          }}
        >
          <Box
            component="img"
            alt=""
            src="/assets/icons/update_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg"
            sx={{ display: 'block', transform: syncing ? 'rotate(360deg)' : 'none', transition: 'transform 0.8s linear' }}
          />
        </IconButton>
        <IconButton
          size="small"
          sx={{
            width: 32,
            height: 32,
            borderRadius: '4px',
            alignSelf: 'center',
            p: 0,
            bgcolor: colsOpen ? '#E0E0F5' : 'transparent',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
          }}
          onClick={(e)=>{ setColResizeMode(true); setColsAnchorEl(e.currentTarget); }}
        >
          <Box component="img" alt="kolumny" src={'/assets/icons/frame-344.svg'} sx={{ width: 32, height: 32 }} />
        </IconButton>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          height: tableHeight ?? 432,
          overflow: 'auto',
          position: 'relative',
          border: '1px solid #d0d0d6',
          borderBottom: '1px solid #d0d0d6',
          borderRadius: 0,
          borderTopRightRadius: 4,
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          overflowX: 'hidden',
          boxShadow: 'none',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { width: 0, height: 0 }
        }}
        ref={tableContainerRef}
      >
        <Table size="small" stickyHeader sx={{
          tableLayout: 'fixed',
          width: '100%',
          '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
          '& .MuiTableCell-body': { borderRight: 'none', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 0 rgba(224,224,224,1)', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
        }}>
          {mainTab === 1 ? TableHeaderTransfer : TableHeaderAcceptance}
          <TableBody>
            {visibleTransferRows.map((inv) => (
              <TableRowCommon key={inv.id} rowKey={`transfer-${inv.id}`} inv={inv} withActions={true} />
            ))}
            {renderSummaryRow(totalsVisibleTransfer)}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Okno „Dostosuj kolumny” */}
      <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme: any) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{ position: 'fixed', bottom: 0, right: 0, height: '50vh', width: '280px', display: 'flex', flexDirection: 'column', boxShadow: '0px 5px 30px rgba(0,0,0,0.15)', borderRadius: 0, zIndex: (theme) => theme.zIndex.modal + 2, backgroundColor: '#fff', pointerEvents: 'auto' }}
          onClick={(e)=> e.stopPropagation()}
        >
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={()=>{ setColsAnchorEl(null); setDraftColumnsVisible(null); setDraftOrder(null); setColResizeMode(false); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisible ?? columnsVisible;
              if (draftColumnsVisible == null) setDraftColumnsVisible({ ...columnsVisible });
              const ord = draftOrder ?? columnOrder;
              if (draftOrder == null) setDraftOrder([...columnOrder]);
              return ord.map((key) => (
                <Box
                  key={key}
                  sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = e.dataTransfer.getData('text/plain');
                    if (!src || src === key) return;
                    setDraftOrder((prev) => {
                      const cur = [...(prev || ord)];
                      const from = cur.indexOf(src);
                      const to = cur.indexOf(key);
                      if (from < 0 || to < 0) return cur;
                      cur.splice(to, 0, cur.splice(from, 1)[0]);
                      return cur;
                    });
                  }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setDraftColumnsVisible(prev=>({ ...(prev||list), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                        {key === 'dates' ? 'DATY' :
                         key === 'sel' ? 'W' :
                         key === 'invoiceNumber' ? 'NR FAKTURY' :
                         key === 'project' ? 'PROJEKT' :
                         key === 'contractor' ? 'KONTRAHENT' :
                         key === 'net' ? 'WARTOŚĆ NETTO' :
                         key === 'gross' ? 'WARTOŚĆ BRUTTO' :
                         key === 'due' ? 'TERMIN PŁATNOŚCI' :
                         key === 'paid' ? 'OPŁACONO' :
                         key === 'approvals' ? 'ZATWIERDZENIA' :
                         key === 'actions' ? 'AKCJE' : key.toUpperCase()}
                      </Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ setColsAnchorEl(null); setDraftColumnsVisible(null); setDraftOrder(null); setColResizeMode(false); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{ const vis = draftColumnsVisible ?? columnsVisible; setColumnsVisible(vis); try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(vis)); } catch {} if (draftOrder) { setColumnOrder(draftOrder); try { localStorage.setItem(ORDER_KEY, JSON.stringify(draftOrder)); } catch {} } setColsAnchorEl(null); setDraftColumnsVisible(null); setDraftOrder(null); setColResizeMode(false); }}>Zapisz zmiany</Button>
          </Box>
        </Paper>
      </Backdrop>
      
      </>
      ) : (
        <>
          {/* Zakładka: Faktury karta */}
          {mainTab === 2 ? (
            <>
              {/* Te same filtry */}
              <Box 
                sx={{ 
                  display: 'grid',
                  gridTemplateColumns: purchasesInvoicesFilterGrid,
                  columnGap: '24px',
                  alignItems: 'start',
                  height: 56,
                  mb: 2,
                  bgcolor: '#FAFAFA'
                }}
              >
                {/* Szukaj */}
                <TextField
                  size="small"
                  placeholder="Szukaj faktury"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  variant="outlined"
                  inputProps={{ 'data-role': 'page-filter' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ width: 20, height: 20, color: '#595966' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{ 
                    width: 300,
                    mt: '20px',
                    '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' }
                  }}
                />
                {!isPurchasesBasicUser && (
                <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Wnioskuje</Typography>
                  <Select
                    size="small"
                    value={requesterFilter}
                    onChange={(e) => setRequesterFilter(String(e.target.value))}
                    displayEmpty
                    renderValue={requesterFilter !== '' ? undefined : () => 'Wybierz z listy'}
                    IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
                    sx={{ height: 36, pr: '8px', backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' } }}
                  >
                    <MenuItem value="">Wszyscy</MenuItem>
                    {Object.values(usersMap).map((u) => (
                      <MenuItem key={u.user_id} value={String(u.display_name || '')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={u.avatar_url || undefined} sx={{ width: 22, height: 22, fontSize: 12 }}>
                            {String(u.display_name || '?').substring(0,1)}
                          </Avatar>
                          <span>{u.display_name}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                )}
                {/* Projekt */}
                <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Projekt</Typography>
                  <Autocomplete
                    size="small"
                    options={activeProjects}
                    getOptionLabel={(p) => `${p.id} ${p.name}`}
                    value={activeProjects.find((p) => p.id === projectFilter) || null}
                    onChange={(_, val) => setProjectFilter(val ? val.id : '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Wybierz z listy"
                        sx={{ '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px', pr: '8px' } }}
                      />
                    )}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    clearOnBlur={false}
                  />
                </Box>
                {/* Miesiąc */}
                <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Miesiąc</Typography>
                  <Select
                    size="small"
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(String(e.target.value))}
                    displayEmpty
                    renderValue={monthFilter !== '' ? () => (months.find((m) => m.key === monthFilter)?.label || '') : () => 'Wybierz z listy'}
                    IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
                    sx={{ height: 36, pr: '8px', backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' } }}
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    {months.map((m) => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
                  </Select>
                </Box>
                {/* Status */}
                <Box sx={{ minWidth: 160, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Status</Typography>
                  <Select
                    size="small"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(String(e.target.value))}
                    displayEmpty
                    renderValue={statusFilter !== '' ? undefined : () => 'Wybierz z listy'}
                    IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
                    sx={{ height: 36, pr: '8px', backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' } }}
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    {ALL_STATUSES.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
                  </Select>
                </Box>
              </Box>

              {/* Zakładki tabeli (2 pozycje) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
                <Tabs
                  value={cardTab}
                  onChange={(_, v) => setCardTab(v)}
                  aria-label="card invoices tabs"
                  sx={{ minHeight: 40, height: 40, flexGrow: 1, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTabs-flexContainer': { gap: '2px' } }}
                >
                  <Tab
                    disableRipple
                    label={`Do wprowadzenia (${sortedCardToEnter.length})`}
                    sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
                  />
                  <Tab
                    disableRipple
                    label={`Wprowadzone do Subiekta (${sortedCardBooked.length})`}
                    sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
                  />
                </Tabs>
                {/* Akcje po prawej – te same */}
                <IconButton
                  size="small"
                  title="Synchronizuj z Subiektem"
                  aria-label="Synchronizuj z Subiektem"
                  sx={{ borderRadius: '4px', p: 0, alignSelf: 'center', bgcolor: syncing ? '#E0E0F5' : 'transparent', transition: 'background-color 0.15s ease', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}
                  disabled={syncing}
                  onClick={async ()=> {
                    try {
                      setSyncing(true);
                      await syncInvoicesBookedFromMssql(true);
                      refresh();
                    } finally {
                      setSyncing(false);
                    }
                  }}
                >
                  <Box component="img" alt="" src="/assets/icons/update_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg" sx={{ display: 'block', transform: syncing ? 'rotate(360deg)' : 'none', transition: 'transform 0.8s linear' }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ width: 32, height: 32, borderRadius: '4px', alignSelf: 'center', p: 0, bgcolor: colsOpen ? '#E0E0F5' : 'transparent', transition: 'background-color 0.15s ease', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}
                  onClick={(e)=>{ setColResizeMode(true); setColsAnchorEl(e.currentTarget); }}
                >
                  <Box component="img" alt="kolumny" src={'/assets/icons/frame-344.svg'} sx={{ width: 32, height: 32 }} />
                </IconButton>
              </Box>

              {/* Tabela */}
              <TableContainer
                component={Paper}
                sx={{ height: tableHeight ?? 432, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderBottom: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}
                ref={tableContainerRef}
              >
                <Table size="small" stickyHeader sx={{
                  tableLayout: 'fixed',
                  width: '100%',
                  '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
                  '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
                  '& .MuiTableCell-body': { borderRight: 'none', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 0 rgba(224,224,224,1)', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
                  '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
                }}>
                  {TableHeaderTransfer}
                  <TableBody>
                    {visibleCardRows.map((inv) => (
                      <TableRowCommon key={inv.id} rowKey={`card-${inv.id}`} inv={inv} withActions={true} />
                    ))}
                    {renderSummaryRow(totalsVisibleCard)}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}
          {/* Zakładka: Faktury gotówkowe */}
          {mainTab === 3 ? (
            <>
              {/* Te same filtry */}
              <Box 
                sx={{ 
                  display: 'grid',
                  gridTemplateColumns: purchasesInvoicesFilterGrid,
                  columnGap: '24px',
                  alignItems: 'start',
                  height: 56,
                  mb: 2,
                  bgcolor: '#FAFAFA'
                }}
              >
                {/* Szukaj */}
                <TextField
                  size="small"
                  placeholder="Szukaj faktury"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  variant="outlined"
                  inputProps={{ 'data-role': 'page-filter' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ width: 20, height: 20, color: '#595966' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{ 
                    width: 300,
                    mt: '20px',
                    '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' }
                  }}
                />
                {!isPurchasesBasicUser && (
                <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Wnioskuje</Typography>
                  <Select
                    size="small"
                    value={requesterFilter}
                    onChange={(e) => setRequesterFilter(String(e.target.value))}
                    displayEmpty
                    renderValue={requesterFilter !== '' ? undefined : () => 'Wybierz z listy'}
                    IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
                    sx={{ height: 36, pr: '8px', backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' } }}
                  >
                    <MenuItem value="">Wszyscy</MenuItem>
                    {Object.values(usersMap).map((u) => (
                      <MenuItem key={u.user_id} value={String(u.display_name || '')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={u.avatar_url || undefined} sx={{ width: 22, height: 22, fontSize: 12 }}>
                            {String(u.display_name || '?').substring(0,1)}
                          </Avatar>
                          <span>{u.display_name}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                )}
                {/* Projekt */}
                <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Projekt</Typography>
                  <Autocomplete
                    size="small"
                    options={activeProjects}
                    getOptionLabel={(p) => `${p.id} ${p.name}`}
                    value={activeProjects.find((p) => p.id === projectFilter) || null}
                    onChange={(_, val) => setProjectFilter(val ? val.id : '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Wybierz z listy"
                        sx={{ '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px', pr: '8px' } }}
                      />
                    )}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    clearOnBlur={false}
                  />
                </Box>
                {/* Miesiąc */}
                <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Miesiąc</Typography>
                  <Select
                    size="small"
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(String(e.target.value))}
                    displayEmpty
                    renderValue={monthFilter !== '' ? () => (months.find((m) => m.key === monthFilter)?.label || '') : () => 'Wybierz z listy'}
                    IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
                    sx={{ height: 36, pr: '8px', backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' } }}
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    {months.map((m) => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
                  </Select>
                </Box>
                {/* Status */}
                <Box sx={{ minWidth: 160, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Status</Typography>
                  <Select
                    size="small"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(String(e.target.value))}
                    displayEmpty
                    renderValue={statusFilter !== '' ? undefined : () => 'Wybierz z listy'}
                    IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
                    sx={{ height: 36, pr: '8px', backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' } }}
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    {ALL_STATUSES.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
                  </Select>
                </Box>
              </Box>

              {/* Zakładki tabeli (2 pozycje) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
                <Tabs
                  value={cashTab}
                  onChange={(_, v) => setCashTab(v)}
                  aria-label="cash invoices tabs"
                  sx={{ minHeight: 40, height: 40, flexGrow: 1, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTabs-flexContainer': { gap: '2px' } }}
                >
                  <Tab
                    disableRipple
                    label={`Do wprowadzenia (${sortedCashToEnter.length})`}
                    sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
                  />
                  <Tab
                    disableRipple
                    label={`Wprowadzone do Subiekta (${sortedCashBooked.length})`}
                    sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
                  />
                </Tabs>
                {/* Akcje po prawej – te same */}
                <IconButton
                  size="small"
                  title="Synchronizuj z Subiektem"
                  aria-label="Synchronizuj z Subiektem"
                  sx={{ borderRadius: '4px', p: 0, alignSelf: 'center', bgcolor: syncing ? '#E0E0F5' : 'transparent', transition: 'background-color 0.15s ease', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}
                  disabled={syncing}
                  onClick={async ()=> {
                    try {
                      setSyncing(true);
                      await syncInvoicesBookedFromMssql(true);
                      refresh();
                    } finally {
                      setSyncing(false);
                    }
                  }}
                >
                  <Box component="img" alt="" src="/assets/icons/update_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg" sx={{ display: 'block', transform: syncing ? 'rotate(360deg)' : 'none', transition: 'transform 0.8s linear' }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ width: 32, height: 32, borderRadius: '4px', alignSelf: 'center', p: 0, bgcolor: colsOpen ? '#E0E0F5' : 'transparent', transition: 'background-color 0.15s ease', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}
                  onClick={(e)=>{ setColResizeMode(true); setColsAnchorEl(e.currentTarget); }}
                >
                  <Box component="img" alt="kolumny" src={'/assets/icons/frame-344.svg'} sx={{ width: 32, height: 32 }} />
                </IconButton>
              </Box>

              {/* Tabela – te same nagłówki i wiersze */}
              <TableContainer
                component={Paper}
                sx={{ height: tableHeight ?? 432, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderBottom: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}
                ref={tableContainerRef}
              >
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%', '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' }, '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' }, '& .MuiTableCell-body': { borderRight: 'none', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 0 rgba(224,224,224,1)', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' }, '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 } }}>
                  {TableHeaderAcceptance}
                  <TableBody>
                    {visibleCashRows.map((inv) => (
                      <TableRowCommon key={`cash-${inv.id}`} rowKey={`cash-${inv.id}`} inv={inv} withActions={true} />
                    ))}
                    {renderSummaryRow(totalsVisibleCash)}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : mainTab === 4 ? (
            // Zaliczki
            <>
              <Box 
                sx={{ 
                  display: 'grid',
                  gridTemplateColumns: purchasesInvoicesFilterGrid,
                  columnGap: '24px',
                  alignItems: 'start',
                  height: 56,
                  mb: 2,
                  bgcolor: '#FAFAFA'
                }}
              >
                <TextField
                  size="small"
                  placeholder="Szukaj faktury"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ width: 20, height: 20, color: '#595966' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{ width: 300, mt: '20px', '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' } }}
                />
                {!isPurchasesBasicUser && (
                <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Wnioskuje</Typography>
                  <Select
                    size="small"
                    value={requesterFilter}
                    onChange={(e) => setRequesterFilter(String(e.target.value))}
                    displayEmpty
                    renderValue={requesterFilter !== '' ? undefined : () => 'Wybierz z listy'}
                    IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
                    sx={{ height: 36, pr: '8px', backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' } }}
                  >
                    <MenuItem value="">Wszyscy</MenuItem>
                    {Object.values(usersMap).map((u) => (
                      <MenuItem key={u.user_id} value={String(u.display_name || '')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={u.avatar_url || undefined} sx={{ width: 22, height: 22, fontSize: 12 }}>
                            {String(u.display_name || '?').substring(0,1)}
                          </Avatar>
                          <span>{u.display_name}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                )}
                <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Projekt</Typography>
                  <Autocomplete
                    size="small"
                    options={activeProjects}
                    getOptionLabel={(p) => `${p.id} ${p.name}`}
                    value={activeProjects.find((p) => p.id === projectFilter) || null}
                    onChange={(_, val) => setProjectFilter(val ? val.id : '')}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Wybierz z listy" sx={{ '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px', pr: '8px' } }} />
                    )}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                  />
                </Box>
                <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Miesiąc</Typography>
                  <Select
                    size="small"
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(String(e.target.value))}
                    displayEmpty
                    renderValue={monthFilter !== '' ? undefined : () => 'Wybierz z listy'}
                    IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
                    sx={{ height: 36, pr: '8px', backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' } }}
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    {months.map((m) => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
                  </Select>
                </Box>
                <Box sx={{ minWidth: 160, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Status</Typography>
                  <Select
                    size="small"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(String(e.target.value))}
                    displayEmpty
                    renderValue={statusFilter !== '' ? undefined : () => 'Wybierz z listy'}
                    IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
                    sx={{ height: 36, pr: '8px', backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' } }}
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    {ALL_STATUSES.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
                  </Select>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
                <Tabs
                  value={advanceTab}
                  onChange={(_, v) => setAdvanceTab(v)}
                  aria-label="advance invoices tabs"
                  sx={{ minHeight: 40, height: 40, flexGrow: 1, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTabs-flexContainer': { gap: '2px' } }}
                >
                  <Tab
                    disableRipple
                    label={`Do wprowadzenia (${sortedAdvanceToEnter.length})`}
                    sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
                  />
                  <Tab
                    disableRipple
                    label={`Wprowadzone do Subiekta (${sortedAdvanceBooked.length})`}
                    sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
                  />
                </Tabs>
                <IconButton
                  size="small"
                  title="Synchronizuj z Subiektem"
                  sx={{ borderRadius: '4px', p: 0, alignSelf: 'center', bgcolor: syncing ? '#E0E0F5' : 'transparent', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}
                  disabled={syncing}
                  onClick={async () => { try { setSyncing(true); await syncInvoicesBookedFromMssql(true); refresh(); } finally { setSyncing(false); } }}
                >
                  <Box component="img" alt="" src="/assets/icons/update_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg" sx={{ display: 'block', transform: syncing ? 'rotate(360deg)' : 'none', transition: 'transform 0.8s linear' }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ width: 32, height: 32, borderRadius: '4px', alignSelf: 'center', p: 0, bgcolor: colsOpen ? '#E0E0F5' : 'transparent', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}
                  onClick={(e) => { setColResizeMode(true); setColsAnchorEl(e.currentTarget); }}
                >
                  <Box component="img" alt="kolumny" src="/assets/icons/frame-344.svg" sx={{ width: 32, height: 32 }} />
                </IconButton>
              </Box>
              <TableContainer
                component={Paper}
                sx={{ height: tableHeight ?? 432, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}
                ref={tableContainerRef}
              >
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%', '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' }, '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' }, '& .MuiTableCell-body': { borderRight: 'none', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 0 rgba(224,224,224,1)', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' }, '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 } }}>
                  {TableHeaderAcceptance}
                  <TableBody>
                    {visibleAdvanceRows.map((inv) => (
                      <TableRowCommon key={`advance-${inv.id}`} rowKey={`advance-${inv.id}`} inv={inv} withActions={true} />
                    ))}
                    {renderSummaryRow(totalsVisibleAdvance)}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : mainTab === 5 ? (
            // Korekty – filtry, zakładki, tabela (jak Faktury przelewowe)
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: purchasesInvoicesFilterGrid,
                  columnGap: '24px',
                  alignItems: 'start',
                  height: 56,
                  mb: 2,
                  bgcolor: '#FAFAFA'
                }}
              >
                <TextField
                  size="small"
                  placeholder="Szukaj faktury"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  variant="outlined"
                  inputProps={{ 'data-role': 'page-filter', onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); try { (e.currentTarget as HTMLInputElement).blur(); } catch {} } } }}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><Search sx={{ width: 20, height: 20, color: '#595966' }} /></InputAdornment>) }}
                  sx={{ width: 300, mt: '20px', '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' }, '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }, '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }, '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 } }}
                />
                {!isPurchasesBasicUser && (
                <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Wnioskuje</Typography>
                  <Select size="small" value={requesterFilter} onChange={(e) => setRequesterFilter(String(e.target.value))} displayEmpty renderValue={requesterFilter !== '' ? undefined : () => 'Wybierz z listy'} IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)} sx={{ height: 36, pr: '8px', '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: requesterFilter ? '#2D2D32' : '#B9B9B9' }, backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' }, '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' }, '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' } }}>
                    <MenuItem value="">Wszyscy</MenuItem>
                    {Object.values(usersMap).map((u) => (<MenuItem key={u.user_id} value={String(u.display_name || '')}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Avatar src={u.avatar_url || undefined} sx={{ width: 22, height: 22, fontSize: 12 }}>{String(u.display_name || '?').substring(0,1)}</Avatar><span>{u.display_name}</span></Box></MenuItem>))}
                  </Select>
                </Box>
                )}
                <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Projekt</Typography>
                  <Autocomplete size="small" options={activeProjects} getOptionLabel={(p) => `${p.id} ${p.name}`} value={activeProjects.find((p) => p.id === projectFilter) || null} onChange={(_, val) => setProjectFilter(val ? val.id : '')} renderInput={(params) => (<TextField {...params} placeholder="Wybierz z listy" sx={{ '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px', pr: '8px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' }, '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }, '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: projectFilter ? '#2D2D32' : '#B9B9B9' } }} />)} isOptionEqualToValue={(a, b) => a.id === b.id} clearOnBlur={false} />
                </Box>
                <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Miesiąc</Typography>
                  <Select size="small" value={monthFilter} onChange={(e) => setMonthFilter(String(e.target.value))} displayEmpty renderValue={monthFilter !== '' ? () => (months.find((m) => m.key === monthFilter)?.label || '') : () => 'Wybierz z listy'} IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)} sx={{ height: 36, pr: '8px', '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: monthFilter ? '#2D2D32' : '#B9B9B9' }, backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' }, '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' }, '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' }, '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' } }}>
                    <MenuItem value="">Wszystkie</MenuItem>
                    {months.map((m) => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
                  </Select>
                </Box>
                <Box sx={{ minWidth: 160, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Status</Typography>
                  <Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(String(e.target.value))} displayEmpty renderValue={statusFilter !== '' ? undefined : () => 'Wybierz z listy'} IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)} sx={{ width: '100%', height: 36, pr: '8px', '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: statusFilter ? '#2D2D32' : '#B9B9B9' }, backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' }, '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' }, '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' }, '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' } }}>
                    <MenuItem value="">Wszystkie</MenuItem>
                    {ALL_STATUSES.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
                  </Select>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
                <Tabs value={correctionTab} onChange={(_, v) => setCorrectionTab(v)} aria-label="korekty tabs" variant="scrollable" scrollButtons="auto" sx={{ minHeight: 40, height: 40, flexGrow: 1, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTabs-flexContainer': { gap: '2px' } }}>
                  <Tab disableRipple label={`Faktury do akceptacji (${correctionAcceptanceRows.length})`} sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }} />
                  <Tab disableRipple label={`Do wprowadzenia (${correctionToEnterRows.length})`} sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }} />
                  <Tab disableRipple label={`Wprowadzone do Subiekta (${correctionSubiektRows.length})`} sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }} />
                  <Tab disableRipple label={`Nierozliczone (${correctionUnpaidRows.length})`} sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }} />
                  <Tab disableRipple label={`Rozliczone (${correctionPaidRows.length})`} sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }} />
                </Tabs>
                <IconButton size="small" title="Synchronizuj z Subiektem" sx={{ borderRadius: '4px', p: 0, alignSelf: 'center', bgcolor: syncing ? '#E0E0F5' : 'transparent', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }} disabled={syncing} onClick={async () => { try { setSyncing(true); await syncInvoicesBookedFromMssql(true); refresh(); } finally { setSyncing(false); } }}>
                  <Box component="img" alt="" src="/assets/icons/update_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg" sx={{ display: 'block', transform: syncing ? 'rotate(360deg)' : 'none', transition: 'transform 0.8s linear' }} />
                </IconButton>
                <IconButton size="small" sx={{ width: 32, height: 32, borderRadius: '4px', alignSelf: 'center', p: 0, bgcolor: colsOpen ? '#E0E0F5' : 'transparent', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }} onClick={(e) => { setColResizeMode(true); setColsAnchorEl(e.currentTarget); }}>
                  <Box component="img" alt="kolumny" src="/assets/icons/frame-344.svg" sx={{ width: 32, height: 32 }} />
                </IconButton>
              </Box>
              <TableContainer component={Paper} sx={{ height: tableHeight ?? 432, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderBottom: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }} ref={tableContainerRef}>
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%', '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' }, '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' }, '& .MuiTableCell-body': { borderRight: 'none', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 0 rgba(224,224,224,1)', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' }, '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 } }}>
                  {TableHeaderTransfer}
                  <TableBody>
                    {visibleCorrectionRows.map((inv) => (
                      <TableRowCommon key={`correction-${inv.id}`} rowKey={`correction-${inv.id}`} inv={inv} withActions={true} />
                    ))}
                    {renderSummaryRow(totalsVisibleCorrection)}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : mainTab === 6 ? (
            // Wszystkie – filtry + tabela (bez podzakładek)
            <>
      {/* Filtry: Szukaj, Projekt, Miesiąc, Data od, Data do, Sposób płatności, Magazyn */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '250px 180px 170px 140px 140px 180px 160px',
          columnGap: '16px',
          alignItems: 'start',
          height: 56,
          mb: 2,
          bgcolor: '#FAFAFA'
        }}
      >
        {/* Szukaj faktury */}
        <TextField
          size="small"
          placeholder="Szukaj faktury"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          variant="outlined"
          inputProps={{
            'data-role': 'page-filter',
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                try { (e.currentTarget as HTMLInputElement).blur(); } catch {}
              }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ width: 20, height: 20, color: '#595966' }} />
              </InputAdornment>
            )
          }}
          sx={{
            width: '100%',
            mt: '20px',
            '& .MuiOutlinedInput-root': {
              height: 36,
              backgroundColor: '#fff',
              borderRadius: '4px'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d4d4d9'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966'
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966 !important'
            },
            '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
            '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
          }}
        />

        {/* Projekt */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Projekt</Typography>
          <Autocomplete
            size="small"
            options={activeProjects}
            getOptionLabel={(p) => `${p.id} ${p.name}`}
            value={activeProjects.find((p) => p.id === projectFilter) || null}
            onChange={(_, val) => setProjectFilter(val ? val.id : '')}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Wybierz"
                sx={{
                  '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px', pr: '8px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                  '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: projectFilter ? '#2D2D32' : '#B9B9B9' }
                }}
              />
            )}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            clearOnBlur={false}
          />
        </Box>

        {/* Miesiąc */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Miesiąc</Typography>
          <Select
            size="small"
            value={monthFilter}
            onChange={(e) => setMonthFilter(String(e.target.value))}
            displayEmpty
            renderValue={monthFilter !== '' ? () => (months.find((m) => m.key === monthFilter)?.label || '') : () => 'Wybierz z listy'}
            IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
            sx={{
              height: 36, pr: '8px',
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: monthFilter ? '#2D2D32' : '#B9B9B9' },
              backgroundColor: '#fff', borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {months.map((m) => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
          </Select>
        </Box>

        {/* Data od */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data od</Typography>
          <TextField
            size="small"
            type="date"
            value={allDateFrom}
            onChange={(e) => setAllDateFrom(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: allDateFrom ? '#2D2D32' : '#B9B9B9' }
            }}
          />
        </Box>

        {/* Data do */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data do</Typography>
          <TextField
            size="small"
            type="date"
            value={allDateTo}
            onChange={(e) => setAllDateTo(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: allDateTo ? '#2D2D32' : '#B9B9B9' }
            }}
          />
        </Box>

        {/* Sposób płatności */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Sposób płatności</Typography>
          <Select
            size="small"
            value={allPaymentMethod}
            onChange={(e) => setAllPaymentMethod(String(e.target.value))}
            displayEmpty
            renderValue={allPaymentMethod !== '' ? undefined : () => 'Wybierz z listy'}
            IconComponent={(iconProps:any) => (
              <Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />
            )}
            sx={{
              height: 36, pr: '8px',
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: allPaymentMethod ? '#2D2D32' : '#B9B9B9' },
              backgroundColor: '#fff', borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {ALL_PAYMENT_METHODS.map((pm) => (<MenuItem key={pm} value={pm}>{pm}</MenuItem>))}
          </Select>
        </Box>

        {/* Magazyn */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Magazyn</Typography>
          <Select
            size="small"
            value={allWarehouse}
            onChange={(e) => setAllWarehouse(String(e.target.value))}
            displayEmpty
            renderValue={allWarehouse !== '' ? undefined : () => 'Wybierz z listy'}
            IconComponent={(iconProps:any) => (
              <Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />
            )}
            sx={{
              height: 36, pr: '8px',
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: allWarehouse ? '#2D2D32' : '#B9B9B9' },
              backgroundColor: '#fff', borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {WAREHOUSES_OPTIONS.map((w) => (<MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>))}
          </Select>
        </Box>
      </Box>

      {/* Przycisk kolumn */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: 40, mt: '26.5px', mb: 0 }}>
        <IconButton
          size="small"
          sx={{
            width: 32,
            height: 32,
            borderRadius: '4px',
            alignSelf: 'center',
            p: 0,
            bgcolor: colsOpen ? '#E0E0F5' : 'transparent',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
          }}
          onClick={(e)=>{ setColResizeMode(true); setColsAnchorEl(e.currentTarget); }}
        >
          <Box component="img" alt="kolumny" src={'/assets/icons/frame-344.svg'} sx={{ width: 32, height: 32 }} />
        </IconButton>
      </Box>

      {/* Tabela */}
      <TableContainer
        component={Paper}
        sx={{
          height: tableHeight ?? 432,
          overflow: 'auto',
          position: 'relative',
          border: '1px solid #d0d0d6',
          borderBottom: '1px solid #d0d0d6',
          borderRadius: 0,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          overflowX: 'hidden',
          boxShadow: 'none',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { width: 0, height: 0 }
        }}
        ref={tableContainerRef}
      >
        <Table size="small" stickyHeader sx={{
          tableLayout: 'fixed',
          width: '100%',
          '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
          '& .MuiTableCell-body': { borderRight: 'none', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 0 rgba(224,224,224,1)', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
        }}>
          {/* Nagłówek tabeli – Wszystkie */}
          <TableHead>
            <TableRow>
              {(allDraftOrder ?? allColumnOrder).map((key) => {
                if (!allColV(key)) return null;
                if (key === 'dates') return renderResizableTh('dates', (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'created_at')} active={orderBy === 'created_at'} direction={order} onClick={() => handleRequestSort('created_at')}>Daty</TableSortLabel>
                ));
                if (key === 'sel') return renderResizableTh('sel', (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'created_by_name')} active={orderBy === 'created_by_name'} direction={order} onClick={() => handleRequestSort('created_by_name')}>W</TableSortLabel>
                ));
                if (key === 'project') return renderResizableTh('project', (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'project_id')} active={orderBy === 'project_id'} direction={order} onClick={() => handleRequestSort('project_id')}>Projekt</TableSortLabel>
                ));
                if (key === 'warehouse') return renderResizableTh('warehouse', (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'warehouse')} active={orderBy === 'warehouse'} direction={order} onClick={() => handleRequestSort('warehouse')}>Magazyn</TableSortLabel>
                ));
                if (key === 'contractor') return renderResizableTh('contractor', (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'contractor_name')} active={orderBy === 'contractor_name'} direction={order} onClick={() => handleRequestSort('contractor_name')}>Kontrahent</TableSortLabel>
                ));
                if (key === 'nip') return renderResizableTh('nip', (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'nip')} active={orderBy === 'nip'} direction={order} onClick={() => handleRequestSort('nip')}>NIP</TableSortLabel>
                ));
                if (key === 'invoiceNumber') return renderResizableTh('invoiceNumber', 'Nr faktury');
                if (key === 'ksefNumber') return renderResizableTh('ksefNumber', 'Nr KSeF');
                if (key === 'net') return renderResizableTh('net', (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'net_amount')} active={orderBy === 'net_amount'} direction={order} onClick={() => handleRequestSort('net_amount')}>Wartość netto</TableSortLabel>
                ), 'right');
                if (key === 'gross') return renderResizableTh('gross', (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'gross_amount')} active={orderBy === 'gross_amount'} direction={order} onClick={() => handleRequestSort('gross_amount')}>Wartość brutto</TableSortLabel>
                ), 'right');
                if (key === 'paymentMethod') return renderResizableTh('paymentMethod', (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'document_type')} active={orderBy === 'document_type'} direction={order} onClick={() => handleRequestSort('document_type')}>Płatność</TableSortLabel>
                ));
                if (key === 'paid') return renderResizableTh('paid', 'Opłacono');
                if (key === 'preview') return renderResizableTh('preview', null as any, 'center');
                if (key === 'actions') return renderResizableTh('actions', null as any, 'right');
                return null;
              })}
            </TableRow>
          </TableHead>
          {/* Wiersze tabeli – Wszystkie */}
          <TableBody>
            {visibleAllRows.map((inv) => {
              const delayDays = (() => {
                const toUTC = (iso?: string) => { if (!iso) return NaN as any; const d = new Date(String(iso)); return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()); };
                const d1 = toUTC(inv.document_date); const d2 = toUTC(inv.received_date);
                if (isNaN(d1) || isNaN(d2)) return 0;
                return Math.max(0, Math.floor((d2 - d1) / (1000 * 60 * 60 * 24)));
              })();
              const introduceDelayDays = (() => {
                const toUTC = (iso?: string) => { if (!iso) return NaN as any; const d = new Date(String(iso)); return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()); };
                const created = toUTC(inv.created_at); const documentDate = toUTC(inv.document_date);
                if (isNaN(created) || isNaN(documentDate)) return 0;
                return Math.max(0, Math.floor((created - documentDate) / (1000 * 60 * 60 * 24)));
              })();
              return (
              <TableRow
                key={`all-${inv.id}`}
                hover
                data-rowkey={`all-${inv.id}`}
                onClick={() => {
                  const rk = `all-${inv.id}`;
                  setSelectedRowKey((prev) => (prev === rk ? null : rk));
                }}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#F5F5FF !important' },
                  '&.MuiTableRow-hover:hover': { backgroundColor: '#F5F5FF !important' },
                  ...getRowSelectionSx(selectedRowKey === `all-${inv.id}`),
                }}
              >
                {(allDraftOrder ?? allColumnOrder).map((key) => {
                  if (!allColV(key)) return null;
                  if (key === 'dates') return (
                    <TableCell key={key} sx={{ width: getColW('dates'), whiteSpace: 'nowrap', pr: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          <Tooltip title={`Data wprowadzenia: ${String(inv.created_at || '')}`} arrow><span>{String(inv.created_at || '').slice(0,10)}</span></Tooltip>
                          {introduceDelayDays > 0 && <Box component="span" sx={{ ml: 0.5, bgcolor: 'primary.main', color: 'common.white', height: 12, lineHeight: '12px', px: 0.375, borderRadius: '6px', fontSize: 9, fontWeight: 700 }}>{`+${introduceDelayDays}`}</Box>}
                        </Box>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          <Tooltip title={`Data faktury: ${String(inv.document_date || '')}`} arrow><span>{inv.document_date || ''}</span></Tooltip>
                          {delayDays > 0 && <Box component="span" sx={{ ml: 0.5, bgcolor: 'error.main', color: 'common.white', height: 12, lineHeight: '12px', px: 0.375, borderRadius: '6px', fontSize: 9, fontWeight: 700 }}>{`+${delayDays}`}</Box>}
                        </Box>
                      </Box>
                    </TableCell>
                  );
                  if (key === 'sel') return (
                    <TableCell key={key} sx={{ p: 0.5, width: getColW('sel') }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {(inv.created_by_avatar_url || inv.created_by_name) && (
                          <Tooltip title={inv.created_by_name || ''} arrow>
                            <Avatar src={inv.created_by_avatar_url || undefined} sx={{ width: 25, height: 25, fontSize: 12 }}>
                              {(inv.created_by_name || '').substring(0,1)}
                            </Avatar>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  );
                  if (key === 'project') return (
                    <TableCell key={key} sx={{ width: getColW('project'), pl: 0 }}>
                      {(() => {
                        const full = `${inv.project_id} ${inv.project_name || ''}`.trim();
                        return (<Tooltip title={full} arrow><Box sx={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word', maxHeight: '2.5em' }}>{full}</Box></Tooltip>);
                      })()}
                    </TableCell>
                  );
                  if (key === 'warehouse') return (
                    <TableCell key={key} sx={{ width: getColW('warehouse'), whiteSpace: 'nowrap' }}>{inv.warehouse || ''}</TableCell>
                  );
                  if (key === 'contractor') return (
                    <TableCell key={key} sx={{ width: getColW('contractor') }}>
                      {(() => {
                        const full = String(inv.contractor_name || '');
                        return (<Tooltip title={full} arrow><Box sx={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word', maxHeight: '2.5em' }}>{full}</Box></Tooltip>);
                      })()}
                    </TableCell>
                  );
                  if (key === 'nip') return (
                    <TableCell key={key} sx={{ width: getColW('nip'), whiteSpace: 'nowrap' }}>{inv.nip || ''}</TableCell>
                  );
                  if (key === 'invoiceNumber') return (
                    <TableCell key={key} sx={{ width: getColW('invoiceNumber'), whiteSpace: 'nowrap' }}>{inv.invoice_number || ''}</TableCell>
                  );
                  if (key === 'ksefNumber') return (
                    <TableCell key={key} sx={{ width: getColW('ksefNumber'), whiteSpace: 'nowrap' }}>
                      <Tooltip title={inv.ksef_number || ''} arrow><Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{inv.ksef_number || ''}</Box></Tooltip>
                    </TableCell>
                  );
                  if (key === 'net') return <TableCell key={key} align="right" sx={{ width: getColW('net'), whiteSpace: 'nowrap' }}>{currency(Number(inv.net_amount || 0))}</TableCell>;
                  if (key === 'gross') return <TableCell key={key} align="right" sx={{ width: getColW('gross'), whiteSpace: 'nowrap' }}>{currency(Number(inv.gross_amount || 0))}</TableCell>;
                  if (key === 'paymentMethod') {
                    const k = inv.document_kind || (inv.document_type === 'Zaliczka (przelew)' ? 'advance' : 'vat');
                    const label = k === 'advance' ? 'Zaliczka' : k === 'correction' ? 'Korekta' : (inv.document_type || '');
                    return (
                      <TableCell key={key} sx={{ width: getColW('paymentMethod'), whiteSpace: 'nowrap' }}>{label}</TableCell>
                    );
                  }
                  if (key === 'paid') return (
                    <TableCell key={key} sx={{ width: getColW('paid'), whiteSpace: 'nowrap' }}>
                      {String(inv.document_type || '') === 'Gotówka' ? (inv.document_date ? String(inv.document_date).slice(0,10) : 'NIE') : (inv.paid_date ? String(inv.paid_date).slice(0,10) : 'NIE')}
                    </TableCell>
                  );
                  if (key === 'preview') return (
                    <TableCell key={key} align="center" sx={{ width: getColW('preview'), p: 0.5 }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setPreviewInvoice(inv); setPreviewOpen(true); markViewed(inv.id); }} sx={{ p: 0 }}>
                        <Visibility sx={{ fontSize: 18, color: '#595966' }} />
                      </IconButton>
                    </TableCell>
                  );
                  if (key === 'actions') return (
                    <TableCell key={key} align="right" sx={{ width: getColW('actions'), p: 0.5 }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenuPosition({ top: e.clientY, left: e.clientX }); setMenuRow(inv); }} sx={{ p: 0 }}>
                        <MoreVert sx={{ fontSize: 18, color: '#595966' }} />
                      </IconButton>
                    </TableCell>
                  );
                  return null;
                })}
              </TableRow>
              );
            })}
            {renderSummaryRow(totalsVisibleAll)}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Panel „Dostosuj kolumny" – Wszystkie */}
      <Backdrop open={colsOpen && mainTab === 6} sx={{ color: '#fff', zIndex: (theme: any) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{ position: 'fixed', bottom: 0, right: 0, height: '50vh', width: '280px', display: 'flex', flexDirection: 'column', boxShadow: '0px 5px 30px rgba(0,0,0,0.15)', borderRadius: 0, zIndex: (theme) => theme.zIndex.modal + 2, backgroundColor: '#fff', pointerEvents: 'auto' }}
          onClick={(e)=> e.stopPropagation()}
        >
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={()=>{ setColsAnchorEl(null); setAllDraftColumnsVisible(null); setAllDraftOrder(null); setColResizeMode(false); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = allDraftColumnsVisible ?? allColumnsVisible;
              if (allDraftColumnsVisible == null) setAllDraftColumnsVisible({ ...allColumnsVisible });
              const ord = allDraftOrder ?? allColumnOrder;
              if (allDraftOrder == null) setAllDraftOrder([...allColumnOrder]);
              const allColLabel: Record<string, string> = {
                dates: 'DATY', sel: 'W', project: 'PROJEKT', warehouse: 'MAGAZYN', contractor: 'KONTRAHENT', nip: 'NIP',
                invoiceNumber: 'NR FAKTURY', ksefNumber: 'NR KSEF', net: 'WARTOŚĆ NETTO', gross: 'WARTOŚĆ BRUTTO',
                paymentMethod: 'PŁATNOŚĆ', paid: 'OPŁACONO', preview: 'PODGLĄD', actions: 'AKCJE'
              };
              return ord.map((key) => (
                <Box
                  key={key}
                  sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = e.dataTransfer.getData('text/plain');
                    if (!src || src === key) return;
                    setAllDraftOrder((prev) => {
                      const cur = [...(prev || ord)];
                      const from = cur.indexOf(src); const to = cur.indexOf(key);
                      if (from < 0 || to < 0) return cur;
                      cur.splice(to, 0, cur.splice(from, 1)[0]);
                      return cur;
                    });
                  }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setAllDraftColumnsVisible(prev=>({ ...(prev||list), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(allDraftColumnsVisible ?? allColumnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>{allColLabel[key] || key.toUpperCase()}</Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ setColsAnchorEl(null); setAllDraftColumnsVisible(null); setAllDraftOrder(null); setColResizeMode(false); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{
              const vis = allDraftColumnsVisible ?? allColumnsVisible;
              setAllColumnsVisible(vis);
              try { localStorage.setItem(ALL_VISIBLE_KEY, JSON.stringify(vis)); } catch {}
              if (allDraftOrder) { setAllColumnOrder(allDraftOrder); try { localStorage.setItem(ALL_ORDER_KEY, JSON.stringify(allDraftOrder)); } catch {} }
              setColsAnchorEl(null); setAllDraftColumnsVisible(null); setAllDraftOrder(null); setColResizeMode(false);
            }}>Zapisz zmiany</Button>
          </Box>
        </Paper>
      </Backdrop>
            </>
          ) : mainTab === 0 ? (
            // KSEF – filtry + tabela Zakupy/Sprzedaż
            <Box>
              {/* Filtry (jak w innych zakładkach) */}
              <Box
                sx={{
                  display: 'grid',
                  // Filtry w jednej linii: Szukaj, Płatność, Projekt, Miesiąc,
                  // Data od, Data do, Zatwierdzenia (tylko dla „Wszystkie").
                  // Używamy minmax, żeby zmieściło się bez zawijania.
                  gridTemplateColumns: ksefStatusTab === 'all'
                    ? 'minmax(200px, 250px) minmax(140px, 180px) minmax(200px, 250px) minmax(140px, 180px) minmax(140px, 180px) minmax(140px, 180px) minmax(180px, 240px)'
                    : 'minmax(200px, 250px) minmax(140px, 180px) minmax(200px, 250px) minmax(140px, 180px) minmax(140px, 180px) minmax(140px, 180px)',
                  columnGap: '24px',
                  alignItems: 'start',
                  height: 56,
                  mb: 2,
                  bgcolor: '#FAFAFA'
                }}
              >
                <TextField
                  size="small"
                  placeholder="Szukaj faktury"
                  value={ksefQuery}
                  onChange={(e) => setKsefQuery(e.target.value)}
                  variant="outlined"
                  inputProps={{
                    'data-role': 'page-filter',
                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        e.stopPropagation();
                        try { (e.currentTarget as HTMLInputElement).blur(); } catch {}
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ width: 20, height: 20, color: '#595966' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    width: '100%',
                    mt: '20px',
                    '& .MuiOutlinedInput-root': {
                      height: 36,
                      backgroundColor: '#fff',
                      borderRadius: '4px'
                    },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                    '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
                    '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
                  }}
                />

                {/* Filtr „Płatność" – dropdown z unikalnymi metodami z KSeF
                    (np. Przelew, Gotówka, Karta, Zaliczka). */}
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Płatność</Typography>
                  <Select
                    size="small"
                    value={ksefPaymentMethod}
                    displayEmpty
                    onChange={(e) => setKsefPaymentMethod(String(e.target.value))}
                    renderValue={ksefPaymentMethod !== '' ? () => ksefPaymentMethod : () => 'Wszystkie'}
                    IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
                    sx={{
                      height: 36,
                      pr: '8px',
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        height: 36,
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                        color: ksefPaymentMethod ? '#2D2D32' : '#B9B9B9'
                      },
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      '& fieldset': { borderColor: '#d4d4d9' },
                      '&:hover fieldset': { borderColor: '#595966' },
                      '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
                      '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                      '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
                    }}
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    {ksefPaymentMethodOptions.map((pm) => (
                      <MenuItem key={pm} value={pm}>{pm}</MenuItem>
                    ))}
                  </Select>
                </Box>

                {/* Filtr projektu - ta sama lista co w AddTransferInvoiceDialog */}
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Projekt</Typography>
                  <Autocomplete
                    size="small"
                    options={ksefProjects}
                    loading={ksefLoadingProjects}
                    getOptionLabel={(p) => `${p.project_id} ${p.project_name}`}
                    value={ksefProjects.find((p) => p.project_id === ksefProjectFilter) || null}
                    onChange={(_, val) => setKsefProjectFilter(val ? val.project_id : '')}
                    isOptionEqualToValue={(a, b) => a.project_id === b.project_id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Projekt"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: 36,
                            backgroundColor: '#fff',
                            borderRadius: '4px',
                            pr: '8px'
                          },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                          '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                          '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: ksefProjectFilter ? '#2D2D32' : '#B9B9B9' },
                          '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
                        }}
                      />
                    )}
                    clearOnBlur={false}
                  />
                </Box>

                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Miesiąc</Typography>
                  <Select
                    size="small"
                    value={ksefMonth}
                    displayEmpty
                    onChange={(e) => setKsefMonth(String(e.target.value))}
                    renderValue={ksefMonth !== '' ? () => (monthOptions(18).find((m) => m.key === ksefMonth)?.label || '') : () => 'Wybierz z listy'}
                    IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
                    sx={{
                      height: 36,
                      pr: '8px',
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        height: 36,
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                        color: ksefMonth ? '#2D2D32' : '#B9B9B9'
                      },
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      '& fieldset': { borderColor: '#d4d4d9' },
                      '&:hover fieldset': { borderColor: '#595966' },
                      '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
                      '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                      '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
                    }}
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    {monthOptions(18).map((m) => (
                      <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </Box>

                {/* Data od (jak w ProjectsPage – z ikoną kalendarza) */}
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data od</Typography>
                  <TextField
                    size="small"
                    type="date"
                    value={ksefDateFrom}
                    onChange={(e) => setKsefDateFrom(String(e.target.value))}
                    variant="outlined"
                    inputRef={ksefDateFromRef}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{ cursor: 'pointer' }}
                          onClick={() => { try { (ksefDateFromRef.current as any)?.showPicker?.(); } catch { ksefDateFromRef.current?.focus(); } }}
                        >
                          <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                        </InputAdornment>
                      )
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                      '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                      '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: ksefDateFrom ? '#2D2D32' : '#B9B9B9' },
                      '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
                      '& input::-ms-clear': { display: 'none' }
                    }}
                  />
                </Box>

                {/* Data do (jak w ProjectsPage – z ikoną kalendarza) */}
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data do</Typography>
                  <TextField
                    size="small"
                    type="date"
                    value={ksefDateTo}
                    onChange={(e) => setKsefDateTo(String(e.target.value))}
                    variant="outlined"
                    inputRef={ksefDateToRef}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{ cursor: 'pointer' }}
                          onClick={() => { try { (ksefDateToRef.current as any)?.showPicker?.(); } catch { ksefDateToRef.current?.focus(); } }}
                        >
                          <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                        </InputAdornment>
                      )
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                      '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                      '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: ksefDateTo ? '#2D2D32' : '#B9B9B9' },
                      '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
                      '& input::-ms-clear': { display: 'none' }
                    }}
                  />
                </Box>

                {/* Zatwierdzenia - tylko dla zakładki "Wszystkie" */}
                {ksefStatusTab === 'all' && (
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Zatwierdzenia</Typography>
                    <Select
                      size="small"
                      value={ksefApprovals}
                      onChange={(e) => setKsefApprovals(e.target.value as any)}
                      IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
                      sx={{
                        height: 36,
                        pr: '8px',
                        '& .MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                          height: 36,
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: '14px',
                          color: '#2D2D32'
                        },
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        '& fieldset': { borderColor: '#d4d4d9' },
                        '&:hover fieldset': { borderColor: '#595966' },
                        '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
                        '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                        '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
                      }}
                    >
                      <MenuItem value="all">Wszystkie</MenuItem>
                      <MenuItem value="approved">Zatwierdzone</MenuItem>
                      <MenuItem value="unapproved">Niezatwierdzone</MenuItem>
                    </Select>
                  </Box>
                )}
              </Box>

              {/* Zakładki: Wszystkie / Zatwierdzone / Odrzucone */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
                <Tabs
                  value={ksefStatusTab}
                  onChange={(_, v) => setKsefStatusTab(v)}
                  aria-label="ksef status tabs"
                  sx={{ minHeight: 40, height: 40, flexGrow: 1, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTabs-flexContainer': { gap: '2px' } }}
                >
                  <Tab
                    disableRipple
                    value="pending"
                    label={`Do weryfikacji (${ksefTabCounts.pending})`}
                    sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: ksefStatusTab === 'pending' ? 500 : 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: ksefStatusTab === 'pending' ? '#E6E6EC' : '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
                  />
                  <Tab
                    disableRipple
                    value="all"
                    label={`Wszystkie (${ksefTabCounts.all})`}
                    sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: ksefStatusTab === 'all' ? 500 : 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: ksefStatusTab === 'all' ? '#E6E6EC' : '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
                  />
                  <Tab
                    disableRipple
                    value="approved"
                    label={`Zatwierdzone (${ksefTabCounts.approved})`}
                    sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: ksefStatusTab === 'approved' ? 500 : 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: ksefStatusTab === 'approved' ? '#E6E6EC' : '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
                  />
                  <Tab
                    disableRipple
                    value="rejected"
                    label={`Odrzucone (${ksefTabCounts.rejected})`}
                    sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: ksefStatusTab === 'rejected' ? 500 : 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: ksefStatusTab === 'rejected' ? '#E6E6EC' : '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
                  />
                </Tabs>
                <Button
                  size="small"
                  variant="outlined"
                  title={`Synchronizuj z KSEF ${ksefSyncing ? '(w trakcie...)' : ''}`}
                  aria-label="Synchronizuj z KSEF"
                  disabled={ksefSyncing}
                  sx={{
                    borderRadius: '4px',
                    p: 0.5,
                    minWidth: 32,
                    height: 32,
                    alignSelf: 'center',
                    bgcolor: ksefSyncing ? '#E0E0F5' : 'transparent',
                    border: 'none',
                    transition: 'background-color 0.15s ease',
                    '&:hover': !ksefSyncing ? { bgcolor: 'rgba(0,0,0,0.06)', border: 'none' } : {},
                    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
                    cursor: ksefSyncing ? 'wait' : 'pointer',
                    position: 'relative',
                    zIndex: 1000,
                    pointerEvents: 'auto'
                  }}
                  onClick={(e) => {
                    console.log('[KSEF] onClick START - event:', e, 'ksefSyncing:', ksefSyncing, 'disabled:', ksefSyncing);
                    if (ksefSyncing) {
                      console.log('[KSEF] onClick - zablokowane, ksefSyncing=true');
                      return;
                    }
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('[KSEF] onClick - przycisk kliknięty, handleKsefRefresh:', typeof handleKsefRefresh);
                    if (typeof handleKsefRefresh === 'function') {
                      try {
                        handleKsefRefresh();
                        console.log('[KSEF] onClick - handleKsefRefresh wywołane');
                      } catch (err) {
                        console.error('[KSEF] onClick - błąd przy wywołaniu handleKsefRefresh:', err);
                      }
                    } else {
                      console.error('[KSEF] onClick - handleKsefRefresh nie jest funkcją!');
                    }
                  }}
                  onMouseDown={(e) => {
                    console.log('[KSEF] onMouseDown - mysz naciśnięta, ksefSyncing:', ksefSyncing);
                  }}
                  onMouseUp={(e) => {
                    console.log('[KSEF] onMouseUp - mysz zwolniona');
                  }}
                >
                  {ksefSyncing ? (
                    <CircularProgress 
                      size={20} 
                      sx={{ color: '#595966' }}
                    />
                  ) : (
                    <Box
                      component="img"
                      alt="Aktualizuj"
                      src="/assets/icons/update_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg"
                      sx={{ 
                        display: 'block', 
                        width: 24,
                        height: 24,
                        pointerEvents: 'none'
                      }}
                    />
                  )}
                </Button>
                <IconButton
                  size="small"
                  title="Dostosuj kolumny"
                  aria-label="Dostosuj kolumny"
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '4px',
                    alignSelf: 'center',
                    p: 0,
                    bgcolor: colsOpenKsef ? '#E0E0F5' : 'transparent',
                    transition: 'background-color 0.15s ease',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
                  }}
                  onClick={(e) => { setColResizeMode(true); setColsAnchorElKsef(e.currentTarget); }}
                >
                  <Box component="img" alt="kolumny" src={'/assets/icons/frame-344.svg'} sx={{ width: 32, height: 32 }} />
                </IconButton>
              </Box>

              {/* KSEF: na razie nie pokazujemy komunikatów (dopóki dopracowujemy UI tabeli) */}

              <TableContainer
                component={Paper}
                sx={{ height: tableHeight ?? 432, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderBottom: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}
                ref={tableContainerRef}
              >
                <Table
                  key={`ksef-table-${ksefRefreshKey}`}
                  size="small"
                  stickyHeader
                  sx={{
                    tableLayout: 'fixed',
                    width: '100%',
                    '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
                    '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
                    '& .MuiTableCell-body': { borderRight: 'none', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 0 rgba(224,224,224,1)', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
                    '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
                  }}
                >
                  <colgroup>
                    {(draftOrderKsef ?? columnOrderKsef).filter(colVKsef).map((key) => (
                      <col key={key} style={{ width: getColWKsef(key) }} />
                    ))}
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      {(draftOrderKsef ?? columnOrderKsef).map((key) => {
                        if (!colVKsef(key)) return null;
                        if (key === 'dates') {
                          return <React.Fragment key={key}>{renderResizableThKsef(key, 'Data')}</React.Fragment>;
                        }
                        if (key === 'invoiceType') {
                          return <React.Fragment key={key}>{renderResizableThKsef(key, 'Rodzaj')}</React.Fragment>;
                        }
                        if (key === 'invoiceNumber') {
                          return <React.Fragment key={key}>{renderResizableThKsef(key, 'Nr faktury')}</React.Fragment>;
                        }
                        if (key === 'payment') {
                          return <React.Fragment key={key}>{renderResizableThKsef(key, 'Płatność')}</React.Fragment>;
                        }
                        if (key === 'seller') {
                          return <React.Fragment key={key}>{renderResizableThKsef(key, 'Sprzedawca')}</React.Fragment>;
                        }
                        if (key === 'nip') {
                          return <React.Fragment key={key}>{renderResizableThKsef(key, 'NIP')}</React.Fragment>;
                        }
                        if (key === 'net') {
                          return <React.Fragment key={key}>{renderResizableThKsef(key, 'Netto', 'right')}</React.Fragment>;
                        }
                        if (key === 'vatRate') {
                          return <React.Fragment key={key}>{renderResizableThKsef(key, 'Stawka', 'right')}</React.Fragment>;
                        }
                        if (key === 'vatAmount') {
                          return <React.Fragment key={key}>{renderResizableThKsef(key, 'VAT', 'right')}</React.Fragment>;
                        }
                        if (key === 'gross') {
                          return <React.Fragment key={key}>{renderResizableThKsef(key, 'Brutto', 'right')}</React.Fragment>;
                        }
                        if (key === 'status') {
                          return <React.Fragment key={key}>{renderResizableThKsef(key, 'Status')}</React.Fragment>;
                        }
                        if (key === 'actions') {
                          return <React.Fragment key={key}>{renderResizableThKsef(key, '', 'center')}</React.Fragment>;
                        }
                        return null;
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ksefLoading ? (
                      Array.from({ length: 9 }).map((_, idx) => (
                        <TableRow key={`ksef-skeleton-${idx}`}>
                          {(draftOrderKsef ?? columnOrderKsef).filter(colVKsef).map((key) => (
                            <TableCell key={key}>&nbsp;</TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : ksefError ? (
                      <TableRow>
                        <TableCell colSpan={(draftOrderKsef ?? columnOrderKsef).filter(colVKsef).length} align="center" sx={{ py: 3 }}>
                          <Alert severity="error">{ksefError}</Alert>
                        </TableCell>
                      </TableRow>
                    ) : ksefRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={(draftOrderKsef ?? columnOrderKsef).filter(colVKsef).length} align="center" sx={{ py: 3 }}>
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966' }}>
                            Brak faktur do wyświetlenia
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleKsefRows
                        .map((inv) => {
                          const netAmount = Number(inv.net_amount || 0);
                          const grossAmount = Number(inv.gross_amount || 0);
                          const vatAmount = Number(inv.vat_amount || 0) || (grossAmount - netAmount); // Użyj z bazy lub oblicz
                          const vatRate = netAmount > 0 ? ((vatAmount / netAmount) * 100).toFixed(1) : '0.0';
                          const status = inv.status || 'Do weryfikacji';
                          
                          return (
                            <TableRow
                              key={inv.ksef_invoice_id}
                              hover
                              data-rowkey={`ksef-${inv.ksef_invoice_id}`}
                              onClick={() => {
                                const rk = `ksef-${inv.ksef_invoice_id}`;
                                setSelectedRowKey((prev) => (prev === rk ? null : rk));
                              }}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#F5F5FF !important' },
                                ...getRowSelectionSx(selectedRowKey === `ksef-${inv.ksef_invoice_id}`),
                              }}
                            >
                              {(draftOrderKsef ?? columnOrderKsef).map((key) => {
                                if (!colVKsef(key)) return null;
                                
                                if (key === 'dates') {
                                  return (
                                    <TableCell key={key} sx={{ whiteSpace: 'nowrap' }}>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                        <Tooltip title="Data zaksięgowania" arrow>
                                          <Typography sx={{ fontSize: '0.85rem', fontFamily: 'Roboto, sans-serif' }}>
                                            {inv.invoicing_date ? String(inv.invoicing_date).slice(0, 10) : '-'}
                                          </Typography>
                                        </Tooltip>
                                        <Tooltip title="Data wystawienia" arrow>
                                          <Typography sx={{ fontSize: '0.85rem', fontFamily: 'Roboto, sans-serif', color: '#595966' }}>
                                            {inv.issue_date ? String(inv.issue_date).slice(0, 10) : '-'}
                                          </Typography>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  );
                                }
                                if (key === 'invoiceType') {
                                  const invoiceType = inv.invoice_type || null;
                                  const getInvoiceTypeLabel = (type: string | null): string => {
                                    if (!type) return '-';
                                    const typeMap: Record<string, string> = {
                                      'vat': 'Faktura VAT',
                                      'advance': 'Zaliczka',
                                      'settlement': 'Rozliczenie',
                                      'correction': 'Korekta',
                                      'proforma': 'Pro forma'
                                    };
                                    return typeMap[type] || type;
                                  };
                                  const typeVal = getInvoiceTypeLabel(invoiceType);
                                  return (
                                    <TableCell key={key} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                                      <Tooltip title={typeVal} arrow>
                                        <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{typeVal}</Box>
                                      </Tooltip>
                                    </TableCell>
                                  );
                                }
                                if (key === 'invoiceNumber') {
                                  const val = inv.invoice_number || '-';
                                  return (
                                    <TableCell key={key} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                                      <Tooltip title={val} arrow>
                                        <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</Box>
                                      </Tooltip>
                                    </TableCell>
                                  );
                                }
                                if (key === 'payment') {
                                  // Debug: loguj dane płatności
                                  if (inv.ksef_invoice_id && !inv.payment_method) {
                                    console.log('[KSEF Frontend] Brak payment_method dla faktury:', {
                                      ksef_invoice_id: inv.ksef_invoice_id,
                                      invoice_number: inv.invoice_number,
                                      payment_method: inv.payment_method,
                                      allKeys: Object.keys(inv)
                                    });
                                  }
                                  const val = inv.payment_method || '-';
                                  return (
                                    <TableCell key={key} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                                      <Tooltip title={val} arrow>
                                        <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</Box>
                                      </Tooltip>
                                    </TableCell>
                                  );
                                }
                                if (key === 'seller') {
                                  return (
                                    <TableCell key={key}>
                                      <Tooltip title={inv.seller_name || ''} arrow>
                                        <Box sx={{
                                          display: '-webkit-box',
                                          WebkitBoxOrient: 'vertical',
                                          WebkitLineClamp: 2,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          lineHeight: 1.25,
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word',
                                          maxHeight: '2.5em'
                                        }}>
                                          {inv.seller_name || '-'}
                                        </Box>
                                      </Tooltip>
                                    </TableCell>
                                  );
                                }
                                if (key === 'nip') {
                                  const val = inv.seller_tax_id || '-';
                                  return (
                                    <TableCell key={key} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                                      <Tooltip title={val} arrow>
                                        <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</Box>
                                      </Tooltip>
                                    </TableCell>
                                  );
                                }
                                if (key === 'net') {
                                  return (
                                    <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap' }}>
                                      {formatMoney(netAmount)}
                                    </TableCell>
                                  );
                                }
                                if (key === 'vatRate') {
                                  return (
                                    <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap' }}>
                                      {vatRate}%
                                    </TableCell>
                                  );
                                }
                                if (key === 'vatAmount') {
                                  return (
                                    <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap' }}>
                                      {formatMoney(vatAmount)}
                                    </TableCell>
                                  );
                                }
                                if (key === 'gross') {
                                  return (
                                    <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap' }}>
                                      {formatMoney(grossAmount)}
                                    </TableCell>
                                  );
                                }
                                if (key === 'status') {
                                  const displayStatus = status || 'Do weryfikacji';
                                  // Mapowanie kolorów dla statusów KSEF w stylu jak w innych miejscach aplikacji
                                  const statusColorMap: Record<string, { bg: string; label: string }> = {
                                    'Do weryfikacji': { bg: '#FFF8E1', label: 'Do weryfikacji' }, // Żółty pastelowy
                                    'Zatwierdzona': { bg: '#E8F5E9', label: 'Zatwierdzona' }, // Zielony pastelowy
                                    'Odrzucona': { bg: '#FFE5CC', label: 'Odrzucona' }, // Pomarańczowy pastelowy
                                  };
                                  const statusStyle = statusColorMap[displayStatus] || { bg: '#f5f5f5', label: displayStatus };
                                  const textColor = '#020242';
                                  
                                  return (
                                    <TableCell key={key}>
                                      <Chip 
                                        label={statusStyle.label} 
                                        size="small" 
                                        sx={{ 
                                          bgcolor: statusStyle.bg,
                                          color: textColor,
                                          borderRadius: '4px',
                                          minHeight: 28,
                                          height: 28,
                                          '& .MuiChip-label': {
                                            px: 1,
                                            py: 0,
                                            fontFamily: 'Roboto, sans-serif',
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            lineHeight: '16px',
                                            color: textColor
                                          }
                                        }} 
                                      />
                                    </TableCell>
                                  );
                                }
                                if (key === 'actions') {
                                  // Sprawdź czy to faktura KSeF
                                  const isKsefInvoice = inv?.ksef_invoice_id != null;
                                  
                                  return (
                                    <TableCell key={key} align="center">
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                        {isKsefInvoice ? (
                                          <>
                                            {/* Ikona Podgląd */}
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (inv?.ksef_invoice_id) {
                                                  openKsefPreview(inv.ksef_invoice_id);
                                                }
                                              }}
                                              sx={{ p: 0.5 }}
                                              title="Podgląd"
                                            >
                                              <Visibility fontSize="small" sx={{ color: '#595966' }} />
                                            </IconButton>
                                            
                                            {/* Ikona Edycja – otwiera nową kartę */}
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const invoiceData = { 
                                                  ...inv, 
                                                  id: inv.ksef_invoice_id,
                                                  ksef_invoice_id: inv.ksef_invoice_id,
                                                  ksef_number: inv.ksef_invoice_id || '',
                                                  ksef_status: inv.status || 'Do weryfikacji',
                                                  payment_method: inv.payment_method || null,
                                                  payment_due_date: inv.payment_due_date || null,
                                                  document_type: 'Przelew' as const,
                                                  document_kind: inv.invoice_type === 'correction' ? ('correction' as const) : ('vat' as const),
                                                  document_date: inv.issue_date || '',
                                                  received_date: inv.invoicing_date || '',
                                                  project_id: '',
                                                  invoice_number: inv.invoice_number || '',
                                                  contractor_name: inv.seller_name || '',
                                                  nip: inv.seller_tax_id || '',
                                                  account_number: inv.bank_account_number || '',
                                                  net_amount: Number(inv.net_amount || 0),
                                                  vat_rate: '23' as const,
                                                  gross_amount: Number(inv.gross_amount || 0),
                                                  due_date: inv.payment_due_date || '',
                                                  priority: 'zwykły' as const
                                                };
                                                const editId = inv.ksef_invoice_id;
                                                sessionStorage.setItem(`edit-invoice-${editId}`, JSON.stringify(invoiceData));
                                                window.open(`/accounting/edit/${encodeURIComponent(editId)}`, '_blank');
                                              }}
                                              sx={{ p: 0.5 }}
                                              title="Edycja"
                                            >
                                              <Box 
                                                component="img" 
                                                src="/assets/icons/approval_icon.svg" 
                                                alt="Edycja" 
                                                sx={{ width: 20, height: 20 }}
                                              />
                                            </IconButton>
                                            
                                            {/* Ikona Kebab Menu */}
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setMenuRow(inv);
                                                setMenuPosition({ top: e.clientY, left: e.clientX });
                                              }}
                                              sx={{ p: 0.5 }}
                                              title="Menu"
                                            >
                                              <MoreVert fontSize="small" sx={{ color: '#595966' }} />
                                            </IconButton>
                                          </>
                                        ) : (
                                          /* Dla zwykłych faktur tylko kebab menu */
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setMenuRow(inv);
                                              setMenuPosition({ top: e.clientY, left: e.clientX });
                                            }}
                                            sx={{ p: 0.5 }}
                                            title="Menu"
                                          >
                                            <MoreVert fontSize="small" sx={{ color: '#595966' }} />
                                          </IconButton>
                                        )}
                                      </Box>
                                    </TableCell>
                                  );
                                }
                                return null;
                              })}
                            </TableRow>
                          );
                        })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
        </>
      )}
      </>)}
      {/* === KONIEC SEKCJI ZAKUPY === */}

      {/* === SEKCJA MAGAZYNY (sectionChip === 1) === */}
      {sectionChip === 1 && (<>
      {/* Pasek zakładek głównych – Magazyny */}
      <Box sx={{ mt: '28px', mb: 2, borderBottom: '1px solid #EDEDED', display: 'flex', gap: 3, alignItems: 'flex-end' }}>
        {WH_TABS.map((label, idx) => (
          <Box
            key={label}
            onClick={() => { setWhMainTab(idx); }}
            sx={{
              position: 'relative', pb: 1, cursor: 'pointer', fontFamily: 'Roboto, sans-serif',
              fontSize: '16px', color: '#020242', fontWeight: whMainTab === idx ? 500 : 400,
              userSelect: 'none', display: 'inline-flex', alignItems: 'center', pr: '12px'
            }}
          >
            <Box component="span" sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              {label}
            </Box>
            {whMainTab === idx && (
              <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, bgcolor: '#000000' }} />
            )}
          </Box>
        ))}
      </Box>

      {/* Filtry Magazyny */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(200px, 250px) minmax(200px, 250px) minmax(160px, 200px) minmax(150px, 200px) minmax(140px, 180px) minmax(140px, 180px)',
          columnGap: '24px',
          alignItems: 'start',
          height: 56,
          mb: 2,
          bgcolor: '#FAFAFA'
        }}
      >
        {/* Szukaj */}
        <TextField
          size="small"
          placeholder="Szukaj"
          value={whQuery}
          onChange={(e) => setWhQuery(e.target.value)}
          variant="outlined"
          inputProps={{
            'data-role': 'page-filter',
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); try { (e.currentTarget as HTMLInputElement).blur(); } catch {} }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ width: 20, height: 20, color: '#595966' }} />
              </InputAdornment>
            )
          }}
          sx={{
            width: '100%', mt: '20px',
            '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
            '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
            '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
          }}
        />

        {/* Projekt */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Projekt</Typography>
          <Autocomplete
            size="small"
            options={activeProjects}
            getOptionLabel={(p) => `${p.id} ${p.name}`}
            value={activeProjects.find((p) => p.id === whProjectFilter) || null}
            onChange={(_, val) => setWhProjectFilter(val ? val.id : '')}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Wybierz z listy"
                sx={{
                  '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px', pr: '8px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                  '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: whProjectFilter ? '#2D2D32' : '#B9B9B9' }
                }}
              />
            )}
            clearOnBlur={false}
          />
        </Box>

        {/* Magazyn */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Magazyn</Typography>
          <Select
            size="small"
            value={whWarehouseFilter}
            displayEmpty
            onChange={(e) => setWhWarehouseFilter(String(e.target.value))}
            renderValue={whWarehouseFilter !== '' ? undefined : () => 'Wszystkie'}
            IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
            sx={{
              height: 36, pr: '8px',
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: whWarehouseFilter ? '#2D2D32' : '#B9B9B9' },
              backgroundColor: '#fff', borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {WAREHOUSES_OPTIONS.map((w) => (<MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>))}
          </Select>
        </Box>

        {/* Miesiąc */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Miesiąc</Typography>
          <Select
            size="small"
            value={whMonthFilter}
            displayEmpty
            onChange={(e) => setWhMonthFilter(String(e.target.value))}
            renderValue={whMonthFilter !== '' ? () => (monthOptions(18).find((m) => m.key === whMonthFilter)?.label || '') : () => 'Wszystkie'}
            IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
            sx={{
              height: 36, pr: '8px',
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: whMonthFilter ? '#2D2D32' : '#B9B9B9' },
              backgroundColor: '#fff', borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {monthOptions(18).map((m) => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
          </Select>
        </Box>

        {/* Data od */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data od</Typography>
          <TextField
            size="small"
            type="date"
            value={whDateFrom}
            onChange={(e) => setWhDateFrom(String(e.target.value))}
            variant="outlined"
            inputRef={whDateFromRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ cursor: 'pointer' }} onClick={() => { try { (whDateFromRef.current as any)?.showPicker?.(); } catch { whDateFromRef.current?.focus(); } }}>
                  <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                </InputAdornment>
              )
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: whDateFrom ? '#2D2D32' : '#B9B9B9' },
              '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
              '& input::-ms-clear': { display: 'none' }
            }}
          />
        </Box>

        {/* Data do */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data do</Typography>
          <TextField
            size="small"
            type="date"
            value={whDateTo}
            onChange={(e) => setWhDateTo(String(e.target.value))}
            variant="outlined"
            inputRef={whDateToRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ cursor: 'pointer' }} onClick={() => { try { (whDateToRef.current as any)?.showPicker?.(); } catch { whDateToRef.current?.focus(); } }}>
                  <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                </InputAdornment>
              )
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: whDateTo ? '#2D2D32' : '#B9B9B9' },
              '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
              '& input::-ms-clear': { display: 'none' }
            }}
          />
        </Box>
      </Box>

      {/* Tabela Magazyny */}
      {whLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={32} /></Box>
      ) : whMainTab === 0 ? (
        /* ========== STANY MAGAZYNOWE ========== */
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <TableContainer component={Paper} sx={{ borderRadius: '4px', border: '1px solid #d4d4d9', boxShadow: 'none' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: 50 }}>Lp.</TableCell>
                  <TableCell sx={{ ...headerCellSx, minWidth: 250 }}>Nazwa</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 100 }}>Magazyn</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 120 }}>Projekt</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 80 }}>Jedn.</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 100, textAlign: 'right' }}>Ilość</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 120, textAlign: 'right' }}>Cena śr. netto</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 120, textAlign: 'right' }}>Wartość netto</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 160 }}>Ostatnia aktualizacja</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {whStockRows.length === 0 ? (
                  <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 4, color: '#999', fontFamily: 'Roboto, sans-serif', fontSize: 14 }}>Brak danych</TableCell></TableRow>
                ) : whStockRows.map((row, idx) => (
                  <TableRow key={row.id} hover sx={{ '&:hover': { backgroundColor: '#F5F5FA' } }}>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32' }}>{idx + 1}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32', fontWeight: 500 }}>{row.item_name}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32' }}>{row.warehouse_code}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32' }}>{row.project_id}{row.project_name ? ` ${row.project_name}` : ''}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32' }}>{row.unit}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32', textAlign: 'right' }}>{Number(row.quantity).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32', textAlign: 'right' }}>{row.avg_unit_price != null ? currency(Number(row.avg_unit_price)) : '–'}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32', textAlign: 'right' }}>{row.total_value != null ? currency(Number(row.total_value)) : '–'}</TableCell>
                    <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#999' }}>{row.last_updated ? new Date(row.last_updated).toLocaleDateString('pl-PL') : '–'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        /* ========== DOKUMENTY (PZ, WZ, WW, MM) ========== */
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <TableContainer component={Paper} sx={{ borderRadius: '4px', border: '1px solid #d4d4d9', boxShadow: 'none' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: 50 }}>Lp.</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 160 }}>Nr dokumentu</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 70 }}>Typ</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 110 }}>Data</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 120 }}>Magazyn</TableCell>
                  {whMainTab === 4 && <TableCell sx={{ ...headerCellSx, width: 120 }}>Magazyn docelowy</TableCell>}
                  <TableCell sx={{ ...headerCellSx, width: 120 }}>Projekt</TableCell>
                  <TableCell sx={{ ...headerCellSx, minWidth: 180 }}>Kontrahent</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 80, textAlign: 'right' }}>Pozycje</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 120, textAlign: 'right' }}>Netto</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 120, textAlign: 'right' }}>Brutto</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 100 }}>Status</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 130 }}>Utworzył</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 200 }}>Opis</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {whDocRows.length === 0 ? (
                  <TableRow><TableCell colSpan={whMainTab === 4 ? 14 : 13} sx={{ textAlign: 'center', py: 4, color: '#999', fontFamily: 'Roboto, sans-serif', fontSize: 14 }}>Brak dokumentów</TableCell></TableRow>
                ) : whDocRows.map((row, idx) => (
                  <TableRow key={row.id} hover sx={{ '&:hover': { backgroundColor: '#F5F5FA' } }}>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32' }}>{idx + 1}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32', fontWeight: 500 }}>{row.document_number}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>
                      <Chip
                        label={row.document_type}
                        size="small"
                        sx={{
                          height: 22, fontSize: 11, fontWeight: 600,
                          bgcolor: row.document_type === 'PZ' ? '#E8F5E9' : row.document_type === 'WZ' ? '#FFF3E0' : row.document_type === 'WW' ? '#E3F2FD' : '#F3E5F5',
                          color: row.document_type === 'PZ' ? '#2E7D32' : row.document_type === 'WZ' ? '#E65100' : row.document_type === 'WW' ? '#1565C0' : '#7B1FA2',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32' }}>{row.document_date ? new Date(row.document_date).toLocaleDateString('pl-PL') : '–'}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32' }}>{row.warehouse_code}</TableCell>
                    {whMainTab === 4 && <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32' }}>{row.target_warehouse_code || '–'}</TableCell>}
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32' }}>{row.project_id}{row.project_name ? ` ${row.project_name}` : ''}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32' }}>{row.contractor_name || '–'}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32', textAlign: 'right' }}>{row.items_count}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32', textAlign: 'right' }}>{currency(Number(row.total_net))}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32', textAlign: 'right' }}>{currency(Number(row.total_gross))}</TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>
                      <Chip
                        label={row.status}
                        size="small"
                        sx={{
                          height: 22, fontSize: 11, fontWeight: 500,
                          bgcolor: row.status === 'Otwarty' ? '#E8F5E9' : row.status === 'Zamknięty' ? '#E0E0E0' : '#FFEBEE',
                          color: row.status === 'Otwarty' ? '#2E7D32' : row.status === 'Zamknięty' ? '#616161' : '#C62828',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #d4d4d9', fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#2D2D32' }}>{[row.creator_first_name, row.creator_last_name].filter(Boolean).join(' ') || '–'}</TableCell>
                    <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#999', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.description || '–'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      </>)}
      {/* === KONIEC SEKCJI MAGAZYNY === */}

      {/* === SEKCJA SPRZEDAŻ (sectionChip === 0) === */}
      {sectionChip === 0 && (<>
      {/* Pasek zakładek głównych – Sprzedaż */}
      <Box sx={{ mt: '28px', mb: 2, borderBottom: '1px solid #EDEDED', display: 'flex', gap: 3, alignItems: 'flex-end' }}>
        {salesMainTabs.map(({ label, badge }, idx) => (
          <Box
            key={label}
            onClick={() => setSalesMainTab(idx)}
            sx={{
              position: 'relative',
              pb: 1,
              cursor: 'pointer',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '16px',
              color: '#020242',
              fontWeight: salesMainTab === idx ? 500 : 400,
              userSelect: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              pr: '12px'
            }}
          >
            <Box component="span" sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <Box component="span" sx={{ color: label === 'KSEF' ? (ksefConnectionStatus?.connected ? '#16A34A' : '#FBBF24') : 'inherit' }}>
                {label}
              </Box>
            </Box>
            {renderMainTabBadge(badge)}
            {salesMainTab === idx && (
              <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, bgcolor: '#000000' }} />
            )}
          </Box>
        ))}
      </Box>

      {/* Zawartość zakładki Faktury Sprzedażowe */}
      {salesMainTab === 1 ? (
        <>
        {/* Filtry */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(200px, 250px) minmax(200px, 250px) minmax(150px, 200px) minmax(150px, 200px) minmax(150px, 200px)',
            columnGap: '24px',
            alignItems: 'start',
            height: 56,
            mb: 2,
            bgcolor: '#FAFAFA'
          }}
        >
          <TextField
            size="small"
            placeholder="Szukaj faktury"
            value={salesQuery}
            onChange={(e) => setSalesQuery(e.target.value)}
            variant="outlined"
            inputProps={{
              'data-role': 'page-filter',
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  try { (e.currentTarget as HTMLInputElement).blur(); } catch {}
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ width: 20, height: 20, color: '#595966' }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: '100%',
              mt: '20px',
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
              '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
            }}
          />

          {/* Filtr projektu */}
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Projekt</Typography>
            <Autocomplete
              size="small"
              options={activeProjects}
              getOptionLabel={(p) => `${p.id} ${p.name}`}
              value={activeProjects.find((p) => p.id === salesProjectFilter) || null}
              onChange={(_, val) => setSalesProjectFilter(val ? val.id : '')}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Wybierz z listy"
                  sx={{
                    '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px', pr: '8px' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                    '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: salesProjectFilter ? '#2D2D32' : '#B9B9B9' }
                  }}
                />
              )}
              clearOnBlur={false}
            />
          </Box>

          {/* Miesiąc */}
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Miesiąc</Typography>
            <Select
              size="small"
              value={salesMonthFilter}
              displayEmpty
              onChange={(e) => setSalesMonthFilter(String(e.target.value))}
              renderValue={salesMonthFilter !== '' ? () => (monthOptions(18).find((m) => m.key === salesMonthFilter)?.label || '') : () => 'Wybierz z listy'}
              IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
              sx={{
                height: 36, pr: '8px',
                '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: salesMonthFilter ? '#2D2D32' : '#B9B9B9' },
                backgroundColor: '#fff', borderRadius: '4px',
                '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' },
                '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
                '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
              }}
            >
              <MenuItem value="">Wszystkie</MenuItem>
              {monthOptions(18).map((m) => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
            </Select>
          </Box>

          {/* Data od */}
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data od</Typography>
            <TextField
              size="small"
              type="date"
              value={salesDateFrom}
              onChange={(e) => setSalesDateFrom(String(e.target.value))}
              variant="outlined"
              inputRef={salesDateFromRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => { try { (salesDateFromRef.current as any)?.showPicker?.(); } catch { salesDateFromRef.current?.focus(); } }}
                  >
                    <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                  </InputAdornment>
                )
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: salesDateFrom ? '#2D2D32' : '#B9B9B9' },
                '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
                '& input::-ms-clear': { display: 'none' }
              }}
            />
          </Box>

          {/* Data do */}
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data do</Typography>
            <TextField
              size="small"
              type="date"
              value={salesDateTo}
              onChange={(e) => setSalesDateTo(String(e.target.value))}
              variant="outlined"
              inputRef={salesDateToRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => { try { (salesDateToRef.current as any)?.showPicker?.(); } catch { salesDateToRef.current?.focus(); } }}
                  >
                    <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                  </InputAdornment>
                )
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: salesDateTo ? '#2D2D32' : '#B9B9B9' },
                '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
                '& input::-ms-clear': { display: 'none' }
              }}
            />
          </Box>
        </Box>

        {/* Zakładki: Wszystkie / Opłacone / Nieopłacone + przycisk kolumn */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
          <Tabs
            value={salesTab}
            onChange={(_, v) => setSalesTab(v)}
            aria-label="sales tabs"
            sx={{ minHeight: 40, height: 40, flexGrow: 1, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTabs-flexContainer': { gap: '2px' } }}
          >
            <Tab
              disableRipple
              label={`Wszystkie (${salesTabCounts.all})`}
              sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
            />
            <Tab
              disableRipple
              label={`Opłacone (${salesTabCounts.paid})`}
              sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
            />
            <Tab
              disableRipple
              label={`Nieopłacone (${salesTabCounts.unpaid})`}
              sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
            />
          </Tabs>
          <IconButton
            size="small"
            title="Dostosuj kolumny"
            aria-label="Dostosuj kolumny"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '4px',
              alignSelf: 'center',
              p: 0,
              bgcolor: colsOpenSales ? '#E0E0F5' : 'transparent',
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
            }}
            onClick={(e) => { setColResizeMode(true); setColsAnchorElSales(e.currentTarget); }}
          >
            <Box component="img" alt="kolumny" src={'/assets/icons/frame-344.svg'} sx={{ width: 32, height: 32 }} />
          </IconButton>
        </Box>

        {/* Tabela Faktur Sprzedażowych */}
        <TableContainer
          component={Paper}
          sx={{ height: tableHeight ?? 432, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderBottom: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}
          ref={tableContainerRef}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
              '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
              '& .MuiTableCell-body': { borderRight: 'none', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 0 rgba(224,224,224,1)', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
              '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
            }}
          >
            <colgroup>
              {(draftOrderSales ?? columnOrderSales).filter(colVSales).map((key) => (
                <col key={key} style={{ width: getColWSales(key) }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                {(draftOrderSales ?? columnOrderSales).map((key) => {
                  if (!colVSales(key)) return null;
                  if (key === 'dates') return <React.Fragment key={key}>{renderResizableThSales(key, 'Data')}</React.Fragment>;
                  if (key === 'invoiceType') return <React.Fragment key={key}>{renderResizableThSales(key, 'Rodzaj')}</React.Fragment>;
                  if (key === 'invoiceNumber') return <React.Fragment key={key}>{renderResizableThSales(key, 'Nr faktury')}</React.Fragment>;
                  if (key === 'payment') return <React.Fragment key={key}>{renderResizableThSales(key, 'Płatność')}</React.Fragment>;
                  if (key === 'seller') return <React.Fragment key={key}>{renderResizableThSales(key, 'Nabywca')}</React.Fragment>;
                  if (key === 'nip') return <React.Fragment key={key}>{renderResizableThSales(key, 'NIP')}</React.Fragment>;
                  if (key === 'net') return <React.Fragment key={key}>{renderResizableThSales(key, 'Netto', 'right')}</React.Fragment>;
                  if (key === 'vatRate') return <React.Fragment key={key}>{renderResizableThSales(key, 'Stawka', 'right')}</React.Fragment>;
                  if (key === 'vatAmount') return <React.Fragment key={key}>{renderResizableThSales(key, 'VAT', 'right')}</React.Fragment>;
                  if (key === 'gross') return <React.Fragment key={key}>{renderResizableThSales(key, 'Brutto', 'right')}</React.Fragment>;
                  if (key === 'status') return <React.Fragment key={key}>{renderResizableThSales(key, 'Status')}</React.Fragment>;
                  if (key === 'actions') return <React.Fragment key={key}>{renderResizableThSales(key, '', 'center')}</React.Fragment>;
                  return null;
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {salesRowsForTab.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={(draftOrderSales ?? columnOrderSales).filter(colVSales).length} align="center" sx={{ py: 3 }}>
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966' }}>
                      Brak faktur sprzedażowych do wyświetlenia
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                salesRowsForTab.map((inv: any) => {
                  const netAmount = Number(inv.net_amount || 0);
                  const grossAmount = Number(inv.gross_amount || 0);
                  const vatAmount = Number(inv.vat_amount || 0) || (grossAmount - netAmount);
                  const vatRate = netAmount > 0 ? ((vatAmount / netAmount) * 100).toFixed(1) : '0.0';
                  const status = inv.status || 'Wprowadzono';

                  return (
                    <TableRow key={inv.id} hover sx={{ '&:hover': { backgroundColor: '#F5F5FF !important' } }}>
                      {(draftOrderSales ?? columnOrderSales).map((key) => {
                        if (!colVSales(key)) return null;
                        if (key === 'dates') {
                          return (
                            <TableCell key={key} sx={{ whiteSpace: 'nowrap' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                <Typography sx={{ fontSize: '0.85rem', fontFamily: 'Roboto, sans-serif' }}>
                                  {inv.invoicing_date ? String(inv.invoicing_date).slice(0, 10) : '-'}
                                </Typography>
                                <Typography sx={{ fontSize: '0.85rem', fontFamily: 'Roboto, sans-serif', color: '#595966' }}>
                                  {inv.issue_date ? String(inv.issue_date).slice(0, 10) : '-'}
                                </Typography>
                              </Box>
                            </TableCell>
                          );
                        }
                        if (key === 'invoiceType') return <TableCell key={key} sx={{ whiteSpace: 'nowrap' }}>{inv.invoice_type || '-'}</TableCell>;
                        if (key === 'invoiceNumber') return <TableCell key={key} sx={{ whiteSpace: 'nowrap' }}>{inv.invoice_number || '-'}</TableCell>;
                        if (key === 'payment') return <TableCell key={key} sx={{ whiteSpace: 'nowrap' }}>{inv.payment_method || '-'}</TableCell>;
                        if (key === 'seller') {
                          return (
                            <TableCell key={key}>
                              <Tooltip title={inv.buyer_name || inv.seller_name || ''} arrow>
                                <Box sx={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word', maxHeight: '2.5em' }}>
                                  {inv.buyer_name || inv.seller_name || '-'}
                                </Box>
                              </Tooltip>
                            </TableCell>
                          );
                        }
                        if (key === 'nip') return <TableCell key={key} sx={{ whiteSpace: 'nowrap' }}>{inv.buyer_tax_id || inv.seller_tax_id || '-'}</TableCell>;
                        if (key === 'net') return <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap' }}>{formatMoney(netAmount)}</TableCell>;
                        if (key === 'vatRate') return <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap' }}>{vatRate}%</TableCell>;
                        if (key === 'vatAmount') return <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap' }}>{formatMoney(vatAmount)}</TableCell>;
                        if (key === 'gross') return <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap' }}>{formatMoney(grossAmount)}</TableCell>;
                        if (key === 'status') {
                          const statusColorMap: Record<string, { bg: string; label: string }> = {
                            'Wprowadzono': { bg: '#FFF8E1', label: 'Wprowadzono' },
                            'Opłacona': { bg: '#E8F5E9', label: 'Opłacona' },
                            'Nieopłacona': { bg: '#FFE5CC', label: 'Nieopłacona' },
                          };
                          const statusStyle = statusColorMap[status] || { bg: '#f5f5f5', label: status };
                          return (
                            <TableCell key={key}>
                              <Chip
                                label={statusStyle.label}
                                size="small"
                                sx={{
                                  bgcolor: statusStyle.bg,
                                  color: '#020242',
                                  borderRadius: '4px',
                                  minHeight: 28,
                                  height: 28,
                                  '& .MuiChip-label': { px: 1, py: 0, fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, lineHeight: '16px', color: '#020242' }
                                }}
                              />
                            </TableCell>
                          );
                        }
                        if (key === 'actions') {
                          return (
                            <TableCell key={key} align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <IconButton size="small" sx={{ p: 0.5 }} title="Podgląd">
                                  <Visibility fontSize="small" sx={{ color: '#595966' }} />
                                </IconButton>
                                <IconButton size="small" sx={{ p: 0.5 }} title="Menu">
                                  <MoreVert fontSize="small" sx={{ color: '#595966' }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                          );
                        }
                        return null;
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      ) : salesMainTab === 0 ? (
        // KSEF Sprzedaż – identyczny wygląd jak KSEF Zakupy
        <Box>
          {/* Filtry */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: salesKsefStatusTab === 'all'
                ? 'minmax(200px, 250px) minmax(200px, 250px) minmax(150px, 200px) minmax(150px, 200px) minmax(150px, 200px) minmax(200px, 300px)'
                : 'minmax(200px, 250px) minmax(200px, 250px) minmax(150px, 200px) minmax(150px, 200px) minmax(150px, 200px)',
              columnGap: '24px',
              alignItems: 'start',
              height: 56,
              mb: 2,
              bgcolor: '#FAFAFA'
            }}
          >
            <TextField
              size="small"
              placeholder="Szukaj faktury"
              value={salesKsefQuery}
              onChange={(e) => setSalesKsefQuery(e.target.value)}
              variant="outlined"
              inputProps={{
                'data-role': 'page-filter',
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); try { (e.currentTarget as HTMLInputElement).blur(); } catch {} }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ width: 20, height: 20, color: '#595966' }} />
                  </InputAdornment>
                )
              }}
              sx={{
                width: '100%', mt: '20px',
                '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
                '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
              }}
            />

            {/* Projekt */}
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Projekt</Typography>
              <Autocomplete
                size="small"
                options={activeProjects}
                getOptionLabel={(p) => `${p.id} ${p.name}`}
                value={activeProjects.find((p) => p.id === salesKsefProjectFilter) || null}
                onChange={(_, val) => setSalesKsefProjectFilter(val ? val.id : '')}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Projekt"
                    sx={{
                      '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px', pr: '8px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                      '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                      '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: salesKsefProjectFilter ? '#2D2D32' : '#B9B9B9' },
                      '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
                    }}
                  />
                )}
                clearOnBlur={false}
              />
            </Box>

            {/* Miesiąc */}
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Miesiąc</Typography>
              <Select
                size="small"
                value={salesKsefMonth}
                displayEmpty
                onChange={(e) => setSalesKsefMonth(String(e.target.value))}
                renderValue={salesKsefMonth !== '' ? () => (monthOptions(18).find((m) => m.key === salesKsefMonth)?.label || '') : () => 'Wybierz z listy'}
                IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
                sx={{
                  height: 36, pr: '8px',
                  '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: salesKsefMonth ? '#2D2D32' : '#B9B9B9' },
                  backgroundColor: '#fff', borderRadius: '4px',
                  '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' },
                  '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
                  '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
                }}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                {monthOptions(18).map((m) => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
              </Select>
            </Box>

            {/* Data od */}
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data od</Typography>
              <TextField
                size="small"
                type="date"
                value={salesKsefDateFrom}
                onChange={(e) => setSalesKsefDateFrom(String(e.target.value))}
                variant="outlined"
                inputRef={salesKsefDateFromRef}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ cursor: 'pointer' }} onClick={() => { try { (salesKsefDateFromRef.current as any)?.showPicker?.(); } catch { salesKsefDateFromRef.current?.focus(); } }}>
                      <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                    </InputAdornment>
                  )
                }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                  '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: salesKsefDateFrom ? '#2D2D32' : '#B9B9B9' },
                  '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
                  '& input::-ms-clear': { display: 'none' }
                }}
              />
            </Box>

            {/* Data do */}
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data do</Typography>
              <TextField
                size="small"
                type="date"
                value={salesKsefDateTo}
                onChange={(e) => setSalesKsefDateTo(String(e.target.value))}
                variant="outlined"
                inputRef={salesKsefDateToRef}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ cursor: 'pointer' }} onClick={() => { try { (salesKsefDateToRef.current as any)?.showPicker?.(); } catch { salesKsefDateToRef.current?.focus(); } }}>
                      <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                    </InputAdornment>
                  )
                }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                  '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: salesKsefDateTo ? '#2D2D32' : '#B9B9B9' },
                  '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
                  '& input::-ms-clear': { display: 'none' }
                }}
              />
            </Box>

            {/* Zatwierdzenia - tylko dla zakładki "Wszystkie" */}
            {salesKsefStatusTab === 'all' && (
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Zatwierdzenia</Typography>
                <Select
                  size="small"
                  value={salesKsefApprovals}
                  onChange={(e) => setSalesKsefApprovals(e.target.value as any)}
                  IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
                  sx={{
                    height: 36, pr: '8px',
                    '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
                    backgroundColor: '#fff', borderRadius: '4px',
                    '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' },
                    '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
                    '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
                  }}
                >
                  <MenuItem value="all">Wszystkie</MenuItem>
                  <MenuItem value="approved">Zatwierdzone</MenuItem>
                  <MenuItem value="unapproved">Niezatwierdzone</MenuItem>
                </Select>
              </Box>
            )}
          </Box>

          {/* Sub-zakładki: Do weryfikacji / Wszystkie / Zatwierdzone / Odrzucone + przycisk kolumn */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
            <Tabs
              value={salesKsefStatusTab}
              onChange={(_, v) => setSalesKsefStatusTab(v)}
              aria-label="sales ksef status tabs"
              sx={{ minHeight: 40, height: 40, flexGrow: 1, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTabs-flexContainer': { gap: '2px' } }}
            >
              <Tab
                disableRipple
                value="pending"
                label={`Do weryfikacji (${salesKsefTabCounts.pending})`}
                sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: salesKsefStatusTab === 'pending' ? 500 : 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: salesKsefStatusTab === 'pending' ? '#E6E6EC' : '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
              />
              <Tab
                disableRipple
                value="all"
                label={`Wszystkie (${salesKsefTabCounts.all})`}
                sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: salesKsefStatusTab === 'all' ? 500 : 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: salesKsefStatusTab === 'all' ? '#E6E6EC' : '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
              />
              <Tab
                disableRipple
                value="approved"
                label={`Zatwierdzone (${salesKsefTabCounts.approved})`}
                sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: salesKsefStatusTab === 'approved' ? 500 : 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: salesKsefStatusTab === 'approved' ? '#E6E6EC' : '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
              />
              <Tab
                disableRipple
                value="rejected"
                label={`Odrzucone (${salesKsefTabCounts.rejected})`}
                sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: salesKsefStatusTab === 'rejected' ? 500 : 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: salesKsefStatusTab === 'rejected' ? '#E6E6EC' : '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
              />
            </Tabs>
            <IconButton
              size="small"
              title="Dostosuj kolumny"
              aria-label="Dostosuj kolumny"
              sx={{ width: 32, height: 32, borderRadius: '4px', alignSelf: 'center', p: 0, bgcolor: colsOpenSalesKsef ? '#E0E0F5' : 'transparent', transition: 'background-color 0.15s ease', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}
              onClick={(e) => { setColResizeMode(true); setColsAnchorElSalesKsef(e.currentTarget); }}
            >
              <Box component="img" alt="kolumny" src={'/assets/icons/frame-344.svg'} sx={{ width: 32, height: 32 }} />
            </IconButton>
          </Box>

          {/* Tabela KSEF Sprzedaż */}
          <TableContainer
            component={Paper}
            sx={{ height: tableHeight ?? 432, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderBottom: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}
            ref={tableContainerRef}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                tableLayout: 'fixed', width: '100%',
                '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
                '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
                '& .MuiTableCell-body': { borderRight: 'none', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 0 rgba(224,224,224,1)', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
                '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
              }}
            >
              <colgroup>
                {(draftOrderSalesKsef ?? columnOrderSalesKsef).filter(colVSalesKsef).map((key) => (
                  <col key={key} style={{ width: getColWSalesKsef(key) }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  {(draftOrderSalesKsef ?? columnOrderSalesKsef).map((key) => {
                    if (!colVSalesKsef(key)) return null;
                    if (key === 'dates') return <React.Fragment key={key}>{renderResizableThSalesKsef(key, 'Data')}</React.Fragment>;
                    if (key === 'invoiceType') return <React.Fragment key={key}>{renderResizableThSalesKsef(key, 'Rodzaj')}</React.Fragment>;
                    if (key === 'invoiceNumber') return <React.Fragment key={key}>{renderResizableThSalesKsef(key, 'Nr faktury')}</React.Fragment>;
                    if (key === 'payment') return <React.Fragment key={key}>{renderResizableThSalesKsef(key, 'Płatność')}</React.Fragment>;
                    if (key === 'seller') return <React.Fragment key={key}>{renderResizableThSalesKsef(key, 'Nabywca')}</React.Fragment>;
                    if (key === 'nip') return <React.Fragment key={key}>{renderResizableThSalesKsef(key, 'NIP')}</React.Fragment>;
                    if (key === 'net') return <React.Fragment key={key}>{renderResizableThSalesKsef(key, 'Netto', 'right')}</React.Fragment>;
                    if (key === 'vatRate') return <React.Fragment key={key}>{renderResizableThSalesKsef(key, 'Stawka', 'right')}</React.Fragment>;
                    if (key === 'vatAmount') return <React.Fragment key={key}>{renderResizableThSalesKsef(key, 'VAT', 'right')}</React.Fragment>;
                    if (key === 'gross') return <React.Fragment key={key}>{renderResizableThSalesKsef(key, 'Brutto', 'right')}</React.Fragment>;
                    if (key === 'status') return <React.Fragment key={key}>{renderResizableThSalesKsef(key, 'Status')}</React.Fragment>;
                    if (key === 'actions') return <React.Fragment key={key}>{renderResizableThSalesKsef(key, '', 'center')}</React.Fragment>;
                    return null;
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {salesKsefLoading ? (
                  Array.from({ length: 9 }).map((_, idx) => (
                    <TableRow key={`sales-ksef-skeleton-${idx}`}>
                      {(draftOrderSalesKsef ?? columnOrderSalesKsef).filter(colVSalesKsef).map((key) => (
                        <TableCell key={key}>&nbsp;</TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : salesKsefError ? (
                  <TableRow>
                    <TableCell colSpan={(draftOrderSalesKsef ?? columnOrderSalesKsef).filter(colVSalesKsef).length} align="center" sx={{ py: 3 }}>
                      <Alert severity="error">{salesKsefError}</Alert>
                    </TableCell>
                  </TableRow>
                ) : salesKsefRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={(draftOrderSalesKsef ?? columnOrderSalesKsef).filter(colVSalesKsef).length} align="center" sx={{ py: 3 }}>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966' }}>
                        Brak faktur do wyświetlenia
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  salesKsefRows
                    .filter((inv) => {
                      const invoiceStatus = inv.status || 'Do weryfikacji';
                      if (salesKsefStatusTab === 'approved' && invoiceStatus !== 'Zatwierdzona') return false;
                      if (salesKsefStatusTab === 'rejected' && invoiceStatus !== 'Odrzucona') return false;
                      if (salesKsefStatusTab === 'pending' && invoiceStatus !== 'Do weryfikacji') return false;
                      if (salesKsefStatusTab === 'all') {
                        if (salesKsefApprovals === 'approved' && invoiceStatus !== 'Zatwierdzona') return false;
                        if (salesKsefApprovals === 'unapproved' && invoiceStatus === 'Zatwierdzona') return false;
                      }
                      if (salesKsefQuery) {
                        const q = salesKsefQuery.toLowerCase();
                        const matches =
                          (inv.invoice_number || '').toLowerCase().includes(q) ||
                          (inv.seller_name || '').toLowerCase().includes(q) ||
                          (inv.seller_tax_id || '').toLowerCase().includes(q);
                        if (!matches) return false;
                      }
                      if (salesKsefMonth && inv.issue_date) {
                        const invoiceMonth = String(inv.issue_date).slice(0, 7);
                        if (invoiceMonth !== salesKsefMonth) return false;
                      }
                      return true;
                    })
                    .map((inv) => {
                      const netAmount = Number(inv.net_amount || 0);
                      const grossAmount = Number(inv.gross_amount || 0);
                      const vatAmount = Number(inv.vat_amount || 0) || (grossAmount - netAmount);
                      const vatRate = netAmount > 0 ? ((vatAmount / netAmount) * 100).toFixed(1) : '0.0';
                      const status = inv.status || 'Do weryfikacji';

                      return (
                        <TableRow key={inv.ksef_invoice_id} hover sx={{ '&:hover': { backgroundColor: '#F5F5FF !important' } }}>
                          {(draftOrderSalesKsef ?? columnOrderSalesKsef).map((key) => {
                            if (!colVSalesKsef(key)) return null;
                            if (key === 'dates') {
                              return (
                                <TableCell key={key} sx={{ whiteSpace: 'nowrap' }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                    <Tooltip title="Data zaksięgowania" arrow>
                                      <Typography sx={{ fontSize: '0.85rem', fontFamily: 'Roboto, sans-serif' }}>
                                        {inv.invoicing_date ? String(inv.invoicing_date).slice(0, 10) : '-'}
                                      </Typography>
                                    </Tooltip>
                                    <Tooltip title="Data wystawienia" arrow>
                                      <Typography sx={{ fontSize: '0.85rem', fontFamily: 'Roboto, sans-serif', color: '#595966' }}>
                                        {inv.issue_date ? String(inv.issue_date).slice(0, 10) : '-'}
                                      </Typography>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              );
                            }
                            if (key === 'invoiceType') {
                              const typeMap: Record<string, string> = { 'vat': 'Faktura VAT', 'advance': 'Zaliczka', 'settlement': 'Rozliczenie', 'correction': 'Korekta', 'proforma': 'Pro forma' };
                              return <TableCell key={key} sx={{ whiteSpace: 'nowrap' }}>{typeMap[inv.invoice_type || ''] || inv.invoice_type || '-'}</TableCell>;
                            }
                            if (key === 'invoiceNumber') return <TableCell key={key} sx={{ whiteSpace: 'nowrap' }}>{inv.invoice_number || '-'}</TableCell>;
                            if (key === 'payment') return <TableCell key={key} sx={{ whiteSpace: 'nowrap' }}>{inv.payment_method || '-'}</TableCell>;
                            if (key === 'seller') {
                              return (
                                <TableCell key={key}>
                                  <Tooltip title={inv.seller_name || ''} arrow>
                                    <Box sx={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word', maxHeight: '2.5em' }}>
                                      {inv.seller_name || '-'}
                                    </Box>
                                  </Tooltip>
                                </TableCell>
                              );
                            }
                            if (key === 'nip') return <TableCell key={key} sx={{ whiteSpace: 'nowrap' }}>{inv.seller_tax_id || '-'}</TableCell>;
                            if (key === 'net') return <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap' }}>{formatMoney(netAmount)}</TableCell>;
                            if (key === 'vatRate') return <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap' }}>{vatRate}%</TableCell>;
                            if (key === 'vatAmount') return <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap' }}>{formatMoney(vatAmount)}</TableCell>;
                            if (key === 'gross') return <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap' }}>{formatMoney(grossAmount)}</TableCell>;
                            if (key === 'status') {
                              const statusColorMap: Record<string, { bg: string; label: string }> = {
                                'Do weryfikacji': { bg: '#FFF8E1', label: 'Do weryfikacji' },
                                'Zatwierdzona': { bg: '#E8F5E9', label: 'Zatwierdzona' },
                                'Odrzucona': { bg: '#FFE5CC', label: 'Odrzucona' },
                              };
                              const statusStyle = statusColorMap[status] || { bg: '#f5f5f5', label: status };
                              return (
                                <TableCell key={key}>
                                  <Chip label={statusStyle.label} size="small" sx={{ bgcolor: statusStyle.bg, color: '#020242', borderRadius: '4px', minHeight: 28, height: 28, '& .MuiChip-label': { px: 1, py: 0, fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, lineHeight: '16px', color: '#020242' } }} />
                                </TableCell>
                              );
                            }
                            if (key === 'actions') {
                              return (
                                <TableCell key={key} align="center">
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    <IconButton size="small" sx={{ p: 0.5 }} title="Podgląd">
                                      <Visibility fontSize="small" sx={{ color: '#595966' }} />
                                    </IconButton>
                                    <IconButton size="small" sx={{ p: 0.5 }} title="Menu">
                                      <MoreVert fontSize="small" sx={{ color: '#595966' }} />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              );
                            }
                            return null;
                          })}
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : null}
      </>)}
      {/* === KONIEC SEKCJI SPRZEDAŻ === */}

      {/* Sekcje 3 (Inne wydatki), 4 (Wypłaty pracownicze), 9 (Podatki i ZUS) → osobne strony */}

      {/* === SEKCJA ROZRACHUNKI (sectionChip === 5) === */}
      {sectionChip === 5 && (<>
      {/* Pasek zakładek głównych – Rozrachunki */}
      <Box sx={{ mt: '28px', mb: 2, borderBottom: '1px solid #EDEDED', display: 'flex', gap: 3, alignItems: 'flex-end' }}>
        {[{ label: 'Zakupy', badge: null }, { label: 'Sprzedaż', badge: null }].map(({ label }, idx) => (
          <Box
            key={label}
            onClick={() => { setSettlementsMainTab(idx); setSettlementsTab(0); }}
            sx={{
              position: 'relative', pb: 1, cursor: 'pointer', fontFamily: 'Roboto, sans-serif',
              fontSize: '16px', color: '#020242', fontWeight: settlementsMainTab === idx ? 500 : 400,
              userSelect: 'none', display: 'inline-flex', alignItems: 'center', pr: '12px'
            }}
          >
            <Box component="span" sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              {label}
            </Box>
            {settlementsMainTab === idx && (
              <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, bgcolor: '#000000' }} />
            )}
          </Box>
        ))}
      </Box>

      {/* Filtry Rozrachunki */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(200px, 250px) minmax(200px, 250px) minmax(150px, 200px) minmax(150px, 200px) minmax(150px, 200px)',
          columnGap: '24px',
          alignItems: 'start',
          height: 56,
          mb: 2,
          bgcolor: '#FAFAFA'
        }}
      >
        <TextField
          size="small"
          placeholder="Szukaj"
          value={settlementsQuery}
          onChange={(e) => setSettlementsQuery(e.target.value)}
          variant="outlined"
          inputProps={{
            'data-role': 'page-filter',
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); try { (e.currentTarget as HTMLInputElement).blur(); } catch {} }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ width: 20, height: 20, color: '#595966' }} />
              </InputAdornment>
            )
          }}
          sx={{
            width: '100%', mt: '20px',
            '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
            '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
            '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
          }}
        />

        {/* Projekt */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Projekt</Typography>
          <Autocomplete
            size="small"
            options={activeProjects}
            getOptionLabel={(p) => `${p.id} ${p.name}`}
            value={activeProjects.find((p) => p.id === settlementsProjectFilter) || null}
            onChange={(_, val) => setSettlementsProjectFilter(val ? val.id : '')}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Wybierz z listy"
                sx={{
                  '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px', pr: '8px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                  '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: settlementsProjectFilter ? '#2D2D32' : '#B9B9B9' }
                }}
              />
            )}
            clearOnBlur={false}
          />
        </Box>

        {/* Miesiąc (Miesiąc zapłaty na Rozliczone) */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>{settlementsTab === 1 ? 'Miesiąc zapłaty' : 'Miesiąc'}</Typography>
          <Select
            size="small"
            value={settlementsMonthFilter}
            displayEmpty
            onChange={(e) => setSettlementsMonthFilter(String(e.target.value))}
            renderValue={settlementsMonthFilter !== '' ? () => (monthOptions(18).find((m) => m.key === settlementsMonthFilter)?.label || '') : () => 'Wybierz z listy'}
            IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
            sx={{
              height: 36, pr: '8px',
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: settlementsMonthFilter ? '#2D2D32' : '#B9B9B9' },
              backgroundColor: '#fff', borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {monthOptions(18).map((m) => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
          </Select>
        </Box>

        {/* Data od (Data zapłaty od na Rozliczone) */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>{settlementsTab === 1 ? 'Data zapłaty od' : 'Data od'}</Typography>
          <TextField
            size="small"
            type="date"
            value={settlementsDateFrom}
            onChange={(e) => setSettlementsDateFrom(String(e.target.value))}
            variant="outlined"
            inputRef={settlementsDateFromRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ cursor: 'pointer' }} onClick={() => { try { (settlementsDateFromRef.current as any)?.showPicker?.(); } catch { settlementsDateFromRef.current?.focus(); } }}>
                  <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                </InputAdornment>
              )
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: settlementsDateFrom ? '#2D2D32' : '#B9B9B9' },
              '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
              '& input::-ms-clear': { display: 'none' }
            }}
          />
        </Box>

        {/* Data do (Data zapłaty do na Rozliczone) */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>{settlementsTab === 1 ? 'Data zapłaty do' : 'Data do'}</Typography>
          <TextField
            size="small"
            type="date"
            value={settlementsDateTo}
            onChange={(e) => setSettlementsDateTo(String(e.target.value))}
            variant="outlined"
            inputRef={settlementsDateToRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ cursor: 'pointer' }} onClick={() => { try { (settlementsDateToRef.current as any)?.showPicker?.(); } catch { settlementsDateToRef.current?.focus(); } }}>
                  <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                </InputAdornment>
              )
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: settlementsDateTo ? '#2D2D32' : '#B9B9B9' },
              '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
              '& input::-ms-clear': { display: 'none' }
            }}
          />
        </Box>
      </Box>

      {/* Sub-zakładki: Nierozliczone / Rozliczone / Wszystkie + przycisk kolumn */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
        <Tabs
          value={settlementsTab}
          onChange={(_, v) => setSettlementsTab(v)}
          aria-label="settlements tabs"
          sx={{ minHeight: 40, height: 40, flexGrow: 1, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTabs-flexContainer': { gap: '2px' } }}
        >
          <Tab
            disableRipple
            label={`Nierozliczone (${settlementsTabCounts.unsettled})`}
            sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
          />
          <Tab
            disableRipple
            label={`Rozliczone (${settlementsTabCounts.settled})`}
            sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
          />
          <Tab
            disableRipple
            label={`Wszystkie (${settlementsTabCounts.all})`}
            sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
          />
          <Tab
            disableRipple
            label={`Nieuzgodnione (${settlementsTabCounts.unreconciled})`}
            sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
          />
          <Tab
            disableRipple
            label={`Uzgodnione (${settlementsTabCounts.reconciled})`}
            sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
          />
        </Tabs>
        <IconButton
          size="small"
          title="Dostosuj kolumny"
          aria-label="Dostosuj kolumny"
          sx={{ width: 32, height: 32, borderRadius: '4px', alignSelf: 'center', p: 0, bgcolor: colsOpenSettle ? '#E0E0F5' : 'transparent', transition: 'background-color 0.15s ease', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}
          onClick={(e) => { setColResizeMode(true); setColsAnchorElSettle(e.currentTarget); setDraftColumnsVisibleSettle({ ...columnsVisibleSettle }); setDraftOrderSettle([...columnOrderSettle]); }}
        >
          <Box component="img" alt="kolumny" src={'/assets/icons/frame-344.svg'} sx={{ width: 32, height: 32 }} />
        </IconButton>
      </Box>

      {/* Tabela Rozrachunki */}
      <TableContainer
        component={Paper}
        sx={{ height: tableHeight ?? 432, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderBottom: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}
        ref={tableContainerRef}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: 'fixed', width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
            '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
            '& .MuiTableCell-body': { borderRight: 'none', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 0 rgba(224,224,224,1)', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
            '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
          }}
        >
          <colgroup>
            {(draftOrderSettle ?? columnOrderSettle).filter(colVSettle).map((key) => (
              <col key={key} style={{ width: getColWSettle(key) }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {(draftOrderSettle ?? columnOrderSettle).map((key) => {
                if (!colVSettle(key)) return null;
                const align = key === 'grossAmount' || key === 'vatAmount' || key === 'daysLeft' ? 'right' as const : undefined;
                const fieldMap: Record<string, string> = {
                  settlementId: 'settlement_id', dueDate: 'due_date', daysLeft: 'due_date', projectCode: 'project_code', warehouse: 'warehouse',
                  docType: 'document_type', contractor: 'contractor', nip: 'nip', invoiceNumber: 'invoice_number',
                  grossAmount: 'gross_amount', vatAmount: 'vat_amount', paidDate: 'paid_date', accountNumber: 'account_number', createdDate: 'created_date',
                  reconciliationBank: 'reconciliation_bank_name', reconciliationStmtNum: 'reconciliation_statement_number', reconciliationStmtDate: 'reconciliation_statement_date',
                };
                const sortField = fieldMap[key];
                const label = settleColLabel(key);
                const tooltip = settleColTooltip(key);
                const sortable = key !== 'actions' && !!sortField;
                const isActive = sortable && settleSortKey === sortField;
                const sortLabel = sortable && label ? (
                  <TableSortLabel
                    {...getSortLabelProps(isActive)}
                    active={isActive}
                    direction={isActive ? settleSortDir : 'asc'}
                    onClick={() => handleSettleSort(key)}
                  >
                    {label}
                  </TableSortLabel>
                ) : label;
                const headerContent = tooltip ? (
                  <Tooltip title={tooltip} arrow placement="top">
                    <Box component="span">{sortLabel}</Box>
                  </Tooltip>
                ) : sortLabel;
                return <React.Fragment key={key}>{renderResizableThSettle(key, headerContent, align)}</React.Fragment>;
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {settlementsLoading ? (
              <TableRow>
                <TableCell colSpan={(draftOrderSettle ?? columnOrderSettle).filter(colVSettle).length} align="center" sx={{ py: 3 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966' }}>
                    Ładowanie rozrachunków...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : visibleSettleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={(draftOrderSettle ?? columnOrderSettle).filter(colVSettle).length} align="center" sx={{ py: 3 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966' }}>
                    Brak rozrachunków do wyświetlenia
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleSettleRows.map((inv: any, rowIdx: number) => {
                // Oblicz pozostałe dni (obie daty w północy lokalnej, żeby uniknąć błędów strefy czasowej)
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                let dueDate: Date | null = null;
                if (inv.due_date) {
                  const s = String(inv.due_date).slice(0, 10);
                  const [y, m, d] = s.split('-').map(Number);
                  if (y && m && d) dueDate = new Date(y, m - 1, d);
                  else dueDate = new Date(inv.due_date);
                }
                const daysLeft = dueDate ? Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) : null;
                const isAutoSettled = ['Gotówka', 'Karta'].includes(inv.document_type);

                // Kolorowanie wierszy wg terminu płatności (tylko nierozliczone)
                let rowBgColor: string | undefined;
                if (!inv.settled && daysLeft !== null) {
                  if (daysLeft < 0) {
                    rowBgColor = '#F9D6D6';        // po terminie – różowy
                  } else if (daysLeft === 0) {
                    rowBgColor = '#FDE5D0';        // dziś – brzoskwiniowy
                  } else {
                    // bieżący tydzień: oblicz dni do końca tygodnia (niedziela)
                    const dayOfWeek = today.getDay(); // 0=nd, 1=pn ... 6=sb
                    const daysToEndOfWeek = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
                    if (daysLeft <= daysToEndOfWeek) {
                      rowBgColor = '#FEF4DD';      // ten tydzień – jasno żółty
                    }
                  }
                }

                const isSelected = settleSelectedIds.has(inv.id);
                const prevSelected = rowIdx > 0 && visibleSettleRows[rowIdx - 1] && settleSelectedIds.has(visibleSettleRows[rowIdx - 1].id);
                const nextSelected = rowIdx < visibleSettleRows.length - 1 && visibleSettleRows[rowIdx + 1] && settleSelectedIds.has(visibleSettleRows[rowIdx + 1].id);

                return (
                  <TableRow
                    key={inv.id || rowIdx}
                    hover
                    onClick={(e) => handleSettleRowClick(rowIdx, inv.id, e)}
                    sx={{
                      cursor: 'default',
                      userSelect: 'none',
                      position: 'relative',
                      backgroundColor: isSelected
                        ? (rowBgColor
                          ? `color-mix(in srgb, ${rowBgColor} 50%, #D6E4F9 50%)`
                          : '#D6E4F9')
                        : rowBgColor,
                      '& td.MuiTableCell-body': {
                        boxShadow: isSelected
                          ? [
                              !prevSelected ? 'inset 0 2px 0 0 #4285F4' : '',
                              !nextSelected ? 'inset 0 -2px 0 0 #4285F4' : '',
                            ].filter(Boolean).join(', ') || 'none'
                          : undefined,
                      },
                      '& td.MuiTableCell-body:first-of-type': {
                        position: isSelected ? 'relative' as const : undefined,
                        boxShadow: isSelected
                          ? [
                              !prevSelected ? 'inset 0 2px 0 0 #4285F4' : '',
                              !nextSelected ? 'inset 0 -2px 0 0 #4285F4' : '',
                            ].filter(Boolean).join(', ') || 'none'
                          : undefined,
                        '&::after': isSelected ? {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: prevSelected ? '-1px' : 0,
                          bottom: nextSelected ? '-1px' : 0,
                          width: '2px',
                          backgroundColor: '#4285F4',
                          zIndex: 1,
                          pointerEvents: 'none',
                        } : { content: 'none' },
                      },
                      '& td.MuiTableCell-body:last-of-type': {
                        position: isSelected ? 'relative' as const : undefined,
                        boxShadow: isSelected
                          ? [
                              !prevSelected ? 'inset 0 2px 0 0 #4285F4' : '',
                              !nextSelected ? 'inset 0 -2px 0 0 #4285F4' : '',
                            ].filter(Boolean).join(', ') || 'none'
                          : undefined,
                        '&::after': isSelected ? {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: prevSelected ? '-1px' : 0,
                          bottom: nextSelected ? '-1px' : 0,
                          width: '2px',
                          backgroundColor: '#4285F4',
                          zIndex: 1,
                          pointerEvents: 'none',
                        } : { content: 'none' },
                      },
                      '&:hover': {
                        backgroundColor: isSelected
                          ? (rowBgColor
                            ? `color-mix(in srgb, ${rowBgColor} 40%, #C4D7F2 60%) !important`
                            : '#C4D7F2 !important')
                          : (rowBgColor ? `${rowBgColor} !important` : '#F5F5FF !important')
                      }
                    }}
                  >
                    {(draftOrderSettle ?? columnOrderSettle).map((key) => {
                      if (!colVSettle(key)) return null;
                      const ellipsisSx = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 } as const;
                      if (key === 'settlementId') return <TableCell key={key} sx={{ ...ellipsisSx, fontFamily: 'Roboto Mono, monospace', fontSize: '0.75rem', color: '#1565C0', fontWeight: 500 }}>{inv.settlement_id || '-'}</TableCell>;
                      if (key === 'createdDate') return <TableCell key={key} sx={ellipsisSx}>{inv.created_date ? String(inv.created_date).slice(0, 10) : '-'}</TableCell>;
                      if (key === 'projectCode') return <TableCell key={key} sx={ellipsisSx}>{inv.project_code || '-'}</TableCell>;
                      if (key === 'warehouse') return <TableCell key={key} sx={ellipsisSx}>{inv.warehouse || '-'}</TableCell>;
                      if (key === 'docType') {
                        const k = inv.document_kind || (inv.document_type === 'Zaliczka (przelew)' ? 'advance' : 'vat');
                        const displayType = k === 'advance' ? 'Zaliczka' : k === 'correction' ? 'Korekta' : (inv.document_type === 'Zaliczka (przelew)' ? 'Przelew' : inv.document_type);
                        const chipColor = displayType === 'Gotówka' ? '#E8F5E9' : displayType === 'Karta' ? '#FFF3E0' : '#E3F2FD';
                        const textColor = displayType === 'Gotówka' ? '#2E7D32' : displayType === 'Karta' ? '#E65100' : '#1565C0';
                        return (
                          <TableCell key={key} sx={ellipsisSx}>
                            <Box sx={{ display: 'inline-block', px: 1, py: '2px', borderRadius: '4px', bgcolor: chipColor, color: textColor, fontSize: '12px', fontWeight: 500, fontFamily: 'Roboto, sans-serif' }}>
                              {displayType || '-'}
                            </Box>
                          </TableCell>
                        );
                      }
                      if (key === 'dueDate') return <TableCell key={key} sx={ellipsisSx}>{inv.due_date ? String(inv.due_date).slice(0, 10) : '-'}</TableCell>;
                      if (key === 'daysLeft') {
                        if (inv.settled) return <TableCell key={key} align="right" sx={{ ...ellipsisSx, color: '#2E7D32', fontWeight: 500 }}>--</TableCell>;
                        const color = daysLeft !== null && daysLeft < 0 ? '#D92D20' : daysLeft !== null && daysLeft <= 7 ? '#F59E0B' : '#2D2D32';
                        return <TableCell key={key} align="right" sx={{ ...ellipsisSx, color, fontWeight: daysLeft !== null && daysLeft < 0 ? 600 : 400 }}>{daysLeft !== null ? daysLeft : '-'}</TableCell>;
                      }
                      if (key === 'contractor') {
                        return (
                          <TableCell key={key} sx={ellipsisSx}>
                            <Tooltip title={inv.contractor || ''} arrow>
                              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {inv.contractor || '-'}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      }
                      if (key === 'nip') return <TableCell key={key} sx={ellipsisSx}>{inv.nip || '-'}</TableCell>;
                      if (key === 'invoiceNumber') return <TableCell key={key} sx={ellipsisSx}>{inv.invoice_number || '-'}</TableCell>;
                      if (key === 'grossAmount') return <TableCell key={key} align="right" sx={ellipsisSx}>{formatMoney(Number(inv.gross_amount || 0))}</TableCell>;
                      if (key === 'vatAmount') {
                        const vatAmount = Number(inv.vat_amount ?? (Number(inv.gross_amount || 0) - Number(inv.net_amount || 0)));
                        return <TableCell key={key} align="right" sx={ellipsisSx}>{formatMoney(vatAmount)}</TableCell>;
                      }
                      if (key === 'paidDate') return <TableCell key={key} sx={ellipsisSx}>{inv.paid_date ? String(inv.paid_date).slice(0, 10) : (isAutoSettled ? 'Auto' : '-')}</TableCell>;
                      if (key === 'accountNumber') {
                        const acct = inv.account_number || '';
                        return (
                          <TableCell key={key} sx={{ ...ellipsisSx, fontSize: '12px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, minWidth: 0 }}>
                              <Tooltip title={acct ? formatAccountNumber(acct) : ''} arrow>
                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                  {acct ? shortenAccountNumber(acct) : '-'}
                                </Box>
                              </Tooltip>
                              {acct && (
                                <IconButton
                                  size="small"
                                  sx={{ p: 0.5, flexShrink: 0, ml: '-2px' }}
                                  title="Kopiuj nr rachunku"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(acct).then(() => {
                                      showNotification?.('Skopiowano nr rachunku', 'success');
                                    }).catch(() => {});
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 16, color: '#595966' }} />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                        );
                      }
                      if (key === 'reconciliationBank') return <TableCell key={key} sx={ellipsisSx}>{inv.reconciliation_bank_name || '-'}</TableCell>;
                      if (key === 'reconciliationStmtNum') return <TableCell key={key} sx={ellipsisSx}>{inv.reconciliation_statement_number || '-'}</TableCell>;
                      if (key === 'reconciliationStmtDate') return <TableCell key={key} sx={ellipsisSx}>{inv.reconciliation_statement_date ? String(inv.reconciliation_statement_date).slice(0, 10) : '-'}</TableCell>;
                      if (key === 'actions') {
                        return (
                          <TableCell key={key} align="center" sx={{ whiteSpace: 'nowrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                              {/* Podgląd */}
                              <IconButton
                                size="small"
                                sx={{ p: 0.5 }}
                                title="Podgląd"
                                onClick={() => { setPreviewInvoice(inv as any); setPreviewOpen(true); }}
                              >
                                <Visibility fontSize="small" sx={{ color: '#595966' }} />
                              </IconButton>
                              {/* Rozlicz / Podgląd rozliczenia */}
                              {!isAutoSettled && (
                                <Tooltip title={inv.settled ? 'Podgląd rozliczenia' : 'Rozlicz'} arrow>
                                  <IconButton
                                    size="small"
                                    sx={{ p: 0.5 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const drawerData: SettleInvoiceData = {
                                        id: inv.id,
                                        document_type: inv.document_type,
                                        created_date: inv.created_date,
                                        received_date: inv.created_date,
                                        document_date: inv.created_date,
                                        due_date: inv.due_date,
                                        paid_date: inv.paid_date,
                                        project_code: inv.project_code,
                                        contractor: inv.contractor,
                                        contractor_address: (inv as any).contractor_address || null,
                                        contractor_postal_city: (inv as any).contractor_postal_city || null,
                                        nip: inv.nip,
                                        invoice_number: inv.invoice_number,
                                        ksef_number: (inv as any).ksef_number || null,
                                        gross_amount: Number(inv.gross_amount || 0),
                                        net_amount: Number(inv.net_amount || 0),
                                        vat_rate: inv.vat_rate,
                                        account_number: inv.account_number,
                                        warehouse: inv.warehouse,
                                        google_drive_file_id: inv.google_drive_file_id,
                                        google_drive_file_url: inv.google_drive_file_url,
                                      };
                                      setSettleDrawerData(drawerData);
                                      setSettleDrawerViewMode(!!inv.settled);
                                      setSettleDrawerOpen(true);
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src="/assets/icons/paid-green.svg"
                                      alt="paid"
                                      sx={{ width: 20, height: 20 }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {/* Kebab menu */}
                              <IconButton
                                size="small"
                                sx={{ p: 0.5 }}
                                title="Menu"
                                onClick={(e) => { e.stopPropagation(); setSettleMenuRow(inv); setSettleMenuAnchor(e.currentTarget); }}
                              >
                                <MoreVert fontSize="small" sx={{ color: '#595966' }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                        );
                      }
                      return null;
                    })}
                  </TableRow>
                );
              })
            )}
            {/* Wiersz sumy brutto */}
            {settlementsRowsForTab.length > 0 && (() => {
              const visible = (draftOrderSettle ?? columnOrderSettle).filter(colVSettle);
              const grossIdx = visible.indexOf('grossAmount');
              const beforeCount = grossIdx < 0 ? visible.length : grossIdx;
              const hasSelection = settleSelectedIds.size > 0;
              const sourceRows = hasSelection
                ? settlementsRowsForTab.filter((inv: any) => settleSelectedIds.has(inv.id))
                : settlementsRowsForTab;
              const sumGross = sourceRows.reduce((acc: number, inv: any) => acc + Number(inv.gross_amount || 0), 0);
              const sumLabel = hasSelection ? `Zaznaczone (${sourceRows.length}):` : 'Suma:';
              const cells: React.ReactNode[] = [];
              if (beforeCount > 0) {
                cells.push(
                  <TableCell key="sum-label" colSpan={beforeCount} align="right"
                    sx={{ p: '6px 12px', height: 32, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: hasSelection ? '#1565C0' : '#020242' }}>
                    {sumLabel}
                  </TableCell>
                );
              }
              if (grossIdx >= 0) {
                cells.push(
                  <TableCell key="sum-gross" align="right"
                    sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: hasSelection ? '#1565C0' : '#020242' }}>
                    {formatMoney(sumGross)}
                  </TableCell>
                );
                const remaining = visible.length - grossIdx - 1;
                for (let i = 0; i < remaining; i++) {
                  cells.push(<TableCell key={`sum-empty-${i}`} sx={{ backgroundColor: '#F1F1F6' }} />);
                }
              }
              return (
                <TableRow data-sum-row sx={{ position: 'sticky', bottom: 0, backgroundColor: '#F1F1F6', zIndex: 1, '& .MuiTableCell-root': { borderBottom: 'none' } }}>
                  {cells}
                </TableRow>
              );
            })()}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Kebab menu rozrachunków */}
      <Menu
        anchorEl={settleMenuAnchor}
        open={Boolean(settleMenuAnchor)}
        onClose={() => { setSettleMenuAnchor(null); setSettleMenuRow(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 160, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', borderRadius: '8px' } } }}
      >
        <MenuItem onClick={() => {
          if (settleMenuRow) {
            const editId = settleMenuRow.id;
            // Mapowanie pól rozrachunków na pola oczekiwane przez formularz edycji
            const editData = {
              id: settleMenuRow.id,
              document_type: settleMenuRow.document_type,
              document_date: settleMenuRow.created_date,
              received_date: settleMenuRow.created_date,
              project_id: settleMenuRow.project_code,
              contractor_name: settleMenuRow.contractor,
              nip: settleMenuRow.nip,
              contractor_address: settleMenuRow.contractor_address || '',
              contractor_postal_city: settleMenuRow.contractor_postal_city || '',
              ksef_number: settleMenuRow.ksef_number || '',
              invoice_number: settleMenuRow.invoice_number,
              net_amount: Number(settleMenuRow.net_amount || 0),
              vat_rate: settleMenuRow.vat_rate || '23',
              gross_amount: Number(settleMenuRow.gross_amount || 0),
              due_date: settleMenuRow.due_date,
              paid_date: settleMenuRow.paid_date,
              priority: 'zwykły',
              account_number: settleMenuRow.account_number,
              warehouse: settleMenuRow.warehouse,
              google_drive_file_id: settleMenuRow.google_drive_file_id,
              google_drive_file_url: settleMenuRow.google_drive_file_url,
              status: settleMenuRow.status,
            };
            sessionStorage.setItem(`edit-invoice-${editId}`, JSON.stringify(editData));
            window.open(`/accounting/edit/${encodeURIComponent(editId)}`, '_blank');
          }
          setSettleMenuAnchor(null); setSettleMenuRow(null);
        }}>
          <Edit fontSize="small" sx={{ mr: 1, color: '#595966' }} /> Edytuj
        </MenuItem>
      </Menu>
      </>)}
      {/* === KONIEC SEKCJI ROZRACHUNKI === */}

      {/* === SEKCJA OPERACJE BANKOWE (sectionChip === 6) === */}
      {sectionChip === 6 && (<>
      {/* Pasek zakładek głównych: Wypłaty / Wpłaty */}
      <Box sx={{ mt: '28px', mb: 2, borderBottom: '1px solid #EDEDED', display: 'flex', gap: 3, alignItems: 'flex-end' }}>
        {[{ label: 'Wypłaty', badge: null }, { label: 'Wpłaty', badge: null }].map(({ label }, idx) => (
          <Box
            key={label}
            onClick={() => setBankOpsMainTab(idx)}
            sx={{
              position: 'relative', pb: 1, cursor: 'pointer', fontFamily: 'Roboto, sans-serif',
              fontSize: '16px', color: '#020242', fontWeight: bankOpsMainTab === idx ? 500 : 400,
              userSelect: 'none', display: 'inline-flex', alignItems: 'center', pr: '12px'
            }}
          >
            <Box component="span" sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              {label}
            </Box>
            {bankOpsMainTab === idx && (
              <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, bgcolor: '#000000' }} />
            )}
          </Box>
        ))}
      </Box>

      {/* Filtry */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(200px, 250px) minmax(200px, 250px) minmax(150px, 200px) minmax(150px, 200px) minmax(150px, 200px)',
          columnGap: '24px',
          alignItems: 'start',
          height: 56,
          mb: 2,
          bgcolor: '#FAFAFA'
        }}
      >
        {/* Szukaj */}
        <TextField
          size="small"
          placeholder="Szukaj"
          value={bankOpsQuery}
          onChange={(e) => setBankOpsQuery(e.target.value)}
          variant="outlined"
          inputProps={{
            'data-role': 'page-filter',
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); try { (e.currentTarget as HTMLInputElement).blur(); } catch {} }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ width: 20, height: 20, color: '#595966' }} />
              </InputAdornment>
            )
          }}
          sx={{
            width: '100%', mt: '20px',
            '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
            '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
            '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
          }}
        />

        {/* Projekt */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Projekt</Typography>
          <Autocomplete
            size="small"
            options={activeProjects}
            getOptionLabel={(p) => `${p.id} ${p.name}`}
            value={activeProjects.find((p) => p.id === bankOpsProjectFilter) || null}
            onChange={(_, val) => setBankOpsProjectFilter(val ? val.id : '')}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Wybierz z listy"
                sx={{
                  '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px', pr: '8px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                  '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: bankOpsProjectFilter ? '#2D2D32' : '#B9B9B9' }
                }}
              />
            )}
            clearOnBlur={false}
          />
        </Box>

        {/* Miesiąc */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Miesiąc</Typography>
          <Select
            size="small"
            value={bankOpsMonthFilter}
            displayEmpty
            onChange={(e) => setBankOpsMonthFilter(String(e.target.value))}
            renderValue={bankOpsMonthFilter !== '' ? () => (monthOptions(18).find((m) => m.key === bankOpsMonthFilter)?.label || '') : () => 'Wybierz z listy'}
            IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
            sx={{
              height: 36, pr: '8px',
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: bankOpsMonthFilter ? '#2D2D32' : '#B9B9B9' },
              backgroundColor: '#fff', borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {monthOptions(18).map((m) => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
          </Select>
        </Box>

        {/* Data od */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data od</Typography>
          <TextField
            size="small" type="date" value={bankOpsDateFrom}
            onChange={(e) => setBankOpsDateFrom(String(e.target.value))}
            variant="outlined" inputRef={bankOpsDateFromRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ cursor: 'pointer' }} onClick={() => { try { (bankOpsDateFromRef.current as any)?.showPicker?.(); } catch { bankOpsDateFromRef.current?.focus(); } }}>
                  <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                </InputAdornment>
              )
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: bankOpsDateFrom ? '#2D2D32' : '#B9B9B9' },
              '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
              '& input::-ms-clear': { display: 'none' }
            }}
          />
        </Box>

        {/* Data do */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data do</Typography>
          <TextField
            size="small" type="date" value={bankOpsDateTo}
            onChange={(e) => setBankOpsDateTo(String(e.target.value))}
            variant="outlined" inputRef={bankOpsDateToRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ cursor: 'pointer' }} onClick={() => { try { (bankOpsDateToRef.current as any)?.showPicker?.(); } catch { bankOpsDateToRef.current?.focus(); } }}>
                  <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                </InputAdornment>
              )
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: bankOpsDateTo ? '#2D2D32' : '#B9B9B9' },
              '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
              '& input::-ms-clear': { display: 'none' }
            }}
          />
        </Box>
      </Box>

      {/* Sub-zakładki: Niezrealizowane / Zrealizowane / Uzgodnione + ikony */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
        <Tabs
          value={bankOpsSubTab}
          onChange={(_, v) => setBankOpsSubTab(v)}
          aria-label="bank ops sub tabs"
          sx={{ minHeight: 40, height: 40, flexGrow: 1, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTabs-flexContainer': { gap: '2px' } }}
        >
          <Tab
            disableRipple
            label={`Niezrealizowane (${bankOpsSubTabCounts.niezrealizowane})`}
            sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
          />
          <Tab
            disableRipple
            label={`Zrealizowane (${bankOpsSubTabCounts.zrealizowane})`}
            sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
          />
          <Tab
            disableRipple
            label={`Uzgodnione (${bankOpsSubTabCounts.uzgodnione})`}
            sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }, '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' } }}
          />
        </Tabs>
        {bankOpsMainTab === 0 && bankOpsSubTab === 0 && (
        <>
        <Tooltip title={bankOpsSelectedIds.size > 0 ? `Eksportuj przelewy (${bankOpsSelectedIds.size}) do mBank` : 'Zaznacz rekordy, aby eksportować przelewy'}>
          <span>
            <IconButton
              size="small"
              disabled={bankOpsSelectedIds.size === 0}
              sx={{
                width: 32, height: 32, borderRadius: '4px', alignSelf: 'center', p: 0,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                '&.Mui-disabled': { opacity: 1 }
              }}
              onClick={async () => {
                if (bankOpsSelectedIds.size === 0) return;
                try {
                  const ids = Array.from(bankOpsSelectedIds);
                  const blob = await exportMbank(ids);
                  // Pobierz plik
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  const dateNow = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                  a.download = `przelewy_mbank_${dateNow}.txt`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                  // Odśwież dane i wyczyść zaznaczenie
                  setBankOpsSelectedIds(new Set());
                  setBankOpsLastClickedIdx(null);
                  await refreshBankOps();
                } catch (e) {
                  console.error('Błąd eksportu mBank:', e);
                  alert('Błąd eksportu przelewów do mBank');
                }
              }}
            >
              <Box component="img" src="/assets/icons/mbank-icon.webp" alt="Eksport mBank" sx={{ width: 16, height: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={bankOpsSelectedIds.size > 0 ? `Eksportuj przelewy (${bankOpsSelectedIds.size}) do Santander` : 'Zaznacz rekordy, aby eksportować przelewy'}>
          <span>
            <IconButton
              size="small"
              disabled={bankOpsSelectedIds.size === 0}
              sx={{
                width: 32, height: 32, borderRadius: '4px', alignSelf: 'center', p: 0,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                '&.Mui-disabled': { opacity: 1 }
              }}
              onClick={async () => {
                if (bankOpsSelectedIds.size === 0) return;
                // TODO: eksport Santander - format do uzupełnienia
                alert('Eksport do Santander - format zostanie dodany wkrótce');
              }}
            >
              <Box component="img" src="/assets/icons/santander-icon.webp" alt="Eksport Santander" sx={{ width: 16, height: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={bankOpsSelectedIds.size > 0 ? `Eksportuj przelewy (${bankOpsSelectedIds.size}) do Pekao` : 'Zaznacz rekordy, aby eksportować przelewy'}>
          <span>
            <IconButton
              size="small"
              disabled={bankOpsSelectedIds.size === 0}
              sx={{
                width: 32, height: 32, borderRadius: '4px', alignSelf: 'center', p: 0,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                '&.Mui-disabled': { opacity: 1 }
              }}
              onClick={async () => {
                if (bankOpsSelectedIds.size === 0) return;
                // TODO: eksport Pekao - format do uzupełnienia
                alert('Eksport do Pekao - format zostanie dodany wkrótce');
              }}
            >
              <Box component="img" src="/assets/icons/pekao-icon.jpeg" alt="Eksport Pekao" sx={{ width: 16, height: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
        </>
        )}
        <IconButton
          size="small"
          title="Dostosuj kolumny"
          aria-label="Dostosuj kolumny"
          sx={{ width: 32, height: 32, borderRadius: '4px', alignSelf: 'center', p: 0, bgcolor: colsOpenBankOps ? '#E0E0F5' : 'transparent', transition: 'background-color 0.15s ease', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}
          onClick={(e) => { setColResizeMode(true); setColsAnchorElBankOps(e.currentTarget); }}
        >
          <Box component="img" alt="kolumny" src={'/assets/icons/frame-344.svg'} sx={{ width: 32, height: 32 }} />
        </IconButton>
      </Box>

      {/* Tabela operacji bankowych */}
      <TableContainer
        component={Paper}
        sx={{ height: tableHeight ?? 432, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderBottom: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: 'fixed', width: '100%',
            borderCollapse: 'separate', borderSpacing: 0,
            '& .MuiTableCell-root': { padding: '5px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
            '& .MuiTableCell-head': { padding: '1px 4px', position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
            '& .MuiTableCell-body': { borderRight: 'none', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 0 rgba(224,224,224,1)', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
            '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
          }}
        >
          <colgroup>
            {(draftOrderBankOps ?? columnOrderBankOps).filter(colVBankOps).map((key) => (
              <col key={key} style={{ width: getColWBankOps(key) }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {(draftOrderBankOps ?? columnOrderBankOps).map((key) => {
                if (!colVBankOps(key)) return null;
                const label = bankOpsColLabel(key, bankOpsMainTab === 1);
                const align = (key === 'grossAmount' || key === 'vatAmount') ? 'right' as const : undefined;
                const headerContent = (
                  <TableSortLabel
                    {...getSortLabelProps(false)}
                    active={false}
                    direction="asc"
                  >
                    {label}
                  </TableSortLabel>
                );
                return <React.Fragment key={key}>{renderResizableThBankOps(key, headerContent, align)}</React.Fragment>;
              })}
              {bankOpsSubTab === 1 && (
                <TableCell sx={{ width: 40, p: 0, position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA' }} />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {bankOpsLoading ? (
              <TableRow>
                <TableCell colSpan={(draftOrderBankOps ?? columnOrderBankOps).filter(colVBankOps).length} align="center" sx={{ py: 3 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966' }}>
                    Ładowanie operacji bankowych...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredBankOpsRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={(draftOrderBankOps ?? columnOrderBankOps).filter(colVBankOps).length} align="center" sx={{ py: 3 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966' }}>
                    Brak operacji do wyświetlenia
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredBankOpsRows.map((row: any, rowIdx: number) => {
                const ellipsisSx = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 } as const;
                const isSelected = bankOpsSelectedIds.has(row.id);
                const prevSelected = rowIdx > 0 && filteredBankOpsRows[rowIdx - 1] && bankOpsSelectedIds.has(filteredBankOpsRows[rowIdx - 1].id);
                const nextSelected = rowIdx < filteredBankOpsRows.length - 1 && filteredBankOpsRows[rowIdx + 1] && bankOpsSelectedIds.has(filteredBankOpsRows[rowIdx + 1].id);
                return (
                  <TableRow
                    key={row.id || rowIdx}
                    hover
                    onClick={(e) => handleBankOpsRowClick(rowIdx, row.id, e)}
                    sx={{
                      cursor: 'default',
                      userSelect: 'none',
                      position: 'relative',
                      backgroundColor: isSelected ? '#D6E4F9' : undefined,
                      '& td.MuiTableCell-body': {
                        boxShadow: isSelected
                          ? [
                              !prevSelected ? 'inset 0 2px 0 0 #4285F4' : '',
                              !nextSelected ? 'inset 0 -2px 0 0 #4285F4' : '',
                            ].filter(Boolean).join(', ') || 'none'
                          : undefined,
                      },
                      '& td.MuiTableCell-body:first-of-type': {
                        position: isSelected ? 'relative' as const : undefined,
                        boxShadow: isSelected
                          ? [
                              !prevSelected ? 'inset 0 2px 0 0 #4285F4' : '',
                              !nextSelected ? 'inset 0 -2px 0 0 #4285F4' : '',
                            ].filter(Boolean).join(', ') || 'none'
                          : undefined,
                        '&::after': isSelected ? {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: prevSelected ? '-1px' : 0,
                          bottom: nextSelected ? '-1px' : 0,
                          width: '2px',
                          backgroundColor: '#4285F4',
                          zIndex: 1,
                          pointerEvents: 'none',
                        } : { content: 'none' },
                      },
                      '& td.MuiTableCell-body:last-of-type': {
                        position: isSelected ? 'relative' as const : undefined,
                        boxShadow: isSelected
                          ? [
                              !prevSelected ? 'inset 0 2px 0 0 #4285F4' : '',
                              !nextSelected ? 'inset 0 -2px 0 0 #4285F4' : '',
                            ].filter(Boolean).join(', ') || 'none'
                          : undefined,
                        '&::after': isSelected ? {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: prevSelected ? '-1px' : 0,
                          bottom: nextSelected ? '-1px' : 0,
                          width: '2px',
                          backgroundColor: '#4285F4',
                          zIndex: 1,
                          pointerEvents: 'none',
                        } : { content: 'none' },
                      },
                      '&:hover': {
                        backgroundColor: isSelected ? '#C4D7F2 !important' : '#F5F5FF !important'
                      }
                    }}
                  >
                    {(draftOrderBankOps ?? columnOrderBankOps).map((key) => {
                      if (!colVBankOps(key)) return null;
                      if (key === 'operationRef') return <TableCell key={key} sx={{ ...ellipsisSx, fontFamily: 'Roboto Mono, monospace', fontSize: '0.75rem', color: '#1565C0', fontWeight: 500 }}>{row.operationRef || '-'}</TableCell>;
                      if (key === 'status') {
                        const isRealized = row.status === 'zrealizowane';
                        const isReconciled = row.status === 'uzgodnione';
                        const chipBg = isReconciled ? '#E8F5E9' : isRealized ? '#E3F2FD' : '#FFF3E0';
                        const chipColor = isReconciled ? '#2E7D32' : isRealized ? '#1565C0' : '#E65100';
                        const chipLabel = isReconciled ? 'Uzgodnione' : isRealized ? 'Zrealizowane' : 'Niezrealizowane';
                        return (
                          <TableCell key={key} sx={ellipsisSx}>
                            <Box sx={{ display: 'inline-block', px: 1, py: '2px', borderRadius: '4px', bgcolor: chipBg, color: chipColor, fontSize: '12px', fontWeight: 500, fontFamily: 'Roboto, sans-serif' }}>
                              {chipLabel}
                            </Box>
                          </TableCell>
                        );
                      }
                      if (key === 'operationDate') return <TableCell key={key} sx={ellipsisSx}>{row.operationDate ? String(row.operationDate).slice(0, 10) : '-'}</TableCell>;
                      if (key === 'contractor') return (
                        <TableCell key={key} sx={ellipsisSx}>
                          <Tooltip title={row.contractor || ''} arrow>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.contractor || '-'}</Box>
                          </Tooltip>
                        </TableCell>
                      );
                      if (key === 'nip') return <TableCell key={key} sx={ellipsisSx}>{row.nip || '-'}</TableCell>;
                      if (key === 'transferTitle') return (
                        <TableCell key={key} sx={ellipsisSx}>
                          <Tooltip title={row.transferTitle || ''} arrow>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.transferTitle || '-'}</Box>
                          </Tooltip>
                        </TableCell>
                      );
                      if (key === 'paymentType') {
                        const typeLabel = row.paymentType || '-';
                        const chipBg = typeLabel === 'przelew PP' ? '#F3E5F5' : '#E3F2FD';
                        const chipColor = typeLabel === 'przelew PP' ? '#7B1FA2' : '#1565C0';
                        return (
                          <TableCell key={key} sx={ellipsisSx}>
                            <Box sx={{ display: 'inline-block', px: 1, py: '2px', borderRadius: '4px', bgcolor: chipBg, color: chipColor, fontSize: '12px', fontWeight: 500, fontFamily: 'Roboto, sans-serif' }}>
                              {typeLabel}
                            </Box>
                          </TableCell>
                        );
                      }
                      if (key === 'grossAmount') return <TableCell key={key} align="right" sx={ellipsisSx}>{formatMoney(Number(row.grossAmount || 0))}</TableCell>;
                      if (key === 'vatAmount') return <TableCell key={key} align="right" sx={ellipsisSx}>{formatMoney(Number(row.vatAmount || 0))}</TableCell>;
                      if (key === 'contractorAccount') return <TableCell key={key} sx={{ ...ellipsisSx, fontSize: '12px' }}><Tooltip title={formatAccountNumber(row.contractorAccount || '')} arrow><span>{row.contractorAccount ? shortenAccountNumber(row.contractorAccount) : '-'}</span></Tooltip></TableCell>;
                      if (key === 'unitrescoAccount') return <TableCell key={key} sx={{ ...ellipsisSx, fontSize: '12px' }}><Tooltip title={formatAccountNumber(row.unitrescoAccount || '')} arrow><span>{row.unitrescoAccount ? shortenAccountNumber(row.unitrescoAccount) : '-'}</span></Tooltip></TableCell>;
                      if (key === 'statementNumber') return <TableCell key={key} sx={ellipsisSx}>{row.statementNumber || '-'}</TableCell>;
                      if (key === 'reconciliationBank') return <TableCell key={key} sx={ellipsisSx}>{row.reconciliationBankName || '-'}</TableCell>;
                      if (key === 'reconciliationStmtNum') return <TableCell key={key} sx={ellipsisSx}>{row.reconciliationStatementNumber || '-'}</TableCell>;
                      if (key === 'reconciliationStmtDate') return <TableCell key={key} sx={ellipsisSx}>{row.reconciliationStatementDate ? String(row.reconciliationStatementDate).slice(0, 10) : '-'}</TableCell>;
                      return null;
                    })}
                    {bankOpsSubTab === 1 && (
                      <TableCell align="center" sx={{ width: 40, p: 0 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setBankOpsMenuRow(row); setBankOpsMenuAnchor(e.currentTarget); }}
                          sx={{ p: '2px' }}
                        >
                          <MoreVert sx={{ fontSize: 18, color: '#595966' }} />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
            {/* Wiersz podsumowania operacji bankowych */}
            {filteredBankOpsRows.length > 0 && (() => {
              const visible = (draftOrderBankOps ?? columnOrderBankOps).filter(colVBankOps);
              const grossIdx = visible.indexOf('grossAmount');
              const vatIdx = visible.indexOf('vatAmount');
              const hasSelection = bankOpsSelectedIds.size > 0;
              const sourceRows = hasSelection
                ? filteredBankOpsRows.filter((r: any) => bankOpsSelectedIds.has(r.id))
                : filteredBankOpsRows;
              const sumGross = sourceRows.reduce((acc: number, r: any) => acc + Number(r.grossAmount || r.gross_amount || 0), 0);
              const sumVat = sourceRows.reduce((acc: number, r: any) => acc + Number(r.vatAmount || r.vat_amount || 0), 0);
              const sumLabel = hasSelection ? `Zaznaczone (${sourceRows.length}):` : 'Suma:';
              // Oblicz beforeIdx = min z indeksów grossAmount i vatAmount
              const firstSumIdx = Math.min(grossIdx >= 0 ? grossIdx : Infinity, vatIdx >= 0 ? vatIdx : Infinity);
              const beforeCount = firstSumIdx < Infinity ? firstSumIdx : visible.length;
              const cells: React.ReactNode[] = [];
              if (beforeCount > 0) {
                cells.push(
                  <TableCell key="bo-sum-label" colSpan={beforeCount} align="right"
                    sx={{ p: '6px 12px', height: 32, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: hasSelection ? '#1565C0' : '#020242' }}>
                    {sumLabel}
                  </TableCell>
                );
              }
              // Iteruj po widocznych kolumnach od firstSumIdx do końca
              for (let i = firstSumIdx; i < visible.length; i++) {
                const colKey = visible[i];
                if (colKey === 'grossAmount') {
                  cells.push(
                    <TableCell key="bo-sum-gross" align="right"
                      sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: hasSelection ? '#1565C0' : '#020242' }}>
                      {formatMoney(sumGross)}
                    </TableCell>
                  );
                } else if (colKey === 'vatAmount') {
                  cells.push(
                    <TableCell key="bo-sum-vat" align="right"
                      sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: hasSelection ? '#1565C0' : '#020242' }}>
                      {formatMoney(sumVat)}
                    </TableCell>
                  );
                } else {
                  cells.push(<TableCell key={`bo-sum-empty-${i}`} sx={{ backgroundColor: '#F1F1F6' }} />);
                }
              }
              // Dodaj pustą komórkę na kebab menu jeśli jest widoczne (sub-tab zrealizowane)
              if (bankOpsSubTab === 1) {
                cells.push(<TableCell key="bo-sum-kebab" sx={{ backgroundColor: '#F1F1F6', width: 40 }} />);
              }
              return (
                <TableRow data-sum-row sx={{ position: 'sticky', bottom: 0, backgroundColor: '#F1F1F6', zIndex: 1, '& .MuiTableCell-root': { borderBottom: 'none' } }}>
                  {cells}
                </TableRow>
              );
            })()}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Kebab menu operacji bankowych – zmiana statusu */}
      <Menu
        anchorEl={bankOpsMenuAnchor}
        open={Boolean(bankOpsMenuAnchor)}
        onClose={() => { setBankOpsMenuAnchor(null); setBankOpsMenuRow(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 200, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', borderRadius: '8px' } } }}
      >
        <MenuItem onClick={async () => {
          if (bankOpsMenuRow) {
            try {
              await updateBankOperationStatus(bankOpsMenuRow.id, 'niezrealizowane');
              await refreshBankOps();
            } catch (e) {
              console.error('Błąd zmiany statusu:', e);
            }
          }
          setBankOpsMenuAnchor(null);
          setBankOpsMenuRow(null);
        }}>
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '14px', fontFamily: 'Roboto, sans-serif' }}>
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#E65100', flexShrink: 0 }} />
            Zmień status na niezrealizowane
          </Box>
        </MenuItem>
      </Menu>
      </>)}
      {/* === KONIEC SEKCJI OPERACJE BANKOWE === */}

      {/* === SEKCJA WYCIĄGI I UZGODNIENIA (sectionChip === 7) === */}
      {sectionChip === 7 && (<>
      {/* Hidden file input do importu MT940 */}
      <input
        type="file"
        ref={bsFileInputRef}
        accept=".mt940,.sta,.txt"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setBsImporting(true);
          try {
            await importMT940(file);
            await refreshBankStatements();
          } catch (err) {
            console.error('Błąd importu MT940:', err);
            alert('Błąd importu pliku MT940');
          } finally {
            setBsImporting(false);
            if (bsFileInputRef.current) bsFileInputRef.current.value = '';
          }
        }}
      />

      {/* Pasek zakładek: Wyciągi / Uzgodnienia */}
      <Box sx={{ mt: '28px', mb: 2, borderBottom: '1px solid #EDEDED', display: 'flex', gap: 3, alignItems: 'flex-end' }}>
        {[{ label: 'Wyciągi' }, { label: 'Transakcje' }, { label: 'Uzgodnienia' }].map(({ label }, idx) => (
          <Box
            key={label}
            onClick={() => { setBsMainTab(idx); if (idx === 0) setBsSubTab(0); else if (idx === 1 && bsSubTab === 0) setBsSubTab(1); }}
            sx={{
              position: 'relative', pb: 1, cursor: 'pointer', fontFamily: 'Roboto, sans-serif',
              fontSize: '16px', color: '#020242', fontWeight: bsMainTab === idx ? 500 : 400,
              userSelect: 'none', display: 'inline-flex', alignItems: 'center', pr: '12px'
            }}
          >
            <Box component="span" sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              {label}
            </Box>
            {bsMainTab === idx && (
              <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, bgcolor: '#000000' }} />
            )}
          </Box>
        ))}
      </Box>

      {/* ===== KARTA WYCIĄGI ===== */}
      {(bsMainTab === 0 || bsMainTab === 1) && (<>
      {/* Filtry */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 250px) minmax(280px, 340px) minmax(90px, 120px) minmax(110px, 150px) minmax(130px, 180px) minmax(130px, 180px) minmax(100px, 140px)', columnGap: '24px', alignItems: 'start', height: 56, mb: 2, bgcolor: '#FAFAFA' }}>
        <TextField size="small" placeholder="Szukaj" value={bsQuery} onChange={(e) => setBsQuery(e.target.value)} variant="outlined"
          inputProps={{ 'data-role': 'page-filter', onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); try { (e.currentTarget as HTMLInputElement).blur(); } catch {} } } }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><Search sx={{ width: 20, height: 20, color: '#595966' }} /></InputAdornment>) }}
          sx={{ width: '100%', mt: '20px', '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' }, '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }, '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }, '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 } }}
        />
        {/* Rachunek */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Rachunek</Typography>
          <Select size="small" value={bsAccountFilter} displayEmpty onChange={(e) => setBsAccountFilter(String(e.target.value))}
            renderValue={bsAccountFilter !== '' ? () => { const found = bsAccountOptions.find(a => a.raw === bsAccountFilter); return found ? `${found.formatted}` : bsAccountFilter; } : () => 'Wybierz z listy'}
            IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
            sx={{ height: 36, pr: '8px', '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: bsAccountFilter ? '#2D2D32' : '#B9B9B9' }, backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' }, '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' }, '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' }, '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' } }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {bsAccountOptions.map((a) => (<MenuItem key={a.raw} value={a.raw} sx={{ fontFamily: 'Roboto Mono, monospace', fontSize: '12px' }}>{a.formatted} – {a.description} ({a.currency})</MenuItem>))}
          </Select>
        </Box>
        {/* Rok */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Rok</Typography>
          <Select size="small" value={bsYearFilter} displayEmpty onChange={(e) => setBsYearFilter(String(e.target.value))}
            renderValue={bsYearFilter !== '' ? () => bsYearFilter : () => 'Wszystkie'}
            IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
            sx={{ height: 36, pr: '8px', '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: bsYearFilter ? '#2D2D32' : '#B9B9B9' }, backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' }, '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' }, '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' }, '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' } }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {(() => { const now = new Date(); return Array.from({ length: 5 }, (_, i) => String(now.getFullYear() - i)); })().map((y) => (<MenuItem key={y} value={y}>{y}</MenuItem>))}
          </Select>
        </Box>
        {/* Miesiąc */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Miesiąc</Typography>
          <Select size="small" value={bsMonthOnlyFilter} displayEmpty onChange={(e) => setBsMonthOnlyFilter(String(e.target.value))}
            renderValue={bsMonthOnlyFilter !== '' ? () => (['Styczeń','Luty','Marzec','Kwiecień','Maj','Czerwiec','Lipiec','Sierpień','Wrzesień','Październik','Listopad','Grudzień'][Number(bsMonthOnlyFilter) - 1] || '') : () => 'Wszystkie'}
            IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
            sx={{ height: 36, pr: '8px', '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: bsMonthOnlyFilter ? '#2D2D32' : '#B9B9B9' }, backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' }, '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' }, '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' }, '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' } }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {['Styczeń','Luty','Marzec','Kwiecień','Maj','Czerwiec','Lipiec','Sierpień','Wrzesień','Październik','Listopad','Grudzień'].map((name, i) => (<MenuItem key={i} value={String(i + 1).padStart(2, '0')}>{name}</MenuItem>))}
          </Select>
        </Box>
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data od</Typography>
          <TextField size="small" type="date" value={bsDateFrom} onChange={(e) => setBsDateFrom(e.target.value)} variant="outlined" InputLabelProps={{ shrink: true }}
            inputRef={(el: HTMLInputElement | null) => { if (el) (el as any)._bsDateFromRef = el; }}
            InputProps={{ startAdornment: (<InputAdornment position="start" sx={{ cursor: 'pointer' }} onClick={(e) => { const input = (e.currentTarget.parentElement?.querySelector('input') as HTMLInputElement); input?.showPicker?.(); input?.focus(); }}><Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" /></InputAdornment>) }}
            sx={{ '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' }, '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }, '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: bsDateFrom ? '#2D2D32' : '#B9B9B9', cursor: 'pointer' }, '& input[type="date"]::-webkit-calendar-picker-indicator': { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }, '& input[type="date"]::-webkit-inner-spin-button': { display: 'none' } }}
          />
        </Box>
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data do</Typography>
          <TextField size="small" type="date" value={bsDateTo} onChange={(e) => setBsDateTo(e.target.value)} variant="outlined" InputLabelProps={{ shrink: true }}
            InputProps={{ startAdornment: (<InputAdornment position="start" sx={{ cursor: 'pointer' }} onClick={(e) => { const input = (e.currentTarget.parentElement?.querySelector('input') as HTMLInputElement); input?.showPicker?.(); input?.focus(); }}><Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" /></InputAdornment>) }}
            sx={{ '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' }, '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }, '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: bsDateTo ? '#2D2D32' : '#B9B9B9', cursor: 'pointer' }, '& input[type="date"]::-webkit-calendar-picker-indicator': { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }, '& input[type="date"]::-webkit-inner-spin-button': { display: 'none' } }}
          />
        </Box>
        {/* Uzgodnienie */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Uzgodnienie</Typography>
          <Select size="small" value={bsReconciledFilter} displayEmpty onChange={(e) => setBsReconciledFilter(String(e.target.value))}
            renderValue={bsReconciledFilter !== '' ? () => (bsReconciledFilter === 'yes' ? 'Tak' : 'Nie') : () => 'Wszystkie'}
            IconComponent={(iconProps:any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }} />)}
            sx={{ height: 36, pr: '8px', '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: bsReconciledFilter ? '#2D2D32' : '#B9B9B9' }, backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' }, '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' }, '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' }, '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' } }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            <MenuItem value="yes">Tak</MenuItem>
            <MenuItem value="no">Nie</MenuItem>
          </Select>
        </Box>
      </Box>

      {/* Sub-zakładki: Transakcje + przycisk Dostosuj kolumny */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
        {bsMainTab === 1 && (() => {
          const subTabs = [
            { label: `Wszystkie (${filteredBsTransactions.length})`, origIdx: 1 },
            { label: `Wpłaty (${filteredBsTxCredits.length})`, origIdx: 2 },
            { label: `Wypłaty (${filteredBsTxDebits.length})`, origIdx: 3 },
            { label: `Wypłaty z rach. VAT (${filteredBsTxVatCredits.length})`, origIdx: 4 },
          ];
          const tabsValue = subTabs.findIndex(t => t.origIdx === bsSubTab);
          return (
          <Tabs value={tabsValue >= 0 ? tabsValue : 0} onChange={(_, visualIdx) => {
            const actualIdx = subTabs[visualIdx].origIdx;
            // Zapisz kolumny bieżącej zakładki PRZED przełączeniem
            saveBsColSettings(bsSubTab, colsVisBs, colsOrdBs, colsWidBs);
            // Natychmiast załaduj kolumny nowej zakładki
            loadBsColSettings(actualIdx);
            // Wyczyść draft kolumn
            setDraftColsVisBs(null); setDraftColsOrdBs(null);
            setBsSubTab(actualIdx);
          }} aria-label="bs sub tabs" variant="scrollable" scrollButtons="auto"
            sx={{ minHeight: 40, height: 40, flexGrow: 1, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTabs-flexContainer': { gap: '2px' }, '& .MuiTabs-scrollButtons': { width: 24, minWidth: 24 } }}>
            {subTabs.map((t) => (
              <Tab key={t.origIdx} disableRipple label={t.label}
                sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } }} />
            ))}
          </Tabs>
          );
        })()}
        {bsMainTab === 0 && (() => {
          const bankTabs = ['Wszystkie', ...bsBankOptions];
          const bankTabsValue = bsBankFilter === '' ? 0 : bankTabs.indexOf(bsBankFilter);
          return (
          <Tabs value={bankTabsValue >= 0 ? bankTabsValue : 0} onChange={(_, idx) => { setBsBankFilter(idx === 0 ? '' : bankTabs[idx]); setBsAccountFilter(''); }} aria-label="bs bank tabs" variant="scrollable" scrollButtons="auto"
            sx={{ minHeight: 40, height: 40, flexGrow: 1, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTabs-flexContainer': { gap: '2px' }, '& .MuiTabs-scrollButtons': { width: 24, minWidth: 24 } }}>
            {bankTabs.map((bankLabel) => (
              <Tab key={bankLabel} disableRipple label={bankLabel}
                sx={{ textTransform: 'none', color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, px: 3, minHeight: 32, height: 32, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } }} />
            ))}
          </Tabs>
          );
        })()}
        <Tooltip title={bsSelectedIds.size > 0 ? `Operacje na wielu (${bsSelectedIds.size})` : 'Zaznacz rekordy, aby wykonać operacje na wielu'}>
          <span>
            <IconButton size="small" disabled={bsSelectedIds.size === 0}
              onClick={() => setBsBulkDrawerOpen(true)}
              sx={{ width: 32, height: 32, borderRadius: '4px', alignSelf: 'center', p: 0, '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }, '&.Mui-disabled': { opacity: 0.4 } }}>
              <Box component="img" src="/assets/icons/playlist_add_check.svg" alt="Operacje na wielu" sx={{ width: 22, height: 22 }} />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" title="Dostosuj kolumny" aria-label="Dostosuj kolumny"
          sx={{ width: 32, height: 32, borderRadius: '4px', alignSelf: 'center', p: 0, bgcolor: colsOpenBs ? '#E0E0F5' : 'transparent', transition: 'background-color 0.15s ease', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}
          onClick={(e) => { setColResizeMode(true); setColsAnchorElBs(e.currentTarget); }}>
          <Box component="img" alt="kolumny" src="/assets/icons/frame-344.svg" sx={{ width: 32, height: 32 }} />
        </IconButton>
      </Box>

      {/* Tabela */}
      <TableContainer ref={bsTableContainerRef} component={Paper}
        sx={{ height: tableHeight ?? 432, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderBottom: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}>
        <Table size="small" stickyHeader
          sx={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'separate', borderSpacing: 0,
            '& .MuiTableCell-root': { padding: '5px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
            '& .MuiTableCell-head': { padding: '1px 4px', position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
            '& .MuiTableCell-body': { borderRight: 'none', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 0 rgba(224,224,224,1)', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
            '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 } }}>
          <colgroup>
            {activeBsColsOrd.filter(colVBs).map((key) => (
              <col key={key} style={{ width: getColWBs(key) }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {activeBsColsOrd.map((key) => {
                if (!colVBs(key)) return null;
                const label = bsSubTab === 0 ? bsStmtColLabel(key) : bsTxColLabel(key);
                const headerTooltip = bsSubTab >= 1 ? bsTxColTooltip(key) : null;
                const align = ['openingBalance','closingBalance','totalDebit','totalCredit','amount'].includes(key) ? 'right' as const : ['txCount','reconciled'].includes(key) ? 'center' as const : undefined;
                return (
                  <TableCell key={key} align={align} sx={{ position: 'relative', userSelect: colResizeMode ? 'none' : undefined, width: getColWBs(key), maxWidth: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {bsSubTab === 0 ? (
                      <TableSortLabel {...getSortLabelProps(bsSortKey === key)} active={bsSortKey === key} direction={bsSortKey === key ? bsSortDir : 'asc'} onClick={() => handleBsSort(key)}>
                        {label}
                      </TableSortLabel>
                    ) : (
                      <Tooltip title={headerTooltip || ''} arrow disableHoverListener={!headerTooltip}>
                        <span>{label}</span>
                      </Tooltip>
                    )}
                    {colResizeMode ? (
                      <Box role="separator" aria-orientation="vertical"
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setDragCol({ key, startX: e.clientX, startW: safeBsColsWid[key] ?? 120, tableType: 'bs' }); }}
                        sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '12px', minHeight: '100%', cursor: 'col-resize', zIndex: 15, touchAction: 'none', pointerEvents: 'auto', '&::before': { content: '""', position: 'absolute', left: '50%', top: 0, bottom: 0, width: '2px', transform: 'translateX(-50%)', bgcolor: colResizeMode ? 'rgba(211,47,47,0.45)' : 'transparent', transition: 'background-color 0.15s' }, '&:hover::before': { bgcolor: 'rgba(211,47,47,0.7)' } }} />
                    ) : null}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {bsLoading || bsImporting ? (
              <TableRow><TableCell colSpan={activeBsColsOrd.filter(colVBs).length} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : (bsSubTab === 0 ? filteredBsStatements : bsSubTabTxData(bsSubTab)).length === 0 ? (
              <TableRow><TableCell colSpan={activeBsColsOrd.filter(colVBs).length} align="center" sx={{ py: 3 }}>
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#595966' }}>{bsSubTab === 0 ? 'Brak wyciągów – zaimportuj plik MT940' : 'Brak transakcji'}</Typography>
              </TableCell></TableRow>
            ) : bsSubTab === 0 ? (
              /* Wiersze wyciągów — inteligentna paginacja (infinite scroll) */
              <>
              {filteredBsStatements.slice(0, bsVisibleCount).map((row, rowIdx) => {
                if (rowIdx === 0) bsVisibleRowsRef.current = filteredBsStatements;
                const isSelected = bsSelectedIds.has(row.id);
                const prevSelected = rowIdx > 0 && bsSelectedIds.has(filteredBsStatements[rowIdx - 1]?.id);
                const nextSelected = rowIdx < filteredBsStatements.length - 1 && bsSelectedIds.has(filteredBsStatements[rowIdx + 1]?.id);
                return (
                <TableRow key={row.id} hover onClick={(e) => handleBsRowClick(rowIdx, row.id, e)}
                  sx={{ cursor: 'default', userSelect: 'none', position: 'relative',
                    backgroundColor: isSelected ? '#D6E4F9' : undefined,
                    '& td.MuiTableCell-body': { boxShadow: isSelected ? [!prevSelected ? 'inset 0 2px 0 0 #4285F4' : '', !nextSelected ? 'inset 0 -2px 0 0 #4285F4' : ''].filter(Boolean).join(', ') || undefined : undefined },
                    '& td:first-of-type': isSelected ? { boxShadow: [!prevSelected ? 'inset 0 2px 0 0 #4285F4' : '', 'inset 2px 0 0 0 #4285F4', !nextSelected ? 'inset 0 -2px 0 0 #4285F4' : ''].filter(Boolean).join(', ') } : {},
                    '& td:last-of-type': isSelected ? { boxShadow: ['inset -2px 0 0 0 #4285F4', !prevSelected ? 'inset 0 2px 0 0 #4285F4' : '', !nextSelected ? 'inset 0 -2px 0 0 #4285F4' : ''].filter(Boolean).join(', ') } : {},
                  }}>
                  {activeBsColsOrd.map((key) => {
                    if (!colVBs(key)) return null;
                    const ellipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 } as const;
                    if (key === 'statementNumber') return <TableCell key={key} sx={{ fontWeight: 500 }}>{row.statement_number || '-'}</TableCell>;
                    if (key === 'bankName') return <TableCell key={key}>{row.bank_name || '-'}</TableCell>;
                    if (key === 'accountNumber') return <TableCell key={key} sx={{ fontFamily: 'Roboto Mono, monospace', fontSize: '0.75rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}>
                      {row.account_number ? (
                        <Tooltip title={formatAccountNumber(row.account_number)} arrow>
                          <span>{formatAccountNumber(row.account_number)}</span>
                        </Tooltip>
                      ) : '-'}
                    </TableCell>;
                    if (key === 'closingDate') return <TableCell key={key}>{row.closing_date || '-'}</TableCell>;
                    if (key === 'currency') return <TableCell key={key}>{row.currency}</TableCell>;
                    if (key === 'openingBalance') return <TableCell key={key} align="right">{row.opening_balance != null ? formatMoney(Number(row.opening_balance)) : '-'}</TableCell>;
                    if (key === 'closingBalance') return <TableCell key={key} align="right">{row.closing_balance != null ? formatMoney(Number(row.closing_balance)) : '-'}</TableCell>;
                    if (key === 'totalDebit') return <TableCell key={key} align="right" sx={{ color: '#D92D20' }}>{formatMoney(Number(row.total_debit))}</TableCell>;
                    if (key === 'totalCredit') return <TableCell key={key} align="right" sx={{ color: '#2E7D32' }}>{formatMoney(Number(row.total_credit))}</TableCell>;
                    if (key === 'txCount') return <TableCell key={key} align="center">{row.transaction_count_actual || row.transaction_count}</TableCell>;
                    if (key === 'sourceFilename') return <TableCell key={key} sx={ellipsis}>{row.source_filename ? <Box component="span" sx={{ color: '#1565C0', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, cursor: 'pointer', fontSize: 'inherit' }} onClick={async (e: React.MouseEvent) => { e.stopPropagation(); try { const resp = await api.get(`/bank-statements/${row.id}/file`, { responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([resp.data])); const a = document.createElement('a'); a.href = url; a.download = row.source_filename || 'mt940.txt'; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url); } catch { /* ignore */ } }}>{row.source_filename}</Box> : '-'}</TableCell>;
                    return <TableCell key={key}>-</TableCell>;
                  })}
                </TableRow>
                );
              })}
              {/* Sentinel row — niewidoczny wiersz triggerujący doładowanie kolejnej porcji */}
              {bsVisibleCount < filteredBsStatements.length && (
                <TableRow ref={bsSentinelRef} sx={{ height: 1, p: 0, border: 'none', '& td': { p: 0, border: 'none', height: 1 } }}>
                  <TableCell colSpan={activeBsColsOrd.filter(colVBs).length} />
                </TableRow>
              )}
              </>
            ) : (
              /* Wiersze transakcji — inteligentna paginacja (infinite scroll) */
              (() => {
                const txDataAll = bsSubTabTxData(bsSubTab);
                if (txDataAll.length > 0) bsVisibleRowsRef.current = txDataAll;
                const renderTxCell = (r: BankStatementTransaction, rKey: string, rIsDebit: boolean, rDirLabel: string) => {
                  const ellipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 } as const;
                  if (rKey === 'reconciled') return <TableCell key={rKey} align="center" sx={{ px: 0 }}><Tooltip title={r.reconciled_at ? 'Uzgodnione' : 'Nieuzgodnione'} arrow><Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><img src={r.reconciled_at ? '/assets/icons/verified.svg' : '/assets/icons/dangerous.svg'} alt={r.reconciled_at ? 'Uzgodnione' : 'Nieuzgodnione'} style={{ width: 20, height: 20 }} /></Box></Tooltip></TableCell>;
                  if (rKey === 'dates') return <TableCell key={rKey} sx={{ lineHeight: 1.3, fontSize: '0.8rem' }}><Box>{r.value_date || '-'}</Box><Box sx={{ color: '#888', fontSize: '0.72rem' }}>{r.booking_date && r.booking_date !== r.value_date ? r.booking_date : ''}</Box></TableCell>;
                  if (rKey === 'direction') return <TableCell key={rKey} align="center" sx={{ px: 0 }}><Tooltip title={rDirLabel} arrow><Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: rIsDebit ? '#D92D20' : '#2E7D32', display: 'inline-block' }} /></Tooltip></TableCell>;
                  if (rKey === 'txType') return <TableCell key={rKey} align="center" sx={{ px: 0 }}><Tooltip title={r.transaction_type === 'split_payment' ? 'Split Payment' : 'Zwykły'} arrow><Box component="span" sx={{ fontWeight: 600, fontSize: '0.75rem', color: r.transaction_type === 'split_payment' ? '#5E35B1' : '#555' }}>{r.transaction_type === 'split_payment' ? 'SP' : 'Z'}</Box></Tooltip></TableCell>;
                  if (rKey === 'statementAccount') {
                    if (!r.statement_account) return <TableCell key={rKey}>-</TableCell>;
                    const ap = accountParts(r.statement_account);
                    return <TableCell key={rKey} sx={{ fontFamily: 'Roboto Mono, monospace', fontSize: '0.7rem', p: '5px 4px' }}><Tooltip title={ap.full} arrow><Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', whiteSpace: 'nowrap' }}><Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1, minWidth: 0 }}>{ap.prefix}</Box><Box component="span" sx={{ flexShrink: 0 }}>{ap.suffix}</Box></Box></Tooltip></TableCell>;
                  }
                  if (rKey === 'statementNumber') return <TableCell key={rKey} sx={{ fontWeight: 500 }}>{r.statement_number || '-'}</TableCell>;
                  if (rKey === 'currency') return <TableCell key={rKey}>{r.statement_currency || '-'}</TableCell>;
                  if (rKey === 'counterpartyName') return <TableCell key={rKey} sx={{ overflow: 'hidden', maxWidth: 0, lineHeight: 1.3 }}><Box sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>{r.counterparty_name || (r.split_payment_role === 'vat_transfer' ? '(z rach. VAT)' : '-')}</Box></TableCell>;
                  if (rKey === 'counterpartyAccount') {
                    if (!r.counterparty_account) return <TableCell key={rKey}>-</TableCell>;
                    const ap = accountParts(r.counterparty_account);
                    return <TableCell key={rKey} sx={{ fontFamily: 'Roboto Mono, monospace', fontSize: '0.7rem', p: '5px 4px' }}><Tooltip title={ap.full} arrow><Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', whiteSpace: 'nowrap' }}><Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1, minWidth: 0 }}>{ap.prefix}</Box><Box component="span" sx={{ flexShrink: 0 }}>{ap.suffix}</Box></Box></Tooltip></TableCell>;
                  }
                  if (rKey === 'amount') return <TableCell key={rKey} align="right" sx={{ fontWeight: 500, color: rIsDebit ? '#D92D20' : '#2E7D32' }}>{rIsDebit ? '-' : '+'}{formatMoney(Number(r.amount))}</TableCell>;
                  if (rKey === 'vatAmount') return <TableCell key={rKey} align="right" sx={{ color: '#5E35B1' }}>{r.vat_amount != null ? formatMoney(Number(r.vat_amount)) : '-'}</TableCell>;
                  if (rKey === 'counterpartyNip') return <TableCell key={rKey} sx={{ fontFamily: 'Roboto Mono, monospace', fontSize: '0.75rem' }}>{r.counterparty_nip || '-'}</TableCell>;
                  if (rKey === 'invoiceTitle') return <TableCell key={rKey} sx={{ lineHeight: 1.3, overflow: 'hidden', maxWidth: 0 }}>{r.invoice_number && <Box sx={{ fontSize: '0.78rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.invoice_number}</Box>}<Box sx={{ fontSize: '0.75rem', color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title || '-'}</Box></TableCell>;
                  if (rKey === 'tnr') return <TableCell key={rKey} sx={{ fontFamily: 'Roboto Mono, monospace', fontSize: '0.68rem', ...ellipsis }}>{r.tnr || '-'}</TableCell>;
                  return <TableCell key={rKey}>-</TableCell>;
                };
                const visibleTx = txDataAll.slice(0, bsVisibleCount);
                return (
                  <>
                  {visibleTx.map((row, rowIdx) => {
                    const isDebit = row.direction === 'D' || row.direction === 'RD';
                    const dirLabel = row.direction === 'C' ? 'Uznanie' : row.direction === 'D' ? 'Obciążenie' : row.direction === 'RC' ? 'Storno uzn.' : 'Storno obc.';
                    const isSP = row.transaction_type === 'split_payment';
                    const isSelected = bsSelectedIds.has(row.id);
                    const prevSelected = rowIdx > 0 && bsSelectedIds.has(txDataAll[rowIdx - 1]?.id);
                    const nextSelected = rowIdx < txDataAll.length - 1 && bsSelectedIds.has(txDataAll[rowIdx + 1]?.id);
                    const selBg = isSelected ? '#D6E4F9' : undefined;
                    const selSx = {
                      cursor: 'default', userSelect: 'none', position: 'relative',
                      backgroundColor: selBg,
                      '& td.MuiTableCell-body': { boxShadow: isSelected ? [!prevSelected ? 'inset 0 2px 0 0 #4285F4' : '', !nextSelected ? 'inset 0 -2px 0 0 #4285F4' : ''].filter(Boolean).join(', ') || undefined : undefined },
                      '& td:first-of-type': isSelected ? { boxShadow: [!prevSelected ? 'inset 0 2px 0 0 #4285F4' : '', 'inset 2px 0 0 0 #4285F4', !nextSelected ? 'inset 0 -2px 0 0 #4285F4' : ''].filter(Boolean).join(', ') } : {},
                      '& td:last-of-type': isSelected ? { boxShadow: ['inset -2px 0 0 0 #4285F4', !prevSelected ? 'inset 0 2px 0 0 #4285F4' : '', !nextSelected ? 'inset 0 -2px 0 0 #4285F4' : ''].filter(Boolean).join(', ') } : {},
                    } as const;
                    return (
                      <TableRow key={row.id} hover onClick={(e) => handleBsRowClick(rowIdx, row.id, e)} sx={selSx}>
                        {activeBsColsOrd.map((key) => {
                          if (!colVBs(key)) return null;
                          return renderTxCell(row, key, isDebit, dirLabel);
                        })}
                      </TableRow>
                    );
                  })}
                  {/* Sentinel row — niewidoczny wiersz triggerujący doładowanie kolejnej porcji */}
                  {bsVisibleCount < txDataAll.length && (
                    <TableRow ref={bsSentinelRef} sx={{ height: 1, p: 0, border: 'none', '& td': { p: 0, border: 'none', height: 1 } }}>
                      <TableCell colSpan={activeBsColsOrd.filter(colVBs).length} />
                    </TableRow>
                  )}
                  </>
                );
              })()
            )}
            {/* Wiersz podsumowania transakcji */}
            {bsSubTab >= 1 && bsSubTabTxData(bsSubTab).length > 0 && (() => {
              const txDataForSum = bsSubTabTxData(bsSubTab);
              const visible = activeBsColsOrd.filter(colVBs);
              const amountIdx = visible.indexOf('amount');
              const vatIdx = visible.indexOf('vatAmount');
              const hasSelection = bsSelectedIds.size > 0;
              const sourceRows = hasSelection
                ? txDataForSum.filter((r: any) => bsSelectedIds.has(r.id))
                : txDataForSum;
              const sumAmount = sourceRows.reduce((acc: number, r: any) => {
                const isDebit = r.direction === 'D' || r.direction === 'RD';
                return acc + (isDebit ? -1 : 1) * Number(r.amount || 0);
              }, 0);
              const sumVat = sourceRows.reduce((acc: number, r: any) => acc + Number(r.vat_amount || 0), 0);
              const sumLabel = hasSelection ? `Zaznaczone (${sourceRows.length}):` : 'Suma:';
              const firstSumIdx = Math.min(amountIdx >= 0 ? amountIdx : Infinity, vatIdx >= 0 ? vatIdx : Infinity);
              const beforeCount = firstSumIdx < Infinity ? firstSumIdx : visible.length;
              const cells: React.ReactNode[] = [];
              if (beforeCount > 0) {
                cells.push(
                  <TableCell key="tx-sum-label" colSpan={beforeCount} align="right"
                    sx={{ p: '6px 12px', height: 32, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: hasSelection ? '#1565C0' : '#020242' }}>
                    {sumLabel}
                  </TableCell>
                );
              }
              for (let i = firstSumIdx; i < visible.length; i++) {
                const colKey = visible[i];
                if (colKey === 'amount') {
                  cells.push(
                    <TableCell key="tx-sum-amount" align="right"
                      sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: hasSelection ? '#1565C0' : '#020242' }}>
                      {formatMoney(sumAmount)}
                    </TableCell>
                  );
                } else if (colKey === 'vatAmount') {
                  cells.push(
                    <TableCell key="tx-sum-vat" align="right"
                      sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: hasSelection ? '#1565C0' : '#020242' }}>
                      {formatMoney(sumVat)}
                    </TableCell>
                  );
                } else {
                  cells.push(<TableCell key={`tx-sum-empty-${i}`} sx={{ backgroundColor: '#F1F1F6' }} />);
                }
              }
              return (
                <TableRow data-sum-row sx={{ position: 'sticky', bottom: 0, backgroundColor: '#F1F1F6', zIndex: 1, '& .MuiTableCell-root': { borderBottom: 'none' } }}>
                  {cells}
                </TableRow>
              );
            })()}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Panel Dostosuj kolumny */}
      <Backdrop open={colsOpenBs} sx={{ color: '#fff', zIndex: (theme: any) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          onClick={(e) => e.stopPropagation()}
          sx={{ position: 'fixed', bottom: 0, right: 0, height: '50vh', width: '280px', display: 'flex', flexDirection: 'column', boxShadow: '0px 5px 30px rgba(0,0,0,0.15)', borderRadius: 0, zIndex: (theme: any) => theme.zIndex.modal + 2, backgroundColor: '#fff', pointerEvents: 'auto' }}
        >
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={() => { setColsAnchorElBs(null); setDraftColsVisBs(null); setDraftColsOrdBs(null); setColResizeMode(false); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColsVisBs ?? safeBsColsVis;
              if (draftColsVisBs == null) setDraftColsVisBs({ ...safeBsColsVis });
              const ord = draftColsOrdBs ?? safeBsColsOrd;
              if (draftColsOrdBs == null) setDraftColsOrdBs([...safeBsColsOrd]);
              return ord.map((key) => (
                <Box
                  key={key}
                  sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = e.dataTransfer.getData('text/plain');
                    if (!src || src === key) return;
                    setDraftColsOrdBs((prev) => {
                      const cur = [...(prev || ord)];
                      const from = cur.indexOf(src);
                      const to = cur.indexOf(key);
                      if (from < 0 || to < 0) return cur;
                      cur.splice(to, 0, cur.splice(from, 1)[0]);
                      return cur;
                    });
                  }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={() => setDraftColsVisBs(prev => ({ ...(prev || list), [key]: !((prev || list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColsVisBs ?? safeBsColsVis)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                        {(bsSubTab === 0 ? bsStmtColLabel(key) : bsTxColLabel(key)).toUpperCase()}
                      </Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={() => { setColsAnchorElBs(null); setDraftColsVisBs(null); setDraftColsOrdBs(null); setColResizeMode(false); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }}
              onClick={() => {
                const vis = draftColsVisBs ?? safeBsColsVis;
                const ord = draftColsOrdBs ?? safeBsColsOrd;
                setColsVisBs(vis); setColsOrdBs(ord);
                saveBsColSettings(bsSubTab, vis, ord, safeBsColsWid);
                setColsAnchorElBs(null); setDraftColsVisBs(null); setDraftColsOrdBs(null); setColResizeMode(false);
              }}>Zapisz zmiany</Button>
          </Box>
        </Paper>
      </Backdrop>
      </>)}

      {/* ===== KARTA UZGODNIENIA (dwie tabele z selekcją) ===== */}
      {bsMainTab === 2 && (() => {
        const txConnMap = new Map<string, string[]>();
        const settConnMap = new Map<string, string[]>();
        uzgConnections.forEach(c => {
          txConnMap.set(c.txId, [...(txConnMap.get(c.txId) || []), c.settId]);
          settConnMap.set(c.settId, [...(settConnMap.get(c.settId) || []), c.txId]);
        });
        const txSuggMap = new Map<string, Array<{txId: string; settId: string; score: 1 | 2; txDate: string}>>();
        const settSuggMap = new Map<string, Array<{txId: string; settId: string; score: 1 | 2; txDate: string}>>();
        uzgSuggestions.forEach(s => {
          txSuggMap.set(s.txId, [...(txSuggMap.get(s.txId) || []), s]);
          settSuggMap.set(s.settId, [...(settSuggMap.get(s.settId) || []), s]);
        });

        let filteredTx = uzgDemoTx;
        if (uzgTxQuery) { const q = uzgTxQuery.toLowerCase(); filteredTx = filteredTx.filter(t => t.contractor.toLowerCase().includes(q) || t.invoice.toLowerCase().includes(q)); }
        if (uzgTxYear) filteredTx = filteredTx.filter(t => t.date.startsWith(uzgTxYear));
        if (uzgTxMonthOnly) filteredTx = filteredTx.filter(t => t.date.slice(5, 7) === uzgTxMonthOnly);
        const matchedTxIds = new Set(uzgConnections.map(c => c.txId));
        const unmatchedTxCount = filteredTx.filter(t => !matchedTxIds.has(t.id)).length;
        const matchedTxCount = filteredTx.filter(t => matchedTxIds.has(t.id)).length;
        if (uzgTxSubTab === 0) filteredTx = filteredTx.filter(t => !matchedTxIds.has(t.id));
        else filteredTx = filteredTx.filter(t => matchedTxIds.has(t.id));
        filteredTx = [...filteredTx].sort((a, b) => {
          let cmp = 0;
          switch (uzgTxSortKey) {
            case 'amount': cmp = Math.abs(a.amount) - Math.abs(b.amount); break;
            case 'contractor': cmp = (a.contractor || '').localeCompare(b.contractor || ''); break;
            case 'invoice': cmp = (a.invoice || '').localeCompare(b.invoice || ''); break;
            default: cmp = (a.date || '').localeCompare(b.date || '');
          }
          return uzgTxSortDir === 'asc' ? cmp : -cmp;
        });

        let filteredSett = uzgDemoSett;
        if (uzgSettQuery) { const q = uzgSettQuery.toLowerCase(); filteredSett = filteredSett.filter(s => s.contractor.toLowerCase().includes(q) || s.invoice.toLowerCase().includes(q)); }
        if (uzgSettYear) filteredSett = filteredSett.filter(s => s.date.startsWith(uzgSettYear));
        if (uzgSettMonthOnly) filteredSett = filteredSett.filter(s => s.date.slice(5, 7) === uzgSettMonthOnly);
        const matchedSettIds = new Set(uzgConnections.map(c => c.settId));
        const unmatchedSettCount = filteredSett.filter(s => !matchedSettIds.has(s.id)).length;
        const matchedSettCount = filteredSett.filter(s => matchedSettIds.has(s.id)).length;
        if (uzgSettSubTab === 0) filteredSett = filteredSett.filter(s => !matchedSettIds.has(s.id));
        else filteredSett = filteredSett.filter(s => matchedSettIds.has(s.id));

        const selTxConns = new Set(uzgSelectedTxId ? (txConnMap.get(uzgSelectedTxId) || []) : []);
        const selTxSuggs = uzgSelectedTxId ? (txSuggMap.get(uzgSelectedTxId) || []) : [];
        const selTxSuggSet = new Set(selTxSuggs.map(s => s.settId));
        const selTxSuggBySettId = new Map(selTxSuggs.map(s => [s.settId, s]));
        const selectedTxData = uzgSelectedTxId ? uzgDemoTx.find(t => t.id === uzgSelectedTxId) : null;

        const compareSett = (a: typeof filteredSett[0], b: typeof filteredSett[0]) => {
          let cmp = 0;
          switch (uzgSettSortKey) {
            case 'grossAmount': cmp = Number(a.grossAmount) - Number(b.grossAmount); break;
            case 'vatAmount': cmp = Number(a.vatAmount) - Number(b.vatAmount); break;
            case 'invoice': cmp = (a.invoice || '').localeCompare(b.invoice || ''); break;
            case 'contractor': cmp = (a.contractor || '').localeCompare(b.contractor || ''); break;
            default: cmp = (a.date || '').localeCompare(b.date || '');
          }
          return uzgSettSortDir === 'asc' ? cmp : -cmp;
        };
        if (uzgSelectedTxId) {
          filteredSett = [...filteredSett].sort((a, b) => {
            const aP = selTxConns.has(a.id) ? 0 : selTxSuggSet.has(a.id) ? 1 : 2;
            const bP = selTxConns.has(b.id) ? 0 : selTxSuggSet.has(b.id) ? 1 : 2;
            if (aP !== bP) return aP - bP;
            return compareSett(a, b);
          });
        } else {
          filteredSett = [...filteredSett].sort(compareSett);
        }

        const txById = new Map(uzgDemoTx.map(t => [t.id, t]));

        const tblSx = {
          tableLayout: 'fixed' as const, width: '100%', borderCollapse: 'separate' as const, borderSpacing: 0,
          '& .MuiTableCell-root': { padding: '5px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableCell-head': { padding: '1px 4px', position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
          '& .MuiTableCell-body': { borderRight: 'none', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 0 rgba(224,224,224,1)', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 },
        };
        const containerSx = { flex: 1, overflow: 'auto', border: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, boxShadow: 'none' };
        const filterRowSx = { display: 'flex', gap: '12px', alignItems: 'flex-end', mb: '10px' };
        const selectSx = (val: string) => ({ height: 36, pr: '8px', '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: val ? '#2D2D32' : '#B9B9B9' }, backgroundColor: '#fff', borderRadius: '4px', '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' }, '&.Mui-focused fieldset': { borderColor: '#595966 !important' } });
        const searchSx = { flex: 1, minWidth: 100, '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' }, '& .MuiInputBase-input': { px: '10px', fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#2D2D32' }, '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 } };
        const tabSx = { textTransform: 'none' as const, color: '#020242', fontFamily: 'Roboto, sans-serif', fontSize: '13px', fontWeight: 400, px: 2, minHeight: 30, height: 30, mt: '8px', bgcolor: '#F1F1F6', borderRadius: '4px 4px 0 0', '&.Mui-selected': { mt: 0, height: 38, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } };
        const tabsSx = { minHeight: 38, height: 38, flexGrow: 1, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTabs-flexContainer': { gap: '2px' }, '& .MuiTabs-scrollButtons': { width: 24, minWidth: 24 } };
        const iconDd = (iconProps: any) => (<Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps} sx={{ width: 18, height: 18, mr: '6px', pointerEvents: 'none' }} />);
        const yearItems = (() => { const now = new Date(); return Array.from({ length: 5 }, (_, i) => String(now.getFullYear() - i)); })();
        const monthNames = ['Styczeń','Luty','Marzec','Kwiecień','Maj','Czerwiec','Lipiec','Sierpień','Wrzesień','Październik','Listopad','Grudzień'];
        const fmtAmt = (v: number) => Number(v).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/\u00A0|\u202F/g, ' ');

        return (
      <Box sx={{ mt: 2, height: 'calc(100vh - 260px)', minHeight: 400, display: 'flex', gap: '20px' }}>

        {/* ==== LEWA: TRANSAKCJE ==== */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '15px', color: '#2D2D32', mb: '8px' }}>Transakcje bankowe</Typography>
          <Box sx={filterRowSx}>
            <TextField size="small" placeholder="Szukaj..." value={uzgTxQuery} onChange={(e) => setUzgTxQuery(e.target.value)} variant="outlined"
              InputProps={{ startAdornment: (<InputAdornment position="start"><Search sx={{ width: 18, height: 18, color: '#595966' }} /></InputAdornment>) }} sx={searchSx} />
            <Box sx={{ minWidth: 80, display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966' }}>Rok</Typography>
              <Select size="small" value={uzgTxYear} displayEmpty onChange={(e) => setUzgTxYear(String(e.target.value))} renderValue={uzgTxYear ? () => uzgTxYear : () => 'Wszystkie'} IconComponent={iconDd} sx={selectSx(uzgTxYear)}>
                <MenuItem value="">Wszystkie</MenuItem>
                {yearItems.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </Box>
            <Box sx={{ minWidth: 100, display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966' }}>Miesiąc</Typography>
              <Select size="small" value={uzgTxMonthOnly} displayEmpty onChange={(e) => setUzgTxMonthOnly(String(e.target.value))} renderValue={uzgTxMonthOnly ? () => monthNames[Number(uzgTxMonthOnly) - 1] || '' : () => 'Wszystkie'} IconComponent={iconDd} sx={selectSx(uzgTxMonthOnly)}>
                <MenuItem value="">Wszystkie</MenuItem>
                {monthNames.map((n, i) => <MenuItem key={i} value={String(i + 1).padStart(2, '0')}>{n}</MenuItem>)}
              </Select>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 38, mb: 0 }}>
            <Tabs value={uzgTxSubTab} onChange={(_, v) => { setUzgTxSubTab(v); setUzgSelectedTxId(null); }} variant="scrollable" scrollButtons="auto" sx={tabsSx}>
              {[`Do uzgodnienia (${unmatchedTxCount})`, `Uzgodnione (${matchedTxCount})`].map((label, i) => (
                <Tab key={i} disableRipple label={label} sx={tabSx} />
              ))}
            </Tabs>
          </Box>
          <TableContainer component={Paper} sx={containerSx}>
            <Table size="small" stickyHeader sx={tblSx}>
              <colgroup><col style={{ width: 90 }} /><col style={{ width: '25%' }} /><col style={{ width: '30%' }} /><col style={{ width: 100 }} /><col style={{ width: 85 }} /><col style={{ width: 55 }} /><col style={{ width: 30 }} /></colgroup>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <TableSortLabel {...getSortLabelProps(uzgTxSortKey === 'date')} active={uzgTxSortKey === 'date'} direction={uzgTxSortKey === 'date' ? uzgTxSortDir : 'asc'}
                      onClick={() => { if (uzgTxSortKey === 'date') setUzgTxSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setUzgTxSortKey('date'); setUzgTxSortDir('desc'); } }}>Data</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <TableSortLabel {...getSortLabelProps(uzgTxSortKey === 'contractor')} active={uzgTxSortKey === 'contractor'} direction={uzgTxSortKey === 'contractor' ? uzgTxSortDir : 'asc'}
                      onClick={() => { if (uzgTxSortKey === 'contractor') setUzgTxSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setUzgTxSortKey('contractor'); setUzgTxSortDir('asc'); } }}>Kontrahent</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <TableSortLabel {...getSortLabelProps(uzgTxSortKey === 'invoice')} active={uzgTxSortKey === 'invoice'} direction={uzgTxSortKey === 'invoice' ? uzgTxSortDir : 'asc'}
                      onClick={() => { if (uzgTxSortKey === 'invoice') setUzgTxSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setUzgTxSortKey('invoice'); setUzgTxSortDir('asc'); } }}>Tytuł / Faktura</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <TableSortLabel {...getSortLabelProps(uzgTxSortKey === 'amount')} active={uzgTxSortKey === 'amount'} direction={uzgTxSortKey === 'amount' ? uzgTxSortDir : 'asc'}
                      onClick={() => { if (uzgTxSortKey === 'amount') setUzgTxSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setUzgTxSortKey('amount'); setUzgTxSortDir('desc'); } }}>Kwota</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>VAT</TableCell>
                  <TableCell align="center" sx={{ borderRight: 'none !important', whiteSpace: 'nowrap' }}>Status</TableCell>
                  <TableCell sx={{ borderRight: 'none !important', p: 0 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTx.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><Typography sx={{ fontSize: 13, color: '#595966' }}>Brak transakcji</Typography></TableCell></TableRow>
                ) : filteredTx.map(tx => {
                  const isSel = uzgSelectedTxId === tx.id;
                  const txConns = txConnMap.get(tx.id) || [];
                  const txSuggs = txSuggMap.get(tx.id) || [];
                  return (
                  <TableRow key={tx.id} hover onClick={() => setUzgSelectedTxId(isSel ? null : tx.id)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: isSel ? '#DBEAFE' : '#F5F7FA' }, ...(isSel ? { bgcolor: '#DBEAFE', '& .MuiTableCell-body': { fontWeight: 500 } } : {}) }}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{tx.date}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 }}>{tx.contractor}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 }}>{tx.invoice}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'Roboto Mono, monospace', whiteSpace: 'nowrap', color: tx.amount >= 0 ? '#2E7D32' : '#D92D20' }}>
                      {`${tx.isOutflow ? '-' : '+'}${fmtAmt(Math.abs(tx.amount))} zł`}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'Roboto Mono, monospace', whiteSpace: 'nowrap', color: '#757575' }}>
                      {tx.vatAmount != null && tx.vatAmount > 0 ? `${fmtAmt(tx.vatAmount)} zł` : tx.isVatTransfer ? <Chip size="small" label="VAT" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 600, '& .MuiChip-label': { px: '4px' } }} /> : ''}
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: 'none !important' }}>
                      {txConns.length > 0 ? (
                        <Chip size="small" label={`${txConns.length} rozr.`} sx={{ height: 20, fontSize: '0.68rem', bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, '& .MuiChip-label': { px: '5px' } }} />
                      ) : txSuggs.length > 0 ? (
                        <Chip size="small" variant="outlined" label={`${txSuggs.length}`} sx={{ height: 20, fontSize: '0.68rem', borderColor: '#FFB74D', color: '#E65100', fontWeight: 600, '& .MuiChip-label': { px: '5px' } }} />
                      ) : (
                        <Typography sx={{ fontSize: '0.75rem', color: '#ccc' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ p: '0 !important', borderRight: 'none !important', textAlign: 'center' }}>
                      <Tooltip title="Podgląd" arrow>
                        <IconButton size="small" sx={{ p: '2px' }} onClick={(e) => { e.stopPropagation(); setUzgPreviewPair({ txId: tx.id, settId: txConns[0] || '' }); }}>
                          <Visibility sx={{ fontSize: 16, color: '#9E9E9E', '&:hover': { color: '#5C6BC0' } }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* ==== PRAWA: ROZRACHUNKI ==== */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '15px', color: '#2D2D32', mb: '8px' }}>Rozrachunki</Typography>

          <Box sx={filterRowSx}>
            <TextField size="small" placeholder="Szukaj..." value={uzgSettQuery} onChange={(e) => setUzgSettQuery(e.target.value)} variant="outlined"
              InputProps={{ startAdornment: (<InputAdornment position="start"><Search sx={{ width: 18, height: 18, color: '#595966' }} /></InputAdornment>) }} sx={searchSx} />
            <Box sx={{ minWidth: 80, display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966' }}>Rok</Typography>
              <Select size="small" value={uzgSettYear} displayEmpty onChange={(e) => setUzgSettYear(String(e.target.value))} renderValue={uzgSettYear ? () => uzgSettYear : () => 'Wszystkie'} IconComponent={iconDd} sx={selectSx(uzgSettYear)}>
                <MenuItem value="">Wszystkie</MenuItem>
                {yearItems.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </Box>
            <Box sx={{ minWidth: 100, display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966' }}>Miesiąc</Typography>
              <Select size="small" value={uzgSettMonthOnly} displayEmpty onChange={(e) => setUzgSettMonthOnly(String(e.target.value))} renderValue={uzgSettMonthOnly ? () => monthNames[Number(uzgSettMonthOnly) - 1] || '' : () => 'Wszystkie'} IconComponent={iconDd} sx={selectSx(uzgSettMonthOnly)}>
                <MenuItem value="">Wszystkie</MenuItem>
                {monthNames.map((n, i) => <MenuItem key={i} value={String(i + 1).padStart(2, '0')}>{n}</MenuItem>)}
              </Select>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 38, mb: 0 }}>
            <Tabs value={uzgSettSubTab} onChange={(_, v) => setUzgSettSubTab(v)} variant="scrollable" scrollButtons="auto" sx={tabsSx}>
              {[`Do uzgodnienia (${unmatchedSettCount})`, `Uzgodnione (${matchedSettCount})`].map((label, i) => (
                <Tab key={i} disableRipple label={label} sx={tabSx} />
              ))}
            </Tabs>
          </Box>
          <TableContainer component={Paper} sx={containerSx}>
            <Table size="small" stickyHeader sx={tblSx}>
              <colgroup><col style={{ width: 60 }} /><col style={{ width: 100 }} /><col style={{ width: 85 }} /><col style={{ width: '30%' }} /><col style={{ width: '24%' }} /><col style={{ width: 90 }} /><col style={{ width: 30 }} /></colgroup>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ borderRight: 'none !important', whiteSpace: 'nowrap' }}>{uzgSelectedTxId ? 'Akcja' : 'Status'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <TableSortLabel {...getSortLabelProps(uzgSettSortKey === 'grossAmount')} active={uzgSettSortKey === 'grossAmount'} direction={uzgSettSortKey === 'grossAmount' ? uzgSettSortDir : 'asc'}
                      onClick={() => { if (uzgSettSortKey === 'grossAmount') setUzgSettSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setUzgSettSortKey('grossAmount'); setUzgSettSortDir('desc'); } }}>Brutto</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <TableSortLabel {...getSortLabelProps(uzgSettSortKey === 'vatAmount')} active={uzgSettSortKey === 'vatAmount'} direction={uzgSettSortKey === 'vatAmount' ? uzgSettSortDir : 'asc'}
                      onClick={() => { if (uzgSettSortKey === 'vatAmount') setUzgSettSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setUzgSettSortKey('vatAmount'); setUzgSettSortDir('desc'); } }}>VAT</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <TableSortLabel {...getSortLabelProps(uzgSettSortKey === 'invoice')} active={uzgSettSortKey === 'invoice'} direction={uzgSettSortKey === 'invoice' ? uzgSettSortDir : 'asc'}
                      onClick={() => { if (uzgSettSortKey === 'invoice') setUzgSettSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setUzgSettSortKey('invoice'); setUzgSettSortDir('asc'); } }}>Faktura</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <TableSortLabel {...getSortLabelProps(uzgSettSortKey === 'contractor')} active={uzgSettSortKey === 'contractor'} direction={uzgSettSortKey === 'contractor' ? uzgSettSortDir : 'asc'}
                      onClick={() => { if (uzgSettSortKey === 'contractor') setUzgSettSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setUzgSettSortKey('contractor'); setUzgSettSortDir('asc'); } }}>Kontrahent</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', borderRight: 'none !important' }}>
                    <TableSortLabel {...getSortLabelProps(uzgSettSortKey === 'date')} active={uzgSettSortKey === 'date'} direction={uzgSettSortKey === 'date' ? uzgSettSortDir : 'asc'}
                      onClick={() => { if (uzgSettSortKey === 'date') setUzgSettSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setUzgSettSortKey('date'); setUzgSettSortDir('desc'); } }}>Data</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ borderRight: 'none !important', p: 0 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSett.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><Typography sx={{ fontSize: 13, color: '#595966' }}>Brak rozrachunków</Typography></TableCell></TableRow>
                ) : filteredSett.map(s => {
                  const isLinkedToSel = selTxConns.has(s.id);
                  const isSugg = selTxSuggSet.has(s.id);
                  const sugg = selTxSuggBySettId.get(s.id);
                  const allLinkedTxIds = settConnMap.get(s.id) || [];
                  const otherTxCount = uzgSelectedTxId ? allLinkedTxIds.filter(tid => tid !== uzgSelectedTxId).length : allLinkedTxIds.length;
                  const rowBg = uzgSelectedTxId ? (isLinkedToSel ? '#E8F5E9' : isSugg ? '#FFF8E1' : undefined) : undefined;
                  return (
                  <TableRow key={s.id} sx={{ bgcolor: rowBg, '&:hover': { bgcolor: isLinkedToSel ? '#C8E6C9' : isSugg ? '#FFECB3' : '#F5F7FA' } }}>
                    <TableCell align="center" sx={{ p: '2px 0', borderRight: 'none !important' }}>
                      {uzgSelectedTxId ? (<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1px' }}>
                        {isLinkedToSel && (
                          <Tooltip title="Odłącz" arrow>
                            <IconButton size="small" sx={{ p: '3px' }} onClick={async (e) => { e.stopPropagation();
                              const realTxId = uzgSelectedTxId!.replace(/^tx/, '');
                              const realSId = s.id.replace(/^s/, '');
                              try {
                                await removeReconciliationLink(realTxId, realSId);
                                // Jeśli to ostatni link tego rozrachunku, cofnij reconcile na rozrachunku
                                const remainingLinks = uzgConnections.filter(c => c.settId === s.id && c.txId !== uzgSelectedTxId);
                                if (remainingLinks.length === 0) {
                                  await unreconcileSettlement(realSId);
                                }
                              } catch (err) { console.error('[unreconcile]', err); }
                              setUzgConnections(prev => prev.filter(c => !(c.txId === uzgSelectedTxId && c.settId === s.id)));
                              setUzgNeedsRefresh(n => n + 1);
                            }}><DoNotDisturbOn sx={{ fontSize: 18, color: '#EF5350' }} /></IconButton>
                          </Tooltip>
                        )}
                        {isSugg && !isLinkedToSel && (<>
                          <Tooltip title={sugg?.score === 2 ? 'Powiąż (kwota brutto)' : 'Powiąż (kwota VAT)'} arrow>
                            <IconButton size="small" sx={{ p: '3px' }} onClick={(e) => { e.stopPropagation(); if (uzgSelectedTxId) confirmUzgPair(uzgSelectedTxId, s.id); }}>
                              <CheckCircle sx={{ fontSize: 18, color: '#4CAF50' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Odrzuć sugestię" arrow>
                            <IconButton size="small" sx={{ p: '3px' }} onClick={(e) => { e.stopPropagation();
                              if (!uzgSelectedTxId) return;
                              setUzgDismissedSuggestions(prev => new Set(prev).add(`${uzgSelectedTxId}|${s.id}`));
                              setUzgSuggestions(prev => prev.filter(sg => !(sg.txId === uzgSelectedTxId && sg.settId === s.id)));
                              deleteReconciliationSuggestion(uzgSelectedTxId.replace(/^tx/, ''), s.id.replace(/^s/, '')).catch(() => {});
                            }}><Close sx={{ fontSize: 16, color: '#BDBDBD' }} /></IconButton>
                          </Tooltip>
                        </>)}
                        {!isLinkedToSel && !isSugg && (
                          <Tooltip title="Powiąż" arrow>
                            <IconButton size="small" sx={{ p: '3px', opacity: 0.5, '&:hover': { opacity: 1 } }}
                              onClick={(e) => { e.stopPropagation(); if (uzgSelectedTxId) confirmUzgPair(uzgSelectedTxId, s.id); }}>
                              <Add sx={{ fontSize: 18, color: '#5C6BC0' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {otherTxCount > 0 && <Typography sx={{ fontSize: '8px', color: '#9E9E9E', ml: '1px' }}>+{otherTxCount}</Typography>}
                      </Box>) : (<>
                        {allLinkedTxIds.length > 0 ? (
                          <Chip size="small" label={`${allLinkedTxIds.length} tx`} sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, '& .MuiChip-label': { px: '4px' } }} />
                        ) : (
                          <Typography sx={{ fontSize: '0.75rem', color: '#ccc' }}>—</Typography>
                        )}
                      </>)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'Roboto Mono, monospace', whiteSpace: 'nowrap' }}>{fmtAmt(s.grossAmount)} zł</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'Roboto Mono, monospace', whiteSpace: 'nowrap', color: '#757575' }}>{s.vatAmount ? `${fmtAmt(s.vatAmount)} zł` : ''}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 }}>{s.invoice || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 }}>{s.contractor}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', borderRight: 'none !important' }}>{s.date}</TableCell>
                    <TableCell sx={{ p: '0 !important', borderRight: 'none !important', textAlign: 'center' }}>
                      <Tooltip title="Podgląd" arrow>
                        <IconButton size="small" sx={{ p: '2px' }} onClick={(e) => { e.stopPropagation(); setUzgPreviewPair({ txId: allLinkedTxIds[0] || '', settId: s.id }); }}>
                          <Visibility sx={{ fontSize: 16, color: '#9E9E9E', '&:hover': { color: '#5C6BC0' } }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

      </Box>
        );
      })()}
      </>)}
      {/* Drawer podglądu transakcji (z lewej) */}
      <Drawer
        anchor="left"
        open={!!uzgPreviewPair}
        onClose={() => setUzgPreviewPair(null)}
        hideBackdrop
        disableScrollLock
        disableEnforceFocus
        disableAutoFocus
        PaperProps={{ sx: { width: uzgDrawerWidths.left || 'calc(50vw - 60px)', maxWidth: '100vw', borderRadius: 0, top: '52px', height: 'calc(100vh - 52px)', boxShadow: '4px 0 16px rgba(0,0,0,0.12)', pointerEvents: 'auto' } }}
        ModalProps={{ keepMounted: false, sx: { pointerEvents: 'none' } }}
      >
        {(() => {
          if (!uzgPreviewPair) return null;
          const realId = uzgPreviewPair.txId.replace(/^tx/, '');
          const txFull = bsTransactions.find(t => String(t.id) === realId);
          if (!txFull) return <Box sx={{ p: 4 }}><Typography>Nie znaleziono transakcji</Typography></Box>;
          const fields: Array<{ label: string; value: string | number | null | undefined }> = [
            { label: 'ID transakcji', value: txFull.id },
            { label: 'Nr referencyjny', value: txFull.transaction_ref },
            { label: 'Data waluty', value: txFull.value_date },
            { label: 'Data księgowania', value: txFull.booking_date },
            { label: 'Kierunek', value: txFull.direction === 'C' ? 'Wpłata (C)' : txFull.direction === 'D' ? 'Wypłata (D)' : txFull.direction === 'RC' ? 'Wpłata z rach. VAT (RC)' : 'Wypłata z rach. VAT (RD)' },
            { label: 'Kwota', value: `${txFull.amount.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł` },
            { label: 'Kwota VAT', value: txFull.vat_amount != null ? `${txFull.vat_amount.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł` : '—' },
            { label: 'Kontrahent', value: txFull.counterparty_name },
            { label: 'Konto kontrahenta', value: txFull.counterparty_account },
            { label: 'NIP kontrahenta', value: txFull.counterparty_nip },
            { label: 'Nr faktury', value: txFull.invoice_number },
            { label: 'Tytuł', value: txFull.title },
            { label: 'Dodatkowe info', value: txFull.additional_info },
            { label: 'Kod operacji', value: txFull.operation_code },
            { label: 'Typ transakcji', value: txFull.transaction_type === 'split_payment' ? 'Split payment' : 'Zwykła' },
            { label: 'Ref. banku', value: txFull.bank_ref },
            { label: 'TNR', value: txFull.tnr },
            { label: 'Nr wyciągu', value: txFull.statement_number },
            { label: 'Bank', value: txFull.bank_name },
            { label: 'Konto', value: txFull.statement_account },
            { label: 'Waluta', value: txFull.statement_currency },
            { label: 'Plik źródłowy', value: txFull.source_filename },
            { label: 'Uzgodniono', value: txFull.reconciled_at ? `${txFull.reconciled_at.slice(0, 16)}` : 'Nie' },
          ];
          return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ pl: 3, pr: 2, pt: '18px', pb: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #EDEDED', flexShrink: 0 }}>
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '16px', color: '#2D2D32' }}>
                  Podgląd transakcji
                </Typography>
                <IconButton size="small" onClick={() => setUzgPreviewPair(null)}><Close sx={{ fontSize: 20 }} /></IconButton>
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #f0f0f0', fontFamily: 'Roboto, sans-serif', fontSize: '0.85rem', py: '6px' } }}>
                  <TableBody>
                    {fields.map((f, idx) => (
                      <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#F5F7FA' } }}>
                        <TableCell sx={{ fontWeight: 500, color: '#595966', whiteSpace: 'nowrap', width: 160 }}>{f.label}</TableCell>
                        <TableCell sx={{ color: '#2D2D32', wordBreak: 'break-word' }}>{f.value ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          );
        })()}
      </Drawer>

      {/* Drawer podglądu PDF faktury (z prawej) */}
      <Drawer
        anchor="right"
        open={!!uzgPreviewPair}
        onClose={() => setUzgPreviewPair(null)}
        hideBackdrop
        disableScrollLock
        disableEnforceFocus
        disableAutoFocus
        PaperProps={{ sx: { width: uzgDrawerWidths.right || 'calc(50vw - 60px)', maxWidth: '100vw', borderRadius: 0, top: '52px', height: 'calc(100vh - 52px)', boxShadow: '-4px 0 16px rgba(0,0,0,0.12)', pointerEvents: 'auto' } }}
        ModalProps={{ keepMounted: false, sx: { pointerEvents: 'none' } }}
      >
        {(() => {
          if (!uzgPreviewPair) return null;
          const realId = uzgPreviewPair.settId.replace(/^s/, '');
          const settRow = settlementsRows.find(r => String(r.id) === realId);
          if (!settRow) return <Box sx={{ p: 4 }}><Typography>Nie znaleziono rozrachunku</Typography></Box>;
          const url = String(settRow.google_drive_file_url || '')
            || (settRow.google_drive_file_id ? `https://drive.google.com/file/d/${settRow.google_drive_file_id}/preview` : '');
          const m1 = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
          const m2 = url.match(/id=([a-zA-Z0-9_-]+)/);
          const fileId = (m1 && m1[1]) || (m2 && m2[1]) || settRow.google_drive_file_id || '';
          const apiBase = (process.env.REACT_APP_API_URL || '');
          const src = fileId ? `${apiBase}/kzms/attachments/${encodeURIComponent(fileId)}/content` : `${apiBase}/transfer-invoices/${settRow.id}/pdf`;
          return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ pl: 3, pr: 2, pt: '18px', pb: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #EDEDED', flexShrink: 0 }}>
                <Box>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '16px', color: '#2D2D32' }}>
                    Podgląd dokumentu
                  </Typography>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#595966' }}>
                    {settRow.invoice_number} • {settRow.contractor}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setUzgPreviewPair(null)}><Close sx={{ fontSize: 20 }} /></IconButton>
              </Box>
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {(fileId || settRow.id) ? (
                  <iframe title="uzg-pdf-preview" src={src} style={{ width: '100%', height: '100%', border: 0 }} />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography sx={{ color: '#595966' }}>Brak pliku PDF</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })()}
      </Drawer>

      {/* Drawer operacji na wielu wyciągach/transakcjach */}
      <Drawer
        anchor="right"
        open={bsBulkDrawerOpen}
        onClose={() => setBsBulkDrawerOpen(false)}
        PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0, top: '52px', height: 'calc(100vh - 52px)' } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #EDEDED', flexShrink: 0 }}>
            <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
              Operacje na wielu ({bsSelectedIds.size})
            </Typography>
            <IconButton onClick={() => setBsBulkDrawerOpen(false)} size="small"
              sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 30, minHeight: 30, '& > *': { pointerEvents: 'none' } }}>
              <Box component="img" src="/assets/icons/close-x.svg" alt="Zamknij"
                sx={{ width: 13, height: 13, display: 'block', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }} />
            </IconButton>
          </Box>
          <Box sx={{ p: 4, pt: 3, flex: 1, overflowY: 'auto' }}>
            <Box sx={{ display: 'grid', gap: 2 }}>
              {/* Opcja: Usuń wyciągi */}
              {bsSubTab === 0 && (
              <Button
                variant="outlined"
                fullWidth
                disabled={bsBulkDeleting || bsSelectedIds.size === 0}
                onClick={async () => {
                  const count = bsSelectedIds.size;
                  if (!window.confirm(`Czy na pewno usunąć ${count} zaznaczonych wyciągów? Usunie to również wszystkie transakcje z tych wyciągów.`)) return;
                  setBsBulkDeleting(true);
                  try {
                    let successCount = 0;
                    let errorCount = 0;
                    for (const id of Array.from(bsSelectedIds)) {
                      try { await deleteBankStatement(id); successCount++; } catch { errorCount++; }
                    }
                    setBsSelectedIds(new Set());
                    setBsLastClickedIdx(null);
                    await refreshBankStatements();
                    setBsBulkDrawerOpen(false);
                    alert(`Usunięto ${successCount} wyciągów${errorCount > 0 ? `, błędy: ${errorCount}` : ''}`);
                  } catch (e) {
                    console.error('Błąd usuwania wyciągów:', e);
                    alert('Wystąpił błąd podczas usuwania wyciągów');
                  } finally {
                    setBsBulkDeleting(false);
                  }
                }}
                sx={{
                  borderColor: '#D92D20', color: '#D92D20', height: 72, textTransform: 'none',
                  justifyContent: 'flex-start', px: 3,
                  '&:hover': { borderColor: '#B42318', backgroundColor: '#FEF3F2' },
                  '&:disabled': { borderColor: '#d4d4d9', color: '#B9B9B9' }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', fontWeight: 400, color: 'inherit' }}>
                    Usuń zaznaczone wyciągi
                  </Typography>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966', mt: 0.5 }}>
                    {bsSelectedIds.size} {bsSelectedIds.size === 1 ? 'wyciąg' : bsSelectedIds.size < 5 ? 'wyciągi' : 'wyciągów'} do usunięcia (z transakcjami)
                  </Typography>
                </Box>
              </Button>
              )}
              {/* Opcja: Usuń transakcje (tylko na tab transakcji) */}
              {bsSubTab === 1 && (
              <Button
                variant="outlined"
                fullWidth
                disabled={bsBulkDeleting || bsSelectedIds.size === 0}
                onClick={async () => {
                  alert('Usuwanie pojedynczych transakcji – funkcja w przygotowaniu');
                }}
                sx={{
                  borderColor: '#D92D20', color: '#D92D20', height: 72, textTransform: 'none',
                  justifyContent: 'flex-start', px: 3,
                  '&:hover': { borderColor: '#B42318', backgroundColor: '#FEF3F2' },
                  '&:disabled': { borderColor: '#d4d4d9', color: '#B9B9B9' }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', fontWeight: 400, color: 'inherit' }}>
                    Usuń zaznaczone transakcje
                  </Typography>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966', mt: 0.5 }}>
                    {bsSelectedIds.size} {bsSelectedIds.size === 1 ? 'transakcja' : bsSelectedIds.size < 5 ? 'transakcje' : 'transakcji'} do usunięcia
                  </Typography>
                </Box>
              </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>
      {/* === KONIEC SEKCJI WYCIĄGI I UZGODNIENIA === */}

      {renderMenu()}
      {/* Modal podglądu istniejącej faktury – dostępny niezależnie od zakładki */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>
          {previewInvoice ? `Podgląd: ${previewInvoice.project_id} • ${previewInvoice.contractor_name}` : 'Podgląd'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, height: { xs: 'calc(100vh - 180px)', md: 'calc(100vh - 180px)' } }}>
            {/* Lewa – PDF viewer */}
            <Box sx={{ flex: 7, border: '2px dashed #9e9e9e', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.paper' }}>
              {previewInvoice ? (
                (() => {
                  const url = String(previewInvoice.google_drive_file_url || '')
                    || (previewInvoice.google_drive_file_id ? `https://drive.google.com/file/d/${previewInvoice.google_drive_file_id}/preview` : '');
                  const m1 = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
                  const m2 = url.match(/id=([a-zA-Z0-9_-]+)/);
                  const fileId = (m1 && m1[1]) || (m2 && m2[1]) || previewInvoice.google_drive_file_id || '';
                  const apiBase = (process.env.REACT_APP_API_URL || '');
                  const src = fileId ? `${apiBase}/kzms/attachments/${encodeURIComponent(fileId)}/content` : `${apiBase}/transfer-invoices/${previewInvoice.id}/pdf`;
                  return (<iframe title="preview" src={src} style={{ width: '100%', height: '100%', border: 0 }} />);
                })()
              ) : null}
            </Box>
            {/* Prawa – Historia + Chat */}
            <Box sx={{ flex: 3, display: 'flex', flexDirection: 'column', minHeight: 0, pb: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Historia dokumentu</Typography>
              <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
                {/* 1) Timeline meta faktury */}
                {previewInvoice ? (
                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{String(previewInvoice.created_at || '').slice(0,16)}</Typography>
                      <Typography variant="body2">Wprowadzono{previewInvoice.created_by_name ? ` w dniu ${String(previewInvoice.created_at||'').slice(0,10)} przez ${previewInvoice.created_by_name}` : ''}</Typography>
                    </Box>
                    {previewInvoice.document_date ? (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{String(previewInvoice.document_date)}</Typography>
                        <Typography variant="body2">Data faktury</Typography>
                      </Box>
                    ) : null}
                    {previewInvoice.received_date ? (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{String(previewInvoice.received_date)}</Typography>
                        <Typography variant="body2">Data otrzymania</Typography>
                      </Box>
                    ) : null}
                    {previewInvoice.created_at ? (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{String(previewInvoice.created_at).slice(0,16)}</Typography>
                        <Typography variant="body2">Wprowadzono{previewInvoice.created_by_name ? ` przez ${previewInvoice.created_by_name}` : ''}</Typography>
                      </Box>
                    ) : null}
                    {previewInvoice.accounting_approval_status && previewInvoice.accounting_approval_status !== 'pending' ? (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{String(previewInvoice.accounting_approval_at || '').slice(0,16)}</Typography>
                        <Typography variant="body2">Księgowość: {previewInvoice.accounting_approval_status}</Typography>
                      </Box>
                    ) : null}
                    {previewInvoice.admin_approval_status && previewInvoice.admin_approval_status !== 'pending' ? (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{String(previewInvoice.admin_approval_at || '').slice(0,16)}</Typography>
                        <Typography variant="body2">Admin: {previewInvoice.admin_approval_status}</Typography>
                      </Box>
                    ) : null}
                    {previewInvoice.updated_at && (!previewInvoice.accounting_approval_at && !previewInvoice.admin_approval_at) ? (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{String(previewInvoice.updated_at).slice(0,16)}</Typography>
                        <Typography variant="body2">Zmieniono status: {previewInvoice.status}</Typography>
                      </Box>
                    ) : null}
                  </Box>
                ) : null}
                {(() => {
                  const lines = String(previewInvoice?.comment || '')
                    .split('\n')
                    .filter(Boolean)
                    .map((line: string) => {
                      let m = line.match(/^\[(.*?)\]\s*\|\s*(.*?)\s*\|\s*([0-9\-:\s]{19}):\s*(.*)$/);
                      if (m) return { userId: m[1], name: m[2], date: m[3], text: m[4] };
                      m = line.match(/^\[(.*?)\]\s*(.*)\s*\[([0-9\-:\s]{19})\]$/);
                      if (m) return { userId: '', name: m[1], date: m[3], text: m[2] };
                      m = line.match(/^\[(.*?)\]\s*(.*)$/);
                      if (m) return { userId: '', name: m[1], date: '', text: m[2] };
                      return { userId: '', name: '', date: '', text: line };
                    });
                  const palette = ['#E3F2FD', '#E8F5E9', '#FFF3E0', '#F3E5F5', '#E0F7FA', '#FBE9E7'];
                  const borderPalette = ['#90CAF9', '#A5D6A7', '#FFCC80', '#CE93D8', '#80DEEA', '#FFAB91'];
                  const colorIndex = (key: string) => { let h = 0; for (let i=0;i<key.length;i++) h = (h*31 + key.charCodeAt(i)) >>> 0; return h % palette.length; };
                  return lines.map((m: any, idx: number) => {
                    let user = m.userId ? usersMap[m.userId] : undefined;
                    if (!user && m.name) {
                      const lower = String(m.name).toLowerCase();
                      user = Object.values(usersMap).find((u) => String(u.display_name || '').toLowerCase() === lower);
                    }
                    const name = user?.display_name || m.name || '';
                    const avatar = user?.avatar_url || undefined;
                    const key = m.userId || name || 'unknown';
                    const idxColor = colorIndex(key);
                    return (
                      <Box key={idx} sx={{ mb: 1.5, display: 'flex', gap: 1 }}>
                        <Tooltip title={name || 'Użytkownik'} arrow>
                          <Avatar src={avatar} sx={{ width: 25, height: 25, fontSize: 12 }}>{(name || '?').slice(0,1)}</Avatar>
                        </Tooltip>
                        <Box sx={{ borderRadius: 1, border: `1px solid ${borderPalette[idxColor]}`, backgroundColor: palette[idxColor], px: 1, py: 0.5 }}>
                          {m.date ? (<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{m.date.slice(0,16)}</Typography>) : null}
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.text}</Typography>
                        </Box>
                      </Box>
                    );
                  });
                })()}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexShrink: 0 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Napisz komentarz (Uzupełnić / odpowiedź)"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendPreviewComment();
                    }
                  }}
                />
                <Button variant="contained" onClick={sendPreviewComment}>Wyślij</Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Zamknij</Button>
        </DialogActions>
      </Dialog>

      {/* Podgląd faktury KSeF */}
      <Dialog open={ksefPreviewOpen} onClose={() => setKsefPreviewOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>
          {ksefPreview?.invoice?.ksef_invoice_id ? `KSeF: ${ksefPreview.invoice.ksef_invoice_id}` : 'Podgląd faktury KSeF'}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '80vh', overflow: 'auto', p: 3 }}>
          {ksefPreview ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Górna część - podstawowe informacje o fakturze */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, pb: 2, borderBottom: '2px solid #e0e0e0' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>Numer faktury</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>{ksefPreview.invoice.invoice_number || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>Numer faktury KSeF</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.9rem', wordBreak: 'break-word' }}>{ksefPreview.invoice.ksef_invoice_id || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>Data wystawienia</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{ksefPreview.invoice.issue_date || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>Data fakturowania</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{ksefPreview.invoice.invoicing_date || '-'}</Typography>
                </Box>
                {ksefPreview.invoice.payment_due_date && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>Termin płatności</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{ksefPreview.invoice.payment_due_date}</Typography>
                  </Box>
                )}
                {ksefPreview.invoice.bank_account_number && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>Numer rachunku bankowego</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.9rem', wordBreak: 'break-word' }}>{ksefPreview.invoice.bank_account_number}</Typography>
                  </Box>
                )}
              </Box>

              {/* Sprzedawca i Kupujący obok siebie */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                {/* Sprzedawca - lewa strona */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, fontSize: '1rem', pb: 1, borderBottom: '1px solid #e0e0e0' }}>Sprzedawca</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>Nazwa</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.9rem', wordBreak: 'break-word' }}>{ksefPreview.invoice.seller_name || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>NIP</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{ksefPreview.invoice.seller_tax_id || '-'}</Typography>
                    </Box>
                    {ksefPreview.invoice.seller_address && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>Adres</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{ksefPreview.invoice.seller_address}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Kupujący - prawa strona */}
                {(ksefPreview.invoice.buyer_name || ksefPreview.invoice.buyer_tax_id) && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, fontSize: '1rem', pb: 1, borderBottom: '1px solid #e0e0e0' }}>Kupujący</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>Nazwa</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', wordBreak: 'break-word' }}>{ksefPreview.invoice.buyer_name || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>NIP</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{ksefPreview.invoice.buyer_tax_id || '-'}</Typography>
                      </Box>
                      {ksefPreview.invoice.buyer_address && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>Adres</Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{ksefPreview.invoice.buyer_address}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Tabela z pozycjami faktury */}
              {(() => {
                const items = ksefPreview.items || [];
                const hasItems = Array.isArray(items) && items.length > 0;
                const isNetto = Boolean(ksefPreview.isNetto ?? (ksefPreview.calcMode !== 'gross'));
                const headerTotals = {
                  net: Number(ksefPreview.invoice.net_amount) || 0,
                  vat: Number(ksefPreview.invoice.vat_amount) || 0,
                  gross: Number(ksefPreview.invoice.gross_amount) || 0,
                };
                const fromItems = ksefPreview.totalsFromItems || items.reduce((acc: any, it: any) => {
                  acc.net += Number(it.net_price) || 0;
                  acc.vat += Number(it.vat_amount) || 0;
                  acc.gross += Number(it.gross_price) || 0;
                  return acc;
                }, { net: 0, vat: 0, gross: 0 });
                const match = ksefPreview.totalsMatch || {
                  net: Math.abs(fromItems.net - headerTotals.net) < 0.02,
                  vat: Math.abs(fromItems.vat - headerTotals.vat) < 0.02,
                  gross: Math.abs(fromItems.gross - headerTotals.gross) < 0.02,
                };
                return hasItems ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem', flex: 1 }}>
                        Pozycje faktury
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                        Ceny w kolumnach jednostkowych: {isNetto ? 'netto (P_9A)' : 'brutto (P_9B)'}
                      </Typography>
                    </Box>
                    <Box sx={{ overflow: 'auto' }}>
                      <Table size="small" sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, bgcolor: '#f5f5f5' }}>Lp.</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, bgcolor: '#f5f5f5' }}>Nazwa</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, bgcolor: '#f5f5f5' }}>Ilość</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, bgcolor: '#f5f5f5' }}>Jedn.</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, bgcolor: '#f5f5f5' }}>Cena jedn. netto</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, bgcolor: '#f5f5f5' }}>Cena jedn. brutto</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, bgcolor: '#f5f5f5' }}>Netto</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, bgcolor: '#f5f5f5' }}>VAT %</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, bgcolor: '#f5f5f5' }}>VAT</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, bgcolor: '#f5f5f5' }}>Brutto</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {items.map((item: any, idx: number) => {
                            const upNet = item.unit_price_net != null ? Number(item.unit_price_net) : null;
                            const upGross = item.unit_price_gross != null ? Number(item.unit_price_gross) : null;
                            const fallbackUp = item.unit_price != null ? Number(item.unit_price) : null;
                            const showNetUp = upNet ?? (isNetto ? fallbackUp : null);
                            const showGrossUp = upGross ?? (!isNetto ? fallbackUp : null);
                            const vatLabel = item.vat_rate_raw || (item.vat_rate != null ? `${Number(item.vat_rate).toFixed(0)}%` : '-');
                            return (
                              <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                <TableCell sx={{ fontSize: '0.85rem', py: 1 }}>{item.line_number || idx + 1}</TableCell>
                                <TableCell sx={{ fontSize: '0.85rem', py: 1 }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{item.name || '-'}</Typography>
                                    {item.description && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                                        {item.description}
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.85rem', py: 1 }}>{item.quantity != null ? Number(item.quantity).toFixed(Number(item.quantity) % 1 === 0 ? 0 : 4) : '-'}</TableCell>
                                <TableCell sx={{ fontSize: '0.85rem', py: 1 }}>{item.unit || '-'}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.85rem', py: 1 }}>{showNetUp != null ? formatMoney(showNetUp) : '-'}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.85rem', py: 1 }}>{showGrossUp != null ? formatMoney(showGrossUp) : '-'}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.85rem', py: 1 }}>{item.net_price != null ? formatMoney(item.net_price) : '-'}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.85rem', py: 1 }}>{vatLabel}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.85rem', py: 1 }}>{item.vat_amount != null ? formatMoney(item.vat_amount) : '-'}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.85rem', py: 1 }}>{item.gross_price != null ? formatMoney(item.gross_price) : '-'}</TableCell>
                              </TableRow>
                            );
                          })}
                          {/* Wiersze podsumowania: suma z pozycji + nagłówek faktury */}
                          <TableRow sx={{ bgcolor: '#f5f5f5', '& td': { borderTop: '2px solid #ddd', fontWeight: 600 } }}>
                            <TableCell colSpan={6} align="right" sx={{ fontSize: '0.9rem', py: 1.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>Suma z pozycji:</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.9rem', py: 1.5 }}>{formatMoney(fromItems.net)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.9rem', py: 1.5 }}>-</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.9rem', py: 1.5 }}>{formatMoney(fromItems.vat)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.95rem', py: 1.5 }}>
                              <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                {formatMoney(fromItems.gross)} {ksefPreview.invoice.currency || 'PLN'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ bgcolor: '#fafafa', '& td': { fontWeight: 500 } }}>
                            <TableCell colSpan={6} align="right" sx={{ fontSize: '0.85rem', py: 1.25 }}>
                              <Typography variant="caption" color="text.secondary">Nagłówek (KSeF):</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.85rem', py: 1.25, color: match.net ? 'text.secondary' : 'error.main' }}>{formatMoney(headerTotals.net)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.85rem', py: 1.25 }}>-</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.85rem', py: 1.25, color: match.vat ? 'text.secondary' : 'error.main' }}>{formatMoney(headerTotals.vat)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.85rem', py: 1.25, color: match.gross ? 'text.secondary' : 'error.main' }}>{formatMoney(headerTotals.gross)} {ksefPreview.invoice.currency || 'PLN'}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                    {(!match.net || !match.vat || !match.gross) && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Suma z pozycji różni się od wartości z nagłówka faktury KSeF: 
                        {!match.net && ` netto Δ=${formatMoney(fromItems.net - headerTotals.net)};`}
                        {!match.vat && ` VAT Δ=${formatMoney(fromItems.vat - headerTotals.vat)};`}
                        {!match.gross && ` brutto Δ=${formatMoney(fromItems.gross - headerTotals.gross)};`} sprawdź, czy faktura nie zawiera rabatów w nagłówku lub dodatkowych opłat.
                      </Alert>
                    )}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, fontSize: '1rem', pb: 1, borderBottom: '1px solid #e0e0e0' }}>Pozycje faktury</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                      Brak pozycji faktury w bazie danych.
                    </Typography>
                  </Box>
                );
              })()}

              {/* Podsumowanie faktury */}
              <Box sx={{ mt: 2, p: 2.5, bgcolor: '#f9f9f9', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>Podsumowanie faktury</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>Wartość netto:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                      {formatMoney(ksefPreview.invoice.net_amount || 0)} {ksefPreview.invoice.currency || 'PLN'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>VAT:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                      {formatMoney(ksefPreview.invoice.vat_amount || 0)} {ksefPreview.invoice.currency || 'PLN'}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>DO ZAPŁATY:</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1.3rem' }}>
                      {formatMoney(ksefPreview.invoice.gross_amount || 0)} {ksefPreview.invoice.currency || 'PLN'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Forma płatności i rachunki bankowe */}
              {(ksefPreview.invoice.payment_method || ksefPreview.invoice.bank_account_number || (ksefPreview.bankAccounts && ksefPreview.bankAccounts.length > 0)) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                    {ksefPreview.invoice.payment_method && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>Forma płatności</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                          {ksefPreview.invoice.payment_method}
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                        {(ksefPreview.bankAccounts && ksefPreview.bankAccounts.length > 1) ? 'Rachunki bankowe' : 'Numer rachunku bankowego'}
                      </Typography>
                      {ksefPreview.bankAccounts && ksefPreview.bankAccounts.length > 0 ? (
                        ksefPreview.bankAccounts.map((acc, i) => (
                          <Typography key={i} variant="body2" sx={{ fontSize: '0.9rem', fontWeight: acc.is_primary ? 600 : 400, wordBreak: 'break-word' }}>
                            {acc.nr_rb}{acc.is_primary ? ' (główny)' : ''}
                            {acc.bank_name ? ` – ${acc.bank_name}` : ''}
                            {acc.currency ? ` [${acc.currency}]` : ''}
                          </Typography>
                        ))
                      ) : (
                        ksefPreview.invoice.bank_account_number && (
                          <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500, wordBreak: 'break-word' }}>
                            {ksefPreview.invoice.bank_account_number}
                          </Typography>
                        )
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKsefPreviewOpen(false)} sx={{ textTransform: 'none' }}>Zamknij</Button>
        </DialogActions>
      </Dialog>

      {/* Okno edycji i dodawania faktur otwiera się teraz w nowej karcie przeglądarki */}

      {/* Drawer ustawień KSEF */}
      <KsefSettingsDrawer
        open={ksefSettingsOpen}
        onClose={() => setKsefSettingsOpen(false)}
        onConnectionStatusChange={(status) => {
          setKsefConnectionStatus(status);
        }}
      />

      <AddWarehouseDialog
        open={addWarehouseOpen}
        onClose={() => setAddWarehouseOpen(false)}
        onSaved={() => { refreshWarehouse(); loadWarehouses(); }}
      />

      {/* Okno „Dostosuj kolumny” dla KSEF */}
      <Backdrop open={colsOpenKsef} sx={{ color: '#fff', zIndex: (theme: any) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{ position: 'fixed', bottom: 0, right: 0, height: '50vh', width: '280px', display: 'flex', flexDirection: 'column', boxShadow: '0px 5px 30px rgba(0,0,0,0.15)', borderRadius: 0, zIndex: (theme) => theme.zIndex.modal + 2, backgroundColor: '#fff', pointerEvents: 'auto' }}
          onClick={(e)=> e.stopPropagation()}
        >
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={()=>{ setColsAnchorElKsef(null); setDraftColumnsVisibleKsef(null); setDraftOrderKsef(null); setColResizeMode(false); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisibleKsef ?? columnsVisibleKsef;
              if (draftColumnsVisibleKsef == null) setDraftColumnsVisibleKsef({ ...columnsVisibleKsef });
              const ord = draftOrderKsef ?? columnOrderKsef;
              if (draftOrderKsef == null) setDraftOrderKsef([...columnOrderKsef]);
              return ord.map((key) => (
                <Box
                  key={key}
                  sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = e.dataTransfer.getData('text/plain');
                    if (!src || src === key) return;
                    setDraftOrderKsef((prev) => {
                      const cur = [...(prev || ord)];
                      const from = cur.indexOf(src);
                      const to = cur.indexOf(key);
                      if (from < 0 || to < 0) return cur;
                      cur.splice(to, 0, cur.splice(from, 1)[0]);
                      return cur;
                    });
                  }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setDraftColumnsVisibleKsef(prev=>({ ...(prev||list), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisibleKsef ?? columnsVisibleKsef)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                        {key === 'dates' ? 'DATY' :
                         key === 'invoiceNumber' ? 'NR FAKTURY' :
                         key === 'payment' ? 'Płatność' :
                         key === 'seller' ? 'Sprzedawca' :
                         key === 'nip' ? 'NIP' :
                         key === 'net' ? 'Netto' :
                         key === 'vatRate' ? 'Stawka' :
                         key === 'vatAmount' ? 'VAT' :
                         key === 'gross' ? 'Brutto' :
                         key === 'status' ? 'STATUS' :
                         key === 'preview' ? 'PODGLĄD' :
                         key === 'actions' ? 'AKCJE' : key.toUpperCase()}
                      </Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ setColsAnchorElKsef(null); setDraftColumnsVisibleKsef(null); setDraftOrderKsef(null); setColResizeMode(false); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{ const vis = draftColumnsVisibleKsef ?? columnsVisibleKsef; setColumnsVisibleKsef(vis); try { localStorage.setItem(VISIBLE_KEY_KSEF, JSON.stringify(vis)); } catch {} if (draftOrderKsef) { setColumnOrderKsef(draftOrderKsef); try { localStorage.setItem(ORDER_KEY_KSEF, JSON.stringify(draftOrderKsef)); } catch {} } setColsAnchorElKsef(null); setDraftColumnsVisibleKsef(null); setDraftOrderKsef(null); setColResizeMode(false); }}>Zapisz zmiany</Button>
          </Box>
        </Paper>
      </Backdrop>

      {/* Okno „Dostosuj kolumny" dla KSEF Sprzedaż */}
      <Backdrop open={colsOpenSalesKsef} sx={{ color: '#fff', zIndex: (theme: any) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{ position: 'fixed', bottom: 0, right: 0, height: '50vh', width: '280px', display: 'flex', flexDirection: 'column', boxShadow: '0px 5px 30px rgba(0,0,0,0.15)', borderRadius: 0, zIndex: (theme) => theme.zIndex.modal + 2, backgroundColor: '#fff', pointerEvents: 'auto' }}
          onClick={(e)=> e.stopPropagation()}
        >
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={()=>{ setColsAnchorElSalesKsef(null); setDraftColumnsVisibleSalesKsef(null); setDraftOrderSalesKsef(null); setColResizeMode(false); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisibleSalesKsef ?? columnsVisibleSalesKsef;
              if (draftColumnsVisibleSalesKsef == null) setDraftColumnsVisibleSalesKsef({ ...columnsVisibleSalesKsef });
              const ord = draftOrderSalesKsef ?? columnOrderSalesKsef;
              if (draftOrderSalesKsef == null) setDraftOrderSalesKsef([...columnOrderSalesKsef]);
              return ord.map((key) => (
                <Box
                  key={key}
                  sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = e.dataTransfer.getData('text/plain');
                    if (!src || src === key) return;
                    setDraftOrderSalesKsef((prev) => {
                      const cur = [...(prev || ord)];
                      const from = cur.indexOf(src);
                      const to = cur.indexOf(key);
                      if (from < 0 || to < 0) return cur;
                      cur.splice(to, 0, cur.splice(from, 1)[0]);
                      return cur;
                    });
                  }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setDraftColumnsVisibleSalesKsef(prev=>({ ...(prev||list), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisibleSalesKsef ?? columnsVisibleSalesKsef)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                        {key === 'dates' ? 'DATY' :
                         key === 'invoiceType' ? 'RODZAJ' :
                         key === 'invoiceNumber' ? 'NR FAKTURY' :
                         key === 'payment' ? 'Płatność' :
                         key === 'seller' ? 'Nabywca' :
                         key === 'nip' ? 'NIP' :
                         key === 'net' ? 'Netto' :
                         key === 'vatRate' ? 'Stawka' :
                         key === 'vatAmount' ? 'VAT' :
                         key === 'gross' ? 'Brutto' :
                         key === 'status' ? 'STATUS' :
                         key === 'actions' ? 'AKCJE' : key.toUpperCase()}
                      </Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ setColsAnchorElSalesKsef(null); setDraftColumnsVisibleSalesKsef(null); setDraftOrderSalesKsef(null); setColResizeMode(false); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{ const vis = draftColumnsVisibleSalesKsef ?? columnsVisibleSalesKsef; setColumnsVisibleSalesKsef(vis); try { localStorage.setItem(VISIBLE_KEY_SALES_KSEF, JSON.stringify(vis)); } catch {} if (draftOrderSalesKsef) { setColumnOrderSalesKsef(draftOrderSalesKsef); try { localStorage.setItem(ORDER_KEY_SALES_KSEF, JSON.stringify(draftOrderSalesKsef)); } catch {} } setColsAnchorElSalesKsef(null); setDraftColumnsVisibleSalesKsef(null); setDraftOrderSalesKsef(null); setColResizeMode(false); }}>Zapisz zmiany</Button>
          </Box>
        </Paper>
      </Backdrop>

      {/* Okno „Dostosuj kolumny" dla Rozrachunki */}
      <Backdrop open={colsOpenSettle} sx={{ color: '#fff', zIndex: (theme: any) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{ position: 'fixed', bottom: 0, right: 0, height: '50vh', width: '280px', display: 'flex', flexDirection: 'column', boxShadow: '0px 5px 30px rgba(0,0,0,0.15)', borderRadius: 0, zIndex: (theme) => theme.zIndex.modal + 2, backgroundColor: '#fff', pointerEvents: 'auto' }}
          onClick={(e)=> e.stopPropagation()}
        >
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={()=>{ setColsAnchorElSettle(null); setDraftColumnsVisibleSettle(null); setDraftOrderSettle(null); setColResizeMode(false); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisibleSettle ?? columnsVisibleSettle;
              const ord = draftOrderSettle ?? columnOrderSettle;
              return ord.map((key) => (
                <Box
                  key={key}
                  sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = e.dataTransfer.getData('text/plain');
                    if (!src || src === key) return;
                    setDraftOrderSettle((prev) => {
                      const cur = [...(prev || ord)];
                      const from = cur.indexOf(src);
                      const to = cur.indexOf(key);
                      if (from < 0 || to < 0) return cur;
                      cur.splice(to, 0, cur.splice(from, 1)[0]);
                      return cur;
                    });
                  }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setDraftColumnsVisibleSettle(prev=>({ ...(prev||list), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisibleSettle ?? columnsVisibleSettle)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                        {settleColLabel(key)}
                      </Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ setColsAnchorElSettle(null); setDraftColumnsVisibleSettle(null); setDraftOrderSettle(null); setColResizeMode(false); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{ const vis = draftColumnsVisibleSettle ?? columnsVisibleSettle; const ord = draftOrderSettle ?? columnOrderSettle; setColumnsVisibleSettle(vis); setColumnOrderSettle(ord); saveSettleColSettings(settlementsTab, vis, ord, columnWidthsSettle); setColsAnchorElSettle(null); setDraftColumnsVisibleSettle(null); setDraftOrderSettle(null); setColResizeMode(false); }}>Zapisz zmiany</Button>
          </Box>
        </Paper>
      </Backdrop>

      {/* Okno „Dostosuj kolumny" dla Faktury Sprzedażowe */}
      <Backdrop open={colsOpenSales} sx={{ color: '#fff', zIndex: (theme: any) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{ position: 'fixed', bottom: 0, right: 0, height: '50vh', width: '280px', display: 'flex', flexDirection: 'column', boxShadow: '0px 5px 30px rgba(0,0,0,0.15)', borderRadius: 0, zIndex: (theme) => theme.zIndex.modal + 2, backgroundColor: '#fff', pointerEvents: 'auto' }}
          onClick={(e)=> e.stopPropagation()}
        >
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={()=>{ setColsAnchorElSales(null); setDraftColumnsVisibleSales(null); setDraftOrderSales(null); setColResizeMode(false); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisibleSales ?? columnsVisibleSales;
              if (draftColumnsVisibleSales == null) setDraftColumnsVisibleSales({ ...columnsVisibleSales });
              const ord = draftOrderSales ?? columnOrderSales;
              if (draftOrderSales == null) setDraftOrderSales([...columnOrderSales]);
              return ord.map((key) => (
                <Box
                  key={key}
                  sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = e.dataTransfer.getData('text/plain');
                    if (!src || src === key) return;
                    setDraftOrderSales((prev) => {
                      const cur = [...(prev || ord)];
                      const from = cur.indexOf(src);
                      const to = cur.indexOf(key);
                      if (from < 0 || to < 0) return cur;
                      cur.splice(to, 0, cur.splice(from, 1)[0]);
                      return cur;
                    });
                  }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setDraftColumnsVisibleSales(prev=>({ ...(prev||list), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisibleSales ?? columnsVisibleSales)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                        {key === 'dates' ? 'DATY' :
                         key === 'invoiceType' ? 'RODZAJ' :
                         key === 'invoiceNumber' ? 'NR FAKTURY' :
                         key === 'payment' ? 'Płatność' :
                         key === 'seller' ? 'Nabywca' :
                         key === 'nip' ? 'NIP' :
                         key === 'net' ? 'Netto' :
                         key === 'vatRate' ? 'Stawka' :
                         key === 'vatAmount' ? 'VAT' :
                         key === 'gross' ? 'Brutto' :
                         key === 'status' ? 'STATUS' :
                         key === 'actions' ? 'AKCJE' : key.toUpperCase()}
                      </Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ setColsAnchorElSales(null); setDraftColumnsVisibleSales(null); setDraftOrderSales(null); setColResizeMode(false); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{ const vis = draftColumnsVisibleSales ?? columnsVisibleSales; setColumnsVisibleSales(vis); try { localStorage.setItem(VISIBLE_KEY_SALES, JSON.stringify(vis)); } catch {} if (draftOrderSales) { setColumnOrderSales(draftOrderSales); try { localStorage.setItem(ORDER_KEY_SALES, JSON.stringify(draftOrderSales)); } catch {} } setColsAnchorElSales(null); setDraftColumnsVisibleSales(null); setDraftOrderSales(null); setColResizeMode(false); }}>Zapisz zmiany</Button>
          </Box>
        </Paper>
      </Backdrop>

      {/* Okno „Dostosuj kolumny" dla Operacje Bankowe */}
      <Backdrop open={colsOpenBankOps} sx={{ color: '#fff', zIndex: (theme: any) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{ position: 'fixed', bottom: 0, right: 0, height: '50vh', width: '280px', display: 'flex', flexDirection: 'column', boxShadow: '0px 5px 30px rgba(0,0,0,0.15)', borderRadius: 0, zIndex: (theme) => theme.zIndex.modal + 2, backgroundColor: '#fff', pointerEvents: 'auto' }}
          onClick={(e)=> e.stopPropagation()}
        >
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={()=>{ setColsAnchorElBankOps(null); setDraftColumnsVisibleBankOps(null); setDraftOrderBankOps(null); setColResizeMode(false); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisibleBankOps ?? columnsVisibleBankOps;
              if (draftColumnsVisibleBankOps == null) setDraftColumnsVisibleBankOps({ ...columnsVisibleBankOps });
              const ord = draftOrderBankOps ?? columnOrderBankOps;
              if (draftOrderBankOps == null) setDraftOrderBankOps([...columnOrderBankOps]);
              return ord.map((key) => (
                <Box
                  key={key}
                  sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = e.dataTransfer.getData('text/plain');
                    if (!src || src === key) return;
                    setDraftOrderBankOps((prev) => {
                      const cur = [...(prev || ord)];
                      const from = cur.indexOf(src);
                      const to = cur.indexOf(key);
                      if (from < 0 || to < 0) return cur;
                      cur.splice(to, 0, cur.splice(from, 1)[0]);
                      return cur;
                    });
                  }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setDraftColumnsVisibleBankOps(prev=>({ ...(prev||list), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisibleBankOps ?? columnsVisibleBankOps)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                        {bankOpsColLabel(key, bankOpsMainTab === 1)}
                      </Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ setColsAnchorElBankOps(null); setDraftColumnsVisibleBankOps(null); setDraftOrderBankOps(null); setColResizeMode(false); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{ const vis = draftColumnsVisibleBankOps ?? columnsVisibleBankOps; const ord = draftOrderBankOps ?? columnOrderBankOps; setColumnsVisibleBankOps(vis); setColumnOrderBankOps(ord); saveBankOpsColSettings(bankOpsMainTab, bankOpsSubTab, vis, ord, columnWidthsBankOps); setColsAnchorElBankOps(null); setDraftColumnsVisibleBankOps(null); setDraftOrderBankOps(null); setColResizeMode(false); }}>Zapisz zmiany</Button>
          </Box>
        </Paper>
      </Backdrop>

      {/* Drawer rozliczania faktury */}
      <SettleInvoiceDrawer
        open={settleDrawerOpen}
        onClose={() => { setSettleDrawerOpen(false); setSettleDrawerData(null); setSettleDrawerViewMode(false); }}
        invoiceData={settleDrawerData}
        viewMode={settleDrawerViewMode}
        onSettle={async (id, data) => {
          await settleInvoice(Number(id), {
            paid_date: data.paid_date,
            payment_method: data.payment_method,
            currency: data.currency,
            amount_to_pay: data.amount_to_pay,
            amount_vat: data.amount_vat,
            amount_from_vat_account: data.amount_from_vat_account,
            transfer_title: data.transfer_title,
            unitresco_account: data.unitresco_account,
          });
          refreshSettlements();
        }}
        onUnsettle={async (id) => {
          await unsettleInvoice(Number(id));
          refreshSettlements();
        }}
      />
    </Box>
  );
};

export default InvoicesPage;


